<template>
    <div class="flow-main-container">
        <v-container fluid class="flow-container-fluid">
            <v-card class="card2">
                <v-btn class="floating-back-btn mt-6" @click="goBack" variant="elevated" color="white">
                    <v-icon color="primary" size="20">mdi-arrow-left</v-icon>
                    <span class="back-text">Back</span>
                </v-btn>
                    <!-- Undo/Redo buttons -->
                    <div class="undo-redo-container mr-2 mt-6" v-if="!isViewMode">
                        <v-tooltip location="bottom">
                            <template v-slot:activator="{ props }">
                                <v-btn v-bind="props"
                                       @click="undo" 
                                       :disabled="!canUndo" 
                                       variant="outlined" 
                                       size="small" 
                                       color="primary"
                                       class="undo-btn mr-1">
                                    <v-icon size="16">mdi-undo</v-icon>
                                </v-btn>
                            </template>
                            <span>
                                Undo {{ historyIndex >= 0 ? `(${history[historyIndex]?.action || 'Unknown'})` : '(None)' }}
                                <br><small><kbd>Ctrl</kbd> + <kbd>Z</kbd></small>
                            </span>
                        </v-tooltip>
                        <v-tooltip location="bottom">
                            <template v-slot:activator="{ props }">
                                <v-btn v-bind="props"
                                       @click="redo" 
                                       :disabled="!canRedo" 
                                       variant="outlined" 
                                       size="small" 
                                       color="primary"
                                       class="redo-btn">
                                    <v-icon size="16">mdi-redo</v-icon>
                                </v-btn>
                            </template>
                            <span>
                                Redo {{ historyIndex + 1 < history.length ? `(${history[historyIndex + 1]?.action || 'Unknown'})` : '(None)' }}
                                <br><small><kbd>Ctrl</kbd> + <kbd>Y</kbd></small>
                            </span>
                        </v-tooltip>
                    </div>
                <!-- Flow Toolbar -->
                <div class="flow-toolbar mt-8">
                    <div class="toolbar-left">
                        <div class="flow-info">
                            <v-text-field v-model="flowName" label="Flow Name" variant="outlined" density="compact"
                                hide-details="auto" class="modern-input flow-name-input" :readonly="isViewMode"
                                @focus="handleFlowNameFocus" />
                            <div v-if="!isViewMode" class="flow-actions">
                                <!-- Additional Actions -->
                                <v-menu offset-y>
                                    <template v-slot:activator="{ props }">
                                        <v-btn v-bind="props" variant="text" icon size="small" class="more-actions-btn">
                                            <v-icon>mdi-dots-vertical</v-icon>
                                        </v-btn>
                                    </template>
                                    <v-list density="compact" class="menu-list">
                                        <v-list-item @click="exportFlow" :disabled="!tempNode.length">
                                            <template v-slot:prepend>
                                                <v-icon>mdi-export</v-icon>
                                            </template>
                                            <v-list-item-title>Export Flow</v-list-item-title>
                                        </v-list-item>
                                        <v-list-item @click="importFlow">
                                            <template v-slot:prepend>
                                                <v-icon>mdi-import</v-icon>
                                            </template>
                                            <v-list-item-title>Import Flow</v-list-item-title>
                                        </v-list-item>
                                        <div class="menu-divider"></div>
                                        <v-list-item @click="handleDuplicateSelectedNodes" 
                                                   :disabled="!selectedNodes.length" 
                                                   class="text-primary">
                                            <template v-slot:prepend>
                                                <v-icon color="primary">mdi-content-duplicate</v-icon>
                                            </template>
                                            <v-list-item-title>Duplicate Selected ({{ selectedNodes.length }})</v-list-item-title>
                                        </v-list-item>
                                        <v-list-item @click="handleCopySelectedNodes" 
                                                   :disabled="!selectedNodes.length" 
                                                   class="text-info">
                                            <template v-slot:prepend>
                                                <v-icon color="info">mdi-content-copy</v-icon>
                                            </template>
                                            <v-list-item-title>Copy Selected ({{ selectedNodes.length }})</v-list-item-title>
                                        </v-list-item>
                                        <v-list-item @click="handlePasteNodes" 
                                                   :disabled="!copiedNodes.length" 
                                                   class="text-info">
                                            <template v-slot:prepend>
                                                <v-icon color="info">mdi-content-paste</v-icon>
                                            </template>
                                            <v-list-item-title>Paste ({{ copiedNodes.length }})</v-list-item-title>
                                        </v-list-item>
                                        <v-list-item @click="handleDeleteSelectedNodes" 
                                                   :disabled="!selectedNodes.length" 
                                                   class="text-error">
                                            <template v-slot:prepend>
                                                <v-icon color="error">mdi-delete</v-icon>
                                            </template>
                                            <v-list-item-title>Delete Selected ({{ selectedNodes.length }})</v-list-item-title>
                                        </v-list-item>
                                        <v-list-item @click="clearFlow" :disabled="!tempNode.length" class="text-error">
                                            <template v-slot:prepend>
                                                <v-icon color="error">mdi-delete-sweep</v-icon>
                                            </template>
                                            <v-list-item-title>Clear Flow</v-list-item-title>
                                        </v-list-item>
                                    </v-list>
                                </v-menu>
                                <!-- Save Button -->
                                <div>
                                    <v-btn @click="openSaveDialog" :disabled="!tempNode.length" color="success"
                                        class="save-btn" size="small">
                                        <v-icon size="16">mdi-content-save</v-icon>
                                        <span class="btn-text">Save</span>
                                    </v-btn>
                                </div>
                            </div>
                            <div v-else class="ml-4">
                                <v-btn variant="text" size="small" class="view-mode-btn" readonly>
                                    <v-icon size="16">mdi-eye</v-icon>
                                    <span class="btn-text">View Mode</span>
                                </v-btn>
                            </div>
                        </div>
                    </div>
                    <!-- <div class="toolbar-actions">
                        <div>
                            <span class="flow-status" v-if="hasUnsavedChanges">
                                <v-icon size="small" color="warning">mdi-circle</v-icon>
                                Unsaved changes
                            </span>
                            <span class="flow-status saved" v-else>
                                <v-icon size="small" color="green">mdi-check-circle</v-icon>
                                Saved
                            </span>
                        </div>
                        <v-btn @click="toggleLockStatus" :color="interactiveControls ? 'success' : 'error'"
                            :variant="interactiveControls ? 'outlined' : 'elevated'" size="small" class="lock-btn">
                            <v-icon size="16">{{ interactiveControls ? 'mdi-lock-open' : 'mdi-lock' }}</v-icon>
                            <span class="btn-text">{{ interactiveControls ? 'Unlocked' : 'Locked' }}</span>
                        </v-btn>
                    </div> -->
                </div>
                <!-- Vue Flow Container -->
                <div class="flow-container" @drop="onDrop" @dragover.prevent @dragenter.prevent 
                     @keydown="handleKeyDown" @click="focusFlowContainer" tabindex="0" ref="flowContainer">
                    <VueFlow ref="vueFlow" :nodes="tempNode" v-model:edges="tempEdge" :default-viewport="{ zoom: 1.2 }"
                        :snap-to-grid="false" :min-zoom="0.2" :max-zoom="2" class="basic-flow" :pan-on-drag="[2]"
                        :selection-key-code="true" :multi-selection-key-code="true" :only-render-visible-elements="true" 
                        @nodes-change="onNodesChange" @edges-change="onEdgesChange" @node-selection-change="onSelectionChange" 
                        @connect="onConnect" @edge-mouse-enter="onEdgeMouseEnter" @edge-mouse-leave="onEdgeMouseLeave"
                        @node-click="onNodeClick" @node-drag-start="onNodeDragStart" @node-drag-stop="onNodeDragStop"
                        @pane-click="onPaneClick"
                        :fitViewOnInit="true">

                        <template #node-flow-node="props">
                            <FlowNode :id="props.id" :data="props.data" :key="props.id"
                                @delete-node="handleDeleteNode" @update:data="handleNodeDataUpdate"
                                @remove-outgoing-edge="handleRemoveOutgoingEdge" />
                        </template>

                        <template #edge-flow-edge="buttonEdgeProps">
                            <FlowEdge v-bind="buttonEdgeProps" :id="buttonEdgeProps.id"
                                :source-x="buttonEdgeProps.sourceX" :source-y="buttonEdgeProps.sourceY"
                                :target-x="buttonEdgeProps.targetX" :target-y="buttonEdgeProps.targetY"
                                :source-position="buttonEdgeProps.sourcePosition"
                                :target-position="buttonEdgeProps.targetPosition"
                                :marker-end="buttonEdgeProps.markerEnd" :style="buttonEdgeProps.style" />
                        </template>

                        <Background patternColor="rgba(0, 0, 0, 0.4)" :gap="16" />
                        <Controls :show-interactive="false" v-if="!lockStatus" />
                        
                        <!-- Selection Info -->
                        <div v-if="selectedNodes.length > 0 && !isViewMode" class="selection-info">
                            <v-chip color="#0080ff7a" variant="elevated" size="small" class="ml-2">
                                <v-icon size="16" class="mr-1">mdi-cursor-default-click</v-icon>
                                {{ selectedNodes.length }} node{{ selectedNodes.length > 1 ? 's' : '' }} selected
                            </v-chip>
                            <v-chip color="primary" variant="text" size="small">
                                <v-icon size="16" class="mr-1">mdi-content-duplicate</v-icon>
                                Ctrl+D duplicate
                            </v-chip>
                            <v-chip color="error" variant="text" size="small">
                                <v-icon size="16" class="mr-1">mdi-delete</v-icon>
                                Del to remove
                            </v-chip>
                        </div>
                    </VueFlow>
                </div>
            </v-card>
        </v-container>

        <!-- Floating Node Panel -->
        <NodePanel v-if="!isViewMode" @add-node="addNewNode" class="node-panel" />
        <!-- Floating Node Inspector Panel -->
        <div v-if="selectedNode && selectedNodes.length == 1" class="floating-inspector"
            :class="{ 'inspector-expanded': inspectorExpanded }">
            <!-- Inspector Header -->
            <div class="inspector-header">
                <div class="inspector-title">
                    <v-icon class="mr-2" color="white">mdi-cog</v-icon>
                    <span class="text-h6">Node Properties</span>
                </div>
                <div class="inspector-controls">
                    <v-btn icon size="small" @click="inspectorExpanded = !inspectorExpanded" class="mr-1">
                        <v-icon color="white">{{ inspectorExpanded ? 'mdi-fullscreen-exit' : 'mdi-fullscreen' }}</v-icon>
                    </v-btn>
                    <v-btn icon size="small" @click="closeInspector">
                        <v-icon color="white">mdi-close</v-icon>
                    </v-btn>
                </div>
            </div>
            <!-- Inspector Content -->
            <div class="inspector-content">
                <v-form ref="inspectorForm">
                    
                    <!-- invisible dummy fields to trap browser autofill (Edge/Chrome) -->
                    <div style="position:absolute; left:-9999px; opacity:0; height:0; overflow:hidden;">
                        <input type="text" name="prevent_autofill_username" autocomplete="username" tabindex="-1" />
                        <input type="password" name="prevent_autofill_password" autocomplete="current-password" tabindex="-1" />
                    </div>

                    <!-- Node Type Badge -->
                    <div class="node-type-badge mb-4">
                        <v-chip :color="getNodeTypeColor(selectedNode.data.type)" variant="elevated" size="large"
                            class="text-subtitle-1 font-weight-bold header-chip">
                            <v-icon class="mr-2" size="20">{{ getNodeTypeIcon(selectedNode.data.type) }}</v-icon>
                            {{ getNodeTypeLabel(selectedNode.data.type) }}
                        </v-chip>
                    </div>

                    <!-- Common Properties -->
                    <!-- <div class="form-section">
                        <h6 class="section-title">General</h6>
                        <v-text-field v-model="nodeInspector.label" label="Node Label" variant="outlined" disabled
                            density="compact" hide-details="auto" :rules="[v => !!v || 'Label is required']"
                            @update:model-value="updateNodeProperty('label', $event)" class="modern-input" />
                    </div> -->
                    <!-- Node Type Specific Properties -->
                    <div v-if="selectedNode.data.type === 'messageInput'" class="form-section">
                        <h6 class="section-title">Chat AI Model Configuration</h6>
                        <v-autocomplete v-model="nodeInspector.selectedModel" :items="uniqueAiModelOptions"
                            item-title="searchableText" item-value="value" label="Flow AI Model" variant="outlined"
                            density="compact" hide-details="auto"
                            @update:model-value="updateNodeProperty('selectedModel', $event)" class="modern-input mb-3"
                            :clearable="!isViewMode" :readonly="isViewMode" chips multiple
                            :custom-filter="(value, query, item) => {
                                if (!query) return true;
                                const searchQuery = query.toLowerCase();
                                const itemValue = item?.raw?.value?.toString().toLowerCase() || '';
                                const itemName = item?.raw?.name?.toLowerCase() || '';
                                return itemValue.includes(searchQuery) || itemName.includes(searchQuery);
                            }">
                            <template v-slot:item="{ props, item }">
                                <v-list-item v-bind="props" class="model-option"
                                    :title="item.raw.value + ': ' + item.raw.name">
                                    <template v-slot:prepend>
                                        <div class="model-icon-small mr-2" style="background-color: #3b82f6;">
                                            <v-icon size="14" color="white">{{ item.raw.icon }}</v-icon>
                                        </div>
                                    </template>
                                </v-list-item>
                            </template>
                            <template v-slot:chip="{ props, item }">
                                <v-chip v-bind="props" :text="item.raw.value + ': ' + item.raw.name"></v-chip>
                            </template>
                        </v-autocomplete>
                    </div>

                    <div v-if="selectedNode.data.type === 'model'" class="form-section">
                        <!-- Header Section -->
                        <div class="config-header mb-6">
                            <div class="d-flex align-center mb-2">
                                <v-icon icon="mdi-robot" size="24" color="primary" class="me-3"></v-icon>
                                <h5 class="text-h6 font-weight-bold text-primary mb-0">AI Model Configuration</h5>
                            </div>
                            <p class="text-body-2 text-medium-emphasis mb-0">Configure your AI model settings and
                                parameters</p>
                        </div>

                        <!-- Action Buttons -->
                        <div class="action-buttons-container mb-4">
                            <!-- Load from Model Button (First Row) -->
                            <div class="load-model-button-wrapper mb-3">
                                <v-btn @click="openLoadFromModelDialog" color="success" variant="tonal" size="small"
                                    prepend-icon="mdi-download" :disabled="isViewMode" class="load-model-btn">
                                    Load from Model
                                </v-btn>
                            </div>

                            <!-- Use Base Model Prompt Toggle (Second Row) -->
                            <div class="base-prompt-toggle-section">
                                <v-card variant="outlined" class="pa-3" color="green-lighten-5" style="border-radius: 12px;">
                                    <div class="d-flex align-center">
                                        <v-avatar size="24"
                                            color="green"
                                            class="me-3">
                                            <v-icon
                                                color="white"
                                                size="12">mdi-text-box-check</v-icon>
                                        </v-avatar>
                                        <div class="flex-grow-1 me-3">
                                            <div class="text-subtitle-2"><span style="color:#4CAF50;font-weight: 500;"> Use Base Model Prompt</span></div>
                                            <div class="text-caption text-medium-emphasis">Combine with the default model prompt</div>
                                        </div>
                                        <v-switch v-model="nodeInspector.useBaseModelPrompt" color="green"
                                            hide-details density="compact"
                                            @update:model-value="updateNodeProperty('useBaseModelPrompt', $event)"
                                            :readonly="isViewMode" inset />
                                    </div>
                                </v-card>
                            </div>
                        </div>

                        <!-- Model Configuration Panels -->
                        <v-expansion-panels v-model="modelConfigPanels" multiple class="config-panels elevation-0"
                            variant="accordion">
                            <!-- Basic Information Panel -->
                            <v-expansion-panel value="basic" class="panel-item">
                                <v-expansion-panel-title class="panel-title-custom">
                                    <div class="d-flex align-center py-3">
                                        <div class="panel-icon-wrapper me-4">
                                            <v-icon icon="mdi-information" size="20" color="white"></v-icon>
                                        </div>
                                        <div>
                                            <div class="panel-title">Basic Information</div>
                                            <div class="panel-subtitle">Provider, model, and basic settings</div>
                                        </div>
                                    </div>
                                </v-expansion-panel-title>
                                <v-expansion-panel-text class="panel-content-wrapper">
                                    <div class="panel-content">
                                        <!-- Provider and Model -->
                                        <div class="config-section mb-6">
                                            <h6 class="section-label mb-3">Service Configuration</h6>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-autocomplete v-model="nodeInspector.provider"
                                                        :items="listDDProvider" item-title="providerName"
                                                        item-value="providerName" label="Provider*" variant="outlined"
                                                        density="comfortable" hide-details="auto"
                                                        prepend-inner-icon="mdi-cloud-outline"
                                                        @update:model-value="updateNodeProperty('provider', $event); changeProvider($event)"
                                                        class="config-input" :readonly="isViewMode"
                                                        hint="Select AI service provider" persistent-hint required />
                                                </v-col>
                                            </v-row>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-autocomplete v-model="nodeInspector.model" :items="DDModelShow"
                                                        item-title="modelDisplay" item-value="modelName" label="Model*"
                                                        variant="outlined" density="comfortable" hide-details="auto"
                                                        prepend-inner-icon="mdi-brain"
                                                        @update:model-value="updateNodeProperty('model', $event)"
                                                        class="config-input" :readonly="isViewMode"
                                                        :disabled="!nodeInspector.provider"
                                                        hint="Select specific model version" persistent-hint required />
                                                </v-col>
                                            </v-row>
                                        </div>

                                        <!-- Behavior Settings -->
                                        <div class="config-section">
                                            <h6 class="section-label mb-3">Behavior Settings</h6>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-select v-model="nodeInspector.gender"
                                                        :items="[{ id: 'male', name: 'Male' }, { id: 'female', name: 'Female' }]"
                                                        item-title="name" item-value="id" label="Gender"
                                                        variant="outlined" density="comfortable" hide-details="auto"
                                                        prepend-inner-icon="mdi-account"
                                                        @update:model-value="updateNodeProperty('gender', $event)"
                                                        class="config-input" :readonly="isViewMode"
                                                        hint="Voice/persona gender preference" persistent-hint
                                                        :model-value="nodeInspector.gender || 'male'" />
                                                </v-col>
                                            </v-row>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-text-field v-model.number="nodeInspector.typingSpeed"
                                                        label="Typing Speed" type="number" min="0" variant="outlined"
                                                        density="comfortable" hide-details="auto"
                                                        prepend-inner-icon="mdi-keyboard-outline"
                                                        @update:model-value="updateNodeProperty('typingSpeed', $event)"
                                                        @keypress="isNumberpositive($event)" class="config-input"
                                                        :readonly="isViewMode"
                                                        hint="Characters per second (0 = instant)" persistent-hint />
                                                </v-col>
                                            </v-row>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-text-field v-model.number="nodeInspector.maxInputLength"
                                                        label="Max Input Length" type="number" min="0" max="200000"
                                                        variant="outlined" density="comfortable" hide-details="auto"
                                                        prepend-inner-icon="mdi-text-box-outline"
                                                        @update:model-value="updateNodeProperty('maxInputLength', $event)"
                                                        @keypress="maxNumberpositive($event)"
                                                        @focusin="validateAndCorrectValue($event, 1, 200000)"
                                                        @focusout="validateAndCorrectValue($event, 1, 200000)"
                                                        @keydown.enter="validateAndCorrectValue($event, 1, 200000)"
                                                        class="config-input" :readonly="isViewMode"
                                                        hint="Maximum characters allowed in user input"
                                                        persistent-hint />
                                                </v-col>
                                            </v-row>
                                        </div>
                                    </div>
                                </v-expansion-panel-text>
                            </v-expansion-panel>

                            <!-- Response Options Panel -->
                            <v-expansion-panel value="response" class="panel-item">
                                <v-expansion-panel-title class="panel-title-custom">
                                    <div class="d-flex align-center py-3">
                                        <div class="panel-icon-wrapper me-4"
                                            style="background: linear-gradient(135deg, #FF9800, #F57C00);">
                                            <v-icon icon="mdi-tune" size="20" color="white"></v-icon>
                                        </div>
                                        <div>
                                            <div class="panel-title">Response Options</div>
                                            <div class="panel-subtitle">Configure how responses are generated</div>
                                        </div>
                                    </div>
                                </v-expansion-panel-title>
                                <v-expansion-panel-text class="panel-content-wrapper">
                                    <div class="panel-content pa-4">
                                        <!-- Stream Response -->
                                        <div class="switch-option-compact mb-3">
                                            <div class="d-flex align-center justify-space-between py-2 px-3 rounded-lg"
                                                :class="{ 'switch-active-bg': nodeInspector.streamResponse }">
                                                <div class="d-flex align-center">
                                                    <v-avatar size="28"
                                                        :color="nodeInspector.streamResponse ? 'primary' : 'grey-lighten-1'"
                                                        class="me-3">
                                                        <v-icon
                                                            :color="nodeInspector.streamResponse ? 'white' : 'grey-darken-1'"
                                                            size="16">mdi-text-box-outline</v-icon>
                                                    </v-avatar>
                                                    <div>
                                                        <div class="switch-title">Stream Response</div>
                                                        <div class="switch-description">Progressive text generation
                                                        </div>
                                                    </div>
                                                </div>
                                                <v-switch v-model="nodeInspector.streamResponse" color="primary"
                                                    hide-details density="compact"
                                                    @update:model-value="updateNodeProperty('streamResponse', $event)"
                                                    :readonly="isViewMode" inset />
                                            </div>
                                        </div>

                                        <!-- Time Context -->
                                        <div class="switch-option-compact">
                                            <div class="d-flex align-center justify-space-between py-2 px-3 rounded-lg"
                                                :class="{ 'switch-active-bg': nodeInspector.enableTime }">
                                                <div class="d-flex align-center">
                                                    <v-avatar size="28"
                                                        :color="nodeInspector.enableTime ? 'success' : 'grey-lighten-1'"
                                                        class="me-3">
                                                        <v-icon
                                                            :color="nodeInspector.enableTime ? 'white' : 'grey-darken-1'"
                                                            size="16">mdi-clock-outline</v-icon>
                                                    </v-avatar>
                                                    <div>
                                                        <div class="switch-title">Time Context</div>
                                                        <div class="switch-description">Enable time awareness</div>
                                                    </div>
                                                </div>
                                                <v-switch v-model="nodeInspector.enableTime" color="success"
                                                    hide-details density="compact"
                                                    @update:model-value="updateNodeProperty('enableTime', $event)"
                                                    :readonly="isViewMode" inset />
                                            </div>

                                            <!-- Time Zone Dropdown (shown when enableTime is true) -->
                                            <div v-if="nodeInspector.enableTime" class="mt-3">
                                                <v-select v-model="nodeInspector.timeZone" :items="DDTimeZone"
                                                    item-title="name" item-value="value" label="Time Zone"
                                                    variant="outlined" density="comfortable" hide-details="auto"
                                                    prepend-inner-icon="mdi-earth"
                                                    @update:model-value="updateNodeProperty('timeZone', $event)"
                                                    class="config-input" :readonly="isViewMode"
                                                    hint="Select your timezone" persistent-hint
                                                    placeholder="Select your timezone">
                                                    <template v-slot:selection="{ item }">
                                                        <div class="d-flex align-center">
                                                            <v-icon small color="primary"
                                                                class="me-2">mdi-map-marker</v-icon>
                                                            <span class="font-weight-medium">{{ item.raw.name }}</span>
                                                            <v-chip size="x-small" color="grey-darken-1"
                                                                text-color="white" class="ml-2">
                                                                {{ item.raw.value.split('/')[1].replace('_', ' ') }}
                                                            </v-chip>
                                                        </div>
                                                    </template>
                                                </v-select>
                                            </div>
                                        </div>

                                        <!-- Json Response -->
                                        <div class="switch-option-compact mt-3">
                                            <div class="d-flex align-center justify-space-between py-2 px-3 rounded-lg"
                                                :class="{ 'switch-active-bg': nodeInspector.jsonResponse }">
                                                <div class="d-flex align-center">
                                                    <v-avatar size="28"
                                                        :color="nodeInspector.jsonResponse ? 'primary' : 'grey-lighten-1'"
                                                        class="me-3">
                                                        <v-icon
                                                            :color="nodeInspector.jsonResponse ? 'white' : 'grey-darken-1'"
                                                            size="16">mdi-code-json</v-icon>
                                                    </v-avatar>
                                                    <div>
                                                        <div class="switch-title">JSON Response</div>
                                                        <div class="switch-description">Return structured JSON format
                                                        </div>
                                                    </div>
                                                </div>
                                                <v-switch v-model="nodeInspector.jsonResponse" color="primary"
                                                    hide-details density="compact"
                                                    @update:model-value="updateNodeProperty('jsonResponse', $event)"
                                                    :readonly="isViewMode" inset />
                                            </div>
                                        </div>
                                    </div>
                                </v-expansion-panel-text>
                            </v-expansion-panel>

                            <!-- Memory Settings Panel -->
                            <v-expansion-panel value="memory" class="panel-item">
                                <v-expansion-panel-title class="panel-title-custom">
                                    <div class="d-flex align-center py-3">
                                        <div class="panel-icon-wrapper me-4"
                                            style="background: linear-gradient(135deg, #00BCD4, #0097A7);">
                                            <v-icon icon="mdi-brain" size="20" color="white"></v-icon>
                                        </div>
                                        <div>
                                            <div class="panel-title">Memory Settings</div>
                                            <div class="panel-subtitle">Configure conversation memory and context</div>
                                        </div>
                                    </div>
                                </v-expansion-panel-title>
                                <v-expansion-panel-text class="panel-content-wrapper">
                                    <div class="panel-content">
                                        <div class="config-section mb-3">
                                            <h6 class="section-label mb-3">Conversation Memory</h6>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-text-field v-model.number="nodeInspector.memoryTurn"
                                                        label="Turn Count" type="number" min="0" variant="outlined"
                                                        density="comfortable" hide-details="auto"
                                                        prepend-inner-icon="mdi-history"
                                                        @update:model-value="updateNodeProperty('memoryTurn', $event)"
                                                        @keypress="isNumberpositive($event)" class="config-input"
                                                        :readonly="isViewMode"
                                                        hint="Number of conversation turns to remember"
                                                        persistent-hint />
                                                </v-col>
                                            </v-row>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-text-field v-model.number="nodeInspector.memoryDuration"
                                                        label="Duration (minutes)" type="number" min="0"
                                                        variant="outlined" density="comfortable" hide-details="auto"
                                                        prepend-inner-icon="mdi-timer-outline"
                                                        @update:model-value="updateNodeProperty('memoryDuration', $event)"
                                                        @keypress="isNumberpositive($event)" class="config-input"
                                                        :readonly="isViewMode" hint="How long to keep memory active"
                                                        persistent-hint />
                                                </v-col>
                                            </v-row>
                                        </div>

                                        <div class="config-section mb-3">
                                            <h6 class="section-label mb-3">Memory Types</h6>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-text-field v-model.number="nodeInspector.shortTermMemory"
                                                        label="Short-Term Memory" type="number" min="1"
                                                        variant="outlined" density="comfortable" hide-details="auto"
                                                        prepend-inner-icon="mdi-memory"
                                                        @update:model-value="updateNodeProperty('shortTermMemory', $event)"
                                                        @keypress="isNumberpositive($event)" class="config-input"
                                                        :readonly="isViewMode" hint="Recent context window size"
                                                        persistent-hint />
                                                </v-col>
                                            </v-row>
                                            <v-row>
                                                <v-col>
                                                    <!-- Long-Term Memory Switch -->
                                                    <div class="switch-option-compact mb-4">
                                                        <div class="d-flex align-center justify-space-between py-2 px-3 rounded-lg"
                                                            :class="{ 'switch-active-bg': nodeInspector.longTermMemory }">
                                                            <div class="d-flex align-center">
                                                                <v-avatar size="28"
                                                                    :color="nodeInspector.longTermMemory ? 'success' : 'grey-lighten-1'"
                                                                    class="me-3">
                                                                    <v-icon
                                                                        :color="nodeInspector.longTermMemory ? 'white' : 'grey-darken-1'"
                                                                        size="16">mdi-database</v-icon>
                                                                </v-avatar>
                                                                <div>
                                                                    <div class="switch-title">Long-Term Memory</div>
                                                                    <div class="switch-description">Persistent storage
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            <v-switch v-model="nodeInspector.longTermMemory"
                                                                color="success" hide-details density="compact"
                                                                @update:model-value="updateNodeProperty('longTermMemory', $event)"
                                                                :readonly="isViewMode" inset />
                                                        </div>
                                                    </div>
                                                </v-col>
                                            </v-row>

                                        </div>

                                        <!-- Multi-turn Input Configuration -->
                                        <div class="config-section mb-3">
                                            <h6 class="section-label mb-3">Multi-turn Input Configuration</h6>
                                            <v-row>
                                                <v-col>
                                                    <!-- Enable Multi-Turn Input Switch -->
                                                    <div class="switch-option-compact mb-3">
                                                        <div class="d-flex align-center justify-space-between py-2 px-3 rounded-lg"
                                                            :class="{ 'switch-active-bg': nodeInspector.isChainMassage }">
                                                            <div class="d-flex align-center">
                                                                <v-avatar size="28"
                                                                    :color="nodeInspector.isChainMassage ? 'indigo' : 'grey-lighten-1'"
                                                                    class="me-3">
                                                                    <v-icon
                                                                        :color="nodeInspector.isChainMassage ? 'white' : 'grey-darken-1'"
                                                                        size="16">mdi-chat-processing</v-icon>
                                                                </v-avatar>
                                                                <div>
                                                                    <div class="switch-title">Enable Multi-Turn Input
                                                                    </div>
                                                                    <div class="switch-description">Allow continuous
                                                                        conversation</div>
                                                                </div>
                                                            </div>
                                                            <v-switch v-model="nodeInspector.isChainMassage"
                                                                color="indigo" hide-details density="compact"
                                                                @update:model-value="updateNodeProperty('isChainMassage', $event)"
                                                                :readonly="isViewMode" inset />
                                                        </div>
                                                    </div>
                                                </v-col>
                                            </v-row>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-text-field v-model.number="nodeInspector.lenChainMassage"
                                                        label="Sequence" type="number" min="1" variant="outlined"
                                                        density="comfortable" hide-details="auto"
                                                        prepend-inner-icon="mdi-numeric"
                                                        @update:model-value="updateNodeProperty('lenChainMassage', $event)"
                                                        @keypress="isNumberpositive($event)" class="config-input"
                                                        :readonly="isViewMode" :disabled="!nodeInspector.isChainMassage"
                                                        hint="Number of conversation turns in sequence" persistent-hint
                                                        clearable />
                                                </v-col>
                                            </v-row>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-text-field v-model.number="nodeInspector.timeSecondChainMassage"
                                                        label="Duration (seconds)" type="number" min="1"
                                                        variant="outlined" density="comfortable" hide-details="auto"
                                                        prepend-inner-icon="mdi-timer"
                                                        @update:model-value="updateNodeProperty('timeSecondChainMassage', $event)"
                                                        @keypress="isNumberpositive($event)" class="config-input"
                                                        :readonly="isViewMode" :disabled="!nodeInspector.isChainMassage"
                                                        hint="Time duration for multi-turn session" persistent-hint
                                                        clearable />
                                                </v-col>
                                            </v-row>
                                        </div>


                                    </div>
                                </v-expansion-panel-text>
                            </v-expansion-panel>

                            <!-- Parameters Panel -->
                            <v-expansion-panel value="parameters" class="panel-item">
                                <v-expansion-panel-title class="panel-title-custom">
                                    <div class="d-flex align-center py-3">
                                        <div class="panel-icon-wrapper me-4"
                                            style="background: linear-gradient(135deg, #9C27B0, #7B1FA2);">
                                            <v-icon icon="mdi-cog" size="20" color="white"></v-icon>
                                        </div>
                                        <div>
                                            <div class="panel-title">AI Parameters</div>
                                            <div class="panel-subtitle">Fine-tune model behavior and output</div>
                                        </div>
                                    </div>
                                </v-expansion-panel-title>
                                <v-expansion-panel-text class="panel-content-wrapper">
                                    <div class="panel-content">
                                        <div class="config-section mb-3">
                                            <h6 class="section-label mb-3">Core Parameters</h6>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-text-field v-model.number="nodeInspector.temperature"
                                                        label="Temperature*" type="number" step="0.1" min="0" max="1"
                                                        variant="outlined" density="comfortable" hide-details="auto"
                                                        prepend-inner-icon="mdi-thermometer"
                                                        @update:model-value="updateNodeProperty('temperature', $event)"
                                                        @keypress="isNumberpositive($event)" class="config-input"
                                                        :readonly="isViewMode"
                                                        hint="Creativity level (0 = focused, 1 = creative)"
                                                        persistent-hint />
                                                </v-col>
                                            </v-row>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-text-field v-model.number="nodeInspector.topP" label="Top P"
                                                        type="number" step="0.1" min="0" max="1" variant="outlined"
                                                        density="comfortable" hide-details="auto"
                                                        prepend-inner-icon="mdi-filter-variant"
                                                        @update:model-value="updateNodeProperty('topP', $event)"
                                                        @keypress="isNumberpositive($event)" class="config-input"
                                                        :readonly="isViewMode"
                                                        hint="Token selection probability threshold" persistent-hint />
                                                </v-col>
                                            </v-row>
                                        </div>

                                        <div class="config-section mb-3">
                                            <h6 class="section-label mb-3">Penalty Settings</h6>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-text-field v-model.number="nodeInspector.frequencyPenalty"
                                                        label="Frequency Penalty" type="number" step="0.1" min="0"
                                                        max="2" variant="outlined" density="comfortable"
                                                        hide-details="auto" prepend-inner-icon="mdi-repeat-off"
                                                        @update:model-value="updateNodeProperty('frequencyPenalty', $event)"
                                                        @keypress="isNumberpositive($event)" class="config-input"
                                                        :readonly="isViewMode"
                                                        hint="Reduce repetition of frequent tokens" persistent-hint />
                                                </v-col>
                                            </v-row>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-text-field v-model.number="nodeInspector.presencePenalty"
                                                        label="Presence Penalty" type="number" step="0.1" min="0"
                                                        max="2" variant="outlined" density="comfortable"
                                                        hide-details="auto"
                                                        prepend-inner-icon="mdi-minus-circle-outline"
                                                        @update:model-value="updateNodeProperty('presencePenalty', $event)"
                                                        @keypress="isNumberpositive($event)" class="config-input"
                                                        :readonly="isViewMode" hint="Reduce repetition of any tokens"
                                                        persistent-hint />
                                                </v-col>
                                            </v-row>
                                        </div>

                                        <div class="config-section">
                                            <h6 class="section-label mb-3">Output Control</h6>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-text-field v-model.number="nodeInspector.maxTokens"
                                                        label="Max Tokens Response" type="number" min="1"
                                                        variant="outlined" density="comfortable" hide-details="auto"
                                                        prepend-inner-icon="mdi-text-box-check-outline"
                                                        @update:model-value="updateNodeProperty('maxTokens', $event)"
                                                        @keypress="isNumberpositive($event)" class="config-input"
                                                        :readonly="isViewMode" hint="Maximum response length in tokens"
                                                        persistent-hint />
                                                </v-col>
                                            </v-row>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-text-field v-model="nodeInspector.stopSequence"
                                                        label="Stop Sequence" variant="outlined" density="comfortable"
                                                        hide-details="auto" prepend-inner-icon="mdi-stop-circle-outline"
                                                        @update:model-value="updateNodeProperty('stopSequence', $event)"
                                                        class="config-input" :readonly="isViewMode"
                                                        hint="Text sequence to stop generation" persistent-hint />
                                                </v-col>
                                            </v-row>
                                        </div>
                                    </div>
                                </v-expansion-panel-text>
                            </v-expansion-panel>

                            <!-- Line Configuration Panel -->
                            <v-expansion-panel value="line" class="panel-item">
                                <v-expansion-panel-title class="panel-title-custom">
                                    <div class="d-flex align-center py-3">
                                        <div class="panel-icon-wrapper me-4"
                                            style="background: linear-gradient(135deg, #4CAF50, #388E3C);">
                                            <v-icon icon="mdi-message" size="20" color="white"></v-icon>
                                        </div>
                                        <div>
                                            <div class="panel-title">Line Configuration</div>
                                            <div class="panel-subtitle">LINE messaging platform settings</div>
                                        </div>
                                    </div>
                                </v-expansion-panel-title>
                                <v-expansion-panel-text class="panel-content-wrapper">
                                    <div class="panel-content pa-4">
                                        <div class="config-section mb-3">
                                            <h6 class="section-label mb-3">Display Options</h6>

                                            <!-- Show Name -->
                                            <div class="switch-option-compact mb-3">
                                                <div class="d-flex align-center justify-space-between py-2 px-3 rounded-lg"
                                                    :class="{ 'switch-active-bg': nodeInspector.showLineName }">
                                                    <div class="d-flex align-center">
                                                        <v-avatar size="28"
                                                            :color="nodeInspector.showLineName ? 'info' : 'grey-lighten-1'"
                                                            class="me-3">
                                                            <v-icon
                                                                :color="nodeInspector.showLineName ? 'white' : 'grey-darken-1'"
                                                                size="16">mdi-account-circle</v-icon>
                                                        </v-avatar>
                                                        <div>
                                                            <div class="switch-title">Show Name</div>
                                                            <div class="switch-description">Display user name</div>
                                                        </div>
                                                    </div>
                                                    <v-switch v-model="nodeInspector.showLineName" color="info"
                                                        hide-details density="compact"
                                                        @update:model-value="updateNodeProperty('showLineName', $event)"
                                                        :readonly="isViewMode" inset />
                                                </div>
                                            </div>
                                            <!-- Loading Indicator -->
                                            <div class="switch-option-compact mb-3">
                                                <div class="d-flex align-center justify-space-between py-2 px-3 rounded-lg"
                                                    :class="{ 'switch-active-bg': nodeInspector.loadingBar }">
                                                    <div class="d-flex align-center">
                                                        <v-avatar size="28"
                                                            :color="nodeInspector.loadingBar ? 'success' : 'grey-lighten-1'"
                                                            class="me-3">
                                                            <v-icon
                                                                :color="nodeInspector.loadingBar ? 'white' : 'grey-darken-1'"
                                                                size="16">mdi-progress-clock</v-icon>
                                                        </v-avatar>
                                                        <div>
                                                            <div class="switch-title">Loading Indicator</div>
                                                            <div class="switch-description">Show typing animation</div>
                                                        </div>
                                                    </div>
                                                    <v-switch v-model="nodeInspector.loadingBar" color="success"
                                                        hide-details density="compact"
                                                        @update:model-value="updateNodeProperty('loadingBar', $event)"
                                                        :readonly="isViewMode" inset />
                                                </div>
                                            </div>

                                        </div>

                                        <div class="config-section mb-3">
                                            <h6 class="section-label mb-3">Loading & Duration</h6>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-select v-model="nodeInspector.loadingBarDuration"
                                                        :items="[10, 20, 30, 40, 50, 60]"
                                                        label="Loading Duration (seconds)" variant="outlined"
                                                        density="comfortable" hide-details="auto"
                                                        prepend-inner-icon="mdi-timer-sand"
                                                        @update:model-value="updateNodeProperty('loadingBarDuration', $event)"
                                                        class="config-input" :disabled="!nodeInspector.loadingBar"
                                                        :readonly="isViewMode"
                                                        hint="Duration for loading animation display" persistent-hint
                                                        clearable />
                                                </v-col>
                                            </v-row>
                                        </div>

                                        <div class="config-section mb-3">
                                            <h6 class="section-label mb-3">Monitoring</h6>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-text-field v-model="nodeInspector.monitorLineUserId"
                                                        label="Monitor Line User ID" variant="outlined"
                                                        density="comfortable" hide-details="auto"
                                                        prepend-inner-icon="mdi-monitor-eye"
                                                        @update:model-value="updateNodeProperty('monitorLineUserId', $event)"
                                                        class="config-input" :readonly="isViewMode"
                                                        hint="LINE user ID for monitoring notifications"
                                                        persistent-hint />
                                                </v-col>
                                            </v-row>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-autocomplete v-model="nodeInspector.monitorSocialID"
                                                        :items="listMonitorLineName" label="Monitor Line Name"
                                                        variant="outlined" density="comfortable" hide-details="auto"
                                                        prepend-inner-icon="mdi-chat" item-title="name" item-value="id"
                                                        @update:model-value="updateNodeProperty('monitorSocialID', $event)"
                                                        class="config-input" :readonly="isViewMode"
                                                        hint="Select monitor line name" persistent-hint clearable />
                                                </v-col>
                                            </v-row>
                                        </div>

                                        <div class="config-section">
                                            <h6 class="section-label mb-3">Content Filtering</h6>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-toolbar-title style="font-size: 16px">
                                                        Forbidden word
                                                    </v-toolbar-title>
                                                </v-col>
                                            </v-row>
                                            <div class="pr-1" style="
                                                    border: #dfe0e2 1px solid;
                                                    background: #fff;
                                                    margin-top: 10px;
                                                    border-radius: 8px;
                                                ">
                                                <v-row v-for="(item, index) in nodeInspector.forbiddenWords || ['']"
                                                    :key="index" style="margin: 0px">
                                                    <v-col cols="12" style="padding: 0px">
                                                        <div style="display: flex; align-items: center">
                                                            <div style="
                                                                    height: 50px;
                                                                    background: #f8f8f8;
                                                                    padding: 14px 20px 0 20px;
                                                                ">
                                                                {{ index + 1 }}
                                                            </div>
                                                            <v-textarea variant="solo" style="
                                                                    overflow: hidden;
                                                                    overflow-wrap: break-word;
                                                                    resize: none;
                                                                    height: 50px;
                                                                    width: 98%;
                                                                    border-radius: 0px;
                                                                    font-family: Fira code, Fira Mono, Consolas,
                                                                        Menlo, Courier, monospace;
                                                                    font-size: 14px;
                                                                "
                                                                :model-value="(nodeInspector.forbiddenWords && nodeInspector.forbiddenWords[index]) || ''"
                                                                placeholder="Enter word or phrase"
                                                                @keypress="isEnterForbidden($event, index)"
                                                                @keyup.delete="checkDeleteForbiddenWord(index, (nodeInspector.forbiddenWords && nodeInspector.forbiddenWords[index]) || '')"
                                                                @update:model-value="updateForbiddenWordProperty(index, $event)"
                                                                :readonly="isViewMode" />
                                                            <v-btn icon="mdi-attachment" variant="text"
                                                                v-show="(nodeInspector.forbiddenWords || []).length > 1 && index + 1 < (nodeInspector.forbiddenWords || []).length"
                                                                @click="removeForbiddenWordDirect(index)"
                                                                :disabled="isViewMode">
                                                                <v-tooltip location="top" :open-delay="500">
                                                                    <template v-slot:activator="{ props }">
                                                                        <v-icon color="error" style="cursor: pointer"
                                                                            v-bind="props">
                                                                            mdi-delete-forever
                                                                        </v-icon>
                                                                    </template>
                                                                    <span>delete</span>
                                                                </v-tooltip>
                                                            </v-btn>
                                                        </div>
                                                    </v-col>
                                                </v-row>
                                            </div>
                                        </div>
                                    </div>
                                </v-expansion-panel-text>
                            </v-expansion-panel>

                            <!-- System Prompt Panel -->
                            <v-expansion-panel value="prompt" class="panel-item">
                                <v-expansion-panel-title class="panel-title-custom">
                                    <div class="d-flex align-center py-3">
                                        <div class="panel-icon-wrapper me-4"
                                            style="background: linear-gradient(135deg, #607D8B, #455A64);">
                                            <v-icon icon="mdi-message-text" size="20" color="white"></v-icon>
                                        </div>
                                        <div>
                                            <div class="panel-title">System Prompt</div>
                                            <div class="panel-subtitle">Define AI behavior and personality</div>
                                        </div>
                                    </div>
                                </v-expansion-panel-title>
                                <v-expansion-panel-text class="panel-content-wrapper">
                                    <div class="panel-content">
                                        <div class="prompt-section">
                                            <div class="d-flex align-center mb-4">
                                                <v-avatar size="36" color="info" class="me-3">
                                                    <v-icon color="white" size="20">mdi-robot-outline</v-icon>
                                                </v-avatar>
                                                <div>
                                                    <h6 class="text-h6 mb-1">AI Personality & Instructions</h6>
                                                    <p class="text-body-2 text-medium-emphasis mb-0">
                                                        Define how your AI should behave, respond, and interact with
                                                        users
                                                    </p>
                                                </div>
                                            </div>
                                            <h3 class="text-h6 d-flex align-center mb-3" style="font-size: 16px !important;">
                                                <v-icon icon="mdi-pencil-outline" size="small"
                                                    class="me-2"></v-icon>
                                                Configure system message for your AI
                                            </h3>

                                            <!-- Prompt Template Header -->
                                            <div class="mb-2" style="display:flex;flex-direction: column;align-items: flex-end;">
                                                <div style="display: flex; align-items: center;">
                                                    <v-tooltip location="top">
                                                        <template v-slot:activator="{ props }">
                                                            <v-icon icon="mdi-information-outline" size="small"
                                                                color="grey" class="me-3" v-bind="props"></v-icon>
                                                        </template>
                                                        <span>Configure additional prompts and templates</span>
                                                    </v-tooltip>
                                                    <v-btn
                                                        v-if="DDGroupPromptTemplate && DDGroupPromptTemplate.length > 0"
                                                        variant="outlined" color="primary"
                                                        prepend-icon="mdi-playlist-plus" @click="showPromptTemplate"
                                                        class="text-none font-weight-medium" size="small">
                                                        Show Templates
                                                    </v-btn>
                                                </div>
                                            </div>

                                            <!-- Main editor and templates layout -->
                                            <v-row>
                                                <!-- Editor area (full width) -->
                                                <v-col cols="12" class="prompt-editor-col">
                                                    <v-card variant="outlined" class="prompt-editor-card">
                                                        <v-card-text class="px-2 py-1">
                                                            <v-textarea v-model="nodeInspector.systemPrompt" label=""
                                                                variant="plain" counter auto-grow rows="25"
                                                                @update:model-value="updateNodeProperty('systemPrompt', $event)"
                                                                :readonly="isViewMode"
                                                                placeholder="Enter your system message here..."
                                                                class="prompt-textarea"
                                                                @contextmenu="onContextMenu"></v-textarea>
                                                        </v-card-text>
                                                    </v-card>
                                                </v-col>
                                            </v-row>

                                            <!-- Context menu for prompt templates -->
                                            <v-menu v-model="showMenu" :activator="{ x, y }" location="bottom">
                                                <v-card min-width="250" elevation="3">
                                                    <v-list density="compact" class="py-0">
                                                        <v-list-subheader class="d-flex bg-grey-lighten-4 py-2">
                                                            <v-icon icon="mdi-text-box-plus-outline" class="me-2"
                                                                size="small"></v-icon>
                                                            Insert Template
                                                        </v-list-subheader>
                                                        <v-divider :thickness="1"
                                                            class="border-opacity-100"></v-divider>
                                                        <v-list-item v-for="(item, index) in filteredPromptTemplates"
                                                            :key="index"
                                                            @click="setSelectPromptTemplate(item); showMenu = false;"
                                                            :value="item.name" class="template-list-item">
                                                            <template v-slot:prepend>
                                                                <v-icon size="small"
                                                                    icon="mdi-text-box-outline"></v-icon>
                                                            </template>
                                                            <template v-slot:title>
                                                                <span>{{ item.name }}</span>
                                                            </template>
                                                        </v-list-item>
                                                    </v-list>
                                                </v-card>
                                            </v-menu>

                                            <!-- Dialog for showing all templates -->
                                            <v-dialog v-model="dialogShowAll" max-width="550" scrollable>
                                                <v-card>
                                                    <v-card-title class="bg-primary text-white d-flex align-center">
                                                        <v-icon icon="mdi-text-box-multiple-outline"
                                                            class="me-2"></v-icon>
                                                        Prompt Templates
                                                    </v-card-title>
                                                    <v-divider :thickness="1" class="border-opacity-100"></v-divider>
                                                    <v-card-text style="max-height: 500px" class="pt-4">
                                                        <v-text-field v-model="templateSearchQuery"
                                                            label="Search templates" prepend-inner-icon="mdi-magnify"
                                                            variant="outlined" density="compact" hide-details
                                                            class="mb-3" clearable></v-text-field>

                                                        <v-list density="compact" class="template-list-all pa-0">
                                                            <v-list-item v-for="(item, i) in searchedTemplates" :key="i"
                                                                @click="setSelectPromptTemplate(item)"
                                                                class="template-list-item" :ripple="true">
                                                                <template v-slot:prepend>
                                                                    <v-icon size="small" color="primary"
                                                                        icon="mdi-text-box-outline"></v-icon>
                                                                </template>
                                                                <template v-slot:title>
                                                                    <span>{{ item.name }}</span>
                                                                </template>
                                                                <template v-slot:subtitle>
                                                                    <span class="text-caption text-truncate">
                                                                        {{ truncatePrompt(item.prompt) }}
                                                                    </span>
                                                                </template>
                                                            </v-list-item>
                                                        </v-list>
                                                    </v-card-text>
                                                    <v-card-actions>
                                                        <v-spacer></v-spacer>
                                                        <v-btn color="blue darken-1" variant="text"
                                                            @click="dialogShowAll = false">
                                                            Close
                                                        </v-btn>
                                                    </v-card-actions>
                                                </v-card>
                                            </v-dialog>

                                            <!-- Main Prompt Template Dialog -->
                                            <v-dialog v-model="promptTemplateDialog" max-width="800" scrollable>
                                                <v-card>
                                                    <v-card-title class="bg-primary text-white d-flex align-center">
                                                        <v-icon icon="mdi-text-box-multiple-outline" class="me-2"></v-icon>
                                                        Prompt Templates
                                                        <v-spacer></v-spacer>
                                                        <v-btn icon="mdi-close" variant="text" color="white" size="small" 
                                                            @click="promptTemplateDialog = false"></v-btn>
                                                    </v-card-title>
                                                    
                                                    <v-card-text class="pa-4">
                                                        <!-- Template Group Selection -->
                                                        <v-autocomplete 
                                                            label="Select Template Group" 
                                                            :items="DDGroupPromptTemplate" 
                                                            item-title="name"
                                                            item-value="id" 
                                                            density="compact" 
                                                            clearable
                                                            variant="outlined" 
                                                            v-model="groupPromptTemplate"
                                                            prepend-inner-icon="mdi-folder-outline" 
                                                            hide-details
                                                            class="mb-4">
                                                        </v-autocomplete>

                                                        <!-- Search Template -->
                                                        <v-text-field 
                                                            v-model="templateSearchQuery"
                                                            label="Search templates" 
                                                            prepend-inner-icon="mdi-magnify"
                                                            variant="outlined" 
                                                            density="compact" 
                                                            hide-details
                                                            class="mb-4" 
                                                            clearable>
                                                        </v-text-field>

                                                        <!-- Templates List -->
                                                        <div style="max-height: 400px; overflow-y: auto;">
                                                            <div v-if="searchedTemplates.length === 0" 
                                                                class="pa-4 text-center text-grey-darken-1">
                                                                <v-icon icon="mdi-playlist-remove" size="large" 
                                                                    class="d-block mx-auto mb-2"></v-icon>
                                                                <span>No templates found</span>
                                                            </div>

                                                            <v-list v-else density="compact" class="pa-0">
                                                                <v-list-item 
                                                                    v-for="(item, i) in searchedTemplates" 
                                                                    :key="i"
                                                                    @click="setSelectPromptTemplate(item)"
                                                                    class="template-list-item ma-1" 
                                                                    :ripple="true"
                                                                    rounded>
                                                                    <template v-slot:prepend>
                                                                        <v-icon size="small" color="primary" 
                                                                            icon="mdi-text-box-outline"></v-icon>
                                                                    </template>
                                                                    <template v-slot:title>
                                                                        <span class="font-weight-medium">{{ item.name }}</span>
                                                                    </template>
                                                                    <template v-slot:subtitle>
                                                                        <span class="text-caption text-grey">
                                                                            {{ truncatePrompt(item.prompt) }}
                                                                        </span>
                                                                    </template>
                                                                    <template v-slot:append>
                                                                        <v-btn icon="mdi-plus" size="small" variant="text" 
                                                                            color="primary" @click.stop="setSelectPromptTemplate(item)">
                                                                        </v-btn>
                                                                    </template>
                                                                </v-list-item>
                                                            </v-list>
                                                        </div>
                                                    </v-card-text>
                                                    
                                                    <v-card-actions>
                                                        <v-spacer></v-spacer>
                                                        <v-btn color="primary" variant="text" @click="promptTemplateDialog = false">
                                                            Close
                                                        </v-btn>
                                                    </v-card-actions>
                                                </v-card>
                                            </v-dialog>

                                            <div class="prompt-tips mt-4">
                                                <v-card class="tips-card" color="blue-grey-lighten-5" flat>
                                                    <v-card-text class="pa-4">
                                                        <div class="d-flex align-start">
                                                            <v-icon color="info" size="20"
                                                                class="me-2 mt-1">mdi-lightbulb-outline</v-icon>
                                                            <div>
                                                                <div class="text-subtitle-2 font-weight-medium mb-2">
                                                                    Tips for
                                                                    better prompts:</div>
                                                                <ul class="text-body-2 text-medium-emphasis">
                                                                    <li>Be specific about the AI's role and expertise
                                                                    </li>
                                                                    <li>Define the tone and communication style</li>
                                                                    <li>Include any restrictions or guidelines</li>
                                                                    <li>Specify how to handle unknown information</li>
                                                                </ul>
                                                            </div>
                                                        </div>
                                                    </v-card-text>
                                                </v-card>
                                            </div>
                                        </div>
                                    </div>
                                </v-expansion-panel-text>
                            </v-expansion-panel>
                        </v-expansion-panels>

                    </div>

                    <div v-else-if="selectedNode.data.type === 'intentAI'" class="form-section">
                        <!-- Header Section -->
                        <div class="config-header mb-6">
                            <div class="d-flex align-center mb-2">
                                <v-icon icon="mdi-brain" size="24" color="primary" class="me-3"></v-icon>
                                <h5 class="text-h6 font-weight-bold text-primary mb-0">Intent AI Model Configuration</h5>
                            </div>
                            <p class="text-body-2 text-medium-emphasis mb-0">Configure your Intent AI model settings and
                                parameters</p>
                        </div>

                        <!-- Action Button -->
                        <div class="d-flex justify-end mb-4">
                            <v-btn @click="openLoadFromModelDialog" color="success" variant="tonal" size="small"
                                prepend-icon="mdi-download" :disabled="isViewMode" class="load-model-btn">
                                Load from Model
                            </v-btn>
                        </div>

                        <!-- Intent AI Model Configuration Panels -->
                        <v-expansion-panels v-model="intentaiModelConfigPanels" multiple class="config-panels elevation-0"
                            variant="accordion">
                            <!-- Basic Information Panel -->
                            <v-expansion-panel value="intentaibasic" class="panel-item">
                                <v-expansion-panel-title class="panel-title-custom">
                                    <div class="d-flex align-center py-3">
                                        <div class="panel-icon-wrapper me-4">
                                            <v-icon icon="mdi-information" size="20" color="white"></v-icon>
                                        </div>
                                        <div>
                                            <div class="panel-title">Basic Information</div>
                                            <div class="panel-subtitle">Provider, model, and basic settings</div>
                                        </div>
                                    </div>
                                </v-expansion-panel-title>
                                <v-expansion-panel-text class="panel-content-wrapper">
                                    <div class="panel-content">
                                        <!-- Provider and Model -->
                                        <div class="config-section mb-6">
                                            <h6 class="section-label mb-3">Service Configuration</h6>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-autocomplete v-model="nodeInspector.intentaiProvider"
                                                        :items="listDDProvider" item-title="providerName"
                                                        item-value="providerName" label="Provider*" variant="outlined"
                                                        density="comfortable" hide-details="auto"
                                                        prepend-inner-icon="mdi-cloud-outline"
                                                        @update:model-value="updateNodeProperty('intentaiProvider', $event); changeProvider($event)"
                                                        class="config-input" :readonly="isViewMode"
                                                        hint="Select AI service provider" persistent-hint required />
                                                </v-col>
                                            </v-row>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-autocomplete v-model="nodeInspector.intentaiModel" :items="DDModelShow"
                                                        item-title="modelDisplay" item-value="modelName" label="Model*"
                                                        variant="outlined" density="comfortable" hide-details="auto"
                                                        prepend-inner-icon="mdi-brain"
                                                        @update:model-value="updateNodeProperty('intentaiModel', $event)"
                                                        class="config-input" :readonly="isViewMode"
                                                        :disabled="!nodeInspector.intentaiProvider"
                                                        hint="Select specific model version" persistent-hint required />
                                                </v-col>
                                            </v-row>
                                        </div>

                                        <!-- Behavior Settings -->
                                        <div class="config-section">
                                            <h6 class="section-label mb-3">Behavior Settings</h6>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-select v-model="nodeInspector.intentaiGender"
                                                        :items="[{ id: 'male', name: 'Male' }, { id: 'female', name: 'Female' }]"
                                                        item-title="name" item-value="id" label="Gender"
                                                        variant="outlined" density="comfortable" hide-details="auto"
                                                        prepend-inner-icon="mdi-account"
                                                        @update:model-value="updateNodeProperty('intentaiGender', $event)"
                                                        class="config-input" :readonly="isViewMode"
                                                        hint="Voice/persona gender preference" persistent-hint
                                                        :model-value="nodeInspector.intentaiGender || 'male'" />
                                                </v-col>
                                            </v-row>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-text-field v-model.number="nodeInspector.intentaiTypingSpeed"
                                                        label="Typing Speed" type="number" min="0" variant="outlined"
                                                        density="comfortable" hide-details="auto"
                                                        prepend-inner-icon="mdi-keyboard-outline"
                                                        @update:model-value="updateNodeProperty('intentaiTypingSpeed', $event)"
                                                        @keypress="isNumberpositive($event)" class="config-input"
                                                        :readonly="isViewMode"
                                                        hint="Characters per second (0 = instant)" persistent-hint />
                                                </v-col>
                                            </v-row>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-text-field v-model.number="nodeInspector.intentaiMaxInputLength"
                                                        label="Max Input Length" type="number" min="0" max="200000"
                                                        variant="outlined" density="comfortable" hide-details="auto"
                                                        prepend-inner-icon="mdi-text-box-outline"
                                                        @update:model-value="updateNodeProperty('intentaiMaxInputLength', $event)"
                                                        @keypress="maxNumberpositive($event)"
                                                        @focusin="validateAndCorrectValue($event, 1, 200000)"
                                                        @focusout="validateAndCorrectValue($event, 1, 200000)"
                                                        @keydown.enter="validateAndCorrectValue($event, 1, 200000)"
                                                        class="config-input" :readonly="isViewMode"
                                                        hint="Maximum characters allowed in user input"
                                                        persistent-hint />
                                                </v-col>
                                            </v-row>
                                        </div>
                                    </div>
                                </v-expansion-panel-text>
                            </v-expansion-panel>

                            <!-- Response Options Panel -->
                            <v-expansion-panel value="intentairesponse" class="panel-item">
                                <v-expansion-panel-title class="panel-title-custom">
                                    <div class="d-flex align-center py-3">
                                        <div class="panel-icon-wrapper me-4"
                                            style="background: linear-gradient(135deg, #FF9800, #F57C00);">
                                            <v-icon icon="mdi-tune" size="20" color="white"></v-icon>
                                        </div>
                                        <div>
                                            <div class="panel-title">Response Options</div>
                                            <div class="panel-subtitle">Configure how responses are generated</div>
                                        </div>
                                    </div>
                                </v-expansion-panel-title>
                                <v-expansion-panel-text class="panel-content-wrapper">
                                    <div class="panel-content pa-4">
                                        <!-- Stream Response -->
                                        <div class="switch-option-compact mb-3">
                                            <div class="d-flex align-center justify-space-between py-2 px-3 rounded-lg"
                                                :class="{ 'switch-active-bg': nodeInspector.intentaiStreamResponse }">
                                                <div class="d-flex align-center">
                                                    <v-avatar size="28"
                                                        :color="nodeInspector.intentaiStreamResponse ? 'primary' : 'grey-lighten-1'"
                                                        class="me-3">
                                                        <v-icon
                                                            :color="nodeInspector.intentaiStreamResponse ? 'white' : 'grey-darken-1'"
                                                            size="16">mdi-text-box-outline</v-icon>
                                                    </v-avatar>
                                                    <div>
                                                        <div class="switch-title">Stream Response</div>
                                                        <div class="switch-description">Progressive text generation
                                                        </div>
                                                    </div>
                                                </div>
                                                <v-switch v-model="nodeInspector.intentaiStreamResponse" color="primary"
                                                    hide-details density="compact"
                                                    @update:model-value="updateNodeProperty('intentaiStreamResponse', $event)"
                                                    :readonly="isViewMode" inset />
                                            </div>
                                        </div>

                                        <!-- Time Context -->
                                        <div class="switch-option-compact">
                                            <div class="d-flex align-center justify-space-between py-2 px-3 rounded-lg"
                                                :class="{ 'switch-active-bg': nodeInspector.intentaiEnableTime }">
                                                <div class="d-flex align-center">
                                                    <v-avatar size="28"
                                                        :color="nodeInspector.intentaiEnableTime ? 'success' : 'grey-lighten-1'"
                                                        class="me-3">
                                                        <v-icon
                                                            :color="nodeInspector.intentaiEnableTime ? 'white' : 'grey-darken-1'"
                                                            size="16">mdi-clock-outline</v-icon>
                                                    </v-avatar>
                                                    <div>
                                                        <div class="switch-title">Time Context</div>
                                                        <div class="switch-description">Enable time awareness</div>
                                                    </div>
                                                </div>
                                                <v-switch v-model="nodeInspector.intentaiEnableTime" color="success"
                                                    hide-details density="compact"
                                                    @update:model-value="updateNodeProperty('intentaiEnableTime', $event)"
                                                    :readonly="isViewMode" inset />
                                            </div>

                                            <!-- Time Zone Dropdown (shown when enableTime is true) -->
                                            <div v-if="nodeInspector.intentaiEnableTime" class="mt-3">
                                                <v-select v-model="nodeInspector.intentaiTimeZone" :items="DDTimeZone"
                                                    item-title="name" item-value="value" label="Time Zone"
                                                    variant="outlined" density="comfortable" hide-details="auto"
                                                    prepend-inner-icon="mdi-earth"
                                                    @update:model-value="updateNodeProperty('intentaiTimeZone', $event)"
                                                    class="config-input" :readonly="isViewMode"
                                                    hint="Select your timezone" persistent-hint
                                                    placeholder="Select your timezone">
                                                    <template v-slot:selection="{ item }">
                                                        <div class="d-flex align-center">
                                                            <v-icon small color="primary"
                                                                class="me-2">mdi-map-marker</v-icon>
                                                            <span class="font-weight-medium">{{ item.raw.name }}</span>
                                                            <v-chip size="x-small" color="grey-darken-1"
                                                                text-color="white" class="ml-2">
                                                                {{ item.raw.value.split('/')[1].replace('_', ' ') }}
                                                            </v-chip>
                                                        </div>
                                                    </template>
                                                </v-select>
                                            </div>
                                        </div>
                                    </div>
                                </v-expansion-panel-text>
                            </v-expansion-panel>

                            <!-- Memory Settings Panel -->
                            <v-expansion-panel value="intentaimemory" class="panel-item">
                                <v-expansion-panel-title class="panel-title-custom">
                                    <div class="d-flex align-center py-3">
                                        <div class="panel-icon-wrapper me-4"
                                            style="background: linear-gradient(135deg, #00BCD4, #0097A7);">
                                            <v-icon icon="mdi-brain" size="20" color="white"></v-icon>
                                        </div>
                                        <div>
                                            <div class="panel-title">Memory Settings</div>
                                            <div class="panel-subtitle">Configure conversation memory and context</div>
                                        </div>
                                    </div>
                                </v-expansion-panel-title>
                                <v-expansion-panel-text class="panel-content-wrapper">
                                    <div class="panel-content">
                                        <div class="config-section mb-3">
                                            <h6 class="section-label mb-3">Conversation Memory</h6>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-text-field v-model.number="nodeInspector.intentaiMemoryTurn"
                                                        label="Turn Count" type="number" min="0" variant="outlined"
                                                        density="comfortable" hide-details="auto"
                                                        prepend-inner-icon="mdi-history"
                                                        @update:model-value="updateNodeProperty('intentaiMemoryTurn', $event)"
                                                        @keypress="isNumberpositive($event)" class="config-input"
                                                        :readonly="isViewMode"
                                                        hint="Number of conversation turns to remember"
                                                        persistent-hint />
                                                </v-col>
                                            </v-row>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-text-field v-model.number="nodeInspector.intentaiMemoryDuration"
                                                        label="Duration (minutes)" type="number" min="0"
                                                        variant="outlined" density="comfortable" hide-details="auto"
                                                        prepend-inner-icon="mdi-timer-outline"
                                                        @update:model-value="updateNodeProperty('intentaiMemoryDuration', $event)"
                                                        @keypress="isNumberpositive($event)" class="config-input"
                                                        :readonly="isViewMode" hint="How long to keep memory active"
                                                        persistent-hint />
                                                </v-col>
                                            </v-row>
                                        </div>

                                        <div class="config-section mb-3">
                                            <h6 class="section-label mb-3">Memory Types</h6>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-text-field v-model.number="nodeInspector.intentaiShortTermMemory"
                                                        label="Short-Term Memory" type="number" min="1"
                                                        variant="outlined" density="comfortable" hide-details="auto"
                                                        prepend-inner-icon="mdi-memory"
                                                        @update:model-value="updateNodeProperty('intentaiShortTermMemory', $event)"
                                                        @keypress="isNumberpositive($event)" class="config-input"
                                                        :readonly="isViewMode" hint="Recent context window size"
                                                        persistent-hint />
                                                </v-col>
                                            </v-row>
                                            <v-row>
                                                <v-col>
                                                    <!-- Long-Term Memory Switch -->
                                                    <div class="switch-option-compact mb-4">
                                                        <div class="d-flex align-center justify-space-between py-2 px-3 rounded-lg"
                                                            :class="{ 'switch-active-bg': nodeInspector.intentaiLongTermMemory }">
                                                            <div class="d-flex align-center">
                                                                <v-avatar size="28"
                                                                    :color="nodeInspector.intentaiLongTermMemory ? 'success' : 'grey-lighten-1'"
                                                                    class="me-3">
                                                                    <v-icon
                                                                        :color="nodeInspector.intentaiLongTermMemory ? 'white' : 'grey-darken-1'"
                                                                        size="16">mdi-database</v-icon>
                                                                </v-avatar>
                                                                <div>
                                                                    <div class="switch-title">Long-Term Memory</div>
                                                                    <div class="switch-description">Persistent storage
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            <v-switch v-model="nodeInspector.intentaiLongTermMemory"
                                                                color="success" hide-details density="compact"
                                                                @update:model-value="updateNodeProperty('intentaiLongTermMemory', $event)"
                                                                :readonly="isViewMode" inset />
                                                        </div>
                                                    </div>
                                                </v-col>
                                            </v-row>

                                        </div>

                                        <!-- Multi-turn Input Configuration -->
                                        <div class="config-section mb-3">
                                            <h6 class="section-label mb-3">Multi-turn Input Configuration</h6>
                                            <v-row>
                                                <v-col>
                                                    <!-- Enable Multi-Turn Input Switch -->
                                                    <div class="switch-option-compact mb-3">
                                                        <div class="d-flex align-center justify-space-between py-2 px-3 rounded-lg"
                                                            :class="{ 'switch-active-bg': nodeInspector.intentaiIsChainMassage }">
                                                            <div class="d-flex align-center">
                                                                <v-avatar size="28"
                                                                    :color="nodeInspector.intentaiIsChainMassage ? 'indigo' : 'grey-lighten-1'"
                                                                    class="me-3">
                                                                    <v-icon
                                                                        :color="nodeInspector.intentaiIsChainMassage ? 'white' : 'grey-darken-1'"
                                                                        size="16">mdi-chat-processing</v-icon>
                                                                </v-avatar>
                                                                <div>
                                                                    <div class="switch-title">Enable Multi-Turn Input
                                                                    </div>
                                                                    <div class="switch-description">Allow continuous
                                                                        conversation</div>
                                                                </div>
                                                            </div>
                                                            <v-switch v-model="nodeInspector.intentaiIsChainMassage"
                                                                color="indigo" hide-details density="compact"
                                                                @update:model-value="updateNodeProperty('intentaiIsChainMassage', $event)"
                                                                :readonly="isViewMode" inset />
                                                        </div>
                                                    </div>
                                                </v-col>
                                            </v-row>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-text-field v-model.number="nodeInspector.intentaiLenChainMassage"
                                                        label="Sequence" type="number" min="1" variant="outlined"
                                                        density="comfortable" hide-details="auto"
                                                        prepend-inner-icon="mdi-numeric"
                                                        @update:model-value="updateNodeProperty('intentaiLenChainMassage', $event)"
                                                        @keypress="isNumberpositive($event)" class="config-input"
                                                        :readonly="isViewMode" :disabled="!nodeInspector.intentaiIsChainMassage"
                                                        hint="Number of conversation turns in sequence" persistent-hint
                                                        clearable />
                                                </v-col>
                                            </v-row>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-text-field v-model.number="nodeInspector.intentaiTimeSecondChainMassage"
                                                        label="Duration (seconds)" type="number" min="1"
                                                        variant="outlined" density="comfortable" hide-details="auto"
                                                        prepend-inner-icon="mdi-timer"
                                                        @update:model-value="updateNodeProperty('intentaiTimeSecondChainMassage', $event)"
                                                        @keypress="isNumberpositive($event)" class="config-input"
                                                        :readonly="isViewMode" :disabled="!nodeInspector.intentaiIsChainMassage"
                                                        hint="Time duration for multi-turn session" persistent-hint
                                                        clearable />
                                                </v-col>
                                            </v-row>
                                        </div>


                                    </div>
                                </v-expansion-panel-text>
                            </v-expansion-panel>

                            <!-- Parameters Panel -->
                            <v-expansion-panel value="intentaiparameters" class="panel-item">
                                <v-expansion-panel-title class="panel-title-custom">
                                    <div class="d-flex align-center py-3">
                                        <div class="panel-icon-wrapper me-4"
                                            style="background: linear-gradient(135deg, #9C27B0, #7B1FA2);">
                                            <v-icon icon="mdi-cog" size="20" color="white"></v-icon>
                                        </div>
                                        <div>
                                            <div class="panel-title">AI Parameters</div>
                                            <div class="panel-subtitle">Fine-tune model behavior and output</div>
                                        </div>
                                    </div>
                                </v-expansion-panel-title>
                                <v-expansion-panel-text class="panel-content-wrapper">
                                    <div class="panel-content">
                                        <div class="config-section mb-3">
                                            <h6 class="section-label mb-3">Core Parameters</h6>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-text-field v-model.number="nodeInspector.intentaiTemperature"
                                                        label="Temperature*" type="number" step="0.1" min="0" max="1"
                                                        variant="outlined" density="comfortable" hide-details="auto"
                                                        prepend-inner-icon="mdi-thermometer"
                                                        @update:model-value="updateNodeProperty('intentaiTemperature', $event)"
                                                        @keypress="isNumberpositive($event)" class="config-input"
                                                        :readonly="isViewMode"
                                                        hint="Creativity level (0 = focused, 1 = creative)"
                                                        persistent-hint />
                                                </v-col>
                                            </v-row>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-text-field v-model.number="nodeInspector.intentaiTopP" label="Top P"
                                                        type="number" step="0.1" min="0" max="1" variant="outlined"
                                                        density="comfortable" hide-details="auto"
                                                        prepend-inner-icon="mdi-filter-variant"
                                                        @update:model-value="updateNodeProperty('intentaiTopP', $event)"
                                                        @keypress="isNumberpositive($event)" class="config-input"
                                                        :readonly="isViewMode"
                                                        hint="Token selection probability threshold" persistent-hint />
                                                </v-col>
                                            </v-row>
                                        </div>

                                        <div class="config-section mb-3">
                                            <h6 class="section-label mb-3">Penalty Settings</h6>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-text-field v-model.number="nodeInspector.intentaiFrequencyPenalty"
                                                        label="Frequency Penalty" type="number" step="0.1" min="0"
                                                        max="2" variant="outlined" density="comfortable"
                                                        hide-details="auto" prepend-inner-icon="mdi-repeat-off"
                                                        @update:model-value="updateNodeProperty('intentaiFrequencyPenalty', $event)"
                                                        @keypress="isNumberpositive($event)" class="config-input"
                                                        :readonly="isViewMode"
                                                        hint="Reduce repetition of frequent tokens" persistent-hint />
                                                </v-col>
                                            </v-row>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-text-field v-model.number="nodeInspector.intentaiPresencePenalty"
                                                        label="Presence Penalty" type="number" step="0.1" min="0"
                                                        max="2" variant="outlined" density="comfortable"
                                                        hide-details="auto"
                                                        prepend-inner-icon="mdi-minus-circle-outline"
                                                        @update:model-value="updateNodeProperty('intentaiPresencePenalty', $event)"
                                                        @keypress="isNumberpositive($event)" class="config-input"
                                                        :readonly="isViewMode" hint="Reduce repetition of any tokens"
                                                        persistent-hint />
                                                </v-col>
                                            </v-row>
                                        </div>

                                        <div class="config-section">
                                            <h6 class="section-label mb-3">Output Control</h6>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-text-field v-model.number="nodeInspector.intentaiMaxTokens"
                                                        label="Max Tokens Response" type="number" min="1"
                                                        variant="outlined" density="comfortable" hide-details="auto"
                                                        prepend-inner-icon="mdi-text-box-check-outline"
                                                        @update:model-value="updateNodeProperty('intentaiMaxTokens', $event)"
                                                        @keypress="isNumberpositive($event)" class="config-input"
                                                        :readonly="isViewMode" hint="Maximum response length in tokens"
                                                        persistent-hint />
                                                </v-col>
                                            </v-row>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-text-field v-model="nodeInspector.intentaiStopSequence"
                                                        label="Stop Sequence" variant="outlined" density="comfortable"
                                                        hide-details="auto" prepend-inner-icon="mdi-stop-circle-outline"
                                                        @update:model-value="updateNodeProperty('intentaiStopSequence', $event)"
                                                        class="config-input" :readonly="isViewMode"
                                                        hint="Text sequence to stop generation" persistent-hint />
                                                </v-col>
                                            </v-row>
                                        </div>
                                    </div>
                                </v-expansion-panel-text>
                            </v-expansion-panel>

                            <!-- Line Configuration Panel -->
                            <v-expansion-panel value="intentailine" class="panel-item">
                                <v-expansion-panel-title class="panel-title-custom">
                                    <div class="d-flex align-center py-3">
                                        <div class="panel-icon-wrapper me-4"
                                            style="background: linear-gradient(135deg, #4CAF50, #388E3C);">
                                            <v-icon icon="mdi-message" size="20" color="white"></v-icon>
                                        </div>
                                        <div>
                                            <div class="panel-title">Line Configuration</div>
                                            <div class="panel-subtitle">LINE messaging platform settings</div>
                                        </div>
                                    </div>
                                </v-expansion-panel-title>
                                <v-expansion-panel-text class="panel-content-wrapper">
                                    <div class="panel-content pa-4">
                                        <div class="config-section mb-3">
                                            <h6 class="section-label mb-3">Display Options</h6>

                                            <!-- Show Name -->
                                            <div class="switch-option-compact mb-3">
                                                <div class="d-flex align-center justify-space-between py-2 px-3 rounded-lg"
                                                    :class="{ 'switch-active-bg': nodeInspector.intentaiShowLineName }">
                                                    <div class="d-flex align-center">
                                                        <v-avatar size="28"
                                                            :color="nodeInspector.intentaiShowLineName ? 'info' : 'grey-lighten-1'"
                                                            class="me-3">
                                                            <v-icon
                                                                :color="nodeInspector.intentaiShowLineName ? 'white' : 'grey-darken-1'"
                                                                size="16">mdi-account-circle</v-icon>
                                                        </v-avatar>
                                                        <div>
                                                            <div class="switch-title">Show Name</div>
                                                            <div class="switch-description">Display user name</div>
                                                        </div>
                                                    </div>
                                                    <v-switch v-model="nodeInspector.intentaiShowLineName" color="info"
                                                        hide-details density="compact"
                                                        @update:model-value="updateNodeProperty('intentaiShowLineName', $event)"
                                                        :readonly="isViewMode" inset />
                                                </div>
                                            </div>
                                            <!-- Loading Indicator -->
                                            <div class="switch-option-compact mb-3">
                                                <div class="d-flex align-center justify-space-between py-2 px-3 rounded-lg"
                                                    :class="{ 'switch-active-bg': nodeInspector.intentaiLoadingBar }">
                                                    <div class="d-flex align-center">
                                                        <v-avatar size="28"
                                                            :color="nodeInspector.intentaiLoadingBar ? 'success' : 'grey-lighten-1'"
                                                            class="me-3">
                                                            <v-icon
                                                                :color="nodeInspector.intentaiLoadingBar ? 'white' : 'grey-darken-1'"
                                                                size="16">mdi-progress-clock</v-icon>
                                                        </v-avatar>
                                                        <div>
                                                            <div class="switch-title">Loading Indicator</div>
                                                            <div class="switch-description">Show typing animation</div>
                                                        </div>
                                                    </div>
                                                    <v-switch v-model="nodeInspector.intentaiLoadingBar" color="success"
                                                        hide-details density="compact"
                                                        @update:model-value="updateNodeProperty('intentaiLoadingBar', $event)"
                                                        :readonly="isViewMode" inset />
                                                </div>
                                            </div>

                                        </div>

                                        <div class="config-section mb-3">
                                            <h6 class="section-label mb-3">Loading & Duration</h6>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-select v-model="nodeInspector.intentaiLoadingBarDuration"
                                                        :items="[10, 20, 30, 40, 50, 60]"
                                                        label="Loading Duration (seconds)" variant="outlined"
                                                        density="comfortable" hide-details="auto"
                                                        prepend-inner-icon="mdi-timer-sand"
                                                        @update:model-value="updateNodeProperty('intentaiLoadingBarDuration', $event)"
                                                        class="config-input" :disabled="!nodeInspector.intentaiLoadingBar"
                                                        :readonly="isViewMode"
                                                        hint="Duration for loading animation display" persistent-hint
                                                        clearable />
                                                </v-col>
                                            </v-row>
                                        </div>

                                        <div class="config-section mb-3">
                                            <h6 class="section-label mb-3">Monitoring</h6>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-text-field v-model="nodeInspector.intentaiMonitorLineUserId"
                                                        label="Monitor Line User ID" variant="outlined"
                                                        density="comfortable" hide-details="auto"
                                                        prepend-inner-icon="mdi-monitor-eye"
                                                        @update:model-value="updateNodeProperty('intentaiMonitorLineUserId', $event)"
                                                        class="config-input" :readonly="isViewMode"
                                                        hint="LINE user ID for monitoring notifications"
                                                        persistent-hint />
                                                </v-col>
                                            </v-row>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-autocomplete v-model="nodeInspector.intentaiMonitorSocialID"
                                                        :items="listMonitorLineName" label="Monitor Line Name"
                                                        variant="outlined" density="comfortable" hide-details="auto"
                                                        prepend-inner-icon="mdi-chat" item-title="name" item-value="id"
                                                        @update:model-value="updateNodeProperty('intentaiMonitorSocialID', $event)"
                                                        class="config-input" :readonly="isViewMode"
                                                        hint="Select monitor line name" persistent-hint clearable />
                                                </v-col>
                                            </v-row>
                                        </div>

                                        <div class="config-section">
                                            <h6 class="section-label mb-3">Content Filtering</h6>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-toolbar-title style="font-size: 16px">
                                                        Forbidden word
                                                    </v-toolbar-title>
                                                </v-col>
                                            </v-row>
                                            <div class="pr-1" style="
                                                    border: #dfe0e2 1px solid;
                                                    background: #fff;
                                                    margin-top: 10px;
                                                    border-radius: 8px;
                                                ">
                                                <v-row v-for="(item, index) in nodeInspector.intentaiForbiddenWords || ['']"
                                                    :key="index" style="margin: 0px">
                                                    <v-col cols="12" style="padding: 0px">
                                                        <div style="display: flex; align-items: center">
                                                            <div style="
                                                                    height: 50px;
                                                                    background: #f8f8f8;
                                                                    padding: 14px 20px 0 20px;
                                                                ">
                                                                {{ index + 1 }}
                                                            </div>
                                                            <v-textarea variant="solo" style="
                                                                    overflow: hidden;
                                                                    overflow-wrap: break-word;
                                                                    resize: none;
                                                                    height: 50px;
                                                                    width: 98%;
                                                                    border-radius: 0px;
                                                                    font-family: Fira code, Fira Mono, Consolas,
                                                                        Menlo, Courier, monospace;
                                                                    font-size: 14px;
                                                                "
                                                                :model-value="(nodeInspector.intentaiForbiddenWords && nodeInspector.intentaiForbiddenWords[index]) || ''"
                                                                placeholder="Enter word or phrase"
                                                                @keypress="isEnterIntentaiForbidden($event, index)"
                                                                @keyup.delete="checkDeleteIntentaiForbiddenWord(index, (nodeInspector.intentaiForbiddenWords && nodeInspector.intentaiForbiddenWords[index]) || '')"
                                                                @update:model-value="updateIntentaiForbiddenWordProperty(index, $event)"
                                                                :readonly="isViewMode" />
                                                            <v-btn icon="mdi-attachment" variant="text"
                                                                v-show="(nodeInspector.intentaiForbiddenWords || []).length > 1 && index + 1 < (nodeInspector.intentaiForbiddenWords || []).length"
                                                                @click="removeIntentaiForbiddenWordDirect(index)"
                                                                :disabled="isViewMode">
                                                                <v-tooltip location="top" :open-delay="500">
                                                                    <template v-slot:activator="{ props }">
                                                                        <v-icon color="error" style="cursor: pointer"
                                                                            v-bind="props">
                                                                            mdi-delete-forever
                                                                        </v-icon>
                                                                    </template>
                                                                    <span>delete</span>
                                                                </v-tooltip>
                                                            </v-btn>
                                                        </div>
                                                    </v-col>
                                                </v-row>
                                            </div>
                                        </div>
                                    </div>
                                </v-expansion-panel-text>
                            </v-expansion-panel>

                            <!-- System Prompt Panel -->
                            <v-expansion-panel value="intentaiprompt" class="panel-item">
                                <v-expansion-panel-title class="panel-title-custom">
                                    <div class="d-flex align-center py-3">
                                        <div class="panel-icon-wrapper me-4"
                                            style="background: linear-gradient(135deg, #607D8B, #455A64);">
                                            <v-icon icon="mdi-message-text" size="20" color="white"></v-icon>
                                        </div>
                                        <div>
                                            <div class="panel-title">System Prompt</div>
                                            <div class="panel-subtitle">Define AI behavior and personality</div>
                                        </div>
                                    </div>
                                </v-expansion-panel-title>
                                <v-expansion-panel-text class="panel-content-wrapper">
                                    <div class="panel-content">
                                        <div class="prompt-section">
                                            <div class="d-flex align-center mb-4">
                                                <v-avatar size="36" color="info" class="me-3">
                                                    <v-icon color="white" size="20">mdi-robot-outline</v-icon>
                                                </v-avatar>
                                                <div>
                                                    <h6 class="text-h6 mb-1">AI Personality & Instructions</h6>
                                                    <p class="text-body-2 text-medium-emphasis mb-0">
                                                        Define how your AI should behave, respond, and interact with
                                                        users
                                                    </p>
                                                </div>
                                            </div>
                                            <h3 class="text-h6 d-flex align-center mb-3" style="font-size: 16px !important;">
                                                <v-icon icon="mdi-pencil-outline" size="small"
                                                    class="me-2"></v-icon>
                                                Configure system message for your AI
                                            </h3>

                                            <!-- Prompt Template Header -->
                                            <div class="mb-2" style="display:flex;flex-direction: column;align-items: flex-end;">
                                                <div style="display: flex; align-items: center;">
                                                    <v-tooltip location="top">
                                                        <template v-slot:activator="{ props }">
                                                            <v-icon icon="mdi-information-outline" size="small"
                                                                color="grey" class="me-3" v-bind="props"></v-icon>
                                                        </template>
                                                        <span>Configure additional prompts and templates</span>
                                                    </v-tooltip>
                                                    <v-btn
                                                        v-if="DDGroupPromptTemplate && DDGroupPromptTemplate.length > 0"
                                                        variant="outlined" color="primary"
                                                        prepend-icon="mdi-playlist-plus" @click="showIntentaiPromptTemplate"
                                                        class="text-none font-weight-medium" size="small">
                                                        Show Templates
                                                    </v-btn>
                                                </div>
                                            </div>

                                            <!-- Main editor and templates layout -->
                                            <v-row>
                                                <!-- Editor area (full width) -->
                                                <v-col cols="12" class="prompt-editor-col">
                                                    <v-card variant="outlined" class="prompt-editor-card">
                                                        <v-card-text class="px-2 py-1">
                                                            <v-textarea v-model="nodeInspector.intentaiSystemPrompt" label=""
                                                                variant="plain" counter auto-grow rows="25"
                                                                @update:model-value="updateNodeProperty('intentaiSystemPrompt', $event)"
                                                                :readonly="isViewMode"
                                                                placeholder="Enter your system message here..."
                                                                class="prompt-textarea"
                                                                @contextmenu="onIntentaiContextMenu"></v-textarea>
                                                        </v-card-text>
                                                    </v-card>
                                                </v-col>
                                            </v-row>

                                            <!-- Context menu for prompt templates -->
                                            <v-menu v-model="showIntentaiMenu" :activator="{ x, y }" location="bottom">
                                                <v-card min-width="250" elevation="3">
                                                    <v-list density="compact" class="py-0">
                                                        <v-list-subheader class="d-flex bg-grey-lighten-4 py-2">
                                                            <v-icon icon="mdi-text-box-plus-outline" class="me-2"
                                                                size="small"></v-icon>
                                                            Insert Template
                                                        </v-list-subheader>
                                                        <v-divider :thickness="1"
                                                            class="border-opacity-100"></v-divider>
                                                        <v-list-item v-for="(item, index) in filteredIntentaiPromptTemplates"
                                                            :key="index"
                                                            @click="setSelectIntentaiPromptTemplate(item); showIntentaiMenu = false;"
                                                            :value="item.name" class="template-list-item">
                                                            <template v-slot:prepend>
                                                                <v-icon size="small"
                                                                    icon="mdi-text-box-outline"></v-icon>
                                                            </template>
                                                            <template v-slot:title>
                                                                <span>{{ item.name }}</span>
                                                            </template>
                                                        </v-list-item>
                                                    </v-list>
                                                </v-card>
                                            </v-menu>

                                            <!-- Dialog for showing all templates -->
                                            <v-dialog v-model="dialogShowAllIntentai" max-width="550" scrollable>
                                                <v-card>
                                                    <v-card-title class="bg-primary text-white d-flex align-center">
                                                        <v-icon icon="mdi-text-box-multiple-outline"
                                                            class="me-2"></v-icon>
                                                        Prompt Templates
                                                    </v-card-title>
                                                    <v-divider :thickness="1" class="border-opacity-100"></v-divider>
                                                    <v-card-text style="max-height: 500px" class="pt-4">
                                                        <v-text-field v-model="intentaiTemplateSearchQuery"
                                                            label="Search templates" prepend-inner-icon="mdi-magnify"
                                                            variant="outlined" density="compact" hide-details
                                                            class="mb-3" clearable></v-text-field>

                                                        <v-list density="compact" class="template-list-all pa-0">
                                                            <v-list-item v-for="(item, i) in searchedIntentaiTemplates" :key="i"
                                                                @click="setSelectIntentaiPromptTemplate(item)"
                                                                class="template-list-item" :ripple="true">
                                                                <template v-slot:prepend>
                                                                    <v-icon size="small" color="primary"
                                                                        icon="mdi-text-box-outline"></v-icon>
                                                                </template>
                                                                <template v-slot:title>
                                                                    <span>{{ item.name }}</span>
                                                                </template>
                                                                <template v-slot:subtitle>
                                                                    <span class="text-caption text-truncate">
                                                                        {{ truncatePrompt(item.prompt) }}
                                                                    </span>
                                                                </template>
                                                            </v-list-item>
                                                        </v-list>
                                                    </v-card-text>
                                                    <v-card-actions>
                                                        <v-spacer></v-spacer>
                                                        <v-btn color="blue darken-1" variant="text"
                                                            @click="dialogShowAllIntentai = false">
                                                            Close
                                                        </v-btn>
                                                    </v-card-actions>
                                                </v-card>
                                            </v-dialog>

                                            <!-- Main Prompt Template Dialog -->
                                            <v-dialog v-model="intentaiPromptTemplateDialog" max-width="800" scrollable>
                                                <v-card>
                                                    <v-card-title class="bg-primary text-white d-flex align-center">
                                                        <v-icon icon="mdi-text-box-multiple-outline" class="me-2"></v-icon>
                                                        Prompt Templates
                                                        <v-spacer></v-spacer>
                                                        <v-btn icon="mdi-close" variant="text" color="white" size="small" 
                                                            @click="intentaiPromptTemplateDialog = false"></v-btn>
                                                    </v-card-title>
                                                    
                                                    <v-card-text class="pa-4">
                                                        <!-- Template Group Selection -->
                                                        <v-autocomplete 
                                                            label="Select Template Group" 
                                                            :items="DDGroupPromptTemplate" 
                                                            item-title="name"
                                                            item-value="id" 
                                                            density="compact" 
                                                            clearable
                                                            variant="outlined" 
                                                            v-model="intentaiGroupPromptTemplate"
                                                            prepend-inner-icon="mdi-folder-outline" 
                                                            hide-details
                                                            class="mb-4">
                                                        </v-autocomplete>

                                                        <!-- Search Template -->
                                                        <v-text-field 
                                                            v-model="intentaiTemplateSearchQuery"
                                                            label="Search templates" 
                                                            prepend-inner-icon="mdi-magnify"
                                                            variant="outlined" 
                                                            density="compact" 
                                                            hide-details
                                                            class="mb-4" 
                                                            clearable>
                                                        </v-text-field>

                                                        <!-- Templates List -->
                                                        <div style="max-height: 400px; overflow-y: auto;">
                                                            <div v-if="searchedIntentaiTemplates.length === 0" 
                                                                class="pa-4 text-center text-grey-darken-1">
                                                                <v-icon icon="mdi-playlist-remove" size="large" 
                                                                    class="d-block mx-auto mb-2"></v-icon>
                                                                <span>No templates found</span>
                                                            </div>

                                                            <v-list v-else density="compact" class="pa-0">
                                                                <v-list-item 
                                                                    v-for="(item, i) in searchedIntentaiTemplates" 
                                                                    :key="i"
                                                                    @click="setSelectIntentaiPromptTemplate(item)"
                                                                    class="template-list-item ma-1" 
                                                                    :ripple="true"
                                                                    rounded>
                                                                    <template v-slot:prepend>
                                                                        <v-icon size="small" color="primary" 
                                                                            icon="mdi-text-box-outline"></v-icon>
                                                                    </template>
                                                                    <template v-slot:title>
                                                                        <span class="font-weight-medium">{{ item.name }}</span>
                                                                    </template>
                                                                    <template v-slot:subtitle>
                                                                        <span class="text-caption text-grey">
                                                                            {{ truncatePrompt(item.prompt) }}
                                                                        </span>
                                                                    </template>
                                                                    <template v-slot:append>
                                                                        <v-btn icon="mdi-plus" size="small" variant="text" 
                                                                            color="primary" @click.stop="setSelectIntentaiPromptTemplate(item)">
                                                                        </v-btn>
                                                                    </template>
                                                                </v-list-item>
                                                            </v-list>
                                                        </div>
                                                    </v-card-text>
                                                    
                                                    <v-card-actions>
                                                        <v-spacer></v-spacer>
                                                        <v-btn color="primary" variant="text" @click="intentaiPromptTemplateDialog = false">
                                                            Close
                                                        </v-btn>
                                                    </v-card-actions>
                                                </v-card>
                                            </v-dialog>

                                            <div class="prompt-tips mt-4">
                                                <v-card class="tips-card" color="blue-grey-lighten-5" flat>
                                                    <v-card-text class="pa-4">
                                                        <div class="d-flex align-start">
                                                            <v-icon color="info" size="20"
                                                                class="me-2 mt-1">mdi-lightbulb-outline</v-icon>
                                                            <div>
                                                                <div class="text-subtitle-2 font-weight-medium mb-2">
                                                                    Tips for
                                                                    better prompts:</div>
                                                                <ul class="text-body-2 text-medium-emphasis">
                                                                    <li>Be specific about the AI's role and expertise
                                                                    </li>
                                                                    <li>Define the tone and communication style</li>
                                                                    <li>Include any restrictions or guidelines</li>
                                                                    <li>Specify how to handle unknown information</li>
                                                                </ul>
                                                            </div>
                                                        </div>
                                                    </v-card-text>
                                                </v-card>
                                            </div>
                                        </div>
                                    </div>
                                </v-expansion-panel-text>
                            </v-expansion-panel>
                        </v-expansion-panels>

                    </div>
                    <div v-else-if="selectedNode.data.type === 'intent'" class="form-section">
                        <h6 class="section-title">Intent Configuration</h6>
                        <v-text-field v-model="nodeInspector.intentGroupName" label="Intent handler Name"
                            variant="outlined" density="compact" hide-details="auto"
                            @update:model-value="updateNodeProperty('intentGroupName', $event)"
                            class="modern-input mb-4" :readonly="isViewMode" />

                        <!-- Intents Configuration -->
                        <div class="intents-section">
                            <div class="section-header mb-3">
                                <h6 class="section-title mb-0">Intents</h6>
                                <v-btn v-if="!isViewMode" size="small" color="primary" variant="tonal"
                                    @click="addInspectorIntent" class="add-btn">
                                    <v-icon left size="16">mdi-plus</v-icon>
                                    Add Intent
                                </v-btn>
                            </div>

                            <div v-for="(intent, intentIndex) in nodeInspector.intents" :key="intent.id"
                                class="intent-card mb-3">
                                <v-expansion-panels variant="accordion" class="intent-expansion">
                                    <v-expansion-panel>
                                        <v-expansion-panel-title>
                                            <div class="d-flex align-center w-100">
                                                <v-icon class="mr-2" color="primary" size="small">mdi-target</v-icon>
                                                <!-- Priority Number -->
                                                <span v-if="intent.priority || intent.priority === 0"
                                                    class="priority-number mr-2">
                                                    {{ intent.priority }}
                                                </span>
                                                <span class="font-weight-medium">
                                                    {{
                                                        intent.name || `Intent ${intentIndex
                                                        + 1}`
                                                    }}
                                                </span>
                                                <v-spacer></v-spacer>
                                                <v-btn v-if="!isViewMode" icon size="x-small" color="error"
                                                    variant="text" @click.stop="removeInspectorIntent(intentIndex)"
                                                    :disabled="nodeInspector.intents.length <= 1" class="ml-2">
                                                    <v-icon size="16">mdi-delete</v-icon>
                                                </v-btn>
                                            </div>
                                        </v-expansion-panel-title>
                                        <v-expansion-panel-text>
                                            <div class="intent-content pa-2">
                                                <!-- Intent Priority -->
                                                <v-text-field
                                                    v-model.number="nodeInspector.intents[intentIndex].priority"
                                                    label="Priority" type="number" variant="outlined" density="compact"
                                                    hide-details="auto" min="0" step="1" placeholder="Enter priority"
                                                    @update:model-value="updateIntentData()"
                                                    @keypress="isNumberpositive($event)" class="modern-input mb-3"
                                                    :readonly="isViewMode">
                                                    <template v-slot:prepend-inner>
                                                        <v-icon size="small" color="orange">mdi-priority-high</v-icon>
                                                    </template>
                                                    <template v-slot:append-inner>
                                                        <v-tooltip location="top" max-width="200px">
                                                            <template v-slot:activator="{ props }">
                                                                <v-icon size="small" color="grey-darken-1"
                                                                    v-bind="props">
                                                                    mdi-information-outline
                                                                </v-icon>
                                                            </template>
                                                            <span>Priority 0 is the highest importance</span>
                                                        </v-tooltip>
                                                    </template>
                                                </v-text-field>

                                                <!-- Intent Name -->
                                                <v-text-field v-model="nodeInspector.intents[intentIndex].name"
                                                    label="Intent Name" variant="outlined" density="compact"
                                                    hide-details="auto" @update:model-value="updateIntentData()"
                                                    class="modern-input mb-3" :readonly="isViewMode" />

                                                <div
                                                    style="display: flex; align-items: center; justify-content: flex-start;margin-top: -15px;">
                                                    <v-switch v-model="nodeInspector.intents[intentIndex].useMemory"
                                                        hide-details density="compact" color="primary" inset
                                                        style="transform: scale(0.9); margin-left: 0px; margin-top: 10px;"
                                                        @update:model-value="updateIntentData()" class="mb-3"
                                                        :readonly="isViewMode" />
                                                    <span style="font-size: 16px;margin-left: 8px;">
                                                        Use memory
                                                    </span>
                                                </div>

                                                <div class="custom-divider"></div>

                                                <!-- Base Prompt Section -->
                                                <div style="margin-bottom: 36px;">
                                                    <!-- Base Prompt Toggle -->
                                                    <div
                                                        style="display: flex;align-items: center;justify-content: flex-end;">
                                                        <span
                                                            style="font-weight: 500;font-size: 12px;margin-right: 8px;">
                                                            Edit Base Prompt
                                                        </span>
                                                        <v-switch
                                                            v-model="nodeInspector.intents[intentIndex].enableBasePrompt"
                                                            color="primary" density="compact" hide-details
                                                            :readonly="isViewMode"
                                                            @update:model-value="updateIntentData()" inset
                                                            style="transform: scale(0.8);">
                                                        </v-switch>
                                                    </div>

                                                    <!-- Base Prompt Textarea -->
                                                    <v-textarea v-model="nodeInspector.intents[intentIndex].basePrompt"
                                                        label="Base Prompt" variant="outlined" density="compact"
                                                        rows="4" auto-grow hide-details="auto"
                                                        @update:model-value="updateIntentData()" class="modern-input"
                                                        :readonly="isViewMode || !nodeInspector.intents[intentIndex].enableBasePrompt"
                                                        :disabled="!nodeInspector.intents[intentIndex].enableBasePrompt"
                                                        placeholder="Find the Intent from the message of users.&#10;#Condition for Response&#10;1. If the user asks a question related to or similar in meaning to the #Group Intent below. You must response 1. Otherwise, 0" />
                                                </div>

                                                <!-- Intent Prompt -->
                                                <v-textarea v-model="nodeInspector.intents[intentIndex].prompt"
                                                    label="Intent Prompt" variant="outlined" density="compact" rows="2"
                                                    auto-grow hide-details="auto"
                                                    @update:model-value="updateIntentData()" class="modern-input mb-3"
                                                    :readonly="isViewMode" />

                                                <!-- Example Messages -->
                                                <div class="mb-3">
                                                    <div class="d-flex align-center mb-2">
                                                        <span class="text-subtitle-2 font-weight-medium">
                                                            Example Messages
                                                        </span>
                                                        <v-btn v-if="!isViewMode" size="x-small" color="primary"
                                                            variant="tonal"
                                                            @click="addInspectorIntentMessage(intentIndex)"
                                                            class="ml-auto">
                                                            <v-icon size="12">mdi-plus</v-icon>
                                                        </v-btn>
                                                    </div>
                                                    <div v-for="(message, messageIndex) in intent.messages"
                                                        :key="message.id" class="d-flex align-center mb-2">
                                                        <v-text-field
                                                            v-model="nodeInspector.intents[intentIndex].messages[messageIndex].text"
                                                            placeholder="Example user message..." variant="outlined"
                                                            density="compact" hide-details="auto"
                                                            @update:model-value="updateIntentData()"
                                                            class="modern-input flex-grow-1 mr-2"
                                                            :readonly="isViewMode" />
                                                        <v-btn v-if="!isViewMode" icon size="x-small" color="error"
                                                            variant="text"
                                                            @click="removeInspectorIntentMessage(intentIndex, messageIndex)"
                                                            :disabled="intent.messages.length <= 1">
                                                            <v-icon size="14">mdi-close</v-icon>
                                                        </v-btn>
                                                    </div>
                                                </div>

                                                <!-- Actions Section -->
                                                <div class="actions-section">
                                                    <!-- Main Action -->
                                                    <div class="mb-3">
                                                        <span class="text-subtitle-2 font-weight-medium">
                                                            Main
                                                            Action
                                                        </span>
                                                        <v-text-field
                                                            v-model="nodeInspector.intents[intentIndex].mainAction.label"
                                                            placeholder="Main action label..." variant="outlined"
                                                            density="compact" hide-details="auto"
                                                            @update:model-value="updateIntentData()"
                                                            class="modern-input mt-2" :readonly="isViewMode" />
                                                    </div>

                                                    <!-- Parallel Actions -->
                                                    <div>
                                                        <div class="d-flex align-center mb-2">
                                                            <span class="text-subtitle-2 font-weight-medium">
                                                                Parallel
                                                                Actions
                                                            </span>
                                                            <v-btn v-if="!isViewMode" size="x-small" color="primary"
                                                                variant="tonal"
                                                                @click="addInspectorParallelAction(intentIndex)"
                                                                class="ml-auto">
                                                                <v-icon size="12">mdi-plus</v-icon>
                                                            </v-btn>
                                                        </div>
                                                        <div v-for="(parallel, parallelIndex) in intent.parallelActions"
                                                            :key="parallel.id" class="d-flex align-center mb-2">
                                                            <v-text-field
                                                                v-model="nodeInspector.intents[intentIndex].parallelActions[parallelIndex].label"
                                                                placeholder="Parallel action label..."
                                                                variant="outlined" density="compact" hide-details="auto"
                                                                @update:model-value="updateIntentData()"
                                                                class="modern-input flex-grow-1 mr-2"
                                                                :readonly="isViewMode" />
                                                            <v-btn v-if="!isViewMode" icon size="x-small" color="error"
                                                                variant="text"
                                                                @click="removeInspectorParallelAction(intentIndex, parallelIndex)">
                                                                <v-icon size="14">mdi-close</v-icon>
                                                            </v-btn>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </v-expansion-panel-text>
                                    </v-expansion-panel>
                                </v-expansion-panels>
                            </div>
                        </div>
                    </div>

                    <!-- Smart Intent Inspector Section -->
                    <div v-else-if="selectedNode.data.type === 'smartIntent'" class="form-section">
                        <h6 class="section-title">Smart Intent Configuration</h6>
                        <v-text-field v-model="nodeInspector.smartIntentGroupName" label="Smart Intent Handler Name"
                            variant="outlined" density="compact" hide-details="auto"
                            @update:model-value="updateNodeProperty('smartIntentGroupName', $event)"
                            class="modern-input mb-4" :readonly="isViewMode" />

                        <!-- Smart Intents Configuration -->
                        <div class="smart-intents-section">
                            <div class="section-header mb-3">
                                <h6 class="section-title mb-0">Smart Intents</h6>
                                <div class="d-flex gap-2">
                                    <v-btn v-if="!isViewMode" size="small" color="primary" variant="tonal"
                                        @click="addInspectorSmartIntent" class="add-btn">
                                        <v-icon left size="16">mdi-plus</v-icon>
                                        Add Smart Intent
                                    </v-btn>
                                </div>
                            </div>

                            <div v-for="(smartIntent, smartIntentIndex) in nodeInspector.smartIntents" :key="smartIntent.id"
                                class="smart-intent-card mb-3">
                                <v-expansion-panels variant="accordion" class="smart-intent-expansion">
                                    <v-expansion-panel>
                                        <v-expansion-panel-title>
                                            <div class="d-flex align-center w-100">
                                                <v-icon class="mr-2" color="primary" size="small">mdi-brain-outline</v-icon>
                                                <!-- Priority Number -->
                                                <span v-if="smartIntent.priority || smartIntent.priority === 0"
                                                    class="priority-number mr-2">
                                                    {{ smartIntent.priority }}
                                                </span>
                                                <span class="font-weight-medium">
                                                    {{
                                                        smartIntent.name || `Smart Intent ${smartIntentIndex + 1}`
                                                    }}
                                                </span>
                                                <v-spacer></v-spacer>
                                                <v-btn v-if="!isViewMode" icon size="x-small" color="error"
                                                    variant="text" @click.stop="removeInspectorSmartIntent(smartIntentIndex)"
                                                    :disabled="nodeInspector.smartIntents.length <= 1" class="ml-2">
                                                    <v-icon size="16">mdi-delete</v-icon>
                                                </v-btn>
                                            </div>
                                        </v-expansion-panel-title>
                                        <v-expansion-panel-text>
                                            <div class="smart-intent-content pa-2">
                                                <!-- Smart Intent Priority -->
                                                <v-text-field
                                                    v-model.number="nodeInspector.smartIntents[smartIntentIndex].priority"
                                                    label="Priority" type="number" variant="outlined" density="compact"
                                                    hide-details="auto" min="0" step="1" placeholder="Enter priority"
                                                    @update:model-value="updateSmartIntentData()"
                                                    @keypress="isNumberpositive($event)" class="modern-input mb-3"
                                                    :readonly="isViewMode">
                                                    <template v-slot:prepend-inner>
                                                        <v-icon size="small" color="orange">mdi-priority-high</v-icon>
                                                    </template>
                                                    <template v-slot:append-inner>
                                                        <v-tooltip location="top" max-width="200px">
                                                            <template v-slot:activator="{ props }">
                                                                <v-icon size="small" color="grey-darken-1"
                                                                    v-bind="props">
                                                                    mdi-information-outline
                                                                </v-icon>
                                                            </template>
                                                            <span>Priority 0 is the highest importance</span>
                                                        </v-tooltip>
                                                    </template>
                                                </v-text-field>

                                                <!-- Smart Intent Name -->
                                                <v-text-field v-model="nodeInspector.smartIntents[smartIntentIndex].name"
                                                    label="Smart Intent Name" variant="outlined" density="compact"
                                                    hide-details="auto" @update:model-value="updateSmartIntentData()"
                                                    class="modern-input mb-3" :readonly="isViewMode" />

                                                <!-- Use Memory Toggle -->
                                                <div style="display: flex; align-items: center; justify-content: flex-start;margin-top: -15px;">
                                                    <v-switch v-model="nodeInspector.smartIntents[smartIntentIndex].useMemory"
                                                        hide-details density="compact" color="primary" inset
                                                        style="transform: scale(0.9); margin-left: 0px; margin-top: 10px;"
                                                        @update:model-value="updateSmartIntentData()" class="mb-3"
                                                        :readonly="isViewMode" />
                                                    <span style="font-size: 16px;margin-left: 8px;">
                                                        Use memory
                                                    </span>
                                                </div>

                                                <div class="custom-divider"></div>

                                                <!-- Base Prompt Section -->
                                                <div style="margin-bottom: 36px;">
                                                    <!-- Base Prompt Toggle -->
                                                    <div style="display: flex;align-items: center;justify-content: flex-end;">
                                                        <span style="font-weight: 500;font-size: 12px;margin-right: 8px;">
                                                            Edit Base Prompt
                                                        </span>
                                                        <v-switch
                                                            v-model="nodeInspector.smartIntents[smartIntentIndex].enableBasePrompt"
                                                            color="primary" density="compact" hide-details
                                                            :readonly="isViewMode"
                                                            @update:model-value="updateSmartIntentData()" inset
                                                            style="transform: scale(0.8);">
                                                        </v-switch>
                                                    </div>

                                                    <!-- Base Prompt Textarea -->
                                                    <v-textarea v-model="nodeInspector.smartIntents[smartIntentIndex].basePrompt"
                                                        label="Base Prompt" variant="outlined" density="compact"
                                                        rows="4" auto-grow hide-details="auto"
                                                        @update:model-value="updateSmartIntentData()" class="modern-input"
                                                        :readonly="isViewMode || !nodeInspector.smartIntents[smartIntentIndex].enableBasePrompt"
                                                        :disabled="!nodeInspector.smartIntents[smartIntentIndex].enableBasePrompt"
                                                        placeholder="Find the Intent from the message of users.&#10;#Condition for Response&#10;1. If the user asks a question related to or similar in meaning to the #Group Intent below. You must response 1. Otherwise, 0" />
                                                </div>

                                                <!-- Smart Intent Prompt -->
                                                <v-textarea v-model="nodeInspector.smartIntents[smartIntentIndex].prompt"
                                                    label="Smart Intent Prompt" variant="outlined" density="compact" rows="2"
                                                    auto-grow hide-details="auto"
                                                    @update:model-value="updateSmartIntentData()" class="modern-input mb-3"
                                                    :readonly="isViewMode" />

                                                <!-- Example Messages -->
                                                <div class="mb-3">
                                                    <div class="d-flex align-center mb-2">
                                                        <span class="text-subtitle-2 font-weight-medium">
                                                            Example Messages
                                                        </span>
                                                        <v-btn v-if="!isViewMode" size="x-small" color="primary"
                                                            variant="tonal"
                                                            @click="addInspectorSmartIntentMessage(smartIntentIndex)"
                                                            class="ml-auto">
                                                            <v-icon size="12">mdi-plus</v-icon>
                                                        </v-btn>
                                                    </div>
                                                    <div v-for="(message, messageIndex) in smartIntent.messages"
                                                        :key="message.id" class="d-flex align-center mb-2">
                                                        <v-text-field
                                                            v-model="nodeInspector.smartIntents[smartIntentIndex].messages[messageIndex].text"
                                                            placeholder="Example user message..." variant="outlined"
                                                            density="compact" hide-details="auto"
                                                            @update:model-value="updateSmartIntentData()"
                                                            class="modern-input flex-grow-1 mr-2"
                                                            :readonly="isViewMode" />
                                                        <v-btn v-if="!isViewMode" icon size="x-small" color="error"
                                                            variant="text"
                                                            @click="removeInspectorSmartIntentMessage(smartIntentIndex, messageIndex)"
                                                            :disabled="smartIntent.messages.length <= 1">
                                                            <v-icon size="14">mdi-close</v-icon>
                                                        </v-btn>
                                                    </div>
                                                </div>

                                                <!-- Main Action Section with AI Configuration -->
                                                <div class="main-action-section mb-4">
                                                    <div class="d-flex align-center justify-space-between mb-2">
                                                        <span class="text-subtitle-2 font-weight-medium">Action Label</span>
                                                    </div>
                                                    
                                                    <!-- Action Label -->
                                                    <v-text-field
                                                        v-model="nodeInspector.smartIntents[smartIntentIndex].mainAction.label"
                                                        placeholder="Main action label..." variant="outlined"
                                                        density="compact" hide-details="auto"
                                                        @update:model-value="updateSmartIntentData()"
                                                        class="modern-input mt-2 mb-3" :readonly="isViewMode" />

                                                    <!-- <div class="custom-divider my-3"></div> -->
                                                    <!-- AI Toggle -->
                                                    <div class="ai-toggle-section mb-3">
                                                        <div style="display: flex; align-items: center; justify-content: space-between;">
                                                            <span class="text-subtitle-2 font-weight-medium">
                                                                <v-icon size="16" class="mr-2" color="primary">mdi-robot</v-icon>
                                                                Use AI Model
                                                            </span>
                                                            <div style="display: flex; align-items: center; gap: 8px;">
                                                                    <v-switch 
                                                                        v-model="nodeInspector.smartIntents[smartIntentIndex].mainAction.useAI"
                                                                        color="primary" density="compact" hide-details inset
                                                                        @update:model-value="updateSmartIntentData()"
                                                                        :readonly="isViewMode"
                                                                        style="transform: scale(0.8);" />
                                                                </div>
                                                            </div>
                                                    </div>

                                                    <!-- AI Configuration (when enabled) -->
                                                    <div v-if="nodeInspector.smartIntents[smartIntentIndex].mainAction.useAI" 
                                                         class="ai-config-section">
                                                         <div style="display: flex; align-items: center; justify-content: flex-end; margin-bottom: 8px;width: 100%;">  
                                                            <v-btn class="mb-3" v-if="!isViewMode && nodeInspector.smartIntents[smartIntentIndex].mainAction.useAI" @click="openSmartIntentLoadFromModelDialog(smartIntentIndex)" 
                                                                color="success" variant="text" size="small"
                                                                style="background-color: #e8f5e9;"
                                                                prepend-icon="mdi-download">
                                                                Load Config
                                                            </v-btn>
                                                         </div>
                                                        
                                                        <!-- AI Configuration Expandable Section -->
                                                        <v-expansion-panels variant="accordion" class="ai-main-config-expansion">
                                                            <v-expansion-panel>
                                                                <v-expansion-panel-title class="ai-config-title">
                                                                    <div class="d-flex align-center">
                                                                        <v-icon icon="mdi-brain" size="20" color="primary" class="me-3"></v-icon>
                                                                        <div>
                                                                            <div class="text-subtitle-1 font-weight-medium">AI Model Configuration</div>
                                                                            <div class="text-caption text-medium-emphasis">Provider, parameters, and behavior settings</div>
                                                                        </div>
                                                                    </div>
                                                                </v-expansion-panel-title>
                                                                <v-expansion-panel-text>
                                                                    <!-- AI Model Configuration Panels (exactly like AI Model Node) -->
                                                                    <v-expansion-panels multiple class="config-panels elevation-0" variant="accordion">
                                                            <!-- Basic Information Panel -->
                                                            <v-expansion-panel value="basic" class="panel-item mt-3">
                                                                <v-expansion-panel-title class="panel-title-custom">
                                                                    <div class="d-flex align-center py-3">
                                                                        <div class="panel-icon-wrapper me-4">
                                                                            <v-icon icon="mdi-information" size="20" color="white"></v-icon>
                                                                        </div>
                                                                        <div>
                                                                            <div class="panel-title">Basic Information</div>
                                                                            <div class="panel-subtitle">Provider, model, and basic settings</div>
                                                                        </div>
                                                                    </div>
                                                                </v-expansion-panel-title>
                                                                <v-expansion-panel-text class="panel-content-wrapper">
                                                                    <div class="panel-content">
                                                                        <!-- Provider and Model -->
                                                                        <div class="config-section mb-6">
                                                                            <h6 class="section-label mb-3">Service Configuration</h6>
                                                                            <v-row>
                                                                                <v-col cols="12">
                                                                                    <v-autocomplete 
                                                                                        v-model="nodeInspector.smartIntents[smartIntentIndex].mainAction.provider"
                                                                                        :items="listDDProvider" item-title="providerName" item-value="providerName"
                                                                                        label="Provider*" variant="outlined" density="comfortable" hide-details="auto"
                                                                                        prepend-inner-icon="mdi-cloud-outline"
                                                                                        @update:model-value="updateSmartIntentProviderChange(smartIntentIndex, $event)"
                                                                                        class="config-input" :readonly="isViewMode"
                                                                                        hint="Select AI service provider" persistent-hint required />
                                                                                </v-col>
                                                                            </v-row>
                                                                            <v-row>
                                                                                <v-col cols="12">
                                                                                    <v-autocomplete 
                                                                                        v-model="nodeInspector.smartIntents[smartIntentIndex].mainAction.model"
                                                                                        :items="getModelsByProvider(nodeInspector.smartIntents[smartIntentIndex].mainAction.provider)"
                                                                                        item-title="modelDisplay" item-value="modelName" label="Model*"
                                                                                        variant="outlined" density="comfortable" hide-details="auto"
                                                                                        prepend-inner-icon="mdi-brain"
                                                                                        @update:model-value="updateSmartIntentModelChange(smartIntentIndex, $event)"
                                                                                        class="config-input" :readonly="isViewMode"
                                                                                        :disabled="!nodeInspector.smartIntents[smartIntentIndex].mainAction.provider"
                                                                                        hint="Select specific model version" persistent-hint required />
                                                                                </v-col>
                                                                            </v-row>
                                                                        </div>

                                                                        <!-- Behavior Settings -->
                                                                        <div class="config-section">
                                                                            <h6 class="section-label mb-3">Behavior Settings</h6>
                                                                            <v-row>
                                                                                <v-col cols="12">
                                                                                    <v-select 
                                                                                        v-model="nodeInspector.smartIntents[smartIntentIndex].mainAction.gender"
                                                                                        :items="[{ id: 'male', name: 'Male' }, { id: 'female', name: 'Female' }]"
                                                                                        item-title="name" item-value="id" label="Gender"
                                                                                        variant="outlined" density="comfortable" hide-details="auto"
                                                                                        prepend-inner-icon="mdi-account"
                                                                                        @update:model-value="updateSmartIntentData()"
                                                                                        class="config-input" :readonly="isViewMode"
                                                                                        hint="Voice/persona gender preference" persistent-hint
                                                                                        :model-value="nodeInspector.smartIntents[smartIntentIndex].mainAction.gender || 'male'" />
                                                                                </v-col>
                                                                            </v-row>
                                                                            <v-row>
                                                                                <v-col cols="12">
                                                                                    <v-text-field 
                                                                                        v-model.number="nodeInspector.smartIntents[smartIntentIndex].mainAction.typingSpeed"
                                                                                        label="Typing Speed" type="number" min="0" variant="outlined"
                                                                                        density="comfortable" hide-details="auto"
                                                                                        prepend-inner-icon="mdi-keyboard-outline"
                                                                                        @update:model-value="updateSmartIntentData()"
                                                                                        @keypress="isNumberpositive($event)" class="config-input"
                                                                                        :readonly="isViewMode"
                                                                                        hint="Characters per second (0 = instant)" persistent-hint />
                                                                                </v-col>
                                                                            </v-row>
                                                                            <v-row>
                                                                                <v-col cols="12">
                                                                                    <v-text-field 
                                                                                        v-model.number="nodeInspector.smartIntents[smartIntentIndex].mainAction.maxInputLength"
                                                                                        label="Max Input Length" type="number" min="0" max="200000"
                                                                                        variant="outlined" density="comfortable" hide-details="auto"
                                                                                        prepend-inner-icon="mdi-text-box-outline"
                                                                                        @update:model-value="updateSmartIntentData()"
                                                                                        @keypress="maxNumberpositive($event)"
                                                                                        class="config-input" :readonly="isViewMode"
                                                                                        hint="Maximum characters allowed in user input" persistent-hint />
                                                                                </v-col>
                                                                            </v-row>
                                                                        </div>
                                                                    </div>
                                                                </v-expansion-panel-text>
                                                            </v-expansion-panel>

                                                            <!-- Response Options Panel -->
                                                            <v-expansion-panel value="response" class="panel-item">
                                                                <v-expansion-panel-title class="panel-title-custom">
                                                                    <div class="d-flex align-center py-3">
                                                                        <div class="panel-icon-wrapper me-4" style="background: linear-gradient(135deg, #FF9800, #F57C00);">
                                                                            <v-icon icon="mdi-tune" size="20" color="white"></v-icon>
                                                                        </div>
                                                                        <div>
                                                                            <div class="panel-title">Response Options</div>
                                                                            <div class="panel-subtitle">Configure how responses are generated</div>
                                                                        </div>
                                                                    </div>
                                                                </v-expansion-panel-title>
                                                                <v-expansion-panel-text class="panel-content-wrapper">
                                                                    <div class="panel-content pa-4">
                                                                        <!-- Stream Response -->
                                                                        <div class="switch-option-compact mb-3">
                                                                            <div class="d-flex align-center justify-space-between py-2 px-3 rounded-lg"
                                                                                :class="{ 'switch-active-bg': nodeInspector.smartIntents[smartIntentIndex].mainAction.streamResponse }">
                                                                                <div class="d-flex align-center">
                                                                                    <v-avatar size="28"
                                                                                        :color="nodeInspector.smartIntents[smartIntentIndex].mainAction.streamResponse ? 'primary' : 'grey-lighten-1'"
                                                                                        class="me-3">
                                                                                        <v-icon
                                                                                            :color="nodeInspector.smartIntents[smartIntentIndex].mainAction.streamResponse ? 'white' : 'grey-darken-1'"
                                                                                            size="16">mdi-text-box-outline</v-icon>
                                                                                    </v-avatar>
                                                                                    <div>
                                                                                        <div class="switch-title">Stream Response</div>
                                                                                        <div class="switch-description">Progressive text generation</div>
                                                                                    </div>
                                                                                </div>
                                                                                <v-switch 
                                                                                    v-model="nodeInspector.smartIntents[smartIntentIndex].mainAction.streamResponse" 
                                                                                    color="primary" hide-details density="compact"
                                                                                    @update:model-value="updateSmartIntentData()"
                                                                                    :readonly="isViewMode" inset />
                                                                            </div>
                                                                        </div>

                                                                        <!-- Time Context -->
                                                                        <div class="switch-option-compact">
                                                                            <div class="d-flex align-center justify-space-between py-2 px-3 rounded-lg"
                                                                                :class="{ 'switch-active-bg': nodeInspector.smartIntents[smartIntentIndex].mainAction.enableTime }">
                                                                                <div class="d-flex align-center">
                                                                                    <v-avatar size="28"
                                                                                        :color="nodeInspector.smartIntents[smartIntentIndex].mainAction.enableTime ? 'success' : 'grey-lighten-1'"
                                                                                        class="me-3">
                                                                                        <v-icon
                                                                                            :color="nodeInspector.smartIntents[smartIntentIndex].mainAction.enableTime ? 'white' : 'grey-darken-1'"
                                                                                            size="16">mdi-clock-outline</v-icon>
                                                                                    </v-avatar>
                                                                                    <div>
                                                                                        <div class="switch-title">Time Context</div>
                                                                                        <div class="switch-description">Enable time awareness</div>
                                                                                    </div>
                                                                                </div>
                                                                                <v-switch 
                                                                                    v-model="nodeInspector.smartIntents[smartIntentIndex].mainAction.enableTime" 
                                                                                    color="success" hide-details density="compact"
                                                                                    @update:model-value="updateSmartIntentData()"
                                                                                    :readonly="isViewMode" inset />
                                                                            </div>

                                                                            <!-- Time Zone Dropdown (shown when enableTime is true) -->
                                                                            <div v-if="nodeInspector.smartIntents[smartIntentIndex].mainAction.enableTime" class="mt-3">
                                                                                <v-select 
                                                                                    v-model="nodeInspector.smartIntents[smartIntentIndex].mainAction.timeZone" 
                                                                                    :items="DDTimeZone" item-title="name" item-value="value" 
                                                                                    label="Time Zone" variant="outlined" density="comfortable" hide-details="auto"
                                                                                    prepend-inner-icon="mdi-earth"
                                                                                    @update:model-value="updateSmartIntentData()"
                                                                                    class="config-input" :readonly="isViewMode"
                                                                                    hint="Select your timezone" persistent-hint
                                                                                    placeholder="Select your timezone">
                                                                                    <template v-slot:selection="{ item }">
                                                                                        <div class="d-flex align-center">
                                                                                            <v-icon small color="primary" class="me-2">mdi-map-marker</v-icon>
                                                                                            <span class="font-weight-medium">{{ item.raw.name }}</span>
                                                                                            <v-chip size="x-small" color="grey-darken-1" text-color="white" class="ml-2">
                                                                                                {{ item.raw.value && item.raw.value.includes('/') ? item.raw.value.split('/')[1].replace('_', ' ') : item.raw.value }}
                                                                                            </v-chip>
                                                                                        </div>
                                                                                    </template>
                                                                                </v-select>
                                                                            </div>
                                                                        </div>

                                                                        <!-- JSON Response -->
                                                                        <div class="switch-option-compact mt-3">
                                                                            <div class="d-flex align-center justify-space-between py-2 px-3 rounded-lg"
                                                                                :class="{ 'switch-active-bg': nodeInspector.smartIntents[smartIntentIndex].mainAction.jsonResponse }">
                                                                                <div class="d-flex align-center">
                                                                                    <v-avatar size="28"
                                                                                        :color="nodeInspector.smartIntents[smartIntentIndex].mainAction.jsonResponse ? 'primary' : 'grey-lighten-1'"
                                                                                        class="me-3">
                                                                                        <v-icon
                                                                                            :color="nodeInspector.smartIntents[smartIntentIndex].mainAction.jsonResponse ? 'white' : 'grey-darken-1'"
                                                                                            size="16">mdi-code-json</v-icon>
                                                                                    </v-avatar>
                                                                                    <div>
                                                                                        <div class="switch-title">JSON Response</div>
                                                                                        <div class="switch-description">Return structured JSON format</div>
                                                                                    </div>
                                                                                </div>
                                                                                <v-switch 
                                                                                    v-model="nodeInspector.smartIntents[smartIntentIndex].mainAction.jsonResponse" 
                                                                                    color="primary" hide-details density="compact"
                                                                                    @update:model-value="updateSmartIntentData()"
                                                                                    :readonly="isViewMode" inset />
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </v-expansion-panel-text>
                                                            </v-expansion-panel>

                                                            <!-- Parameters Panel -->
                                                            <v-expansion-panel value="parameters" class="panel-item">
                                                                <v-expansion-panel-title class="panel-title-custom">
                                                                    <div class="d-flex align-center py-3">
                                                                        <div class="panel-icon-wrapper me-4" style="background: linear-gradient(135deg, #9C27B0, #7B1FA2);">
                                                                            <v-icon icon="mdi-cog" size="20" color="white"></v-icon>
                                                                        </div>
                                                                        <div>
                                                                            <div class="panel-title">AI Parameters</div>
                                                                            <div class="panel-subtitle">Fine-tune model behavior and output</div>
                                                                        </div>
                                                                    </div>
                                                                </v-expansion-panel-title>
                                                                <v-expansion-panel-text class="panel-content-wrapper">
                                                                    <div class="panel-content">
                                                                        <div class="config-section mb-3">
                                                                            <h6 class="section-label mb-3">Core Parameters</h6>
                                                                            <v-row>
                                                                                <v-col cols="12">
                                                                                    <v-text-field 
                                                                                        v-model.number="nodeInspector.smartIntents[smartIntentIndex].mainAction.temperature"
                                                                                        label="Temperature*" type="number" step="0.1" min="0" max="1"
                                                                                        variant="outlined" density="comfortable" hide-details="auto"
                                                                                        prepend-inner-icon="mdi-thermometer"
                                                                                        @update:model-value="updateSmartIntentData()"
                                                                                        @keypress="isNumberpositive($event)" class="config-input"
                                                                                        :readonly="isViewMode"
                                                                                        hint="Creativity level (0 = focused, 1 = creative)" persistent-hint />
                                                                                </v-col>
                                                                            </v-row>
                                                                            <v-row>
                                                                                <v-col cols="12">
                                                                                    <v-text-field 
                                                                                        v-model.number="nodeInspector.smartIntents[smartIntentIndex].mainAction.topP" 
                                                                                        label="Top P" type="number" step="0.1" min="0" max="1" 
                                                                                        variant="outlined" density="comfortable" hide-details="auto"
                                                                                        prepend-inner-icon="mdi-filter-variant"
                                                                                        @update:model-value="updateSmartIntentData()"
                                                                                        @keypress="isNumberpositive($event)" class="config-input"
                                                                                        :readonly="isViewMode"
                                                                                        hint="Token selection probability threshold" persistent-hint />
                                                                                </v-col>
                                                                            </v-row>
                                                                        </div>

                                                                        <div class="config-section mb-3">
                                                                            <h6 class="section-label mb-3">Penalty Settings</h6>
                                                                            <v-row>
                                                                                <v-col cols="12">
                                                                                    <v-text-field 
                                                                                        v-model.number="nodeInspector.smartIntents[smartIntentIndex].mainAction.frequencyPenalty"
                                                                                        label="Frequency Penalty" type="number" step="0.1" min="0" max="2" 
                                                                                        variant="outlined" density="comfortable" hide-details="auto" 
                                                                                        prepend-inner-icon="mdi-repeat-off"
                                                                                        @update:model-value="updateSmartIntentData()"
                                                                                        @keypress="isNumberpositive($event)" class="config-input"
                                                                                        :readonly="isViewMode"
                                                                                        hint="Reduce repetition of frequent tokens" persistent-hint />
                                                                                </v-col>
                                                                            </v-row>
                                                                            <v-row>
                                                                                <v-col cols="12">
                                                                                    <v-text-field 
                                                                                        v-model.number="nodeInspector.smartIntents[smartIntentIndex].mainAction.presencePenalty"
                                                                                        label="Presence Penalty" type="number" step="0.1" min="0" max="2" 
                                                                                        variant="outlined" density="comfortable" hide-details="auto"
                                                                                        prepend-inner-icon="mdi-minus-circle-outline"
                                                                                        @update:model-value="updateSmartIntentData()"
                                                                                        @keypress="isNumberpositive($event)" class="config-input"
                                                                                        :readonly="isViewMode" 
                                                                                        hint="Reduce repetition of any tokens" persistent-hint />
                                                                                </v-col>
                                                                            </v-row>
                                                                        </div>

                                                                        <div class="config-section">
                                                                            <h6 class="section-label mb-3">Output Control</h6>
                                                                            <v-row>
                                                                                <v-col cols="12">
                                                                                    <v-text-field 
                                                                                        v-model.number="nodeInspector.smartIntents[smartIntentIndex].mainAction.maxTokens"
                                                                                        label="Max Tokens Response" type="number" min="1"
                                                                                        variant="outlined" density="comfortable" hide-details="auto"
                                                                                        prepend-inner-icon="mdi-text-box-check-outline"
                                                                                        @update:model-value="updateSmartIntentData()"
                                                                                        @keypress="isNumberpositive($event)" class="config-input"
                                                                                        :readonly="isViewMode" 
                                                                                        hint="Maximum response length in tokens" persistent-hint />
                                                                                </v-col>
                                                                            </v-row>
                                                                            <v-row>
                                                                                <v-col cols="12">
                                                                                    <v-text-field 
                                                                                        v-model="nodeInspector.smartIntents[smartIntentIndex].mainAction.stopSequence"
                                                                                        label="Stop Sequence" variant="outlined" density="comfortable"
                                                                                        hide-details="auto" prepend-inner-icon="mdi-stop-circle-outline"
                                                                                        @update:model-value="updateSmartIntentData()"
                                                                                        class="config-input" :readonly="isViewMode"
                                                                                        hint="Text sequence to stop generation" persistent-hint />
                                                                                </v-col>
                                                                            </v-row>
                                                                        </div>
                                                                    </div>
                                                                </v-expansion-panel-text>
                                                            </v-expansion-panel>

                                                            <!-- Memory Settings Panel -->
                                                            <v-expansion-panel value="memory" class="panel-item">
                                                                <v-expansion-panel-title class="panel-title-custom">
                                                                    <div class="d-flex align-center py-3">
                                                                        <div class="panel-icon-wrapper me-4"
                                                                            style="background: linear-gradient(135deg, #00BCD4, #0097A7);">
                                                                            <v-icon icon="mdi-brain" size="20" color="white"></v-icon>
                                                                        </div>
                                                                        <div>
                                                                            <div class="panel-title">Memory Settings</div>
                                                                            <div class="panel-subtitle">Configure conversation memory and context</div>
                                                                        </div>
                                                                    </div>
                                                                </v-expansion-panel-title>
                                                                <v-expansion-panel-text class="panel-content-wrapper">
                                                                    <div class="panel-content">
                                                                        <div class="config-section mb-3">
                                                                            <h6 class="section-label mb-3">Conversation Memory</h6>
                                                                            <v-row>
                                                                                <v-col cols="12">
                                                                                    <v-text-field 
                                                                                        v-model.number="nodeInspector.smartIntents[smartIntentIndex].mainAction.memoryTurn"
                                                                                        label="Turn Count" type="number" min="0" variant="outlined"
                                                                                        density="comfortable" hide-details="auto"
                                                                                        prepend-inner-icon="mdi-history"
                                                                                        @update:model-value="updateSmartIntentData()"
                                                                                        @keypress="isNumberpositive($event)" class="config-input"
                                                                                        :readonly="isViewMode"
                                                                                        hint="Number of conversation turns to remember"
                                                                                        persistent-hint />
                                                                                </v-col>
                                                                            </v-row>
                                                                            <v-row>
                                                                                <v-col cols="12">
                                                                                    <v-text-field 
                                                                                        v-model.number="nodeInspector.smartIntents[smartIntentIndex].mainAction.memoryDuration"
                                                                                        label="Duration (minutes)" type="number" min="0"
                                                                                        variant="outlined" density="comfortable" hide-details="auto"
                                                                                        prepend-inner-icon="mdi-timer-outline"
                                                                                        @update:model-value="updateSmartIntentData()"
                                                                                        @keypress="isNumberpositive($event)" class="config-input"
                                                                                        :readonly="isViewMode" hint="How long to keep memory active"
                                                                                        persistent-hint />
                                                                                </v-col>
                                                                            </v-row>
                                                                        </div>

                                                                        <div class="config-section mb-3">
                                                                            <h6 class="section-label mb-3">Memory Types</h6>
                                                                            <v-row>
                                                                                <v-col cols="12">
                                                                                    <v-text-field 
                                                                                        v-model.number="nodeInspector.smartIntents[smartIntentIndex].mainAction.shortTermMemory"
                                                                                        label="Short-Term Memory" type="number" min="1"
                                                                                        variant="outlined" density="comfortable" hide-details="auto"
                                                                                        prepend-inner-icon="mdi-memory"
                                                                                        @update:model-value="updateSmartIntentData()"
                                                                                        @keypress="isNumberpositive($event)" class="config-input"
                                                                                        :readonly="isViewMode" hint="Recent context window size"
                                                                                        persistent-hint />
                                                                                </v-col>
                                                                            </v-row>
                                                                            <v-row>
                                                                                <v-col>
                                                                                    <!-- Long-Term Memory Switch -->
                                                                                    <div class="switch-option-compact mb-4">
                                                                                        <div class="d-flex align-center justify-space-between py-2 px-3 rounded-lg"
                                                                                            :class="{ 'switch-active-bg': nodeInspector.smartIntents[smartIntentIndex].mainAction.longTermMemory }">
                                                                                            <div class="d-flex align-center">
                                                                                                <v-avatar size="28"
                                                                                                    :color="nodeInspector.smartIntents[smartIntentIndex].mainAction.longTermMemory ? 'success' : 'grey-lighten-1'"
                                                                                                    class="me-3">
                                                                                                    <v-icon
                                                                                                        :color="nodeInspector.smartIntents[smartIntentIndex].mainAction.longTermMemory ? 'white' : 'grey-darken-1'"
                                                                                                        size="16">mdi-database</v-icon>
                                                                                                </v-avatar>
                                                                                                <div>
                                                                                                    <div class="switch-title">Long-Term Memory</div>
                                                                                                    <div class="switch-description">Persistent storage
                                                                                                    </div>
                                                                                                </div>
                                                                                            </div>
                                                                                            <v-switch 
                                                                                                v-model="nodeInspector.smartIntents[smartIntentIndex].mainAction.longTermMemory"
                                                                                                color="success" hide-details density="compact"
                                                                                                @update:model-value="updateSmartIntentData()"
                                                                                                :readonly="isViewMode" inset />
                                                                                        </div>
                                                                                    </div>
                                                                                </v-col>
                                                                            </v-row>

                                                                        </div>

                                                                        <!-- Multi-turn Input Configuration -->
                                                                        <div class="config-section mb-3">
                                                                            <h6 class="section-label mb-3">Multi-turn Input Configuration</h6>
                                                                            <v-row>
                                                                                <v-col>
                                                                                    <!-- Enable Multi-Turn Input Switch -->
                                                                                    <div class="switch-option-compact mb-3">
                                                                                        <div class="d-flex align-center justify-space-between py-2 px-3 rounded-lg"
                                                                                            :class="{ 'switch-active-bg': nodeInspector.smartIntents[smartIntentIndex].mainAction.isChainMassage }">
                                                                                            <div class="d-flex align-center">
                                                                                                <v-avatar size="28"
                                                                                                    :color="nodeInspector.smartIntents[smartIntentIndex].mainAction.isChainMassage ? 'indigo' : 'grey-lighten-1'"
                                                                                                    class="me-3">
                                                                                                    <v-icon
                                                                                                        :color="nodeInspector.smartIntents[smartIntentIndex].mainAction.isChainMassage ? 'white' : 'grey-darken-1'"
                                                                                                        size="16">mdi-chat-processing</v-icon>
                                                                                                </v-avatar>
                                                                                                <div>
                                                                                                    <div class="switch-title">Enable Multi-Turn Input
                                                                                                    </div>
                                                                                                    <div class="switch-description">Allow continuous
                                                                                                        conversation</div>
                                                                                                </div>
                                                                                            </div>
                                                                                            <v-switch 
                                                                                                v-model="nodeInspector.smartIntents[smartIntentIndex].mainAction.isChainMassage"
                                                                                                color="indigo" hide-details density="compact"
                                                                                                @update:model-value="updateSmartIntentData()"
                                                                                                :readonly="isViewMode" inset />
                                                                                        </div>
                                                                                    </div>
                                                                                </v-col>
                                                                            </v-row>
                                                                            <v-row>
                                                                                <v-col cols="12">
                                                                                    <v-text-field 
                                                                                        v-model.number="nodeInspector.smartIntents[smartIntentIndex].mainAction.lenChainMassage"
                                                                                        label="Sequence" type="number" min="1" variant="outlined"
                                                                                        density="comfortable" hide-details="auto"
                                                                                        prepend-inner-icon="mdi-numeric"
                                                                                        @update:model-value="updateSmartIntentData()"
                                                                                        @keypress="isNumberpositive($event)" class="config-input"
                                                                                        :readonly="isViewMode" :disabled="!nodeInspector.smartIntents[smartIntentIndex].mainAction.isChainMassage"
                                                                                        hint="Number of conversation turns in sequence" persistent-hint
                                                                                        clearable />
                                                                                </v-col>
                                                                            </v-row>
                                                                            <v-row>
                                                                                <v-col cols="12">
                                                                                    <v-text-field 
                                                                                        v-model.number="nodeInspector.smartIntents[smartIntentIndex].mainAction.timeSecondChainMassage"
                                                                                        label="Duration (seconds)" type="number" min="1"
                                                                                        variant="outlined" density="comfortable" hide-details="auto"
                                                                                        prepend-inner-icon="mdi-timer"
                                                                                        @update:model-value="updateSmartIntentData()"
                                                                                        @keypress="isNumberpositive($event)" class="config-input"
                                                                                        :readonly="isViewMode" :disabled="!nodeInspector.smartIntents[smartIntentIndex].mainAction.isChainMassage"
                                                                                        hint="Time duration for multi-turn session" persistent-hint
                                                                                        clearable />
                                                                                </v-col>
                                                                            </v-row>
                                                                        </div>


                                                                    </div>
                                                                </v-expansion-panel-text>
                                                            </v-expansion-panel>

                                                            <!-- Line Configuration Panel -->
                                                            <v-expansion-panel value="line" class="panel-item">
                                                                <v-expansion-panel-title class="panel-title-custom">
                                                                    <div class="d-flex align-center py-3">
                                                                        <div class="panel-icon-wrapper me-4"
                                                                            style="background: linear-gradient(135deg, #4CAF50, #388E3C);">
                                                                            <v-icon icon="mdi-message" size="20" color="white"></v-icon>
                                                                        </div>
                                                                        <div>
                                                                            <div class="panel-title">Line Configuration</div>
                                                                            <div class="panel-subtitle">LINE messaging platform settings</div>
                                                                        </div>
                                                                    </div>
                                                                </v-expansion-panel-title>
                                                                <v-expansion-panel-text class="panel-content-wrapper">
                                                                    <div class="panel-content pa-4">
                                                                        <div class="config-section mb-3">
                                                                            <h6 class="section-label mb-3">Display Options</h6>

                                                                            <!-- Show Name -->
                                                                            <div class="switch-option-compact mb-3">
                                                                                <div class="d-flex align-center justify-space-between py-2 px-3 rounded-lg"
                                                                                    :class="{ 'switch-active-bg': nodeInspector.smartIntents[smartIntentIndex].mainAction.showLineName }">
                                                                                    <div class="d-flex align-center">
                                                                                        <v-avatar size="28"
                                                                                            :color="nodeInspector.smartIntents[smartIntentIndex].mainAction.showLineName ? 'info' : 'grey-lighten-1'"
                                                                                            class="me-3">
                                                                                            <v-icon
                                                                                                :color="nodeInspector.smartIntents[smartIntentIndex].mainAction.showLineName ? 'white' : 'grey-darken-1'"
                                                                                                size="16">mdi-account-circle</v-icon>
                                                                                        </v-avatar>
                                                                                        <div>
                                                                                            <div class="switch-title">Show Name</div>
                                                                                            <div class="switch-description">Display user name</div>
                                                                                        </div>
                                                                                    </div>
                                                                                    <v-switch 
                                                                                        v-model="nodeInspector.smartIntents[smartIntentIndex].mainAction.showLineName" 
                                                                                        color="info" hide-details density="compact"
                                                                                        @update:model-value="updateSmartIntentData()"
                                                                                        :readonly="isViewMode" inset />
                                                                                </div>
                                                                            </div>
                                                                            <!-- Loading Indicator -->
                                                                            <div class="switch-option-compact mb-3">
                                                                                <div class="d-flex align-center justify-space-between py-2 px-3 rounded-lg"
                                                                                    :class="{ 'switch-active-bg': nodeInspector.smartIntents[smartIntentIndex].mainAction.loadingBar }">
                                                                                    <div class="d-flex align-center">
                                                                                        <v-avatar size="28"
                                                                                            :color="nodeInspector.smartIntents[smartIntentIndex].mainAction.loadingBar ? 'success' : 'grey-lighten-1'"
                                                                                            class="me-3">
                                                                                            <v-icon
                                                                                                :color="nodeInspector.smartIntents[smartIntentIndex].mainAction.loadingBar ? 'white' : 'grey-darken-1'"
                                                                                                size="16">mdi-progress-clock</v-icon>
                                                                                        </v-avatar>
                                                                                        <div>
                                                                                            <div class="switch-title">Loading Indicator</div>
                                                                                            <div class="switch-description">Show typing animation</div>
                                                                                        </div>
                                                                                    </div>
                                                                                    <v-switch 
                                                                                        v-model="nodeInspector.smartIntents[smartIntentIndex].mainAction.loadingBar" 
                                                                                        color="success" hide-details density="compact"
                                                                                        @update:model-value="updateSmartIntentData()"
                                                                                        :readonly="isViewMode" inset />
                                                                                </div>
                                                                            </div>

                                                                        </div>

                                                                        <div class="config-section mb-3">
                                                                            <h6 class="section-label mb-3">Loading & Duration</h6>
                                                                            <v-row>
                                                                                <v-col cols="12">
                                                                                    <v-select 
                                                                                        v-model="nodeInspector.smartIntents[smartIntentIndex].mainAction.loadingBarDuration"
                                                                                        :items="[10, 20, 30, 40, 50, 60]"
                                                                                        label="Loading Duration (seconds)" variant="outlined"
                                                                                        density="comfortable" hide-details="auto"
                                                                                        prepend-inner-icon="mdi-timer-sand"
                                                                                        @update:model-value="updateSmartIntentData()"
                                                                                        class="config-input" :disabled="!nodeInspector.smartIntents[smartIntentIndex].mainAction.loadingBar"
                                                                                        :readonly="isViewMode"
                                                                                        hint="Duration for loading animation display" persistent-hint
                                                                                        clearable />
                                                                                </v-col>
                                                                            </v-row>
                                                                        </div>

                                                                        <div class="config-section mb-3">
                                                                            <h6 class="section-label mb-3">Monitoring</h6>
                                                                            <v-row>
                                                                                <v-col cols="12">
                                                                                    <v-text-field 
                                                                                        v-model="nodeInspector.smartIntents[smartIntentIndex].mainAction.monitorLineUserId"
                                                                                        label="Monitor Line User ID" variant="outlined"
                                                                                        density="comfortable" hide-details="auto"
                                                                                        prepend-inner-icon="mdi-monitor-eye"
                                                                                        @update:model-value="updateSmartIntentData()"
                                                                                        class="config-input" :readonly="isViewMode"
                                                                                        hint="LINE user ID for monitoring notifications"
                                                                                        persistent-hint />
                                                                                </v-col>
                                                                            </v-row>
                                                                            <v-row>
                                                                                <v-col cols="12">
                                                                                    <v-autocomplete 
                                                                                        v-model="nodeInspector.smartIntents[smartIntentIndex].mainAction.monitorSocialID"
                                                                                        :items="listMonitorLineName" label="Monitor Line Name"
                                                                                        variant="outlined" density="comfortable" hide-details="auto"
                                                                                        prepend-inner-icon="mdi-chat" item-title="name" item-value="id"
                                                                                        @update:model-value="updateSmartIntentData()"
                                                                                        class="config-input" :readonly="isViewMode"
                                                                                        hint="Select monitor line name" persistent-hint clearable />
                                                                                </v-col>
                                                                            </v-row>
                                                                        </div>

                                                                        <div class="config-section">
                                                                            <h6 class="section-label mb-3">Content Filtering</h6>
                                                                            <v-row>
                                                                                <v-col cols="12">
                                                                                    <v-toolbar-title style="font-size: 16px">
                                                                                        Forbidden word
                                                                                    </v-toolbar-title>
                                                                                </v-col>
                                                                            </v-row>
                                                                            <div class="pr-1" style="
                                                                                    border: #dfe0e2 1px solid;
                                                                                    background: #fff;
                                                                                    margin-top: 10px;
                                                                                    border-radius: 8px;
                                                                                ">
                                                                                <v-row v-for="(item, forbiddenIndex) in (nodeInspector.smartIntents[smartIntentIndex].mainAction.forbiddenWords || [''])"
                                                                                    :key="forbiddenIndex" style="margin: 0px">
                                                                                    <v-col cols="12" style="padding: 0px">
                                                                                        <div style="display: flex; align-items: center">
                                                                                            <div style="
                                                                                                    height: 50px;
                                                                                                    background: #f8f8f8;
                                                                                                    padding: 14px 20px 0 20px;
                                                                                                ">
                                                                                                {{ forbiddenIndex + 1 }}
                                                                                            </div>
                                                                                            <v-textarea variant="solo" style="
                                                                                                    overflow: hidden;
                                                                                                    overflow-wrap: break-word;
                                                                                                    resize: none;
                                                                                                    height: 50px;
                                                                                                    width: 98%;
                                                                                                    border-radius: 0px;
                                                                                                    font-family: Fira code, Fira Mono, Consolas,
                                                                                                        Menlo, Courier, monospace;
                                                                                                    font-size: 14px;
                                                                                                "
                                                                                                :model-value="((nodeInspector.smartIntents[smartIntentIndex].mainAction.forbiddenWords || [])[forbiddenIndex]) || ''"
                                                                                                placeholder="Enter word or phrase"
                                                                                                @keypress="isEnterSmartIntentForbidden($event, smartIntentIndex, forbiddenIndex)"
                                                                                                @keyup.delete="checkDeleteSmartIntentForbiddenWord(smartIntentIndex, forbiddenIndex, ((nodeInspector.smartIntents[smartIntentIndex].mainAction.forbiddenWords || [])[forbiddenIndex]) || '')"
                                                                                                @update:model-value="updateSmartIntentForbiddenWordProperty(smartIntentIndex, forbiddenIndex, $event)"
                                                                                                :readonly="isViewMode" />
                                                                                            <v-btn icon="mdi-attachment" variant="text"
                                                                                                v-show="(nodeInspector.smartIntents[smartIntentIndex].mainAction.forbiddenWords || []).length > 1 && forbiddenIndex + 1 < (nodeInspector.smartIntents[smartIntentIndex].mainAction.forbiddenWords || []).length"
                                                                                                @click="removeSmartIntentForbiddenWordDirect(smartIntentIndex, forbiddenIndex)"
                                                                                                :disabled="isViewMode">
                                                                                                <v-tooltip location="top" :open-delay="500">
                                                                                                    <template v-slot:activator="{ props }">
                                                                                                        <v-icon color="error" style="cursor: pointer"
                                                                                                            v-bind="props">
                                                                                                            mdi-delete-forever
                                                                                                        </v-icon>
                                                                                                    </template>
                                                                                                    <span>delete</span>
                                                                                                </v-tooltip>
                                                                                            </v-btn>
                                                                                        </div>
                                                                                    </v-col>
                                                                                </v-row>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </v-expansion-panel-text>
                                                            </v-expansion-panel>

                                                            <!-- System Prompt Panel -->
                                                            <v-expansion-panel value="prompt" class="panel-item mb-3">
                                                                <v-expansion-panel-title class="panel-title-custom">
                                                                    <div class="d-flex align-center py-3">
                                                                        <div class="panel-icon-wrapper me-4"
                                                                            style="background: linear-gradient(135deg, #607D8B, #455A64);">
                                                                            <v-icon icon="mdi-message-text" size="20" color="white"></v-icon>
                                                                        </div>
                                                                        <div>
                                                                            <div class="panel-title">System Prompt</div>
                                                                            <div class="panel-subtitle">Define AI behavior and personality</div>
                                                                        </div>
                                                                    </div>
                                                                </v-expansion-panel-title>
                                                                <v-expansion-panel-text class="panel-content-wrapper">
                                                                    <div class="panel-content">
                                                                        <div class="prompt-section">
                                                                            <div class="d-flex align-center mb-4">
                                                                                <v-avatar size="36" color="info" class="me-3">
                                                                                    <v-icon color="white" size="20">mdi-robot-outline</v-icon>
                                                                                </v-avatar>
                                                                                <div>
                                                                                    <h6 class="text-h6 mb-1">AI Personality & Instructions</h6>
                                                                                    <p class="text-body-2 text-medium-emphasis mb-0">
                                                                                        Define how your AI should behave, respond, and interact with
                                                                                        users
                                                                                    </p>
                                                                                </div>
                                                                            </div>
                                                                            <h3 class="text-h6 d-flex align-center mb-3" style="font-size: 16px !important;">
                                                                                <v-icon icon="mdi-pencil-outline" size="small"
                                                                                    class="me-2"></v-icon>
                                                                                Configure system message for your AI
                                                                            </h3>

                                                                            <!-- Prompt Template Header -->
                                                                            <div class="mb-2" style="display:flex;flex-direction: column;align-items: flex-end;">
                                                                                <div style="display: flex; align-items: center;">
                                                                                    <v-tooltip location="top">
                                                                                        <template v-slot:activator="{ props }">
                                                                                            <v-icon icon="mdi-information-outline" size="small"
                                                                                                color="grey" class="me-3" v-bind="props"></v-icon>
                                                                                        </template>
                                                                                        <span>Configure additional prompts and templates</span>
                                                                                    </v-tooltip>
                                                                                    <v-btn
                                                                                        v-if="DDGroupPromptTemplate && DDGroupPromptTemplate.length > 0"
                                                                                        variant="outlined" color="primary"
                                                                                        prepend-icon="mdi-playlist-plus" @click="showSmartIntentPromptTemplate(smartIntentIndex)"
                                                                                        class="text-none font-weight-medium" size="small">
                                                                                        Show Templates
                                                                                    </v-btn>
                                                                                </div>
                                                                            </div>

                                                                            <!-- Main editor and templates layout -->
                                                                            <v-row>
                                                                                <!-- Editor area (full width) -->
                                                                                <v-col cols="12" class="prompt-editor-col">
                                                                                    <v-card variant="outlined" class="prompt-editor-card">
                                                                                        <v-card-text class="px-2 py-1">
                                                                                            <v-textarea 
                                                                                                v-model="nodeInspector.smartIntents[smartIntentIndex].mainAction.systemPrompt" 
                                                                                                label="" variant="plain" counter auto-grow rows="25"
                                                                                                @update:model-value="updateSmartIntentData()"
                                                                                                :readonly="isViewMode"
                                                                                                placeholder="Enter your system message here..."
                                                                                                class="prompt-textarea"
                                                                                                @contextmenu="onSmartIntentContextMenu(smartIntentIndex, $event)"></v-textarea>
                                                                                        </v-card-text>
                                                                                    </v-card>
                                                                                </v-col>
                                                                            </v-row>

                                                                            <div class="prompt-tips mt-4">
                                                                                <v-card class="tips-card" color="blue-grey-lighten-5" flat>
                                                                                    <v-card-text class="pa-4">
                                                                                        <div class="d-flex align-start">
                                                                                            <v-icon color="info" size="20"
                                                                                                class="me-2 mt-1">mdi-lightbulb-outline</v-icon>
                                                                                            <div>
                                                                                                <div class="text-subtitle-2 font-weight-medium mb-2">
                                                                                                    Tips for
                                                                                                    better prompts:</div>
                                                                                                <ul class="text-body-2 text-medium-emphasis">
                                                                                                    <li>Be specific about the AI's role and expertise
                                                                                                    </li>
                                                                                                    <li>Define the tone and communication style</li>
                                                                                                    <li>Include any restrictions or guidelines</li>
                                                                                                    <li>Specify how to handle unknown information</li>
                                                                                                </ul>
                                                                                            </div>
                                                                                        </div>
                                                                                    </v-card-text>
                                                                                </v-card>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </v-expansion-panel-text>
                                                            </v-expansion-panel>

                                                            <!-- Knowledge Panel -->
                                                            <v-expansion-panel value="knowledge" class="panel-item mb-3">
                                                                <v-expansion-panel-title class="panel-title-custom">
                                                                    <div class="d-flex align-center py-3">
                                                                        <div class="panel-icon-wrapper me-4"
                                                                            style="background: linear-gradient(135deg, #FF6F00, #E65100);">
                                                                            <v-icon icon="mdi-database" size="20" color="white"></v-icon>
                                                                        </div>
                                                                        <div>
                                                                            <div class="panel-title">Knowledge Sources</div>
                                                                            <div class="panel-subtitle">Configure knowledge integration for context</div>
                                                                        </div>
                                                                    </div>
                                                                </v-expansion-panel-title>
                                                                <v-expansion-panel-text class="panel-content-wrapper">
                                                                    <div class="panel-content">
                                                                        <!-- Knowledge Sources Selection -->
                                                                        <div class="config-section mb-4">
                                                                            <h6 class="section-label mb-3">Enable Knowledge Sources</h6>
                                                                            <div class="knowledge-sources-grid">
                                                                                <div class="knowledge-source-card mb-3">
                                                                                    <div class="knowledge-source-frame rounded-lg border">
                                                                                        <div class="knowledge-source-title">
                                                                                            <div class="d-flex align-center">
                                                                                                <v-avatar size="32" color="blue" class="me-3">
                                                                                                    <v-icon color="white" size="18">mdi-file-document</v-icon>
                                                                                                </v-avatar>
                                                                                                <div>
                                                                                                    <div class="text-subtitle-2 font-weight-medium">Documents</div>
                                                                                                    <div class="text-caption text-medium-emphasis">Vector-based document search</div>
                                                                                                </div>
                                                                                            </div>
                                                                                            <v-switch 
                                                                                                v-model="nodeInspector.smartIntents[smartIntentIndex].mainAction.enableDocuments"
                                                                                                color="blue" hide-details density="compact"
                                                                                                @update:model-value="updateSmartIntentData()"
                                                                                                :readonly="isViewMode" inset />
                                                                                        </div>
                                                                                        <!-- Documents Configuration -->
                                                                                        <div v-if="nodeInspector.smartIntents[smartIntentIndex].mainAction.enableDocuments" 
                                                                                            class="config-section my-4 knowledge-source-config">
                                                                                            <div class="d-flex align-center mb-3">
                                                                                                <h6 class="section-label mb-0">Documents Configuration</h6>
                                                                                            </div>
                                                                                            <!-- Document Selection -->
                                                                                            <v-autocomplete 
                                                                                                v-model="nodeInspector.smartIntents[smartIntentIndex].mainAction.document" 
                                                                                                :items="DDVector" item-title="name" item-value="name" 
                                                                                                label="Document" variant="outlined" density="comfortable" 
                                                                                                hide-details="auto" chips multiple 
                                                                                                @update:model-value="updateSmartIntentData()"
                                                                                                class="config-input mb-3"
                                                                                                placeholder="Select documents" :clearable="!isViewMode" :readonly="isViewMode">
                                                                                                <template v-slot:item="{ props, item }">
                                                                                                    <v-list-item v-bind="props" class="document-option">
                                                                                                        <template v-slot:prepend>
                                                                                                            <v-icon size="20" color="primary">mdi-file-document</v-icon>
                                                                                                        </template>
                                                                                                        <v-list-item-subtitle>{{ item.raw.description }}</v-list-item-subtitle>
                                                                                                    </v-list-item>
                                                                                                </template>
                                                                                            </v-autocomplete>
                                                                                            
                                                                                            <!-- Top-N Chunks -->
                                                                                            <v-text-field 
                                                                                                v-model.number="nodeInspector.smartIntents[smartIntentIndex].mainAction.topNChunks" 
                                                                                                type="number" label="Top-N Chunks" variant="outlined" 
                                                                                                density="comfortable" hide-details="auto" :readonly="isViewMode" 
                                                                                                min="1" step="1" 
                                                                                                @focus="$event.target.select()"
                                                                                                @blur="validateAndFixSmartIntentTopNChunks($event, smartIntentIndex)"
                                                                                                @keydown.enter="validateAndFixSmartIntentTopNChunks($event, smartIntentIndex)"
                                                                                                class="config-input mb-3" 
                                                                                                hint="Number of document chunks to retrieve" persistent-hint />
                                                                                            
                                                                                            <!-- Cosine Similarity -->
                                                                                            <v-text-field 
                                                                                                v-model.number="nodeInspector.smartIntents[smartIntentIndex].mainAction.cosineSimilarity" 
                                                                                                type="number" label="Cosine Similarity" variant="outlined" 
                                                                                                density="comfortable" hide-details="auto" :readonly="isViewMode" 
                                                                                                min="0.0001" max="1" step="0.0001"
                                                                                                @focus="$event.target.select()"
                                                                                                @blur="validateAndFixSmartIntentCosineSimilarity($event, smartIntentIndex)"
                                                                                                @keydown.enter="validateAndFixSmartIntentCosineSimilarity($event, smartIntentIndex)"
                                                                                                class="config-input" 
                                                                                                hint="Similarity threshold for document matching" persistent-hint />
                                                                                        </div>
                                                                                    </div>
                                                                                </div>

                                                                                <div class="knowledge-source-card mb-3">
                                                                                    <div class="knowledge-source-frame rounded-lg border">
                                                                                        <div class="knowledge-source-title">
                                                                                            <div class="d-flex align-center">
                                                                                                <v-avatar size="32" color="green" class="me-3">
                                                                                                    <v-icon color="white" size="18">mdi-database</v-icon>
                                                                                                </v-avatar>
                                                                                                <div>
                                                                                                    <div class="text-subtitle-2 font-weight-medium">Database Integration</div>
                                                                                                    <div class="text-caption text-medium-emphasis">SQL database queries</div>
                                                                                                </div>
                                                                                            </div>
                                                                                            <v-switch 
                                                                                                v-model="nodeInspector.smartIntents[smartIntentIndex].mainAction.enableDatabaseIntegration"
                                                                                                color="green" hide-details density="compact"
                                                                                                @update:model-value="updateSmartIntentData()"
                                                                                                :readonly="isViewMode" inset />
                                                                                        </div>
                                                                                        <!-- Database Integration Configuration -->
                                                                                        <div v-if="nodeInspector.smartIntents[smartIntentIndex].mainAction.enableDatabaseIntegration" 
                                                                                            class="config-section my-4 knowledge-source-config">
                                                                                            <div class="d-flex align-center mb-3">
                                                                                                <h6 class="section-label mb-0">Database Integration Configuration</h6>
                                                                                            </div>
                                                                                            
                                                                                            <!-- Table Name Selection -->
                                                                                            <v-autocomplete 
                                                                                                v-model="nodeInspector.smartIntents[smartIntentIndex].mainAction.tableName" 
                                                                                                :items="listDataIntegration" item-title="dataName" item-value="id" 
                                                                                                label="Table Name" variant="outlined" density="comfortable" 
                                                                                                hide-details="auto" @update:model-value="updateSmartIntentData()" 
                                                                                                class="config-input mb-3" placeholder="Select database table" 
                                                                                                :clearable="!isViewMode" :readonly="isViewMode">
                                                                                                <template v-slot:item="{ props, item }">
                                                                                                    <v-list-item v-bind="props" class="table-option">
                                                                                                        <template v-slot:prepend>
                                                                                                            <v-icon size="20" color="primary">mdi-table</v-icon>
                                                                                                        </template>
                                                                                                        <v-list-item-title>{{ item.raw.name }}</v-list-item-title>
                                                                                                        <v-list-item-subtitle>{{ item.raw.description }}</v-list-item-subtitle>
                                                                                                    </v-list-item>
                                                                                                </template>
                                                                                            </v-autocomplete>

                                                                                            <!-- SQL Extraction Model Selection -->
                                                                                            <v-autocomplete 
                                                                                                v-model="nodeInspector.smartIntents[smartIntentIndex].mainAction.sqlExtractionModel" 
                                                                                                :items="listSqlExtract" item-title="name" item-value="modelDisplayID" 
                                                                                                label="SQL Extraction Model" variant="outlined" density="comfortable" 
                                                                                                hide-details="auto" :clearable="!isViewMode" :readonly="isViewMode"
                                                                                                @update:model-value="updateSmartIntentData()"
                                                                                                class="config-input mb-3" placeholder="Select AI model for SQL generation">
                                                                                                <template v-slot:item="{ props, item }">
                                                                                                    <v-list-item v-bind="props" class="model-option">
                                                                                                        <template v-slot:prepend>
                                                                                                            <div class="model-icon-small mr-2" :style="{ backgroundColor: item.raw.color }">
                                                                                                                <v-icon size="14" color="green">mdi-robot</v-icon>
                                                                                                            </div>
                                                                                                        </template>
                                                                                                        <v-list-item-subtitle>{{ item.raw.description }}</v-list-item-subtitle>
                                                                                                    </v-list-item>
                                                                                                </template>
                                                                                            </v-autocomplete>

                                                                                            <!-- Prompt for No Data -->
                                                                                            <v-textarea 
                                                                                                v-model="nodeInspector.smartIntents[smartIntentIndex].mainAction.promptSqlNoData" 
                                                                                                label="Prompt for No Data" variant="outlined" density="comfortable" 
                                                                                                rows="3" auto-grow hide-details="auto"
                                                                                                placeholder="Enter the message to display when no data is found in the database..."
                                                                                                @update:model-value="updateSmartIntentData()"
                                                                                                class="config-input" :readonly="isViewMode" 
                                                                                                hint="Message shown when database query returns no results" persistent-hint />
                                                                                        </div>
                                                                                    </div>
                                                                                </div>

                                                                                <div class="knowledge-source-card mb-3">
                                                                                    <div class="knowledge-source-frame rounded-lg border">
                                                                                        <div class="knowledge-source-title">
                                                                                            <div class="d-flex align-center">
                                                                                                <v-avatar size="32" color="purple" class="me-3">
                                                                                                    <v-icon color="white" size="18">mdi-package-variant</v-icon>
                                                                                                </v-avatar>
                                                                                                <div>
                                                                                                    <div class="text-subtitle-2 font-weight-medium">Products</div>
                                                                                                    <div class="text-caption text-medium-emphasis">Product catalog search</div>
                                                                                                </div>
                                                                                            </div>
                                                                                            <v-switch 
                                                                                                v-model="nodeInspector.smartIntents[smartIntentIndex].mainAction.enableProducts"
                                                                                                color="purple" hide-details density="compact"
                                                                                                @update:model-value="updateSmartIntentData()"
                                                                                                :readonly="isViewMode" inset />
                                                                                        </div>
                                                                                        <!-- Products Configuration -->
                                                                                        <div v-if="nodeInspector.smartIntents[smartIntentIndex].mainAction.enableProducts" 
                                                                                            class="config-section my-4 knowledge-source-config">
                                                                                            <div class="d-flex align-center mb-3">
                                                                                                <h6 class="section-label mb-0">Products Configuration</h6>
                                                                                            </div>
                                                                                            
                                                                                            <!-- Product Group List Selection -->
                                                                                            <v-autocomplete 
                                                                                                v-model="nodeInspector.smartIntents[smartIntentIndex].mainAction.productGroupList" 
                                                                                                :items="DDProductGroup" item-title="name" item-value="id" 
                                                                                                label="Product Group List" variant="outlined" density="comfortable" 
                                                                                                hide-details="auto" chips multiple return-object
                                                                                                @update:model-value="updateSmartIntentData()"
                                                                                                class="config-input mb-3" placeholder="Select product group" 
                                                                                                :clearable="!isViewMode" :readonly="isViewMode">
                                                                                                <template v-slot:item="{ props, item }">
                                                                                                    <v-list-item v-bind="props" class="product-group-option">
                                                                                                        <template v-slot:prepend>
                                                                                                            <v-icon size="20" color="primary">mdi-package-variant</v-icon>
                                                                                                        </template>
                                                                                                        <v-list-item-subtitle>{{ item.raw.description }}</v-list-item-subtitle>
                                                                                                    </v-list-item>
                                                                                                </template>
                                                                                            </v-autocomplete>

                                                                                            <!-- Top-N Chunks -->
                                                                                            <v-text-field 
                                                                                                v-model.number="nodeInspector.smartIntents[smartIntentIndex].mainAction.productTopNChunks" 
                                                                                                type="number" label="Top-N Chunks" variant="outlined" 
                                                                                                density="comfortable" hide-details="auto" min="1" step="1"
                                                                                                @focus="$event.target.select()"
                                                                                                @blur="validateAndFixSmartIntentProductTopNChunks($event, smartIntentIndex)"
                                                                                                @keydown.enter="validateAndFixSmartIntentProductTopNChunks($event, smartIntentIndex)"
                                                                                                class="config-input mb-3" placeholder="Enter number of chunks" 
                                                                                                :readonly="isViewMode" 
                                                                                                hint="Number of product chunks to retrieve" persistent-hint />

                                                                                            <!-- Cosine Similarity -->
                                                                                            <v-text-field 
                                                                                                v-model.number="nodeInspector.smartIntents[smartIntentIndex].mainAction.productCosineSimilarity"
                                                                                                type="number" label="Cosine Similarity" variant="outlined" 
                                                                                                density="comfortable" hide-details="auto" min="0.0001" max="1" step="0.0001"
                                                                                                @focus="$event.target.select()"
                                                                                                @blur="validateAndFixSmartIntentProductCosineSimilarity($event, smartIntentIndex)"
                                                                                                @keydown.enter="validateAndFixSmartIntentProductCosineSimilarity($event, smartIntentIndex)"
                                                                                                class="config-input" placeholder="Enter similarity threshold (0.0001 - 1.0)"
                                                                                                :readonly="isViewMode" 
                                                                                                hint="Similarity threshold for product matching" persistent-hint />
                                                                                        </div>
                                                                                    </div>
                                                                                </div>

                                                                                <div class="knowledge-source-card mb-3">
                                                                                    <div class="knowledge-source-frame rounded-lg border">
                                                                                        <div class="knowledge-source-title">    
                                                                                            <div class="d-flex align-center">
                                                                                                <v-avatar size="32" color="orange" class="me-3">
                                                                                                    <v-icon color="white" size="18">mdi-rocket-launch</v-icon>
                                                                                                </v-avatar>
                                                                                                <div>
                                                                                                    <div class="text-subtitle-2 font-weight-medium">API Knowledge</div>
                                                                                                    <div class="text-caption text-medium-emphasis">External API integration</div>
                                                                                                </div>
                                                                                            </div>
                                                                                            <v-switch 
                                                                                                v-model="nodeInspector.smartIntents[smartIntentIndex].mainAction.enableApiKnowledge"
                                                                                                color="orange" hide-details density="compact"
                                                                                                @update:model-value="updateSmartIntentData()"
                                                                                                :readonly="isViewMode" inset />
                                                                                        </div>
                                                                                        <!-- API Knowledge Configuration -->
                                                                                        <div v-if="nodeInspector.smartIntents[smartIntentIndex].mainAction.enableApiKnowledge" 
                                                                                            class="config-section my-4 knowledge-source-config">
                                                                                            <div class="d-flex align-center mb-3">
                                                                                                <h6 class="section-label mb-0">API Knowledge Configuration</h6>
                                                                                            </div>
                                                                                            
                                                                                            <!-- API URL -->
                                                                                            <v-text-field 
                                                                                                v-model="nodeInspector.smartIntents[smartIntentIndex].mainAction.knowledgeApiUrl" 
                                                                                                label="API URL" variant="outlined" density="comfortable" 
                                                                                                hide-details="auto" placeholder="https://api.example.com/knowledge"
                                                                                                @update:model-value="updateSmartIntentData()"
                                                                                                class="config-input mb-3" :readonly="isViewMode">
                                                                                                <template v-slot:prepend-inner>
                                                                                                    <v-icon size="20" color="primary">mdi-link</v-icon>
                                                                                                </template>
                                                                                            </v-text-field>

                                                                                            <!-- HTTP Method -->
                                                                                            <v-select 
                                                                                                v-model="nodeInspector.smartIntents[smartIntentIndex].mainAction.knowledgeHttpMethod" 
                                                                                                :items="['GET', 'POST']" label="HTTP Method" variant="outlined" 
                                                                                                density="comfortable" hide-details="auto"
                                                                                                @update:model-value="updateSmartIntentData()"
                                                                                                class="config-input mb-3" :readonly="isViewMode">
                                                                                                <template v-slot:prepend-inner>
                                                                                                    <v-icon size="20" color="primary">mdi-code-brackets</v-icon>
                                                                                                </template>
                                                                                            </v-select>

                                                                                            <!-- Request Headers -->
                                                                                            <div class="headers-section mb-3">
                                                                                                <div class="section-header mb-2">
                                                                                                    <span class="section-title text-subtitle-2 font-weight-medium">Request Headers</span>
                                                                                                    <v-btn v-if="!isViewMode" @click="addSmartIntentKnowledgeAPIHeader(smartIntentIndex)" size="small" color="primary"
                                                                                                        variant="tonal" class="add-btn">
                                                                                                        <v-icon size="16">mdi-plus</v-icon>
                                                                                                        Add Header
                                                                                                    </v-btn>
                                                                                                </div>
                                                                                                <div v-if="nodeInspector.smartIntents[smartIntentIndex].mainAction.listHeaders" class="headers-list">
                                                                                                    <div v-for="(header, headerIndex) in nodeInspector.smartIntents[smartIntentIndex].mainAction.listHeaders" :key="headerIndex"
                                                                                                        class="header-row mb-2">
                                                                                                        <v-row no-gutters>
                                                                                                            <v-col cols="5">
                                                                                                                <v-text-field
                                                                                                                    v-model="header.key"
                                                                                                                    label="Header Name"
                                                                                                                    variant="outlined"
                                                                                                                    density="compact"
                                                                                                                    hide-details="auto"
                                                                                                                    @update:model-value="updateSmartIntentData()"
                                                                                                                    class="header-input"
                                                                                                                    :readonly="isViewMode"
                                                                                                                    placeholder="Content-Type" />
                                                                                                            </v-col>
                                                                                                            <v-col cols="6" class="pl-2">
                                                                                                                <v-text-field
                                                                                                                    v-model="header.value"
                                                                                                                    label="Header Value"
                                                                                                                    variant="outlined"
                                                                                                                    density="compact"
                                                                                                                    hide-details="auto"
                                                                                                                    @update:model-value="updateSmartIntentData()"
                                                                                                                    class="header-input"
                                                                                                                    :readonly="isViewMode"
                                                                                                                    placeholder="application/json" />
                                                                                                            </v-col>
                                                                                                            <v-col cols="1" class="pl-2 d-flex align-center">
                                                                                                                <v-btn
                                                                                                                    v-if="!isViewMode"
                                                                                                                    @click="removeSmartIntentKnowledgeAPIHeader(smartIntentIndex, headerIndex)"
                                                                                                                    icon
                                                                                                                    size="small"
                                                                                                                    variant="text"
                                                                                                                    color="error">
                                                                                                                    <v-icon size="16">mdi-delete</v-icon>
                                                                                                                </v-btn>
                                                                                                            </v-col>
                                                                                                        </v-row>
                                                                                                    </div>
                                                                                                </div>
                                                                                                <div v-else class="text-caption text-medium-emphasis">
                                                                                                    No headers configured
                                                                                                </div>
                                                                                            </div>

                                                                                            <!-- Request Body (for POST requests) -->
                                                                                            <div class="mb-3">
                                                                                                <div class="section-header mb-2">
                                                                                                    <span class="section-title text-subtitle-2 font-weight-medium">Request Body</span>
                                                                                                </div>
                                                                                                <v-textarea
                                                                                                    v-model="nodeInspector.smartIntents[smartIntentIndex].mainAction.knowledgeRequestBody"
                                                                                                    label="Request Body (JSON)"
                                                                                                    variant="outlined"
                                                                                                    density="comfortable"
                                                                                                    rows="4"
                                                                                                    auto-grow
                                                                                                    hide-details="auto"
                                                                                                    @update:model-value="updateSmartIntentData()"
                                                                                                    class="config-input"
                                                                                                    :readonly="isViewMode"
                                                                                                    placeholder='{"key": "value"}'
                                                                                                    hint="JSON payload for API request (use {{ variables }} for dynamic values)" 
                                                                                                    persistent-hint>
                                                                                                    <template v-slot:prepend-inner>
                                                                                                        <v-icon size="20" color="primary">mdi-code-json</v-icon>
                                                                                                    </template>
                                                                                                    <template v-slot:append-inner>
                                                                                                        <v-tooltip location="top">
                                                                                                            <template v-slot:activator="{ props }">
                                                                                                                <v-icon v-bind="props" size="small" color="grey-darken-1">
                                                                                                                    mdi-information-outline
                                                                                                                </v-icon>
                                                                                                            </template>
                                                                                                            <span>JSON payload for API request (use {{ variables }} for dynamic values)</span>
                                                                                                        </v-tooltip>
                                                                                                    </template>
                                                                                                </v-textarea>
                                                                                            </div>

                                                                                            <!-- Response Data Path -->
                                                                                            <v-text-field 
                                                                                                v-model="nodeInspector.smartIntents[smartIntentIndex].mainAction.knowledgeResponseDataPath" 
                                                                                                label="Response Data Path" variant="outlined" density="comfortable" 
                                                                                                hide-details="auto" placeholder="data.knowledge"
                                                                                                @update:model-value="updateSmartIntentData()"
                                                                                                class="config-input" :readonly="isViewMode">
                                                                                                <template v-slot:prepend-inner>
                                                                                                    <v-icon size="20" color="primary">mdi-code-json</v-icon>
                                                                                                </template>
                                                                                                <template v-slot:append-inner>
                                                                                                    <v-tooltip location="top">
                                                                                                        <template v-slot:activator="{ props }">
                                                                                                            <v-icon v-bind="props" size="small" color="grey-darken-1">
                                                                                                                mdi-information-outline
                                                                                                            </v-icon>
                                                                                                        </template>
                                                                                                        <span>JSON path to extract knowledge data from API response</span>
                                                                                                    </v-tooltip>
                                                                                                </template>
                                                                                            </v-text-field>
                                                                                        </div>
                                                                                    </div>
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                        <!-- Knowledge Integration Tips -->
                                                                        <div class="knowledge-tips mt-4">
                                                                            <v-card class="tips-card" color="orange-lighten-5" flat>
                                                                                <v-card-text class="pa-4">
                                                                                    <div class="d-flex align-start">
                                                                                        <v-icon color="orange-darken-1" size="20"
                                                                                            class="me-2 mt-1">mdi-lightbulb-outline</v-icon>
                                                                                        <div>
                                                                                            <div class="text-subtitle-2 font-weight-medium mb-2">
                                                                                                Knowledge Integration Tips:</div>
                                                                                            <ul class="text-body-2 text-medium-emphasis">
                                                                                                <li>You can enable multiple knowledge sources simultaneously</li>
                                                                                                <li>Configure similarity thresholds for better content matching</li>
                                                                                                <li>Use multiple documents or product groups for broader coverage</li>
                                                                                                <li>Test API endpoints before deploying in production</li>
                                                                                                <li>Knowledge sources will be queried in order and combined for context</li>
                                                                                            </ul>
                                                                                        </div>
                                                                                    </div>
                                                                                </v-card-text>
                                                                            </v-card>
                                                                        </div>
                                                                    </div>
                                                                </v-expansion-panel-text>
                                                            </v-expansion-panel>
                                                        </v-expansion-panels>
                                                                </v-expansion-panel-text>
                                                            </v-expansion-panel>
                                                        </v-expansion-panels>
                                                    </div>
                                                </div>
                                            </div>
                                        </v-expansion-panel-text>
                                    </v-expansion-panel>
                                </v-expansion-panels>
                            </div>
                        </div>

                        <!-- Smart Intent Fallback Configuration -->
                        <div class="smart-intent-fallback-section mt-4">
                            <div class="d-flex align-center justify-space-between mb-3">
                                <h6 class="section-title mb-0">Fallback Configuration</h6>
                            </div>
                            
                            <!-- Fallback AI Toggle -->
                            <div class="ai-toggle-section mb-3">
                                <div style="display: flex; align-items: center; justify-content: space-between;">
                                    <span class="text-subtitle-2 font-weight-medium">
                                        <v-icon size="16" class="mr-2" color="orange">mdi-robot</v-icon>
                                        Use AI Model for Fallback
                                    </span>
                                    <v-switch 
                                        v-model="nodeInspector.smartIntentFallback.useAI"
                                        color="orange" density="compact" hide-details inset
                                        @update:model-value="updateSmartIntentFallbackData()"
                                        :readonly="isViewMode"
                                        style="transform: scale(0.8);" />
                                </div>
                            </div>

                            <!-- Fallback AI Configuration (when enabled) -->
                            <div v-if="nodeInspector.smartIntentFallback && nodeInspector.smartIntentFallback.useAI" 
                                 class="ai-config-section">
                                 <div style="display: flex; align-items: center; justify-content: flex-end; gap: 8px;width: 100%;">
                                    <v-btn v-if="!isViewMode && nodeInspector.smartIntentFallback.useAI" 
                                        @click="openSmartIntentLoadFromModelDialog('fallback')" 
                                        color="success" variant="text" size="small"
                                        style="background-color: #e8f5e9;"
                                        prepend-icon="mdi-download">
                                        Load Config
                                    </v-btn>
                                 </div>
                                 
                                <v-divider class="mb-3"></v-divider>
                                
                                <!-- Fallback AI Configuration Expandable Section -->
                                <v-expansion-panels variant="accordion" class="fallback-ai-config-expansion">
                                    <v-expansion-panel>
                                        <v-expansion-panel-title class="fallback-ai-config-title">
                                            <div class="d-flex align-center">
                                                <v-icon icon="mdi-backup-restore" size="20" color="orange" class="me-3"></v-icon>
                                                <div>
                                                    <div class="text-subtitle-1 font-weight-medium">Fallback AI Configuration</div>
                                                    <div class="text-caption text-medium-emphasis">Backup AI settings when primary intent fails</div>
                                                </div>
                                            </div>
                                        </v-expansion-panel-title>
                                        <v-expansion-panel-text>
                                            <!-- Model Configuration Panels (exactly like AI Model Node) -->
                                            <v-expansion-panels multiple class="config-panels elevation-0" variant="accordion">
                                    <!-- Basic Information Panel -->
                                    <v-expansion-panel value="basic" class="panel-item mt-3">
                                        <v-expansion-panel-title class="panel-title-custom">
                                            <div class="d-flex align-center py-3">
                                                <div class="panel-icon-wrapper me-4">
                                                    <v-icon icon="mdi-information" size="20" color="white"></v-icon>
                                                </div>
                                                <div>
                                                    <div class="panel-title">Basic Information</div>
                                                    <div class="panel-subtitle">Provider, model, and basic settings</div>
                                                </div>
                                            </div>
                                        </v-expansion-panel-title>
                                        <v-expansion-panel-text class="panel-content-wrapper">
                                            <div class="panel-content">
                                                <!-- Provider and Model -->
                                                <div class="config-section mb-6">
                                                    <h6 class="section-label mb-3">Service Configuration</h6>
                                                    <v-row>
                                                        <v-col cols="12">
                                                            <v-autocomplete 
                                                                v-model="nodeInspector.smartIntentFallback.provider"
                                                                :items="listDDProvider" item-title="providerName" item-value="providerName"
                                                                label="Provider*" variant="outlined" density="comfortable" hide-details="auto"
                                                                prepend-inner-icon="mdi-cloud-outline"
                                                                @update:model-value="updateSmartIntentFallbackProviderChange($event)"
                                                                class="config-input" :readonly="isViewMode"
                                                                hint="Select AI service provider" persistent-hint required />
                                                        </v-col>
                                                    </v-row>
                                                    <v-row>
                                                        <v-col cols="12">
                                                            <v-autocomplete 
                                                                v-model="nodeInspector.smartIntentFallback.model"
                                                                :items="getModelsByProvider(nodeInspector.smartIntentFallback.provider)"
                                                                item-title="modelDisplay" item-value="modelName"
                                                                label="Model*" variant="outlined" density="comfortable" hide-details="auto"
                                                                prepend-inner-icon="mdi-brain"
                                                                @update:model-value="updateSmartIntentFallbackModelChange()"
                                                                class="config-input" :readonly="isViewMode"
                                                                :disabled="!nodeInspector.smartIntentFallback.provider"
                                                                hint="Select specific model version" persistent-hint required />
                                                        </v-col>
                                                    </v-row>
                                                </div>

                                                <!-- Behavior Settings -->
                                                <div class="config-section">
                                                    <h6 class="section-label mb-3">Behavior Settings</h6>
                                                    <v-row>
                                                        <v-col cols="12">
                                                            <v-select 
                                                                v-model="nodeInspector.smartIntentFallback.gender"
                                                                :items="[{ id: 'male', name: 'Male' }, { id: 'female', name: 'Female' }]"
                                                                item-title="name" item-value="id" label="Gender"
                                                                variant="outlined" density="comfortable" hide-details="auto"
                                                                prepend-inner-icon="mdi-account"
                                                                @update:model-value="updateSmartIntentFallbackData()"
                                                                class="config-input" :readonly="isViewMode"
                                                                hint="Voice/persona gender preference" persistent-hint />
                                                        </v-col>
                                                    </v-row>
                                                    <v-row>
                                                        <v-col cols="12">
                                                            <v-text-field 
                                                                v-model.number="nodeInspector.smartIntentFallback.typingSpeed"
                                                                label="Typing Speed" type="number" min="0" variant="outlined"
                                                                density="comfortable" hide-details="auto"
                                                                prepend-inner-icon="mdi-keyboard-outline"
                                                                @update:model-value="updateSmartIntentFallbackData()"
                                                                @keypress="isNumberpositive($event)" class="config-input"
                                                                :readonly="isViewMode"
                                                                hint="Characters per second (0 = instant)" persistent-hint />
                                                        </v-col>
                                                    </v-row>
                                                    <v-row>
                                                        <v-col cols="12">
                                                            <v-text-field 
                                                                v-model.number="nodeInspector.smartIntentFallback.maxInputLength"
                                                                label="Max Input Length" type="number" min="0" max="200000"
                                                                variant="outlined" density="comfortable" hide-details="auto"
                                                                prepend-inner-icon="mdi-text-box-outline"
                                                                @update:model-value="updateSmartIntentFallbackData()"
                                                                @keypress="maxNumberpositive($event)"
                                                                class="config-input" :readonly="isViewMode"
                                                                hint="Maximum characters allowed in user input" persistent-hint />
                                                        </v-col>
                                                    </v-row>
                                                </div>
                                            </div>
                                        </v-expansion-panel-text>
                                    </v-expansion-panel>

                                    <!-- Response Options Panel -->
                                    <v-expansion-panel value="response" class="panel-item">
                                        <v-expansion-panel-title class="panel-title-custom">
                                            <div class="d-flex align-center py-3">
                                                <div class="panel-icon-wrapper me-4"
                                                    style="background: linear-gradient(135deg, #FF9800, #F57C00);">
                                                    <v-icon icon="mdi-tune" size="20" color="white"></v-icon>
                                                </div>
                                                <div>
                                                    <div class="panel-title">Response Options</div>
                                                    <div class="panel-subtitle">Configure how responses are generated</div>
                                                </div>
                                            </div>
                                        </v-expansion-panel-title>
                                        <v-expansion-panel-text class="panel-content-wrapper">
                                            <div class="panel-content pa-4">
                                                <!-- Stream Response -->
                                                <div class="switch-option-compact mb-3">
                                                    <div class="d-flex align-center justify-space-between py-2 px-3 rounded-lg"
                                                        :class="{ 'switch-active-bg': nodeInspector.smartIntentFallback.streamingEnabled }">
                                                        <div class="d-flex align-center">
                                                            <v-avatar size="28"
                                                                :color="nodeInspector.smartIntentFallback.streamingEnabled ? 'primary' : 'grey-lighten-1'"
                                                                class="me-3">
                                                                <v-icon
                                                                    :color="nodeInspector.smartIntentFallback.streamingEnabled ? 'white' : 'grey-darken-1'"
                                                                    size="16">mdi-text-box-outline</v-icon>
                                                            </v-avatar>
                                                            <div>
                                                                <div class="switch-title">Stream Response</div>
                                                                <div class="switch-description">Progressive text generation</div>
                                                            </div>
                                                        </div>
                                                        <v-switch 
                                                            v-model="nodeInspector.smartIntentFallback.streamingEnabled" 
                                                            color="primary" hide-details density="compact"
                                                            @update:model-value="updateSmartIntentFallbackData()"
                                                            :readonly="isViewMode" inset />
                                                    </div>
                                                </div>

                                                <!-- Time Context -->
                                                <div class="switch-option-compact">
                                                    <div class="d-flex align-center justify-space-between py-2 px-3 rounded-lg"
                                                        :class="{ 'switch-active-bg': nodeInspector.smartIntentFallback.enableTime }">
                                                        <div class="d-flex align-center">
                                                            <v-avatar size="28"
                                                                :color="nodeInspector.smartIntentFallback.enableTime ? 'success' : 'grey-lighten-1'"
                                                                class="me-3">
                                                                <v-icon
                                                                    :color="nodeInspector.smartIntentFallback.enableTime ? 'white' : 'grey-darken-1'"
                                                                    size="16">mdi-clock-outline</v-icon>
                                                            </v-avatar>
                                                            <div>
                                                                <div class="switch-title">Time Context</div>
                                                                <div class="switch-description">Enable time awareness</div>
                                                            </div>
                                                        </div>
                                                        <v-switch 
                                                            v-model="nodeInspector.smartIntentFallback.enableTime" 
                                                            color="success" hide-details density="compact"
                                                            @update:model-value="updateSmartIntentFallbackData()"
                                                            :readonly="isViewMode" inset />
                                                    </div>

                                                    <!-- Time Zone Dropdown (shown when enableTime is true) -->
                                                    <div v-if="nodeInspector.smartIntentFallback.enableTime" class="mt-3">
                                                        <v-select 
                                                            v-model="nodeInspector.smartIntentFallback.timeZone" 
                                                            :items="DDTimeZone" item-title="name" item-value="value" 
                                                            label="Time Zone" variant="outlined" density="comfortable" 
                                                            hide-details="auto" prepend-inner-icon="mdi-earth"
                                                            @update:model-value="updateSmartIntentFallbackData()"
                                                            class="config-input" :readonly="isViewMode"
                                                            hint="Select your timezone" persistent-hint
                                                            placeholder="Select your timezone">
                                                        </v-select>
                                                    </div>
                                                </div>
                                            </div>
                                        </v-expansion-panel-text>
                                    </v-expansion-panel>

                                    <!-- Memory Settings Panel -->
                                    <v-expansion-panel value="memory" class="panel-item">
                                        <v-expansion-panel-title class="panel-title-custom">
                                            <div class="d-flex align-center py-3">
                                                <div class="panel-icon-wrapper me-4"
                                                    style="background: linear-gradient(135deg, #00BCD4, #0097A7);">
                                                    <v-icon icon="mdi-brain" size="20" color="white"></v-icon>
                                                </div>
                                                <div>
                                                    <div class="panel-title">Memory Settings</div>
                                                    <div class="panel-subtitle">Configure conversation memory and context</div>
                                                </div>
                                            </div>
                                        </v-expansion-panel-title>
                                        <v-expansion-panel-text class="panel-content-wrapper">
                                            <div class="panel-content">
                                                <div class="config-section mb-3">
                                                    <h6 class="section-label mb-3">Conversation Memory</h6>
                                                    <v-row>
                                                        <v-col cols="12">
                                                            <v-text-field 
                                                                v-model.number="nodeInspector.smartIntentFallback.memoryTurn"
                                                                label="Turn Count" type="number" min="0" variant="outlined"
                                                                density="comfortable" hide-details="auto"
                                                                prepend-inner-icon="mdi-history"
                                                                @update:model-value="updateSmartIntentFallbackData()"
                                                                @keypress="isNumberpositive($event)" class="config-input"
                                                                :readonly="isViewMode"
                                                                hint="Number of conversation turns to remember" persistent-hint />
                                                        </v-col>
                                                    </v-row>
                                                    <v-row>
                                                        <v-col cols="12">
                                                            <v-text-field 
                                                                v-model.number="nodeInspector.smartIntentFallback.memoryDuration"
                                                                label="Duration (minutes)" type="number" min="0"
                                                                variant="outlined" density="comfortable" hide-details="auto"
                                                                prepend-inner-icon="mdi-timer-outline"
                                                                @update:model-value="updateSmartIntentFallbackData()"
                                                                @keypress="isNumberpositive($event)" class="config-input"
                                                                :readonly="isViewMode" hint="How long to keep memory active"
                                                                persistent-hint />
                                                        </v-col>
                                                    </v-row>
                                                </div>

                                                <div class="config-section mb-3">
                                                    <h6 class="section-label mb-3">Memory Types</h6>
                                                    <v-row>
                                                        <v-col cols="12">
                                                            <v-text-field 
                                                                v-model.number="nodeInspector.smartIntentFallback.shortTermMemory"
                                                                label="Short-Term Memory" type="number" min="1"
                                                                variant="outlined" density="comfortable" hide-details="auto"
                                                                prepend-inner-icon="mdi-memory"
                                                                @update:model-value="updateSmartIntentFallbackData()"
                                                                @keypress="isNumberpositive($event)" class="config-input"
                                                                :readonly="isViewMode" hint="Recent context window size"
                                                                persistent-hint />
                                                        </v-col>
                                                    </v-row>
                                                    <v-row>
                                                        <v-col>
                                                            <!-- Long-Term Memory Switch -->
                                                            <div class="switch-option-compact mb-4">
                                                                <div class="d-flex align-center justify-space-between py-2 px-3 rounded-lg"
                                                                    :class="{ 'switch-active-bg': nodeInspector.smartIntentFallback.longTermMemory }">
                                                                    <div class="d-flex align-center">
                                                                        <v-avatar size="28"
                                                                            :color="nodeInspector.smartIntentFallback.longTermMemory ? 'success' : 'grey-lighten-1'"
                                                                            class="me-3">
                                                                            <v-icon
                                                                                :color="nodeInspector.smartIntentFallback.longTermMemory ? 'white' : 'grey-darken-1'"
                                                                                size="16">mdi-database</v-icon>
                                                                        </v-avatar>
                                                                        <div>
                                                                            <div class="switch-title">Long-Term Memory</div>
                                                                            <div class="switch-description">Persistent storage</div>
                                                                        </div>
                                                                    </div>
                                                                    <v-switch 
                                                                        v-model="nodeInspector.smartIntentFallback.longTermMemory"
                                                                        color="success" hide-details density="compact"
                                                                        @update:model-value="updateSmartIntentFallbackData()"
                                                                        :readonly="isViewMode" inset />
                                                                </div>
                                                            </div>
                                                        </v-col>
                                                    </v-row>
                                                </div>

                                                <!-- Multi-turn Input Configuration -->
                                                <div class="config-section mb-3">
                                                    <h6 class="section-label mb-3">Multi-turn Input Configuration</h6>
                                                    <v-row>
                                                        <v-col>
                                                            <!-- Enable Multi-Turn Input Switch -->
                                                            <div class="switch-option-compact mb-3">
                                                                <div class="d-flex align-center justify-space-between py-2 px-3 rounded-lg"
                                                                    :class="{ 'switch-active-bg': nodeInspector.smartIntentFallback.isChainMassage }">
                                                                    <div class="d-flex align-center">
                                                                        <v-avatar size="28"
                                                                            :color="nodeInspector.smartIntentFallback.isChainMassage ? 'indigo' : 'grey-lighten-1'"
                                                                            class="me-3">
                                                                            <v-icon
                                                                                :color="nodeInspector.smartIntentFallback.isChainMassage ? 'white' : 'grey-darken-1'"
                                                                                size="16">mdi-chat-processing</v-icon>
                                                                        </v-avatar>
                                                                        <div>
                                                                            <div class="switch-title">Enable Multi-Turn Input</div>
                                                                            <div class="switch-description">Allow continuous conversation</div>
                                                                        </div>
                                                                    </div>
                                                                    <v-switch 
                                                                        v-model="nodeInspector.smartIntentFallback.isChainMassage"
                                                                        color="indigo" hide-details density="compact"
                                                                        @update:model-value="updateSmartIntentFallbackData()"
                                                                        :readonly="isViewMode" inset />
                                                                </div>
                                                            </div>
                                                        </v-col>
                                                    </v-row>
                                                    <v-row>
                                                        <v-col cols="12">
                                                            <v-text-field 
                                                                v-model.number="nodeInspector.smartIntentFallback.lenChainMassage"
                                                                label="Sequence" type="number" min="1" variant="outlined"
                                                                density="comfortable" hide-details="auto"
                                                                prepend-inner-icon="mdi-numeric"
                                                                @update:model-value="updateSmartIntentFallbackData()"
                                                                @keypress="isNumberpositive($event)" class="config-input"
                                                                :readonly="isViewMode" :disabled="!nodeInspector.smartIntentFallback.isChainMassage"
                                                                hint="Number of conversation turns in sequence" persistent-hint
                                                                clearable />
                                                        </v-col>
                                                    </v-row>
                                                    <v-row>
                                                        <v-col cols="12">
                                                            <v-text-field 
                                                                v-model.number="nodeInspector.smartIntentFallback.timeSecondChainMassage"
                                                                label="Duration (seconds)" type="number" min="1"
                                                                variant="outlined" density="comfortable" hide-details="auto"
                                                                prepend-inner-icon="mdi-timer"
                                                                @update:model-value="updateSmartIntentFallbackData()"
                                                                @keypress="isNumberpositive($event)" class="config-input"
                                                                :readonly="isViewMode" :disabled="!nodeInspector.smartIntentFallback.isChainMassage"
                                                                hint="Time duration for multi-turn session" persistent-hint
                                                                clearable />
                                                        </v-col>
                                                    </v-row>
                                                </div>
                                            </div>
                                        </v-expansion-panel-text>
                                    </v-expansion-panel>

                                    <!-- Parameters Panel -->
                                    <v-expansion-panel value="parameters" class="panel-item">
                                        <v-expansion-panel-title class="panel-title-custom">
                                            <div class="d-flex align-center py-3">
                                                <div class="panel-icon-wrapper me-4"
                                                    style="background: linear-gradient(135deg, #9C27B0, #7B1FA2);">
                                                    <v-icon icon="mdi-cog" size="20" color="white"></v-icon>
                                                </div>
                                                <div>
                                                    <div class="panel-title">AI Parameters</div>
                                                    <div class="panel-subtitle">Fine-tune model behavior and output</div>
                                                </div>
                                            </div>
                                        </v-expansion-panel-title>
                                        <v-expansion-panel-text class="panel-content-wrapper">
                                            <div class="panel-content">
                                                <div class="config-section mb-3">
                                                    <h6 class="section-label mb-3">Core Parameters</h6>
                                                    <v-row>
                                                        <v-col cols="12">
                                                            <v-text-field 
                                                                v-model.number="nodeInspector.smartIntentFallback.temperature"
                                                                label="Temperature*" type="number" step="0.1" min="0" max="1"
                                                                variant="outlined" density="comfortable" hide-details="auto"
                                                                prepend-inner-icon="mdi-thermometer"
                                                                @update:model-value="updateSmartIntentFallbackData()"
                                                                @keypress="isNumberpositive($event)" class="config-input"
                                                                :readonly="isViewMode"
                                                                hint="Creativity level (0 = focused, 1 = creative)" persistent-hint />
                                                        </v-col>
                                                    </v-row>
                                                    <v-row>
                                                        <v-col cols="12">
                                                            <v-text-field 
                                                                v-model.number="nodeInspector.smartIntentFallback.topP" 
                                                                label="Top P" type="number" step="0.1" min="0" max="1" 
                                                                variant="outlined" density="comfortable" hide-details="auto"
                                                                prepend-inner-icon="mdi-filter-variant"
                                                                @update:model-value="updateSmartIntentFallbackData()"
                                                                @keypress="isNumberpositive($event)" class="config-input"
                                                                :readonly="isViewMode"
                                                                hint="Token selection probability threshold" persistent-hint />
                                                        </v-col>
                                                    </v-row>
                                                </div>

                                                <div class="config-section mb-3">
                                                    <h6 class="section-label mb-3">Penalty Settings</h6>
                                                    <v-row>
                                                        <v-col cols="12">
                                                            <v-text-field 
                                                                v-model.number="nodeInspector.smartIntentFallback.frequencyPenalty"
                                                                label="Frequency Penalty" type="number" step="0.1" min="0" max="2" 
                                                                variant="outlined" density="comfortable" hide-details="auto" 
                                                                prepend-inner-icon="mdi-repeat-off"
                                                                @update:model-value="updateSmartIntentFallbackData()"
                                                                @keypress="isNumberpositive($event)" class="config-input"
                                                                :readonly="isViewMode"
                                                                hint="Reduce repetition of frequent tokens" persistent-hint />
                                                        </v-col>
                                                    </v-row>
                                                    <v-row>
                                                        <v-col cols="12">
                                                            <v-text-field 
                                                                v-model.number="nodeInspector.smartIntentFallback.presencePenalty"
                                                                label="Presence Penalty" type="number" step="0.1" min="0" max="2" 
                                                                variant="outlined" density="comfortable" hide-details="auto"
                                                                prepend-inner-icon="mdi-minus-circle-outline"
                                                                @update:model-value="updateSmartIntentFallbackData()"
                                                                @keypress="isNumberpositive($event)" class="config-input"
                                                                :readonly="isViewMode" hint="Reduce repetition of any tokens"
                                                                persistent-hint />
                                                        </v-col>
                                                    </v-row>
                                                </div>

                                                <div class="config-section">
                                                    <h6 class="section-label mb-3">Output Control</h6>
                                                    <v-row>
                                                        <v-col cols="12">
                                                            <v-text-field 
                                                                v-model.number="nodeInspector.smartIntentFallback.maxTokens"
                                                                label="Max Tokens Response" type="number" min="1"
                                                                variant="outlined" density="comfortable" hide-details="auto"
                                                                prepend-inner-icon="mdi-text-box-check-outline"
                                                                @update:model-value="updateSmartIntentFallbackData()"
                                                                @keypress="isNumberpositive($event)" class="config-input"
                                                                :readonly="isViewMode" hint="Maximum response length in tokens"
                                                                persistent-hint />
                                                        </v-col>
                                                    </v-row>
                                                    <v-row>
                                                        <v-col cols="12">
                                                            <v-text-field 
                                                                v-model="nodeInspector.smartIntentFallback.stopSequence"
                                                                label="Stop Sequence" variant="outlined" density="comfortable"
                                                                hide-details="auto" prepend-inner-icon="mdi-stop-circle-outline"
                                                                @update:model-value="updateSmartIntentFallbackData()"
                                                                class="config-input" :readonly="isViewMode"
                                                                hint="Text sequence to stop generation" persistent-hint />
                                                        </v-col>
                                                    </v-row>
                                                </div>
                                            </div>
                                        </v-expansion-panel-text>
                                    </v-expansion-panel>

                                    <!-- Line Configuration Panel -->
                                    <v-expansion-panel value="line" class="panel-item">
                                        <v-expansion-panel-title class="panel-title-custom">
                                            <div class="d-flex align-center py-3">
                                                <div class="panel-icon-wrapper me-4"
                                                    style="background: linear-gradient(135deg, #4CAF50, #388E3C);">
                                                    <v-icon icon="mdi-message" size="20" color="white"></v-icon>
                                                </div>
                                                <div>
                                                    <div class="panel-title">Line Configuration</div>
                                                    <div class="panel-subtitle">LINE messaging platform settings</div>
                                                </div>
                                            </div>
                                        </v-expansion-panel-title>
                                        <v-expansion-panel-text class="panel-content-wrapper">
                                            <div class="panel-content pa-4">
                                                <div class="config-section mb-3">
                                                    <h6 class="section-label mb-3">Display Options</h6>

                                                    <!-- Show Name -->
                                                    <div class="switch-option-compact mb-3">
                                                        <div class="d-flex align-center justify-space-between py-2 px-3 rounded-lg"
                                                            :class="{ 'switch-active-bg': nodeInspector.smartIntentFallback.showLineName }">
                                                            <div class="d-flex align-center">
                                                                <v-avatar size="28"
                                                                    :color="nodeInspector.smartIntentFallback.showLineName ? 'info' : 'grey-lighten-1'"
                                                                    class="me-3">
                                                                    <v-icon
                                                                        :color="nodeInspector.smartIntentFallback.showLineName ? 'white' : 'grey-darken-1'"
                                                                        size="16">mdi-account-circle</v-icon>
                                                                </v-avatar>
                                                                <div>
                                                                    <div class="switch-title">Show Name</div>
                                                                    <div class="switch-description">Display user name</div>
                                                                </div>
                                                            </div>
                                                            <v-switch 
                                                                v-model="nodeInspector.smartIntentFallback.showLineName" 
                                                                color="info" hide-details density="compact"
                                                                @update:model-value="updateSmartIntentFallbackData()"
                                                                :readonly="isViewMode" inset />
                                                        </div>
                                                    </div>

                                                    <!-- Loading Indicator -->
                                                    <div class="switch-option-compact mb-3">
                                                        <div class="d-flex align-center justify-space-between py-2 px-3 rounded-lg"
                                                            :class="{ 'switch-active-bg': nodeInspector.smartIntentFallback.loadingBar }">
                                                            <div class="d-flex align-center">
                                                                <v-avatar size="28"
                                                                    :color="nodeInspector.smartIntentFallback.loadingBar ? 'success' : 'grey-lighten-1'"
                                                                    class="me-3">
                                                                    <v-icon
                                                                        :color="nodeInspector.smartIntentFallback.loadingBar ? 'white' : 'grey-darken-1'"
                                                                        size="16">mdi-progress-clock</v-icon>
                                                                </v-avatar>
                                                                <div>
                                                                    <div class="switch-title">Loading Indicator</div>
                                                                    <div class="switch-description">Show typing animation</div>
                                                                </div>
                                                            </div>
                                                            <v-switch 
                                                                v-model="nodeInspector.smartIntentFallback.loadingBar" 
                                                                color="success" hide-details density="compact"
                                                                @update:model-value="updateSmartIntentFallbackData()"
                                                                :readonly="isViewMode" inset />
                                                        </div>
                                                    </div>
                                                </div>

                                                <div class="config-section mb-3">
                                                    <h6 class="section-label mb-3">Loading & Duration</h6>
                                                    <v-row>
                                                        <v-col cols="12">
                                                            <v-select 
                                                                v-model="nodeInspector.smartIntentFallback.loadingBarDuration"
                                                                :items="[10, 20, 30, 40, 50, 60]"
                                                                label="Loading Duration (seconds)" variant="outlined"
                                                                density="comfortable" hide-details="auto"
                                                                prepend-inner-icon="mdi-timer-sand"
                                                                @update:model-value="updateSmartIntentFallbackData()"
                                                                class="config-input" :disabled="!nodeInspector.smartIntentFallback.loadingBar"
                                                                :readonly="isViewMode"
                                                                hint="Duration for loading animation display" persistent-hint
                                                                clearable />
                                                        </v-col>
                                                    </v-row>
                                                </div>

                                                <div class="config-section mb-3">
                                                    <h6 class="section-label mb-3">Monitoring</h6>
                                                    <v-row>
                                                        <v-col cols="12">
                                                            <v-text-field 
                                                                v-model="nodeInspector.smartIntentFallback.monitorLineUserId"
                                                                label="Monitor Line User ID" variant="outlined"
                                                                density="comfortable" hide-details="auto"
                                                                prepend-inner-icon="mdi-monitor-eye"
                                                                @update:model-value="updateSmartIntentFallbackData()"
                                                                class="config-input" :readonly="isViewMode"
                                                                hint="LINE user ID for monitoring notifications" persistent-hint />
                                                        </v-col>
                                                    </v-row>
                                                    <v-row>
                                                        <v-col cols="12">
                                                            <v-autocomplete 
                                                                v-model="nodeInspector.smartIntentFallback.monitorSocialID"
                                                                :items="listMonitorLineName" label="Monitor Line Name"
                                                                variant="outlined" density="comfortable" hide-details="auto"
                                                                prepend-inner-icon="mdi-chat" item-title="name" item-value="id"
                                                                @update:model-value="updateSmartIntentFallbackData()"
                                                                class="config-input" :readonly="isViewMode"
                                                                hint="Select monitor line name" persistent-hint clearable />
                                                        </v-col>
                                                    </v-row>
                                                </div>

                                                <div class="config-section">
                                                    <h6 class="section-label mb-3">Content Filtering</h6>
                                                    <v-row>
                                                        <v-col cols="12">
                                                            <v-toolbar-title style="font-size: 16px">
                                                                Forbidden word
                                                            </v-toolbar-title>
                                                        </v-col>
                                                    </v-row>
                                                    <div class="pr-1" style="
                                                            border: #dfe0e2 1px solid;
                                                            background: #fff;
                                                            margin-top: 10px;
                                                            border-radius: 8px;
                                                        ">
                                                        <v-row v-for="(item, index) in nodeInspector.smartIntentFallback.forbiddenWords || ['']"
                                                            :key="`fallback-forbidden-${index}`" style="margin: 0px">
                                                            <v-col cols="12" style="padding: 0px">
                                                                <div style="display: flex; align-items: center">
                                                                    <div style="
                                                                            height: 50px;
                                                                            background: #f8f8f8;
                                                                            padding: 14px 20px 0 20px;
                                                                        ">
                                                                        {{ index + 1 }}
                                                                    </div>
                                                                    <v-textarea variant="solo" style="
                                                                            overflow: hidden;
                                                                            overflow-wrap: break-word;
                                                                            resize: none;
                                                                            height: 50px;
                                                                            width: 98%;
                                                                            border-radius: 0px;
                                                                            font-family: Fira code, Fira Mono, Consolas,
                                                                                Menlo, Courier, monospace;
                                                                            font-size: 14px;
                                                                        "
                                                                        :model-value="(nodeInspector.smartIntentFallback.forbiddenWords && nodeInspector.smartIntentFallback.forbiddenWords[index]) || ''"
                                                                        placeholder="Enter word or phrase"
                                                                        @keypress="isEnterSmartIntentFallbackForbidden($event, index)"
                                                                        @keyup.delete="checkDeleteSmartIntentFallbackForbiddenWord(index, (nodeInspector.smartIntentFallback.forbiddenWords && nodeInspector.smartIntentFallback.forbiddenWords[index]) || '')"
                                                                        @update:model-value="updateSmartIntentFallbackForbiddenWord(index, $event)"
                                                                        :readonly="isViewMode" />
                                                                    <v-btn icon="mdi-attachment" variant="text"
                                                                        v-show="(nodeInspector.smartIntentFallback.forbiddenWords || []).length > 1 && index + 1 < (nodeInspector.smartIntentFallback.forbiddenWords || []).length"
                                                                        @click="removeSmartIntentFallbackForbiddenWord(index)"
                                                                        :disabled="isViewMode">
                                                                        <v-tooltip location="top" :open-delay="500">
                                                                            <template v-slot:activator="{ props }">
                                                                                <v-icon color="error" style="cursor: pointer"
                                                                                    v-bind="props">
                                                                                    mdi-delete-forever
                                                                                </v-icon>
                                                                            </template>
                                                                            <span>delete</span>
                                                                        </v-tooltip>
                                                                    </v-btn>
                                                                </div>
                                                            </v-col>
                                                        </v-row>
                                                    </div>
                                                </div>
                                            </div>
                                        </v-expansion-panel-text>
                                    </v-expansion-panel>

                                    <!-- System Prompt Panel -->
                                    <v-expansion-panel value="prompt" class="panel-item">
                                        <v-expansion-panel-title class="panel-title-custom">
                                            <div class="d-flex align-center py-3">
                                                <div class="panel-icon-wrapper me-4"
                                                    style="background: linear-gradient(135deg, #607D8B, #455A64);">
                                                    <v-icon icon="mdi-message-text" size="20" color="white"></v-icon>
                                                </div>
                                                <div>
                                                    <div class="panel-title">System Prompt</div>
                                                    <div class="panel-subtitle">Define AI behavior and personality</div>
                                                </div>
                                            </div>
                                        </v-expansion-panel-title>
                                        <v-expansion-panel-text class="panel-content-wrapper">
                                            <div class="panel-content">
                                                <div class="prompt-section">
                                                    <div class="d-flex align-center mb-4">
                                                        <v-avatar size="36" color="info" class="me-3">
                                                            <v-icon color="white" size="20">mdi-robot-outline</v-icon>
                                                        </v-avatar>
                                                        <div>
                                                            <h6 class="text-h6 mb-1">AI Personality & Instructions</h6>
                                                            <p class="text-body-2 text-medium-emphasis mb-0">
                                                                Define how your AI should behave, respond, and interact with users
                                                            </p>
                                                        </div>
                                                    </div>
                                                    <h3 class="text-h6 d-flex align-center mb-3" style="font-size: 16px !important;">
                                                        <v-icon icon="mdi-pencil-outline" size="small"
                                                            class="me-2"></v-icon>
                                                        Configure system message for your AI
                                                    </h3>

                                                    <!-- Prompt Template Header for Fallback -->
                                                    <div class="mb-2" style="display:flex;flex-direction: column;align-items: flex-end;">
                                                        <div style="display: flex; align-items: center;">
                                                            <v-tooltip location="top">
                                                                <template v-slot:activator="{ props }">
                                                                    <v-icon icon="mdi-information-outline" size="small"
                                                                        color="grey" class="me-3" v-bind="props"></v-icon>
                                                                </template>
                                                                <span>Configure additional prompts and templates</span>
                                                            </v-tooltip>
                                                            <v-btn
                                                                v-if="DDGroupPromptTemplate && DDGroupPromptTemplate.length > 0"
                                                                variant="outlined" color="orange"
                                                                prepend-icon="mdi-playlist-plus" @click="showSmartIntentFallbackPromptTemplate()"
                                                                class="text-none font-weight-medium" size="small">
                                                                Show Templates
                                                            </v-btn>
                                                        </div>
                                                    </div>

                                                    <!-- Main editor -->
                                                    <v-row>
                                                        <v-col cols="12" class="prompt-editor-col">
                                                            <v-card variant="outlined" class="prompt-editor-card">
                                                                <v-card-text class="px-2 py-1">
                                                                    <v-textarea 
                                                                        v-model="nodeInspector.smartIntentFallback.systemPrompt" 
                                                                        label="" variant="plain" counter auto-grow rows="10"
                                                                        @update:model-value="updateSmartIntentFallbackData()"
                                                                        :readonly="isViewMode"
                                                                        placeholder="Enter your system message here..."
                                                                        class="prompt-textarea" />
                                                                </v-card-text>
                                                            </v-card>
                                                        </v-col>
                                                    </v-row>
                                                </div>
                                            </div>
                                        </v-expansion-panel-text>
                                    </v-expansion-panel>

                                    <!-- Knowledge Panel for Fallback -->
                                    <v-expansion-panel value="knowledge" class="panel-item mb-3">
                                        <v-expansion-panel-title class="panel-title-custom">
                                            <div class="d-flex align-center py-3">
                                                <div class="panel-icon-wrapper me-4"
                                                    style="background: linear-gradient(135deg, #FF6F00, #E65100);">
                                                    <v-icon icon="mdi-database" size="20" color="white"></v-icon>
                                                </div>
                                                <div>
                                                    <div class="panel-title">Knowledge Sources</div>
                                                    <div class="panel-subtitle">Configure knowledge integration for fallback</div>
                                                </div>
                                            </div>
                                        </v-expansion-panel-title>
                                        <v-expansion-panel-text class="panel-content-wrapper">
                                            <div class="panel-content">
                                                <!-- Knowledge Sources Selection -->
                                                <div class="config-section mb-4">
                                                    <h6 class="section-label mb-3">Enable Knowledge Sources</h6>
                                                    <div class="knowledge-sources-grid">
                                                        <div class="knowledge-source-card mb-3">
                                                            <div class="knowledge-source-frame rounded-lg border">
                                                                <div class="knowledge-source-title">    
                                                                    <div class="d-flex align-center">
                                                                        <v-avatar size="32" color="blue" class="me-3">
                                                                            <v-icon color="white" size="18">mdi-file-document</v-icon>
                                                                        </v-avatar>
                                                                        <div>
                                                                            <div class="text-subtitle-2 font-weight-medium">Documents</div>
                                                                            <div class="text-caption text-medium-emphasis">Vector-based document search</div>
                                                                        </div>
                                                                    </div>
                                                                    <v-switch 
                                                                        v-model="nodeInspector.smartIntentFallback.enableDocuments"
                                                                        color="blue" hide-details density="compact"
                                                                        @update:model-value="updateSmartIntentFallbackData()"
                                                                        :readonly="isViewMode" inset />
                                                                </div>
                                                                <!-- Documents Configuration -->
                                                                <div v-if="nodeInspector.smartIntentFallback.enableDocuments" 
                                                                    class="config-section mb-4 knowledge-source-config">
                                                                    <div class="d-flex align-center mb-3">
                                                                        <h6 class="section-label mb-0">Documents Configuration</h6>
                                                                    </div>
                                                                    <!-- Document Selection -->
                                                                    <v-autocomplete 
                                                                        v-model="nodeInspector.smartIntentFallback.document" 
                                                                        :items="DDVector" item-title="name" item-value="name" 
                                                                        label="Document" variant="outlined" density="comfortable" 
                                                                        hide-details="auto" chips multiple 
                                                                        @update:model-value="updateSmartIntentFallbackData()"
                                                                        class="config-input mb-3"
                                                                        placeholder="Select documents" :clearable="!isViewMode" :readonly="isViewMode">
                                                                        <template v-slot:item="{ props, item }">
                                                                            <v-list-item v-bind="props" class="document-option">
                                                                                <template v-slot:prepend>
                                                                                    <v-icon size="20" color="primary">mdi-file-document</v-icon>
                                                                                </template>
                                                                                <v-list-item-subtitle>{{ item.raw.description }}</v-list-item-subtitle>
                                                                            </v-list-item>
                                                                        </template>
                                                                    </v-autocomplete>
                                                                    
                                                                    <!-- Top-N Chunks -->
                                                                    <v-text-field 
                                                                        v-model.number="nodeInspector.smartIntentFallback.topNChunks" 
                                                                        type="number" label="Top-N Chunks" variant="outlined" 
                                                                        density="comfortable" hide-details="auto" :readonly="isViewMode" 
                                                                        min="1" step="1" 
                                                                        @focus="$event.target.select()"
                                                                        @blur="validateAndFixSmartIntentFallbackTopNChunks($event)"
                                                                        @keydown.enter="validateAndFixSmartIntentFallbackTopNChunks($event)"
                                                                        class="config-input mb-3" 
                                                                        hint="Number of document chunks to retrieve" persistent-hint />
                                                                    
                                                                    <!-- Cosine Similarity -->
                                                                    <v-text-field 
                                                                        v-model.number="nodeInspector.smartIntentFallback.cosineSimilarity" 
                                                                        type="number" label="Cosine Similarity" variant="outlined" 
                                                                        density="comfortable" hide-details="auto" :readonly="isViewMode" 
                                                                        min="0.0001" max="1" step="0.0001"
                                                                        @focus="$event.target.select()"
                                                                        @blur="validateAndFixSmartIntentFallbackCosineSimilarity($event)"
                                                                        @keydown.enter="validateAndFixSmartIntentFallbackCosineSimilarity($event)"
                                                                        class="config-input" 
                                                                        hint="Similarity threshold for document matching" persistent-hint />
                                                                </div>
                                                            </div>
                                                        </div>

                                                        <div class="knowledge-source-card mb-3">
                                                            <div class="knowledge-source-frame rounded-lg border">
                                                                <div class="knowledge-source-title">
                                                                    <div class="d-flex align-center">
                                                                        <v-avatar size="32" color="green" class="me-3">
                                                                            <v-icon color="white" size="18">mdi-database</v-icon>
                                                                        </v-avatar>
                                                                        <div>
                                                                            <div class="text-subtitle-2 font-weight-medium">Database Integration</div>
                                                                            <div class="text-caption text-medium-emphasis">SQL database queries</div>
                                                                        </div>
                                                                    </div>
                                                                    <v-switch 
                                                                        v-model="nodeInspector.smartIntentFallback.enableDatabaseIntegration"
                                                                        color="green" hide-details density="compact"
                                                                        @update:model-value="updateSmartIntentFallbackData()"
                                                                        :readonly="isViewMode" inset />
                                                                </div>
                                                                <!-- Database Integration Configuration -->
                                                                <div v-if="nodeInspector.smartIntentFallback.enableDatabaseIntegration" 
                                                                    class="config-section mb-4 knowledge-source-config">
                                                                    <div class="d-flex align-center mb-3">
                                                                        <h6 class="section-label mb-0">Database Integration Configuration</h6>
                                                                    </div>
                                                                    
                                                                    <!-- Table Name Selection -->
                                                                    <v-autocomplete 
                                                                        v-model="nodeInspector.smartIntentFallback.tableName" 
                                                                        :items="listDataIntegration" item-title="dataName" item-value="id" 
                                                                        label="Table Name" variant="outlined" density="comfortable" 
                                                                        hide-details="auto" @update:model-value="updateSmartIntentFallbackData()" 
                                                                        class="config-input mb-3" placeholder="Select database table" 
                                                                        :clearable="!isViewMode" :readonly="isViewMode">
                                                                        <template v-slot:item="{ props, item }">
                                                                            <v-list-item v-bind="props" class="table-option">
                                                                                <template v-slot:prepend>
                                                                                    <v-icon size="20" color="primary">mdi-table</v-icon>
                                                                                </template>
                                                                                <v-list-item-title>{{ item.raw.name }}</v-list-item-title>
                                                                                <v-list-item-subtitle>{{ item.raw.description }}</v-list-item-subtitle>
                                                                            </v-list-item>
                                                                        </template>
                                                                    </v-autocomplete>

                                                                    <!-- SQL Extraction Model Selection -->
                                                                    <v-autocomplete 
                                                                        v-model="nodeInspector.smartIntentFallback.sqlExtractionModel" 
                                                                        :items="listSqlExtract" item-title="name" item-value="modelDisplayID" 
                                                                        label="SQL Extraction Model" variant="outlined" density="comfortable" 
                                                                        hide-details="auto" :clearable="!isViewMode" :readonly="isViewMode"
                                                                        @update:model-value="updateSmartIntentFallbackData()"
                                                                        class="config-input mb-3" placeholder="Select AI model for SQL generation">
                                                                        <template v-slot:item="{ props, item }">
                                                                            <v-list-item v-bind="props" class="model-option">
                                                                                <template v-slot:prepend>
                                                                                    <div class="model-icon-small mr-2" :style="{ backgroundColor: item.raw.color }">
                                                                                        <v-icon size="14" color="green">mdi-robot</v-icon>
                                                                                    </div>
                                                                                </template>
                                                                                <v-list-item-subtitle>{{ item.raw.description }}</v-list-item-subtitle>
                                                                            </v-list-item>
                                                                        </template>
                                                                    </v-autocomplete>

                                                                    <!-- Prompt for No Data -->
                                                                    <v-textarea 
                                                                        v-model="nodeInspector.smartIntentFallback.promptSqlNoData" 
                                                                        label="Prompt for No Data" variant="outlined" density="comfortable" 
                                                                        rows="3" auto-grow hide-details="auto"
                                                                        placeholder="Enter the message to display when no data is found in the database..."
                                                                        @update:model-value="updateSmartIntentFallbackData()"
                                                                        class="config-input" :readonly="isViewMode" 
                                                                        hint="Message shown when database query returns no results" persistent-hint />
                                                                </div>
                                                            </div>
                                                        </div>

                                                        <div class="knowledge-source-card mb-3">
                                                            <div class="knowledge-source-frame rounded-lg border">
                                                                <div class="knowledge-source-title">
                                                                    <div class="d-flex align-center">
                                                                        <v-avatar size="32" color="purple" class="me-3">
                                                                            <v-icon color="white" size="18">mdi-package-variant</v-icon>
                                                                        </v-avatar>
                                                                        <div>
                                                                            <div class="text-subtitle-2 font-weight-medium">Products</div>
                                                                            <div class="text-caption text-medium-emphasis">Product catalog search</div>
                                                                        </div>
                                                                    </div>
                                                                    <v-switch 
                                                                        v-model="nodeInspector.smartIntentFallback.enableProducts"
                                                                        color="purple" hide-details density="compact"
                                                                        @update:model-value="updateSmartIntentFallbackData()"
                                                                        :readonly="isViewMode" inset />
                                                                </div>
                                                                <!-- Products Configuration -->
                                                                <div v-if="nodeInspector.smartIntentFallback.enableProducts" 
                                                                    class="config-section mb-4 knowledge-source-config">
                                                                    <div class="d-flex align-center mb-3">
                                                                        <h6 class="section-label mb-0">Products Configuration</h6>
                                                                    </div>
                                                                    
                                                                    <!-- Product Group List Selection -->
                                                                    <v-autocomplete 
                                                                        v-model="nodeInspector.smartIntentFallback.productGroupList" 
                                                                        :items="DDProductGroup" item-title="name" item-value="id" 
                                                                        label="Product Group List" variant="outlined" density="comfortable" 
                                                                        hide-details="auto" chips multiple return-object
                                                                        @update:model-value="updateSmartIntentFallbackData()"
                                                                        class="config-input mb-3" placeholder="Select product group" 
                                                                        :clearable="!isViewMode" :readonly="isViewMode">
                                                                        <template v-slot:item="{ props, item }">
                                                                            <v-list-item v-bind="props" class="product-group-option">
                                                                                <template v-slot:prepend>
                                                                                    <v-icon size="20" color="primary">mdi-package-variant</v-icon>
                                                                                </template>
                                                                                <v-list-item-subtitle>{{ item.raw.description }}</v-list-item-subtitle>
                                                                            </v-list-item>
                                                                        </template>
                                                                    </v-autocomplete>

                                                                    <!-- Top-N Chunks -->
                                                                    <v-text-field 
                                                                        v-model.number="nodeInspector.smartIntentFallback.productTopNChunks" 
                                                                        type="number" label="Top-N Chunks" variant="outlined" 
                                                                        density="comfortable" hide-details="auto" min="1" step="1"
                                                                        @focus="$event.target.select()"
                                                                        @blur="validateAndFixSmartIntentFallbackProductTopNChunks($event)"
                                                                        @keydown.enter="validateAndFixSmartIntentFallbackProductTopNChunks($event)"
                                                                        class="config-input mb-3" placeholder="Enter number of chunks" 
                                                                        :readonly="isViewMode" 
                                                                        hint="Number of product chunks to retrieve" persistent-hint />

                                                                    <!-- Cosine Similarity -->
                                                                    <v-text-field 
                                                                        v-model.number="nodeInspector.smartIntentFallback.productCosineSimilarity"
                                                                        type="number" label="Cosine Similarity" variant="outlined" 
                                                                        density="comfortable" hide-details="auto" min="0.0001" max="1" step="0.0001"
                                                                        @focus="$event.target.select()"
                                                                        @blur="validateAndFixSmartIntentFallbackProductCosineSimilarity($event)"
                                                                        @keydown.enter="validateAndFixSmartIntentFallbackProductCosineSimilarity($event)"
                                                                        class="config-input" placeholder="Enter similarity threshold (0.0001 - 1.0)"
                                                                        :readonly="isViewMode" 
                                                                        hint="Similarity threshold for product matching" persistent-hint />
                                                                </div>
                                                            </div>
                                                        </div>

                                                        <div class="knowledge-source-card mb-3">
                                                            <div class="knowledge-source-frame rounded-lg border">
                                                                <div class="knowledge-source-title">
                                                                    <div class="d-flex align-center">
                                                                        <v-avatar size="32" color="orange" class="me-3">
                                                                            <v-icon color="white" size="18">mdi-rocket-launch</v-icon>
                                                                        </v-avatar>
                                                                        <div>
                                                                            <div class="text-subtitle-2 font-weight-medium">API Knowledge</div>
                                                                            <div class="text-caption text-medium-emphasis">External API integration</div>
                                                                        </div>
                                                                    </div>
                                                                    <v-switch 
                                                                        v-model="nodeInspector.smartIntentFallback.enableApiKnowledge"
                                                                        color="orange" hide-details density="compact"
                                                                        @update:model-value="updateSmartIntentFallbackData()"
                                                                        :readonly="isViewMode" inset />
                                                                </div>
                                                                <!-- API Knowledge Configuration -->
                                                                <div v-if="nodeInspector.smartIntentFallback.enableApiKnowledge" 
                                                                    class="config-section mb-4 knowledge-source-config">
                                                                    <div class="d-flex align-center mb-3">
                                                                        <h6 class="section-label mb-0">API Knowledge Configuration</h6>
                                                                    </div>
                                                                    
                                                                    <!-- API URL -->
                                                                    <v-text-field 
                                                                        v-model="nodeInspector.smartIntentFallback.knowledgeApiUrl" 
                                                                        label="API URL" variant="outlined" density="comfortable" 
                                                                        hide-details="auto" placeholder="https://api.example.com/knowledge"
                                                                        @update:model-value="updateSmartIntentFallbackData()"
                                                                        class="config-input mb-3" :readonly="isViewMode">
                                                                        <template v-slot:prepend-inner>
                                                                            <v-icon size="20" color="primary">mdi-link</v-icon>
                                                                        </template>
                                                                    </v-text-field>

                                                                    <!-- HTTP Method -->
                                                                    <v-select 
                                                                        v-model="nodeInspector.smartIntentFallback.knowledgeHttpMethod" 
                                                                        :items="['GET', 'POST']" label="HTTP Method" variant="outlined" 
                                                                        density="comfortable" hide-details="auto"
                                                                        @update:model-value="updateSmartIntentFallbackData()"
                                                                        class="config-input mb-3" :readonly="isViewMode">
                                                                        <template v-slot:prepend-inner>
                                                                            <v-icon size="20" color="primary">mdi-code-brackets</v-icon>
                                                                        </template>
                                                                    </v-select>

                                                                    <!-- Request Headers -->
                                                                    <div class="headers-section mb-3">
                                                                        <div class="section-header mb-2">
                                                                            <span class="section-title text-subtitle-2 font-weight-medium">Request Headers</span>
                                                                            <v-btn v-if="!isViewMode" @click="addSmartIntentFallbackKnowledgeAPIHeader()" size="small" color="primary"
                                                                                variant="tonal" class="add-btn">
                                                                                <v-icon size="16">mdi-plus</v-icon>
                                                                                Add Header
                                                                            </v-btn>
                                                                        </div>
                                                                        <div v-if="nodeInspector.smartIntentFallback.listHeaders" class="headers-list">
                                                                            <div v-for="(header, headerIndex) in nodeInspector.smartIntentFallback.listHeaders" :key="headerIndex"
                                                                                class="header-row mb-2">
                                                                                <v-row no-gutters>
                                                                                    <v-col cols="5">
                                                                                        <v-text-field
                                                                                            v-model="header.key"
                                                                                            label="Header Name"
                                                                                            variant="outlined"
                                                                                            density="compact"
                                                                                            hide-details="auto"
                                                                                            @update:model-value="updateSmartIntentFallbackData()"
                                                                                            class="header-input"
                                                                                            :readonly="isViewMode"
                                                                                            placeholder="Content-Type" />
                                                                                    </v-col>
                                                                                    <v-col cols="6" class="pl-2">
                                                                                        <v-text-field
                                                                                            v-model="header.value"
                                                                                            label="Header Value"
                                                                                            variant="outlined"
                                                                                            density="compact"
                                                                                            hide-details="auto"
                                                                                            @update:model-value="updateSmartIntentFallbackData()"
                                                                                            class="header-input"
                                                                                            :readonly="isViewMode"
                                                                                            placeholder="application/json" />
                                                                                    </v-col>
                                                                                    <v-col cols="1" class="pl-2 d-flex align-center">
                                                                                        <v-btn
                                                                                            v-if="!isViewMode"
                                                                                            @click="removeSmartIntentFallbackKnowledgeAPIHeader(headerIndex)"
                                                                                            icon
                                                                                            size="small"
                                                                                            variant="text"
                                                                                            color="error">
                                                                                            <v-icon size="16">mdi-delete</v-icon>
                                                                                        </v-btn>
                                                                                    </v-col>
                                                                                </v-row>
                                                                            </div>
                                                                        </div>
                                                                        <div v-else class="text-caption text-medium-emphasis">
                                                                            No headers configured
                                                                        </div>
                                                                    </div>

                                                                    <!-- Request Body -->
                                                                    <div class="mb-3">
                                                                        <div class="section-header mb-2">
                                                                            <span class="section-title text-subtitle-2 font-weight-medium">Request Body</span>
                                                                        </div>
                                                                        <v-textarea
                                                                            v-model="nodeInspector.smartIntentFallback.knowledgeRequestBody"
                                                                            label="Request Body (JSON)"
                                                                            variant="outlined"
                                                                            density="comfortable"
                                                                            rows="4"
                                                                            auto-grow
                                                                            hide-details="auto"
                                                                            @update:model-value="updateSmartIntentFallbackData()"
                                                                            class="config-input"
                                                                            :readonly="isViewMode"
                                                                            placeholder='{"query": "{{userMessage}}", "limit": 10}'
                                                                            hint="JSON payload for API request (use {{ variables }} for dynamic values)" 
                                                                            persistent-hint>
                                                                            <template v-slot:prepend-inner>
                                                                                <v-icon size="20" color="primary">mdi-code-json</v-icon>
                                                                            </template>
                                                                            <template v-slot:append-inner>
                                                                                <v-tooltip location="top">
                                                                                    <template v-slot:activator="{ props }">
                                                                                        <v-icon v-bind="props" size="small" color="grey-darken-1">
                                                                                            mdi-information-outline
                                                                                        </v-icon>
                                                                                    </template>
                                                                                    <span>JSON payload for API request (use {{ variables }} for dynamic values)</span>
                                                                                </v-tooltip>
                                                                            </template>
                                                                        </v-textarea>
                                                                    </div>

                                                                    <!-- Response Data Path -->
                                                                    <v-text-field 
                                                                        v-model="nodeInspector.smartIntentFallback.knowledgeResponseDataPath" 
                                                                        label="Response Data Path" variant="outlined" density="comfortable" 
                                                                        hide-details="auto" placeholder="data.knowledge"
                                                                        @update:model-value="updateSmartIntentFallbackData()"
                                                                        class="config-input" :readonly="isViewMode">
                                                                        <template v-slot:prepend-inner>
                                                                            <v-icon size="20" color="primary">mdi-code-json</v-icon>
                                                                        </template>
                                                                        <template v-slot:append-inner>
                                                                            <v-tooltip location="top">
                                                                                <template v-slot:activator="{ props }">
                                                                                    <v-icon v-bind="props" size="small" color="grey-darken-1">
                                                                                        mdi-information-outline
                                                                                    </v-icon>
                                                                                </template>
                                                                                <span>JSON path to extract knowledge data from API response</span>
                                                                            </v-tooltip>
                                                                        </template>
                                                                    </v-text-field>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>





                                                <!-- Knowledge Integration Tips -->
                                                <div class="knowledge-tips mt-4">
                                                    <v-card class="tips-card" color="orange-lighten-5" flat>
                                                        <v-card-text class="pa-4">
                                                            <div class="d-flex align-start">
                                                                <v-icon color="orange-darken-1" size="20"
                                                                    class="me-2 mt-1">mdi-lightbulb-outline</v-icon>
                                                                <div>
                                                                    <div class="text-subtitle-2 font-weight-medium mb-2">
                                                                        Fallback Knowledge Integration Tips:</div>
                                                                    <ul class="text-body-2 text-medium-emphasis">
                                                                        <li>Fallback knowledge is used when no Smart Intent matches</li>
                                                                        <li>Configure broader knowledge sources for better coverage</li>
                                                                        <li>Consider using general documents or FAQ data</li>
                                                                        <li>Test fallback responses to ensure helpful answers</li>
                                                                    </ul>
                                                                </div>
                                                            </div>
                                                        </v-card-text>
                                                    </v-card>
                                                </div>
                                            </div>
                                        </v-expansion-panel-text>
                                    </v-expansion-panel>
                                </v-expansion-panels>
                                        </v-expansion-panel-text>
                                    </v-expansion-panel>
                                </v-expansion-panels>
                            </div>
                        </div>
                        <!-- Smart Intent Prompt Template Dialog -->
                        <v-dialog v-model="smartIntentPromptTemplateDialog" max-width="800" scrollable>
                            <v-card>
                                <v-card-title class="bg-secondary text-white d-flex align-center">
                                    <v-icon icon="mdi-brain" class="me-2"></v-icon>
                                    Smart Intent Prompt Templates
                                    <v-spacer></v-spacer>
                                    <v-btn icon="mdi-close" variant="text" color="white" size="small" 
                                        @click="smartIntentPromptTemplateDialog = false"></v-btn>
                                </v-card-title>
                                
                                <v-card-text class="pa-4">
                                    <!-- Template Group Selection -->
                                    <v-autocomplete 
                                        label="Select Template Group" 
                                        :items="DDGroupPromptTemplate" 
                                        item-title="name"
                                        item-value="id" 
                                        density="compact" 
                                        clearable
                                        variant="outlined" 
                                        v-model="smartIntentGroupPromptTemplate"
                                        prepend-inner-icon="mdi-folder-outline" 
                                        hide-details
                                        class="mb-4">
                                    </v-autocomplete>
                                    <!-- Search Template -->
                                    <v-text-field 
                                        v-model="smartIntentTemplateSearchQuery"
                                        label="Search templates" 
                                        prepend-inner-icon="mdi-magnify"
                                        variant="outlined" 
                                        density="compact" 
                                        hide-details
                                        class="mb-4" 
                                        clearable>
                                    </v-text-field>
                                    <!-- Templates List -->
                                    <div style="max-height: 400px; overflow-y: auto;">
                                        <div v-if="smartIntentSearchedTemplates.length === 0" 
                                            class="pa-4 text-center text-grey-darken-1">
                                            <v-icon icon="mdi-playlist-remove" size="large" 
                                                class="d-block mx-auto mb-2"></v-icon>
                                            <span>No templates found</span>
                                        </div>
                                        <v-list v-else density="compact" class="pa-0">
                                            <v-list-item 
                                                v-for="(item, i) in smartIntentSearchedTemplates" 
                                                :key="i"
                                                @click="setSelectSmartIntentPromptTemplate(item)"
                                                class="template-list-item ma-1" 
                                                :ripple="true"
                                                rounded>
                                                <template v-slot:prepend>
                                                    <v-icon size="small" color="secondary" 
                                                        icon="mdi-brain"></v-icon>
                                                </template>
                                                <template v-slot:title>
                                                    <span class="font-weight-medium">{{ item.name }}</span>
                                                </template>
                                                <template v-slot:subtitle>
                                                    <span class="text-caption text-grey">
                                                        {{ truncatePrompt(item.prompt) }}
                                                    </span>
                                                </template>
                                                <template v-slot:append>
                                                    <v-btn icon="mdi-plus" size="small" variant="text" 
                                                        color="secondary" @click.stop="setSelectSmartIntentPromptTemplate(item)">
                                                    </v-btn>
                                                </template>
                                            </v-list-item>
                                        </v-list>
                                    </div>
                                </v-card-text>
                                
                                <v-card-actions>
                                    <v-spacer></v-spacer>
                                    <v-btn color="secondary" variant="text" @click="smartIntentPromptTemplateDialog = false">
                                        Close
                                    </v-btn>
                                </v-card-actions>
                            </v-card>
                        </v-dialog>

                        <!-- Smart Intent Fallback Prompt Template Dialog -->
                        <v-dialog v-model="smartIntentFallbackPromptTemplateDialog" max-width="800" scrollable>
                            <v-card>
                                <v-card-title class="bg-orange text-white d-flex align-center">
                                    <v-icon icon="mdi-shield-alert" class="me-2"></v-icon>
                                    Fallback Prompt Templates
                                    <v-spacer></v-spacer>
                                    <v-btn icon="mdi-close" variant="text" color="white" size="small" 
                                        @click="smartIntentFallbackPromptTemplateDialog = false"></v-btn>
                                </v-card-title>
                                
                                <v-card-text class="pa-4">
                                    <!-- Template Group Selection -->
                                    <v-autocomplete 
                                        label="Select Template Group" 
                                        :items="DDGroupPromptTemplate" 
                                        item-title="name"
                                        item-value="id" 
                                        density="compact" 
                                        clearable
                                        variant="outlined" 
                                        v-model="smartIntentFallbackGroupPromptTemplate"
                                        prepend-inner-icon="mdi-folder-outline" 
                                        hide-details
                                        class="mb-4">
                                    </v-autocomplete>

                                    <!-- Search Template -->
                                    <v-text-field 
                                        v-model="smartIntentFallbackTemplateSearchQuery"
                                        label="Search templates" 
                                        prepend-inner-icon="mdi-magnify"
                                        variant="outlined" 
                                        density="compact" 
                                        hide-details
                                        class="mb-4" 
                                        clearable>
                                    </v-text-field>

                                    <!-- Templates List -->
                                    <div style="max-height: 400px; overflow-y: auto;">
                                        <div v-if="smartIntentFallbackSearchedTemplates.length === 0" 
                                            class="pa-4 text-center text-grey-darken-1">
                                            <v-icon icon="mdi-playlist-remove" size="large" 
                                                class="d-block mx-auto mb-2"></v-icon>
                                            <span>No templates found</span>
                                        </div>

                                        <v-list v-else density="compact" class="pa-0">
                                            <v-list-item 
                                                v-for="(item, i) in smartIntentFallbackSearchedTemplates" 
                                                :key="i"
                                                @click="setSelectSmartIntentFallbackPromptTemplate(item)"
                                                class="template-list-item ma-1" 
                                                :ripple="true"
                                                rounded>
                                                <template v-slot:prepend>
                                                    <v-icon size="small" color="orange" 
                                                        icon="mdi-shield-alert"></v-icon>
                                                </template>
                                                <template v-slot:title>
                                                    <span class="font-weight-medium">{{ item.name }}</span>
                                                </template>
                                                <template v-slot:subtitle>
                                                    <span class="text-caption text-grey">
                                                        {{ truncatePrompt(item.prompt) }}
                                                    </span>
                                                </template>
                                                <template v-slot:append>
                                                    <v-btn icon="mdi-plus" size="small" variant="text" 
                                                        color="orange" @click.stop="setSelectSmartIntentFallbackPromptTemplate(item)">
                                                    </v-btn>
                                                </template>
                                            </v-list-item>
                                        </v-list>
                                    </div>
                                </v-card-text>
                                
                                <v-card-actions>
                                    <v-spacer></v-spacer>
                                    <v-btn color="orange" variant="text" @click="smartIntentFallbackPromptTemplateDialog = false">
                                        Close
                                    </v-btn>
                                </v-card-actions>
                            </v-card>
                        </v-dialog>
                    </div>

                    <div v-else-if="selectedNode.data.type === 'apiTrigger'" class="form-section">
                        <h6 class="section-title">API Configuration</h6>
                        <v-text-field v-model="nodeInspector.apiUrl" label="API URL" variant="outlined"
                            density="compact" hide-details="auto" :readonly="isViewMode"
                            @update:model-value="updateNodeProperty('apiUrl', $event)" class="modern-input mb-3" />
                        <v-select v-model="nodeInspector.httpMethod" :items="['GET', 'POST', 'PUT', 'DELETE']"
                            label="HTTP Method" variant="outlined" density="compact" hide-details="auto"
                            :readonly="isViewMode" @update:model-value="updateNodeProperty('httpMethod', $event)"
                            class="modern-input mb-3" />

                        <!-- JSON Validation -->
                        <h6 class="section-title mt-4">JSON Request Body</h6>
                        <v-autocomplete v-model="nodeInspector.modelJsonValidator" :items="uniqueAiModelOptions"
                            item-title="name" item-value="value" label="JSON Validator Model" variant="outlined" density="compact"
                            hide-details="auto" :readonly="isViewMode" :clearable="!isViewMode"
                            @update:model-value="updateNodeProperty('modelJsonValidator', $event)"
                            class="modern-input mb-3">
                            <template v-slot:item="{ props, item }">
                                <v-list-item v-bind="props" class="model-option"
                                    :title="item.raw.value + ': ' + item.raw.name">
                                    <template v-slot:prepend>
                                        <div class="model-icon-small mr-2" style="background-color: #3b82f6;">
                                            <v-icon size="14" color="white">{{ item.raw.icon }}</v-icon>
                                        </div>
                                    </template>
                                </v-list-item>
                            </template>
                            <template v-slot:selection="{ item }">
                                <v-chip variant="text" size="medium"
                                    :text="item.raw.value + ': ' + item.raw.name"></v-chip>
                            </template>
                        </v-autocomplete>

                        <!-- JSON Editor with Variables - Updated Version -->
                        <div class="json-editor-container mb-3">
                            <!-- Variables Panel Header -->
                            <div class="json-variables-header" v-if="!isViewMode">
                                <div class="variables-header-content">
                                    <div class="variables-title">
                                        <!-- <v-icon size="16" class="mr-2">mdi-variable</v-icon> -->
                                        <span class="text-subtitle-2">Json</span>
                                        <v-chip size="x-small" variant="tonal" color="primary" class="ml-2">
                                            {{ filteredVariables.length }} available variables
                                        </v-chip>
                                    </div>

                                    <!-- Category Filter -->
                                    <v-menu offset-y>
                                        <template v-slot:activator="{ props }">
                                            <v-btn v-bind="props" variant="text" size="small"
                                                class="category-filter-btn"
                                                :class="{ 'filter-active': selectedVariableCategory !== 'All' }">
                                                <v-icon size="14">mdi-filter-variant</v-icon>
                                                <span class="ml-1">{{ selectedVariableCategory }}</span>
                                                <v-icon size="12" class="ml-1">mdi-chevron-down</v-icon>
                                            </v-btn>
                                        </template>
                                        <v-list density="compact" class="category-menu">
                                            <v-list-item v-for="category in variableCategories" :key="category"
                                                @click="selectedVariableCategory = category"
                                                :class="{ 'v-list-item--active': selectedVariableCategory === category }">
                                                <v-list-item-title>{{ category }}</v-list-item-title>
                                            </v-list-item>
                                        </v-list>
                                    </v-menu>

                                    <!-- Expand/Collapse Button -->
                                    <v-btn icon variant="text" size="small"
                                        @click="variablesPanelExpanded = !variablesPanelExpanded" class="expand-btn">
                                        <v-icon size="16">
                                            {{ variablesPanelExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
                                        </v-icon>
                                    </v-btn>
                                </div>
                            </div>

                            <!-- Variables Grid - Collapsible -->
                            <v-expand-transition>
                                <div v-show="variablesPanelExpanded" class="json-variables-content">
                                    <!-- Quick Insert Buttons -->
                                    <div class="variables-quick-grid">
                                        <v-tooltip v-for="variable in filteredVariables" :key="variable.key"
                                            :text="variable.description" location="top">
                                            <template v-slot:activator="{ props }">
                                                <v-chip v-bind="props" class="variable-chip-compact"
                                                    @dragstart="onDragStart($event, variable.key)"
                                                    @click="insertVariable(variable.key)" draggable="true"
                                                    :color="getCategoryColor(variable.category)" variant="tonal"
                                                    size="small">
                                                    <v-icon start size="12">{{ variable.icon }}</v-icon>
                                                    <span class="variable-label">{{ variable.label }}</span>
                                                    <v-icon end size="10" class="drag-handle">mdi-drag-vertical</v-icon>
                                                </v-chip>
                                            </template>
                                        </v-tooltip>
                                    </div>
                                </div>
                            </v-expand-transition>
                            <!-- JSON Textarea -->
                            <div class="json-textarea-container">
                                <v-textarea v-model="nodeInspector.jsonValidate" label="JSON Validation Schema"
                                    variant="outlined" density="compact" rows="6" auto-grow :readonly="isViewMode"
                                    hide-details="auto" placeholder='{"Name": "Value"}'
                                    @update:model-value="updateNodeProperty('jsonValidate', $event)"
                                    class="modern-json-editor" @drop.stop.prevent="onJsonDrop($event)"
                                    @dragover.stop.prevent="onJsonDragOver($event)"
                                    @dragleave.stop="onJsonDragLeave($event)" ref="jsonTextarea" />

                                <!-- Insert Helper -->
                                <div class="insert-helper" v-if="!isViewMode">
                                    <v-icon size="12" class="mr-1">mdi-information-outline</v-icon>
                                    <span class="helper-text">
                                        Click variables to insert or drag & drop into editor
                                    </span>
                                </div>
                            </div>
                        </div>

                        <!-- Headers Configuration -->
                        <h6 class="section-title mt-4">Headers Configuration</h6>
                        <div v-if="nodeInspector.listHeadersAPITrigger" class="headers-section">
                            <div v-for="(header, headerIndex) in nodeInspector.listHeadersAPITrigger" :key="headerIndex"
                                class="header-row mb-3">
                                <div class="d-flex gap-2">
                                    <v-text-field v-model="header.key" label="Key" variant="outlined" density="compact"
                                        hide-details="auto" :readonly="isViewMode"
                                        @update:model-value="updateHeaderProperty(headerIndex, 'key', $event)"
                                        class="modern-input" />
                                    <v-text-field v-model="header.value" label="Value" variant="outlined"
                                        density="compact" hide-details="auto" :readonly="isViewMode"
                                        @update:model-value="updateHeaderProperty(headerIndex, 'value', $event)"
                                        class="modern-input" />
                                    <v-btn icon size="small" color="error" variant="text" v-if="!isViewMode"
                                        @click="deleteHeader(headerIndex)">
                                        <v-icon size="16">mdi-delete</v-icon>
                                    </v-btn>
                                </div>
                            </div>
                            <v-btn size="small" color="primary" variant="tonal" @click="addHeader" v-if="!isViewMode"
                                class="add-btn">
                                <v-icon left size="16">mdi-plus</v-icon>
                                Add Header
                            </v-btn>
                        </div>

                        <!-- Validation Configuration -->
                        <h6 class="section-title mt-4">Validation Configuration</h6>
                        <div v-if="nodeInspector.listValidation" class="validation-section">
                            <div v-for="(validation, validationIndex) in nodeInspector.listValidation"
                                :key="validationIndex" class="validation-row mb-3">
                                <div class="d-flex gap-2 mb-2">
                                    <v-text-field v-model="validation.key" label="Key" variant="outlined"
                                        density="compact" hide-details="auto" :readonly="isViewMode"
                                        @update:model-value="updateValidationProperty(validationIndex, 'key', $event)"
                                        class="modern-input" />
                                    <v-select v-model="validation.typeValidation" :items="DDTypeValidation"
                                        item-title="name" item-value="value" label="Validation Type" variant="outlined"
                                        density="compact" hide-details="auto" :readonly="isViewMode"
                                        @update:model-value="updateValidationProperty(validationIndex, 'typeValidation', $event)"
                                        class="modern-input" />
                                    <v-btn icon size="small" color="error" variant="text" v-if="!isViewMode"
                                        @click="deleteValidation(validationIndex)">
                                        <v-icon size="16">mdi-delete</v-icon>
                                    </v-btn>
                                </div>
                                <div class="d-flex gap-2 mb-2">
                                    <v-text-field v-if="validation.typeValidation === 'MCV'"
                                        v-model="validation.numberOfCharacter" label="Number of Characters"
                                        type="number" variant="outlined" density="compact" hide-details="auto"
                                        :readonly="isViewMode"
                                        @update:model-value="updateValidationProperty(validationIndex, 'numberOfCharacter', $event)"
                                        @keypress="isNumberpositive($event)" class="modern-input" />
                                    <v-text-field v-if="validation.typeValidation === 'RV'" v-model="validation.regExp"
                                        label="Regular Expression" variant="outlined" density="compact"
                                        hide-details="auto" :readonly="isViewMode"
                                        @update:model-value="updateValidationProperty(validationIndex, 'regExp', $event)"
                                        class="modern-input" />
                                </div>
                                <v-textarea v-model="validation.promptMessage" label="Error Message" variant="outlined"
                                    density="compact" rows="2" auto-grow hide-details="auto"
                                    placeholder="Error message to display when validation fails" :readonly="isViewMode"
                                    @update:model-value="updateValidationProperty(validationIndex, 'promptMessage', $event)"
                                    class="modern-input" />
                            </div>
                            <v-btn size="small" color="primary" variant="tonal" @click="addValidation"
                                v-if="!isViewMode" class="add-btn">
                                <v-icon left size="16">mdi-plus</v-icon>
                                Add Validation
                            </v-btn>
                        </div>

                        <!-- Text Response -->
                        <h6 class="section-title mt-4">Text Response</h6>
                        <v-textarea v-model="nodeInspector.textResponse" label="Response Message" variant="outlined"
                            density="compact" rows="3" auto-grow hide-details="auto"
                            placeholder="Enter the response message..." :readonly="isViewMode"
                            @update:model-value="updateNodeProperty('textResponse', $event)"
                            class="modern-input mb-3" />
                    </div>

                    <div v-else-if="selectedNode.data.type === 'contactAgent'" class="form-section">
                        <h6 class="section-title">Configuration</h6>

                        <!-- Contact Agent Message -->
                        <v-textarea v-model="nodeInspector.contactAgentMessage" label="Contact Agent Message"
                            variant="outlined" density="compact" rows="3" auto-grow hide-details="auto"
                            placeholder="Enter the message for agent handoff..."
                            @update:model-value="updateNodeProperty('contactAgentMessage', $event)"
                            :readonly="isViewMode" class="modern-input mb-3" />

                        <!-- Bot Disable Time -->
                        <div class="time-field-container mb-3">
                            <v-text-field v-model="nodeInspector.botDisableTime" label="Bot Disable Time (minutes)"
                                variant="outlined" density="compact" hide-details="auto"
                                placeholder="Enter time in minutes" type="number" min="1"
                                @update:model-value="validateAndUpdateBotDisableTime($event)"
                                @keypress="isNumberpositive($event)" :readonly="isViewMode" class="modern-input">
                                <template v-slot:append-inner>
                                    <v-tooltip location="top" max-width="300px">
                                        <template v-slot:activator="{ props }">
                                            <v-icon size="small" color="grey-darken-1" class="info-icon" v-bind="props">
                                                mdi-information-outline
                                            </v-icon>
                                        </template>
                                        <span>Specify the duration for bot deactivation (in minutes) when transferring
                                            to an agent. Minimum value is 1 minute. If
                                            left blank, the bot will remain active.</span>
                                    </v-tooltip>
                                </template>
                            </v-text-field>
                        </div>
                    </div>

                    <!-- Email Node Configuration -->
                    <div v-else-if="selectedNode.data.type === 'email'" class="form-section">
                        <h6 class="section-title">Email Configuration</h6>
                        
                        <!-- Email Provider Selection -->
                        <v-select 
                            disabled
                            v-model="nodeInspector.emailProvider" 
                            :items="[
                                { title: 'MailGun', value: 'mailgun' },
                                { title: 'SMTP', value: 'smtp' }
                            ]" 
                            item-title="title" 
                            item-value="value"
                            label="Email Provider" 
                            variant="outlined" 
                            density="compact" 
                            hide-details="auto"
                            @update:model-value="updateNodeProperty('emailProvider', $event)"
                            class="modern-input mb-3" 
                            :readonly="isViewMode" />

                        <!-- Recipient Configuration -->
                        <h6 class="section-title mt-4">Recipient Configuration</h6>
                        
                        <!-- TO Email -->
                        <v-text-field 
                            v-model="nodeInspector.toEmail" 
                            label="To Email *" 
                            variant="outlined"
                            density="compact" 
                            hide-details="auto" 
                            placeholder="recipient@example.com"
                            @update:model-value="updateNodeProperty('toEmail', $event)" 
                            class="modern-input mb-3"
                            :readonly="isViewMode"
                            required>
                            <template v-slot:prepend-inner>
                                <v-icon size="small" color="primary">mdi-email</v-icon>
                            </template>
                        </v-text-field>

                        <!-- CC Emails -->
                        <div class="email-list-section mb-3">
                            <div class="d-flex align-center mb-2">
                                <span class="text-subtitle-2 font-weight-medium">CC Emails (Optional)</span>
                                <v-btn v-if="!isViewMode" size="x-small" color="primary" variant="tonal"
                                    @click="addEmailToList('ccEmails')" class="ml-auto">
                                    <v-icon size="12">mdi-plus</v-icon>
                                </v-btn>
                            </div>
                            <div v-for="(email, emailIndex) in nodeInspector.ccEmails || []" :key="emailIndex" 
                                class="d-flex align-center mb-2">
                                <v-text-field 
                                    v-model="nodeInspector.ccEmails[emailIndex]"
                                    placeholder="cc@example.com" 
                                    variant="outlined"
                                    density="compact" 
                                    hide-details="auto"
                                    @update:model-value="updateEmailList('ccEmails')"
                                    class="modern-input flex-grow-1 mr-2"
                                    :readonly="isViewMode" />
                                <v-btn v-if="!isViewMode" icon size="x-small" color="error" variant="text"
                                    @click="removeEmailFromList('ccEmails', emailIndex)">
                                    <v-icon size="14">mdi-close</v-icon>
                                </v-btn>
                            </div>
                        </div>

                        <!-- BCC Emails hide for issues now -->
                        <!-- <div class="email-list-section mb-3">
                            <div class="d-flex align-center mb-2">
                                <span class="text-subtitle-2 font-weight-medium">BCC Emails (Optional)</span>
                                <v-btn v-if="!isViewMode" size="x-small" color="primary" variant="tonal"
                                    @click="addEmailToList('bccEmails')" class="ml-auto">
                                    <v-icon size="12">mdi-plus</v-icon>
                                </v-btn>
                            </div>
                            <div v-for="(email, emailIndex) in nodeInspector.bccEmails || []" :key="emailIndex" 
                                class="d-flex align-center mb-2">
                                <v-text-field 
                                    v-model="nodeInspector.bccEmails[emailIndex]"
                                    placeholder="bcc@example.com" 
                                    variant="outlined"
                                    density="compact" 
                                    hide-details="auto"
                                    @update:model-value="updateEmailList('bccEmails')"
                                    class="modern-input flex-grow-1 mr-2"
                                    :readonly="isViewMode" />
                                <v-btn v-if="!isViewMode" icon size="x-small" color="error" variant="text"
                                    @click="removeEmailFromList('bccEmails', emailIndex)">
                                    <v-icon size="14">mdi-close</v-icon>
                                </v-btn>
                            </div>
                        </div> -->

                        <!-- Email Content -->
                        <h6 class="section-title mt-4">Email Content</h6>
                        
                        <!-- Subject -->
                        <v-text-field 
                            v-model="nodeInspector.subject" 
                            label="Subject" 
                            variant="outlined"
                            density="compact" 
                            hide-details="auto" 
                            placeholder="Email subject"
                            @update:model-value="updateNodeProperty('subject', $event)" 
                            class="modern-input mb-3"
                            :readonly="isViewMode">
                            <template v-slot:prepend-inner>
                                <v-icon size="small" color="primary">mdi-format-title</v-icon>
                            </template>
                        </v-text-field>

                        <!-- Email Template Selection -->
                        <v-select 
                            v-model="nodeInspector.emailTemplate" 
                            :items="[
                                { title: 'Custom (Manual Content)', value: 'custom' },
                                { title: 'Registration', value: 'register' },
                                { title: 'Reset Password', value: 'resetPassword' }
                            ]" 
                            item-title="title" 
                            item-value="value"
                            label="Email Template" 
                            variant="outlined" 
                            density="compact" 
                            hide-details="auto"
                            @update:model-value="updateNodeProperty('emailTemplate', $event)"
                            class="modern-input mb-3" 
                            :readonly="isViewMode"  style="display:none"/>

                        <!-- Email Body (only show for custom template) -->
                        <v-textarea 
                            v-if="!nodeInspector.emailTemplate || nodeInspector.emailTemplate === 'custom'"
                            v-model="nodeInspector.emailBody" 
                            label="Email Body" 
                            variant="outlined"
                            density="compact" 
                            rows="6" 
                            auto-grow 
                            hide-details="auto"
                            placeholder="Enter email content here..."
                            @update:model-value="updateNodeProperty('emailBody', $event)"
                            class="modern-input mb-3" 
                            :readonly="isViewMode" />

                        <!-- Template Info (show for non-custom templates) -->
                        <v-alert 
                            v-if="nodeInspector.emailTemplate && nodeInspector.emailTemplate !== 'custom'"
                            type="info" 
                            variant="tonal" 
                            density="compact" 
                            class="mb-3">
                            <template v-slot:prepend>
                                <v-icon>mdi-information</v-icon>
                            </template>
                            <div class="text-caption">
                                <strong>Template Selected:</strong> {{ nodeInspector.emailTemplate }}<br>
                                This template will automatically generate the email content based on context variables.
                            </div>
                        </v-alert>

                        <!-- Email Options -->
                        <!-- <h6 class="section-title mt-4">Email Options</h6> -->
                        <!-- HTML Format -->
                        <!-- <v-switch 
                            v-model="nodeInspector.isHtml" 
                            label="HTML Format"
                            hide-details 
                            density="compact"
                            color="primary"
                            inset
                            @update:model-value="updateNodeProperty('isHtml', $event)"
                            class="mb-3" 
                            :readonly="isViewMode" /> -->
                    </div>

                    <div v-else-if="selectedNode.data.type === 'switch'" class="form-section">
                        <h6 class="section-title">Switch Configuration</h6>
                        <v-text-field v-model="nodeInspector.switchName" label="Switch Name" variant="outlined"
                            density="compact" hide-details="auto" placeholder="Conditional Switch"
                            @update:model-value="updateNodeProperty('switchName', $event)" class="modern-input mb-3" />

                        <!-- Switch Outputs Management -->
                        <div class="switch-outputs-section">
                            <div class="section-header">
                                <h6 class="section-title">Output Paths</h6>
                                <v-btn size="small" color="primary" variant="tonal" @click="addInspectorSwitchOutput"
                                    class="add-btn">
                                    <v-icon left size="16">mdi-plus</v-icon>
                                    Add Output
                                </v-btn>
                            </div>

                            <div v-for="(output, outputIndex) in nodeInspector.switchOutputs" :key="output.id"
                                class="switch-output-card mb-3">
                                <v-expansion-panels variant="accordion" class="switch-output-expansion">
                                    <v-expansion-panel>
                                        <v-expansion-panel-title>
                                            <div class="d-flex align-center w-100">
                                                <v-icon class="mr-2" color="primary"
                                                    size="small">mdi-arrow-decision</v-icon>
                                                <span class="font-weight-medium">
                                                    {{
                                                        output.name || `Output ${outputIndex
                                                        + 1}`
                                                    }}
                                                </span>
                                                <v-spacer></v-spacer>
                                                <v-btn icon size="x-small" color="error" variant="text"
                                                    @click.stop="removeInspectorSwitchOutput(outputIndex)"
                                                    :disabled="nodeInspector.switchOutputs.length <= 2" class="ml-2">
                                                    <v-icon size="16">mdi-delete</v-icon>
                                                </v-btn>
                                            </div>
                                        </v-expansion-panel-title>
                                        <v-expansion-panel-text>
                                            <div class="switch-output-content pa-2">
                                                <!-- Output Name -->
                                                <v-text-field v-model="nodeInspector.switchOutputs[outputIndex].name"
                                                    label="Output Name" variant="outlined" density="compact"
                                                    hide-details="auto" @update:model-value="updateSwitchData()"
                                                    class="modern-input mb-3" />

                                                <!-- Condition Builder -->
                                                <div class="condition-builder-section mb-3">
                                                    <div class="d-flex align-center mb-2">
                                                        <h6 class="condition-section-title">Condition Builder</h6>
                                                        <v-spacer></v-spacer>
                                                        <v-btn-toggle
                                                            v-model="nodeInspector.switchOutputs[outputIndex].conditionMode"
                                                            mandatory density="compact" variant="outlined"
                                                            class="condition-mode-toggle">
                                                            <v-btn value="simple" size="small">
                                                                <v-icon size="14" class="mr-1">mdi-cog</v-icon>
                                                                Simple
                                                            </v-btn>
                                                            <v-btn value="advanced" size="small">
                                                                <v-icon size="14" class="mr-1">mdi-code-braces</v-icon>
                                                                Advanced
                                                            </v-btn>
                                                        </v-btn-toggle>
                                                    </div>

                                                    <!-- Simple Condition Builder -->
                                                    <div v-if="!nodeInspector.switchOutputs[outputIndex].conditionMode || nodeInspector.switchOutputs[outputIndex].conditionMode === 'simple'"
                                                        class="simple-condition-builder">

                                                        <!-- Condition Type -->
                                                        <v-select
                                                            v-model="nodeInspector.switchOutputs[outputIndex].conditionType"
                                                            :items="[
                                                                { title: 'Text Contains', value: 'text_contains' },
                                                                { title: 'Text Equals', value: 'text_equals' },
                                                                { title: 'Number Comparison', value: 'number_comparison' },
                                                                { title: 'Boolean Check', value: 'boolean_check' },
                                                                { title: 'Regex Match', value: 'regex_match' },
                                                                { title: 'Length Check', value: 'length_check' }
                                                            ]" item-title="title" item-value="value"
                                                            label="Condition Type" variant="outlined" density="compact"
                                                            hide-details="auto" @update:model-value="updateSwitchData()"
                                                            class="modern-input mb-2" />

                                                        <!-- Dynamic Condition Fields -->
                                                        <div v-if="nodeInspector.switchOutputs[outputIndex].conditionType === 'text_contains'"
                                                            class="condition-fields">
                                                            <div class="d-flex gap-2">
                                                                <v-text-field
                                                                    v-model="nodeInspector.switchOutputs[outputIndex].sourceField"
                                                                    label="Source Field" variant="outlined"
                                                                    density="compact" hide-details="auto"
                                                                    placeholder="input.text"
                                                                    @update:model-value="updateSwitchData()"
                                                                    class="modern-input" />
                                                                <v-text-field
                                                                    v-model="nodeInspector.switchOutputs[outputIndex].searchValue"
                                                                    label="Contains Text" variant="outlined"
                                                                    density="compact" hide-details="auto"
                                                                    placeholder="keyword"
                                                                    @update:model-value="updateSwitchData()"
                                                                    class="modern-input" />
                                                            </div>
                                                            <v-checkbox
                                                                v-model="nodeInspector.switchOutputs[outputIndex].caseSensitive"
                                                                label="Case Sensitive" density="compact" hide-details
                                                                @update:model-value="updateSwitchData()" class="mt-1" />
                                                        </div>

                                                        <div v-else-if="nodeInspector.switchOutputs[outputIndex].conditionType === 'text_equals'"
                                                            class="condition-fields">
                                                            <div class="d-flex gap-2">
                                                                <v-text-field
                                                                    v-model="nodeInspector.switchOutputs[outputIndex].sourceField"
                                                                    label="Source Field" variant="outlined"
                                                                    density="compact" hide-details="auto"
                                                                    placeholder="input.text"
                                                                    @update:model-value="updateSwitchData()"
                                                                    class="modern-input" />
                                                                <v-text-field
                                                                    v-model="nodeInspector.switchOutputs[outputIndex].compareValue"
                                                                    label="Equals To" variant="outlined"
                                                                    density="compact" hide-details="auto"
                                                                    placeholder="exact text"
                                                                    @update:model-value="updateSwitchData()"
                                                                    class="modern-input" />
                                                            </div>
                                                            <v-checkbox
                                                                v-model="nodeInspector.switchOutputs[outputIndex].caseSensitive"
                                                                label="Case Sensitive" density="compact" hide-details
                                                                @update:model-value="updateSwitchData()" class="mt-1" />
                                                        </div>

                                                        <div v-else-if="nodeInspector.switchOutputs[outputIndex].conditionType === 'number_comparison'"
                                                            class="condition-fields">
                                                            <div class="d-flex gap-2 mb-2">
                                                                <v-text-field
                                                                    v-model="nodeInspector.switchOutputs[outputIndex].sourceField"
                                                                    label="Number Source" variant="outlined"
                                                                    density="compact" hide-details="auto"
                                                                    placeholder="input.value"
                                                                    @update:model-value="updateSwitchData()"
                                                                    class="modern-input" />
                                                                <v-select
                                                                    v-model="nodeInspector.switchOutputs[outputIndex].operator"
                                                                    :items="[
                                                                        { title: 'Greater than (>)', value: 'gt' },
                                                                        { title: 'Less than (<)', value: 'lt' },
                                                                        { title: 'Equal (=)', value: 'eq' },
                                                                        { title: 'Greater or equal (>=)', value: 'gte' },
                                                                        { title: 'Less or equal (<=)', value: 'lte' },
                                                                        { title: 'Not equal (!=)', value: 'ne' }
                                                                    ]" item-title="title" item-value="value"
                                                                    label="Operator" variant="outlined"
                                                                    density="compact" hide-details="auto"
                                                                    @update:model-value="updateSwitchData()"
                                                                    class="modern-input" />
                                                                <v-text-field
                                                                    v-model="nodeInspector.switchOutputs[outputIndex].compareValue"
                                                                    label="Compare Value" type="number"
                                                                    variant="outlined" density="compact"
                                                                    hide-details="auto"
                                                                    @update:model-value="updateSwitchData()"
                                                                    @keypress="isNumberpositive($event)"
                                                                    class="modern-input" />
                                                            </div>
                                                        </div>

                                                        <div v-else-if="nodeInspector.switchOutputs[outputIndex].conditionType === 'boolean_check'"
                                                            class="condition-fields">
                                                            <div class="d-flex gap-2">
                                                                <v-text-field
                                                                    v-model="nodeInspector.switchOutputs[outputIndex].sourceField"
                                                                    label="Boolean Source" variant="outlined"
                                                                    density="compact" hide-details="auto"
                                                                    placeholder="user.isLoggedIn"
                                                                    @update:model-value="updateSwitchData()"
                                                                    class="modern-input" />
                                                                <v-select
                                                                    v-model="nodeInspector.switchOutputs[outputIndex].expectedValue"
                                                                    :items="[
                                                                        { title: 'True', value: true },
                                                                        { title: 'False', value: false }
                                                                    ]" item-title="title" item-value="value"
                                                                    label="Expected Value" variant="outlined"
                                                                    density="compact" hide-details="auto"
                                                                    @update:model-value="updateSwitchData()"
                                                                    class="modern-input" />
                                                            </div>
                                                        </div>

                                                        <div v-else-if="nodeInspector.switchOutputs[outputIndex].conditionType === 'regex_match'"
                                                            class="condition-fields">
                                                            <div class="d-flex gap-2">
                                                                <v-text-field
                                                                    v-model="nodeInspector.switchOutputs[outputIndex].sourceField"
                                                                    label="Source Field" variant="outlined"
                                                                    density="compact" hide-details="auto"
                                                                    placeholder="input.text"
                                                                    @update:model-value="updateSwitchData()"
                                                                    class="modern-input" />
                                                                <v-text-field
                                                                    v-model="nodeInspector.switchOutputs[outputIndex].regexPattern"
                                                                    label="Regex Pattern" variant="outlined"
                                                                    density="compact" hide-details="auto"
                                                                    placeholder="^[0-9]+$"
                                                                    @update:model-value="updateSwitchData()"
                                                                    class="modern-input" />
                                                            </div>
                                                            <v-text-field
                                                                v-model="nodeInspector.switchOutputs[outputIndex].regexFlags"
                                                                label="Regex Flags (Optional)" variant="outlined"
                                                                density="compact" hide-details="auto" placeholder="gi"
                                                                @update:model-value="updateSwitchData()"
                                                                class="modern-input mt-2" />
                                                        </div>

                                                        <div v-else-if="nodeInspector.switchOutputs[outputIndex].conditionType === 'length_check'"
                                                            class="condition-fields">
                                                            <div class="d-flex gap-2">
                                                                <v-text-field
                                                                    v-model="nodeInspector.switchOutputs[outputIndex].sourceField"
                                                                    label="Source Field" variant="outlined"
                                                                    density="compact" hide-details="auto"
                                                                    placeholder="input.text"
                                                                    @update:model-value="updateSwitchData()"
                                                                    class="modern-input" />
                                                                <v-select
                                                                    v-model="nodeInspector.switchOutputs[outputIndex].lengthOperator"
                                                                    :items="[
                                                                        { title: 'Greater than', value: 'gt' },
                                                                        { title: 'Less than', value: 'lt' },
                                                                        { title: 'Equal to', value: 'eq' },
                                                                        { title: 'Between', value: 'between' }
                                                                    ]" item-title="title" item-value="value"
                                                                    label="Length Operator" variant="outlined"
                                                                    density="compact" hide-details="auto"
                                                                    @update:model-value="updateSwitchData()"
                                                                    class="modern-input" />
                                                                <v-text-field
                                                                    v-model="nodeInspector.switchOutputs[outputIndex].lengthValue"
                                                                    label="Length Value" type="number"
                                                                    variant="outlined" density="compact"
                                                                    hide-details="auto"
                                                                    @update:model-value="updateSwitchData()"
                                                                    @keypress="isNumberpositive($event)"
                                                                    class="modern-input" />
                                                            </div>
                                                            <v-text-field
                                                                v-if="nodeInspector.switchOutputs[outputIndex].lengthOperator === 'between'"
                                                                v-model="nodeInspector.switchOutputs[outputIndex].lengthValueMax"
                                                                label="Max Length" type="number" variant="outlined"
                                                                density="compact" hide-details="auto"
                                                                @update:model-value="updateSwitchData()"
                                                                @keypress="isNumberpositive($event)"
                                                                class="modern-input mt-2" />
                                                        </div>

                                                        <!-- Generated Condition Preview -->
                                                        <v-alert
                                                            v-if="nodeInspector.switchOutputs[outputIndex].conditionType"
                                                            type="info" variant="tonal" density="compact" class="mt-3">
                                                            <template v-slot:prepend>
                                                                <v-icon>mdi-code-braces</v-icon>
                                                            </template>
                                                            <div class="text-caption">
                                                                <strong>Generated Condition:</strong><br>
                                                                <code class="condition-preview">
                                                            {{
                                                                generateConditionPreview(nodeInspector.switchOutputs[outputIndex])
                                                            }}
                                                        </code>
                                                            </div>
                                                        </v-alert>
                                                    </div>

                                                    <!-- Advanced Condition Builder -->
                                                    <div v-else class="advanced-condition-builder">
                                                        <v-textarea
                                                            v-model="nodeInspector.switchOutputs[outputIndex].condition"
                                                            label="Custom Condition Expression" variant="outlined"
                                                            density="compact" rows="3" auto-grow hide-details="auto"
                                                            placeholder="input.text.toLowerCase().includes('hello') && input.confidence > 0.8"
                                                            @update:model-value="updateSwitchData()"
                                                            class="modern-input" />

                                                        <div class="condition-hints mt-2">
                                                            <v-chip-group column>
                                                                <v-chip size="small" variant="outlined"
                                                                    @click="insertConditionHelper(outputIndex, 'input.text')"
                                                                    class="hint-chip">input.text</v-chip>
                                                                <v-chip size="small" variant="outlined"
                                                                    @click="insertConditionHelper(outputIndex, 'input.confidence')"
                                                                    class="hint-chip">input.confidence</v-chip>
                                                                <v-chip size="small" variant="outlined"
                                                                    @click="insertConditionHelper(outputIndex, 'user.data')"
                                                                    class="hint-chip">user.data</v-chip>
                                                                <v-chip size="small" variant="outlined"
                                                                    @click="insertConditionHelper(outputIndex, 'context.variables')"
                                                                    class="hint-chip">context.variables</v-chip>
                                                            </v-chip-group>
                                                        </div>
                                                    </div>
                                                </div>

                                                <!-- Output Description -->
                                                <v-textarea
                                                    v-model="nodeInspector.switchOutputs[outputIndex].description"
                                                    label="Description" variant="outlined" density="compact" rows="1"
                                                    auto-grow hide-details="auto" placeholder="Optional description..."
                                                    @update:model-value="updateSwitchData()" class="modern-input" />
                                            </div>
                                        </v-expansion-panel-text>
                                    </v-expansion-panel>
                                </v-expansion-panels>
                            </div>
                        </div>
                    </div>

                    <div v-else-if="selectedNode.data.type === 'condition'" class="form-section">
                        <h6 class="section-title">Condition Configuration</h6>

                        <!-- Condition Type -->
                        <v-select v-model="nodeInspector.conditionType" :items="[
                            { title: 'Value Comparison', value: 'value_comparison' },
                            { title: 'Text Contains', value: 'text_contains' },
                            { title: 'Number Range', value: 'number_range' },
                            { title: 'Boolean Check', value: 'boolean_check' },
                            { title: 'Custom Expression', value: 'custom_expression' }
                        ]" item-title="title" item-value="value" label="Condition Type" variant="outlined"
                            density="compact" hide-details="auto"
                            @update:model-value="updateNodeProperty('conditionType', $event)"
                            class="modern-input mb-3" />

                        <!-- Condition Builder based on type -->
                        <div v-if="nodeInspector.conditionType === 'value_comparison'" class="condition-builder mb-3">
                            <div class="row-container">
                                <v-text-field v-model="nodeInspector.leftOperand" label="Left Value" variant="outlined"
                                    density="compact" hide-details="auto" placeholder="e.g., input.text"
                                    @update:model-value="updateNodeProperty('leftOperand', $event)"
                                    class="modern-input mb-2" />

                                <v-select v-model="nodeInspector.operator" :items="[
                                    { title: 'Equals', value: 'equals' },
                                    { title: 'Not Equals', value: 'not_equals' },
                                    { title: 'Greater Than', value: 'greater_than' },
                                    { title: 'Less Than', value: 'less_than' },
                                    { title: 'Greater or Equal', value: 'greater_equal' },
                                    { title: 'Less or Equal', value: 'less_equal' }
                                ]" item-title="title" item-value="value" label="Operator" variant="outlined"
                                    density="compact" hide-details="auto"
                                    @update:model-value="updateNodeProperty('operator', $event)"
                                    class="modern-input mb-2" />

                                <v-text-field v-model="nodeInspector.rightOperand" label="Right Value"
                                    variant="outlined" density="compact" hide-details="auto" placeholder="e.g., 'hello'"
                                    @update:model-value="updateNodeProperty('rightOperand', $event)"
                                    class="modern-input mb-2" />
                            </div>
                        </div>

                        <div v-else-if="nodeInspector.conditionType === 'text_contains'" class="condition-builder mb-3">
                            <v-text-field v-model="nodeInspector.leftOperand" label="Text Source" variant="outlined"
                                density="compact" hide-details="auto" placeholder="e.g., input.text"
                                @update:model-value="updateNodeProperty('leftOperand', $event)"
                                class="modern-input mb-2" />

                            <v-text-field v-model="nodeInspector.rightOperand" label="Contains Text" variant="outlined"
                                density="compact" hide-details="auto" placeholder="e.g., 'keyword'"
                                @update:model-value="updateNodeProperty('rightOperand', $event)"
                                class="modern-input mb-2" />
                        </div>

                        <div v-else-if="nodeInspector.conditionType === 'number_range'" class="condition-builder mb-3">
                            <v-text-field v-model="nodeInspector.leftOperand" label="Number Source" variant="outlined"
                                density="compact" hide-details="auto" placeholder="e.g., input.value"
                                @update:model-value="updateNodeProperty('leftOperand', $event)"
                                class="modern-input mb-2" />

                            <div class="d-flex gap-2">
                                <v-text-field v-model="nodeInspector.minValue" label="Min Value" type="number"
                                    variant="outlined" density="compact" hide-details="auto"
                                    @update:model-value="updateNodeProperty('minValue', $event)"
                                    @keypress="isNumberpositive($event)" class="modern-input" />

                                <v-text-field v-model="nodeInspector.maxValue" label="Max Value" type="number"
                                    variant="outlined" density="compact" hide-details="auto"
                                    @update:model-value="updateNodeProperty('maxValue', $event)"
                                    @keypress="isNumberpositive($event)" class="modern-input" />
                            </div>
                        </div>

                        <div v-else-if="nodeInspector.conditionType === 'boolean_check'" class="condition-builder mb-3">
                            <v-text-field v-model="nodeInspector.leftOperand" label="Boolean Source" variant="outlined"
                                density="compact" hide-details="auto" placeholder="e.g., user.isLoggedIn"
                                @update:model-value="updateNodeProperty('leftOperand', $event)"
                                class="modern-input mb-2" />

                            <v-switch v-model="nodeInspector.expectedValue" label="Expected Value" color="primary"
                                hide-details @update:model-value="updateNodeProperty('expectedValue', $event)"
                                class="modern-switch" />
                        </div>

                        <div v-else-if="nodeInspector.conditionType === 'custom_expression'"
                            class="condition-builder mb-3">
                            <v-textarea v-model="nodeInspector.conditionExpression" label="Custom Expression"
                                variant="outlined" density="compact" rows="2" auto-grow hide-details="auto"
                                placeholder="e.g., input.text.length > 5 && input.text.includes('hello')"
                                @update:model-value="updateNodeProperty('conditionExpression', $event)"
                                class="modern-input" />
                        </div>

                        <!-- Output Labels -->
                        <div class="output-labels-section">
                            <h6 class="section-title mb-2">Output Labels</h6>
                            <div class="d-flex gap-2">
                                <v-text-field v-model="nodeInspector.trueLabel" label="True Output Label"
                                    variant="outlined" density="compact" hide-details="auto"
                                    @update:model-value="updateNodeProperty('trueLabel', $event)"
                                    class="modern-input" />

                                <v-text-field v-model="nodeInspector.falseLabel" label="False Output Label"
                                    variant="outlined" density="compact" hide-details="auto"
                                    @update:model-value="updateNodeProperty('falseLabel', $event)"
                                    class="modern-input" />
                            </div>
                        </div>

                        <!-- Description -->
                        <v-textarea v-model="nodeInspector.description" label="Description" variant="outlined"
                            density="compact" rows="2" auto-grow hide-details="auto"
                            placeholder="Optional description of this condition..."
                            @update:model-value="updateNodeProperty('description', $event)" class="modern-input mt-3" />
                    </div>

                    <div v-else-if="selectedNode.data.type === 'delay'" class="form-section">
                        <h6 class="section-title">Delay Configuration</h6>

                        <!-- Delay Type -->
                        <v-select v-model="nodeInspector.delayType" :items="[
                            { title: 'Fixed Duration', value: 'fixed' },
                            { title: 'Random Range', value: 'random' },
                            { title: 'Variable Delay', value: 'variable' }
                        ]" item-title="title" item-value="value" label="Delay Type" variant="outlined"
                            density="compact" hide-details="auto"
                            @update:model-value="updateNodeProperty('delayType', $event)" class="modern-input mb-3" />

                        <!-- Fixed Duration Configuration -->
                        <div v-if="nodeInspector.delayType === 'fixed'" class="delay-config-section mb-3">
                            <div class="d-flex gap-2">
                                <v-text-field v-model="nodeInspector.delayValue" label="Duration" type="number" min="0"
                                    step="0.1" variant="outlined" density="compact" hide-details="auto"
                                    @update:model-value="updateNodeProperty('delayValue', $event)"
                                    @keypress="isNumberpositive($event)" class="modern-input flex-grow-1" />

                                <v-select v-model="nodeInspector.delayUnit" :items="[
                                    { title: 'Seconds', value: 'seconds' },
                                    { title: 'Minutes', value: 'minutes' },
                                    { title: 'Hours', value: 'hours' }
                                ]" item-title="title" item-value="value" label="Unit" variant="outlined"
                                    density="compact" hide-details="auto"
                                    @update:model-value="updateNodeProperty('delayUnit', $event)" class="modern-input"
                                    style="min-width: 100px;" />
                            </div>
                        </div>

                        <!-- Random Range Configuration -->
                        <div v-else-if="nodeInspector.delayType === 'random'" class="delay-config-section mb-3">
                            <div class="d-flex gap-2 mb-2">
                                <v-text-field v-model="nodeInspector.randomMin" label="Min Duration" type="number"
                                    min="0" step="0.1" variant="outlined" density="compact" hide-details="auto"
                                    @update:model-value="updateNodeProperty('randomMin', $event)"
                                    class="modern-input" />

                                <v-text-field v-model="nodeInspector.randomMax" label="Max Duration" type="number"
                                    min="0" step="0.1" variant="outlined" density="compact" hide-details="auto"
                                    @update:model-value="updateNodeProperty('randomMax', $event)"
                                    class="modern-input" />
                            </div>

                            <v-select v-model="nodeInspector.delayUnit" :items="[
                                { title: 'Seconds', value: 'seconds' },
                                { title: 'Minutes', value: 'minutes' },
                                { title: 'Hours', value: 'hours' }
                            ]" item-title="title" item-value="value" label="Unit" variant="outlined" density="compact"
                                hide-details="auto" @update:model-value="updateNodeProperty('delayUnit', $event)"
                                class="modern-input" />
                        </div>

                        <!-- Variable Delay Configuration -->
                        <div v-else-if="nodeInspector.delayType === 'variable'" class="delay-config-section mb-3">
                            <v-text-field v-model="nodeInspector.delayExpression" label="Delay Expression"
                                variant="outlined" density="compact" hide-details="auto"
                                placeholder="e.g., input.length * 0.5"
                                @update:model-value="updateNodeProperty('delayExpression', $event)"
                                class="modern-input mb-2" />

                            <v-select v-model="nodeInspector.delayUnit" :items="[
                                { title: 'Seconds', value: 'seconds' },
                                { title: 'Minutes', value: 'minutes' },
                                { title: 'Hours', value: 'hours' }
                            ]" item-title="title" item-value="value" label="Unit" variant="outlined" density="compact"
                                hide-details="auto" @update:model-value="updateNodeProperty('delayUnit', $event)"
                                class="modern-input" />
                        </div>

                        <!-- Delay Message -->
                        <v-text-field v-model="nodeInspector.delayMessage" label="Delay Message (Optional)"
                            variant="outlined" density="compact" hide-details="auto"
                            placeholder="Please wait while processing..."
                            @update:model-value="updateNodeProperty('delayMessage', $event)"
                            class="modern-input mb-3" />

                        <!-- Progress Indicator -->
                        <v-switch v-model="nodeInspector.showProgress" label="Show Progress Indicator" color="primary"
                            hide-details @update:model-value="updateNodeProperty('showProgress', $event)"
                            class="modern-switch mb-3" />

                        <!-- Duration Preview -->
                        <div class="duration-preview">
                            <v-alert type="info" variant="tonal" density="compact" class="mb-2">
                                <template v-slot:prepend>
                                    <v-icon>mdi-clock-outline</v-icon>
                                </template>
                                <div class="text-caption">
                                    <strong>Duration Preview:</strong><br>
                                    <span v-if="nodeInspector.delayType === 'fixed'">
                                        {{ nodeInspector.delayValue }} {{ nodeInspector.delayUnit }}
                                    </span>
                                    <span v-else-if="nodeInspector.delayType === 'random'">
                                        {{ nodeInspector.randomMin }} - {{ nodeInspector.randomMax }} {{
                                            nodeInspector.delayUnit
                                        }}
                                    </span>
                                    <span v-else-if="nodeInspector.delayType === 'variable'">
                                        Variable based on expression
                                    </span>
                                </div>
                            </v-alert>
                        </div>

                        <!-- Description -->
                        <v-textarea v-model="nodeInspector.description" label="Description" variant="outlined"
                            density="compact" rows="2" auto-grow hide-details="auto"
                            placeholder="Optional description of this delay..."
                            @update:model-value="updateNodeProperty('description', $event)" class="modern-input" />
                    </div>

                    <!-- Documents Node Properties -->
                    <div v-else-if="selectedNode.data.type === 'documents'" class="form-section">
                        <h6 class="section-title">Documents Configuration</h6>
                        <!-- Document Selection -->
                        <v-autocomplete v-model="nodeInspector.document" :items="DDVector" item-title="name"
                            item-value="name" label="Document" variant="outlined" density="compact" hide-details="auto"
                            chips multiple @update:model-value="updateNodeProperty('document', $event);"
                            @click:clear="resetDocumentDataAndSelection" class="modern-input mb-3"
                            placeholder="Select documents" :clearable="!isViewMode" :readonly="isViewMode">
                            <template v-slot:item="{ props, item }">
                                <v-list-item v-bind="props" class="document-option">
                                    <template v-slot:prepend>
                                        <v-icon size="20" color="primary">mdi-file-document</v-icon>
                                    </template>
                                    <!-- <v-list-item-title>{{ item.raw.name }}</v-list-item-title> -->
                                    <v-list-item-subtitle>{{ item.raw.description }}</v-list-item-subtitle>
                                </v-list-item>
                            </template>
                        </v-autocomplete>

                        <!-- Document Data Selection Section -->
                        <div v-if="nodeInspector.document && nodeInspector.document.length > 0"
                            class="document-data-section mb-3">
                            <!-- Document Data Selection Header with Toggle -->
                            <div style="display: flex;align-items: center;justify-content: space-between;">
                                <!-- Enable Specific Data Selection Switch -->
                                <div style="display: flex; align-items: center;">
                                    <v-switch v-model="nodeInspector.enableSpecificDataSelection" color="primary"
                                        hide-details density="compact"
                                        @update:model-value="onSpecificDataSelectionChange($event)"
                                        :readonly="isViewMode" class="mr-2" inset style="transform: scale(0.8);">
                                    </v-switch>
                                    <span class="text-subtitle-2">
                                        Use Specific Data Selection
                                    </span>
                                </div>
                                <!-- Expand/Collapse Button -->
                                <v-btn v-if="nodeInspector.enableSpecificDataSelection"
                                    @click="documentDataExpanded = !documentDataExpanded" icon variant="text"
                                    size="small" color="primary">
                                    <v-icon>{{ documentDataExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
                                </v-btn>
                            </div>

                            <!-- Expandable Content -->
                            <v-expand-transition>
                                <div v-show="documentDataExpanded && nodeInspector.enableSpecificDataSelection"
                                    class="document-data-content pa-3 bg-grey-lighten-5 rounded-lg">

                                    <h6 class="section-title mb-0">
                                        <v-icon size="20" class="mr-2 mb-4" color="primary">mdi-file-search</v-icon>
                                        Select Document Data
                                    </h6>
                                    <!-- Vector Name Selection for Document Data -->
                                    <v-autocomplete v-if="!isViewMode" v-model="nodeInspector.documentVectorName"
                                        :items="nodeInspector.document" label="Select Document for Data"
                                        variant="outlined" density="compact" hide-details="auto"
                                        @update:model-value="updateNodeProperty('documentVectorName', $event); getDocumentData();"
                                        class="modern-input mb-3" clearable :readonly="isViewMode">
                                        <template v-slot:prepend-inner>
                                            <v-icon size="20" color="primary">mdi-file-document-outline</v-icon>
                                        </template>
                                        <template v-slot:append-inner>
                                            <v-tooltip location="top">
                                                <template v-slot:activator="{ props }">
                                                    <v-icon v-bind="props" size="small" color="grey-darken-1">
                                                        mdi-information-outline
                                                    </v-icon>
                                                </template>
                                                <span>Select a document to view its data</span>
                                            </v-tooltip>
                                        </template>
                                    </v-autocomplete>

                                    <!-- Selected Document Data Display -->
                                    <div class="selected-document-data mb-4"
                                        v-if="nodeInspector.selectedDocumentData && nodeInspector.selectedDocumentData.length > 0">
                                        <div class="mb-2">
                                            <span class="text-subtitle-2 font-weight-medium text-primary">
                                                <v-icon size="16" class="mr-1">mdi-check-circle</v-icon>
                                                Selected Document Data:
                                            </span>
                                        </div>
                                        <div class="d-flex flex-wrap ga-2">
                                            <v-chip v-for="(item, dataIndex) in nodeInspector.selectedDocumentData"
                                                :key="dataIndex + '_DocumentData'" :closable="!isViewMode"
                                                @click:close="deleteSelectedDocumentData(item.id)" color="primary"
                                                variant="elevated" size="small" class="selected-document-data-chip">
                                                <v-tooltip location="top" open-delay="500">
                                                    <template v-slot:activator="{ props }">
                                                        <span v-bind="props" class="chip-text">{{
                                                            item.textORG.length > 50 ? item.textORG.substring(0, 50) +
                                                                '...' : item.textORG
                                                        }}</span>
                                                    </template>
                                                    <span>{{ item.textORG }}</span>
                                                </v-tooltip>
                                            </v-chip>
                                        </div>
                                    </div>

                                    <div v-if="loadingDocumentDataTable && nodeInspector.documentVectorName"
                                        class="text-center mb-4">
                                        <v-progress-circular indeterminate color="primary"
                                            size="24"></v-progress-circular>

                                    </div>

                                    <!-- Document Data Table -->
                                    <v-data-table
                                        v-show="showDocumentDataTable && nodeInspector.enableSpecificDataSelection"
                                        :items-per-page="documentDataItemsPerPage"
                                        :server-items-length="documentDataTotalTable"
                                        v-model:options="documentDataOptions" :items="documentDataTable"
                                        :headers="documentDataHeaders" loading-text="Loading document data..."
                                        fixed-header class="elevation-2 document-data-table mb-4 rounded-lg"
                                        density="comfortable">
                                        <template v-slot:item="{ item }">
                                            <tr>
                                                <td class="text-center pa-2">
                                                    <v-checkbox v-model="item.selected"
                                                        @change="selectDocumentData(item.selected, item.id, item.textORG)"
                                                        hide-details density="compact" color="primary">
                                                    </v-checkbox>
                                                </td>
                                                <td class="pa-2">
                                                    <div class="text-container">
                                                        <span
                                                            :class="[item.show ? 'text-content-show' : 'text-content']"
                                                            v-html="formatText(item.textORG)">
                                                        </span>
                                                    </div>
                                                </td>
                                                <td class="pa-2">
                                                    <div class="text-container">
                                                        <span
                                                            :class="[item.show ? 'text-content-show' : 'text-content']"
                                                            v-html="formatText(item.textEN)">
                                                        </span>
                                                        <v-btn @click="item.show = !item.show" icon size="small"
                                                            variant="text" class="ml-2">
                                                            <v-icon>{{
                                                                item.show ? "mdi-chevron-up" : "mdi-chevron-down"
                                                            }}</v-icon>
                                                        </v-btn>
                                                    </div>
                                                </td>
                                            </tr>
                                        </template>
                                    </v-data-table>

                                </div>
                            </v-expand-transition>
                        </div>
                        <v-divider class="my-4"></v-divider>

                        <!-- Text Replacement -->
                        <v-autocomplete v-model="nodeInspector.textReplacement" :items="DDGroupReplace"
                            item-title="name" item-value="id" label="Text Replacement" variant="outlined"
                            density="compact" hide-details="auto" chips multiple
                            @update:model-value="updateNodeProperty('textReplacement', $event)"
                            class="modern-input mb-3" placeholder="Select text replacement groups"
                            :clearable="!isViewMode" :readonly="isViewMode">
                            <template v-slot:item="{ props, item }">
                                <v-list-item v-bind="props" class="text-replacement-option">
                                    <template v-slot:prepend>
                                        <v-icon size="20" color="primary">mdi-find-replace</v-icon>
                                    </template>
                                    <!-- <v-list-item-title>{{ item.raw.name }}</v-list-item-title> -->
                                    <v-list-item-subtitle>{{ item.raw.description }}</v-list-item-subtitle>
                                </v-list-item>
                            </template>
                        </v-autocomplete>
                        <!-- Top-N Chunks -->
                        <v-text-field v-model="nodeInspector.topNChunks" type="number" label="Top-N Chunks"
                            variant="outlined" density="compact" hide-details="auto" :readonly="isViewMode" min="1"
                            step="1" @focus="$event.target.select()" 
                            @blur="validateAndFixTopNChunks($event)"
                            @keydown.enter="validateAndFixTopNChunks($event)"
                            class="modern-input mb-3" />
                        <!-- Cosine Similarity -->
                        <v-text-field v-model="nodeInspector.cosineSimilarity" type="number" label="Cosine Similarity"
                            variant="outlined" density="compact" hide-details="auto" :readonly="isViewMode" min="0.0001"
                            max="1" step="0.0001"
                            @focus="$event.target.select()"
                            @blur="validateAndFixCosineSimilarity($event)"
                            @keydown.enter="validateAndFixCosineSimilarity($event)"
                            class="modern-input" />
                    </div>

                    <!-- DB Integration Node Properties -->
                    <div v-else-if="selectedNode.data.type === 'databaseIntegration'" class="form-section">
                        <h6 class="section-title">Database Integration Configuration</h6>

                        <!-- Table Name Selection -->
                        <v-autocomplete v-model="nodeInspector.tableName" :items="listDataIntegration"
                            item-title="dataName" item-value="id" label="Table Name" variant="outlined"
                            density="compact" hide-details="auto"
                            @update:model-value="updateNodeProperty('tableName', $event)" class="modern-input mb-3"
                            placeholder="Select database table" :clearable="!isViewMode" :readonly="isViewMode">
                            <template v-slot:item="{ props, item }">
                                <v-list-item v-bind="props" class="table-option">
                                    <template v-slot:prepend>
                                        <v-icon size="20" color="primary">mdi-table</v-icon>
                                    </template>
                                    <v-list-item-title>{{ item.raw.name }}</v-list-item-title>
                                    <v-list-item-subtitle>{{ item.raw.description }}</v-list-item-subtitle>
                                </v-list-item>
                            </template>
                        </v-autocomplete>

                        <!-- SQL Extraction Model Selection -->
                        <v-autocomplete v-model="nodeInspector.sqlExtractionModel" :items="listSqlExtract"
                            item-title="name" item-value="modelDisplayID" label="SQL Extraction Model"
                            variant="outlined" density="compact" hide-details="auto" :clearable="!isViewMode"
                            :readonly="isViewMode"
                            @update:model-value="updateNodeProperty('sqlExtractionModel', $event)"
                            class="modern-input mb-3" placeholder="Select AI model for SQL generation">
                            <template v-slot:item="{ props, item }">
                                <v-list-item v-bind="props" class="model-option">
                                    <template v-slot:prepend>
                                        <div class="model-icon-small mr-2" :style="{ backgroundColor: item.raw.color }">
                                            <v-icon size="14" color="green">mdi-robot</v-icon>
                                        </div>
                                    </template>
                                    <!-- <v-list-item-title>{{ item.raw.name }}</v-list-item-title> -->
                                    <v-list-item-subtitle>{{ item.raw.description }}</v-list-item-subtitle>
                                </v-list-item>
                            </template>
                        </v-autocomplete>

                        <!-- Prompt for No Data -->
                        <v-textarea v-model="nodeInspector.promptSqlNoData" label="Prompt for No Data"
                            variant="outlined" density="compact" rows="3" auto-grow hide-details="auto"
                            placeholder="Enter the message to display when no data is found in the database..."
                            @update:model-value="updateNodeProperty('promptSqlNoData', $event)"
                            class="modern-input mb-3" :readonly="isViewMode" />
                    </div>

                    <!-- Knowledge API Node Properties -->
                    <div v-else-if="selectedNode.data.type === 'apiknowledge'" class="form-section">
                        <h6 class="section-title">Knowledge API Configuration</h6>

                        <!-- API URL -->
                        <v-text-field v-model="nodeInspector.knowledgeApiUrl" label="API URL" variant="outlined"
                            density="compact" hide-details="auto" placeholder="https://api.example.com/knowledge"
                            @update:model-value="updateNodeProperty('knowledgeApiUrl', $event)"
                            class="modern-input mb-3" :readonly="isViewMode">
                            <template v-slot:prepend-inner>
                                <v-icon size="20" color="primary">mdi-link</v-icon>
                            </template>
                        </v-text-field>

                        <!-- HTTP Method -->
                        <v-select v-model="nodeInspector.knowledgeHttpMethod" :items="['GET', 'POST']"
                            label="HTTP Method" variant="outlined" density="compact" hide-details="auto"
                            @update:model-value="updateNodeProperty('knowledgeHttpMethod', $event)"
                            class="modern-input mb-3" :readonly="isViewMode">
                            <template v-slot:prepend-inner>
                                <v-icon size="20" color="primary">mdi-code-brackets</v-icon>
                            </template>
                        </v-select>

                        <!-- Request Headers -->
                        <div class="headers-section mb-3">
                            <div class="section-header mb-2">
                                <span class="section-title">Request Headers</span>
                                <v-btn v-if="!isViewMode" @click="addKnowledgeAPIHeader" size="small" color="primary"
                                    variant="tonal" class="add-btn">
                                    <v-icon size="16">mdi-plus</v-icon>
                                    Add Header
                                </v-btn>
                            </div>
                            <div v-if="nodeInspector.listHeaders" class="headers-list">
                                <div v-for="(header, headerIndex) in nodeInspector.listHeaders" :key="headerIndex"
                                    class="header-row mb-2">
                                    <v-text-field v-model="header.key" label="Header Name" variant="outlined"
                                        density="compact" hide-details="auto" class="modern-input mr-2 mb-2"
                                        @update:model-value="updateKnowledgeAPIHeaderProperty(headerIndex, 'key', $event)"
                                        :readonly="isViewMode" />

                                    <v-text-field v-model="header.value" label="Header Value" variant="outlined"
                                        density="compact" hide-details="auto" class="modern-input mr-2"
                                        @update:model-value="updateKnowledgeAPIHeaderProperty(headerIndex, 'value', $event)"
                                        :readonly="isViewMode" />
                                    <div style="display:flex; justify-content:flex-end; width:100%;">
                                        <v-btn v-if="!isViewMode" @click="deleteKnowledgeAPIHeader(headerIndex)"
                                            size="small" color="error" variant="text" icon>
                                            <v-icon size="16">mdi-delete</v-icon>
                                        </v-btn>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- Request Body with Variables -->
                        <h6 class="section-title mt-4">Request Body (JSON)</h6>
                        <div class="json-editor-container mb-3">
                            <!-- Variables Panel Header -->
                            <div class="json-variables-header" v-if="!isViewMode">
                                <div class="variables-header-content">
                                    <div class="variables-title">
                                        <span class="text-subtitle-2">Json</span>
                                        <v-chip size="x-small" variant="tonal" color="primary" class="ml-2">
                                            {{ filteredVariables.length }} available variables
                                        </v-chip>
                                    </div>

                                    <!-- Category Filter -->
                                    <v-menu offset-y>
                                        <template v-slot:activator="{ props }">
                                            <v-btn v-bind="props" variant="text" size="small"
                                                class="category-filter-btn"
                                                :class="{ 'filter-active': selectedVariableCategory !== 'All' }">
                                                <v-icon size="14">mdi-filter-variant</v-icon>
                                                <span class="ml-1">{{ selectedVariableCategory }}</span>
                                                <v-icon size="12" class="ml-1">mdi-chevron-down</v-icon>
                                            </v-btn>
                                        </template>
                                        <v-list density="compact" class="category-menu">
                                            <v-list-item v-for="category in variableCategories" :key="category"
                                                @click="selectedVariableCategory = category"
                                                :class="{ 'v-list-item--active': selectedVariableCategory === category }">
                                                <v-list-item-title>{{ category }}</v-list-item-title>
                                            </v-list-item>
                                        </v-list>
                                    </v-menu>

                                    <!-- Expand/Collapse Button -->
                                    <v-btn icon variant="text" size="small"
                                        @click="variablesPanelExpanded = !variablesPanelExpanded" class="expand-btn">
                                        <v-icon size="16">
                                            {{ variablesPanelExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
                                        </v-icon>
                                    </v-btn>
                                </div>
                            </div>

                            <!-- Variables Grid - Collapsible -->
                            <v-expand-transition>
                                <div v-show="variablesPanelExpanded" class="json-variables-content">
                                    <!-- Quick Insert Buttons -->
                                    <div class="variables-quick-grid">
                                        <v-tooltip v-for="variable in filteredVariables" :key="variable.key"
                                            :text="variable.description" location="top">
                                            <template v-slot:activator="{ props }">
                                                <v-chip v-bind="props" class="variable-chip-compact"
                                                    @dragstart="onDragStart($event, variable.key)"
                                                    @click="insertKnowledgeVariable(variable.key)" draggable="true"
                                                    :color="getCategoryColor(variable.category)" variant="tonal"
                                                    size="small">
                                                    <v-icon start size="12">{{ variable.icon }}</v-icon>
                                                    <span class="variable-label">{{ variable.label }}</span>
                                                    <v-icon end size="10" class="drag-handle">mdi-drag-vertical</v-icon>
                                                </v-chip>
                                            </template>
                                        </v-tooltip>
                                    </div>
                                </div>
                            </v-expand-transition>

                            <!-- JSON Textarea -->
                            <div class="json-textarea-container">
                                <v-textarea v-model="nodeInspector.knowledgeRequestBody" label="Request Body (JSON)"
                                    variant="outlined" density="compact" rows="6" auto-grow hide-details="auto"
                                    placeholder='{"query": "{{userMessage}}", "limit": 10}'
                                    @update:model-value="updateNodeProperty('knowledgeRequestBody', $event)"
                                    class="modern-json-editor" :readonly="isViewMode"
                                    @drop.stop.prevent="onKnowledgeJsonDrop($event)"
                                    @dragover.stop.prevent="onJsonDragOver($event)"
                                    @dragleave.stop="onJsonDragLeave($event)" 
                                    ref="knowledgeJsonTextarea">
                                    <template v-slot:prepend-inner>
                                        <v-icon size="20" color="primary">mdi-code-json</v-icon>
                                    </template>
                                </v-textarea>

                                <!-- Insert Helper -->
                                <div class="insert-helper" v-if="!isViewMode">
                                    <v-icon size="12" class="mr-1">mdi-information-outline</v-icon>
                                    <span class="helper-text">
                                        Click variables to insert or drag & drop into editor
                                    </span>
                                </div>
                            </div>
                        </div>

                        <!-- Response Processing -->
                        <v-text-field v-model="nodeInspector.responseDataPath" label="Response Data Path"
                            variant="outlined" density="compact" hide-details="auto"
                            placeholder="data.results (JSON path to extract knowledge)"
                            @update:model-value="updateNodeProperty('responseDataPath', $event)"
                            class="modern-input mb-3" :readonly="isViewMode">
                            <template v-slot:prepend-inner>
                                <v-icon size="20" color="primary">mdi-format-list-bulleted</v-icon>
                            </template>
                            <template v-slot:append-inner>
                                <v-tooltip location="top">
                                    <template v-slot:activator="{ props }">
                                        <v-icon v-bind="props" size="small" color="grey-darken-1">
                                            mdi-information-outline
                                        </v-icon>
                                    </template>
                                    <span>JSON path to extract knowledge data from API response</span>
                                </v-tooltip>
                            </template>
                        </v-text-field>
                    </div>

                    <!-- Products Node Properties -->
                    <div v-else-if="selectedNode.data.type === 'products'" class="form-section">
                        <h6 class="section-title">Products Configuration</h6>

                        <!-- Product Group List Selection -->
                        <v-autocomplete v-model="nodeInspector.productGroupList" :items="DDProductGroup"
                            item-title="name" item-value="id" label="Product Group List" variant="outlined"
                            density="compact" hide-details="auto" chips multiple return-object
                            @update:model-value="updateNodeProperty('productGroupList', $event)"
                            class="modern-input mb-3" placeholder="Select product group" :clearable="!isViewMode"
                            :readonly="isViewMode">
                            <template v-slot:item="{ props, item }">
                                <v-list-item v-bind="props" class="product-group-option">
                                    <template v-slot:prepend>
                                        <v-icon size="20" color="primary">mdi-package-variant</v-icon>
                                    </template>
                                    <!-- <v-list-item-title>{{ item.raw.name }}</v-list-item-title> -->
                                    <v-list-item-subtitle>{{ item.raw.description }}</v-list-item-subtitle>
                                </v-list-item>
                            </template>
                        </v-autocomplete>

                        <!-- Top-N Chunks -->
                        <v-text-field v-model="nodeInspector.productTopNChunks" type="number" label="Top-N Chunks"
                            variant="outlined" density="compact" hide-details="auto" min="1" step="1"
                            @focus="$event.target.select()"
                            @blur="validateAndFixProductTopNChunks($event)"
                            @keydown.enter="validateAndFixProductTopNChunks($event)"
                            class="modern-input mb-3" placeholder="Enter number of chunks" :readonly="isViewMode" />

                        <!-- Cosine Similarity -->
                        <v-text-field v-model="nodeInspector.productCosineSimilarity" type="number"
                            label="Cosine Similarity" variant="outlined" density="compact" hide-details="auto"
                            min="0.0001" max="1" step="0.0001"
                            @focus="$event.target.select()"
                            @blur="validateAndFixProductCosineSimilarity($event)"
                            @keydown.enter="validateAndFixProductCosineSimilarity($event)"
                            class="modern-input" placeholder="Enter similarity threshold (0.0001 - 1.0)"
                            :readonly="isViewMode" />
                    </div>

                    <!-- SharePoint Node Properties -->
                    <div v-else-if="selectedNode.data.type === 'sharepoint'" class="form-section">
                        <h6 class="section-title">SharePoint Configuration</h6>
                        
                        <!-- File Location Section -->
                        <div class="subsection">
                            <div class="subsection-header mb-3">
                                <v-icon size="20" color="info" class="mr-2">mdi-file-search</v-icon>
                                <span class="subsection-title">File Location</span>
                            </div>
                            
                            <!-- Filename -->
                            <v-text-field 
                                v-model="nodeInspector.sharepoint.filename" 
                                label="Filename" 
                                variant="outlined" 
                                density="compact" 
                                hide-details="auto"
                                @update:model-value="updateNodeProperty('sharepoint.filename', $event)"
                                class="modern-input mb-3" 
                                placeholder="example.docx"
                                :readonly="isViewMode">
                                <template v-slot:prepend-inner>
                                    <v-icon size="18" color="info">mdi-file-document</v-icon>
                                </template>
                            </v-text-field>

                            <!-- Folder Path -->
                            <v-text-field 
                                v-model="nodeInspector.sharepoint.folderPath" 
                                label="Subfolder Path"
                                variant="outlined" 
                                density="compact" 
                                hide-details="auto"
                                @update:model-value="updateNodeProperty('sharepoint.folderPath', $event)"
                                class="modern-input" 
                                placeholder="/SubfolderName (optional)"
                                :readonly="isViewMode">
                                <template v-slot:prepend-inner>
                                    <v-icon size="18" color="info">mdi-folder</v-icon>
                                </template>
                                <template v-slot:append-inner>
                                    <v-tooltip location="top">
                                        <template v-slot:activator="{ props }">
                                            <v-icon v-bind="props" size="16" color="grey-darken-1">mdi-help-circle-outline</v-icon>
                                        </template>
                                        <span>Subfolder path within the SharePoint site<br><strong>Root: /Documents</strong><br>Example: /Data will become /Documents/Data</span>
                                    </v-tooltip>
                                </template>
                            </v-text-field>
                        </div>
                        
                        <!-- Connection Settings Section -->
                        <div class="subsection mb-4">
                            <div class="subsection-header mb-3">
                                <v-icon size="20" color="primary" class="mr-2">mdi-connection</v-icon>
                                <span class="subsection-title">Connection Settings</span>
                            </div>
                            
                            <!-- Site URL -->
                            <v-text-field 
                                v-model="nodeInspector.sharepoint.siteUrl" 
                                label="Site URL" 
                                variant="outlined" 
                                density="compact" 
                                hide-details="auto"
                                @update:model-value="updateNodeProperty('sharepoint.siteUrl', $event)"
                                class="modern-input mb-3" 
                                placeholder="https://yourtenant.sharepoint.com/sites/yoursite"
                                :readonly="isViewMode">
                                <template v-slot:prepend-inner>
                                    <v-icon size="18" color="primary">mdi-web</v-icon>
                                </template>
                            </v-text-field>

                            <!-- Tenant ID -->
                            <v-text-field 
                                v-model="nodeInspector.sharepoint.tenantId" 
                                label="Tenant ID" 
                                variant="outlined" 
                                density="compact" 
                                hide-details="auto"
                                @update:model-value="updateNodeProperty('sharepoint.tenantId', $event)"
                                class="modern-input mb-3" 
                                placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                                autocomplete="off"
                                :readonly="isViewMode">
                                <template v-slot:prepend-inner>
                                    <v-icon size="18" color="primary">mdi-domain</v-icon>
                                </template>
                            </v-text-field>
                        </div>

                        <!-- Authentication Section -->
                        <div class="subsection mb-4">
                            <div class="subsection-header mb-3">
                                <v-icon size="20" color="success" class="mr-2">mdi-shield-lock</v-icon>
                                <span class="subsection-title">Authentication</span>
                            </div>
                            
                            <!-- Client ID -->
                            <v-text-field 
                                v-model="nodeInspector.sharepoint.clientId" 
                                label="Client ID" 
                                variant="outlined" 
                                density="compact" 
                                hide-details="auto"
                                @update:model-value="updateNodeProperty('sharepoint.clientId', $event)"
                                class="modern-input mb-3" 
                                placeholder="Application (client) ID"
                                :readonly="isViewMode">
                                <template v-slot:prepend-inner>
                                    <v-icon size="18" color="success">mdi-key-variant</v-icon>
                                </template>
                            </v-text-field>

                            <!-- Client Secret -->
                            <v-text-field 
                                v-model="nodeInspector.sharepoint.clientSecret" 
                                label="Client Secret" 
                                variant="outlined" 
                                density="compact" 
                                hide-details="auto"
                                type="password"
                                @update:model-value="updateNodeProperty('sharepoint.clientSecret', $event)"
                                class="modern-input" 
                                autocomplete="off"
                                placeholder="Enter client secret"
                                :readonly="isViewMode">
                                <template v-slot:prepend-inner>
                                    <v-icon size="18" color="success">mdi-shield-key</v-icon>
                                </template>
                            </v-text-field>
                        </div>

                    </div>

                </v-form>
            </div>
        </div>
        <!-- Loading Dialog -->
        <v-dialog v-model="dialog" persistent width="300">
            <v-card color="#1976D2" dark>
                <v-card-text class="white--text">
                    Loading Please Wait...
                    <v-progress-linear indeterminate color="#FAA91B" class="mb-0"></v-progress-linear>
                </v-card-text>
            </v-card>
        </v-dialog>

        <!-- Save Confirmation Dialog -->
        <v-dialog v-model="saveDialog" max-width="500" persistent>
            <v-card class="save-dialog">
                <v-card-title class="dialog-header">
                    <div class="header-content">
                        <v-icon class="mr-3" color="white" size="28">mdi-content-save</v-icon>
                        <span class="text-h5 font-weight-bold">{{ isEditMode ? 'Update Flow' : 'Save Flow' }}</span>
                    </div>
                </v-card-title>

                <v-card-text class="dialog-content">
                    <div class="confirmation-content">
                        <!-- Flow Name Display -->
                        <div class="flow-name-display">
                            <v-icon class="mr-3" color="primary" size="32">mdi-chart-timeline-variant-shimmer</v-icon>
                            <div>
                                <div class="flow-name">{{ flowName || 'Untitled Flow' }}</div>
                                <div class="flow-subtitle">{{ isEditMode ? 'Update this flow?' : 'Save this flow?' }}
                                </div>
                            </div>
                        </div>

                        <!-- Warning for validation errors -->
                        <v-alert v-if="getValidationErrors().length > 0" type="warning" variant="tonal"
                            density="compact" class="mt-4">
                            <template v-slot:prepend>
                                <v-icon>mdi-alert</v-icon>
                            </template>
                            <div class="text-caption">
                                <strong>Warning:</strong> Flow has {{ getValidationErrors().length }} validation
                                issue(s).
                                <br>You can still save, but the flow may not work correctly.
                            </div>
                        </v-alert>

                        <!-- Success message for no issues -->
                        <v-alert v-else type="success" variant="tonal" density="compact" class="mt-4">
                            <template v-slot:prepend>
                                <v-icon>mdi-check-circle</v-icon>
                            </template>
                            <div class="text-caption">
                                <strong>Ready to save:</strong> Flow validation passed successfully.
                            </div>
                        </v-alert>
                    </div>
                </v-card-text>

                <v-card-actions class="dialog-actions">
                    <v-spacer></v-spacer>
                    <v-btn @click="closeSaveDialog" variant="outlined" class="mr-2" :disabled="saving"
                        style="padding: 0 15px; border-radius: 8px;">
                        Cancel
                    </v-btn>
                    <v-btn @click="confirmSave" :loading="saving" color="success" variant="elevated"
                        style="padding: 0 15px; border-radius: 8px;">
                        <v-icon class="mr-2">{{ isEditMode ? 'mdi-content-save-edit' : 'mdi-content-save' }}</v-icon>
                        {{ isEditMode ? 'Update Flow' : 'Save Flow' }}
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Load from Model Dialog -->
        <v-dialog v-model="loadFromModelDialog" max-width="600" persistent>
            <v-card class="load-model-dialog">
                <v-card-title class="dialog-header">
                    <div class="header-content">
                        <v-icon class="mr-3" color="white" size="28">mdi-download</v-icon>
                        <span class="text-h5 font-weight-bold">Load from Existing Model</span>
                    </div>
                </v-card-title>

                <v-card-text class="dialog-content">
                    <div class="load-model-content">
                        <!-- Model Selection -->
                        <div class="model-selection-section">
                            <div class="section-title mb-3">Select Model to Load</div>
                            <v-autocomplete v-model="selectedModelToLoad" :items="uniqueAiModelOptions"
                                item-title="searchableText" item-value="modelId" label="Select Model" variant="outlined"
                                density="comfortable" hide-details="auto" prepend-inner-icon="mdi-robot" class="mb-4"
                                placeholder="Choose a model to load configuration from"
                                :loading="loadingAvailableModels" clearable
                                :custom-filter="(value, query, item) => {
                                    if (!query) return true;
                                    const searchQuery = query.toLowerCase();
                                    const itemValue = item?.raw?.value?.toString().toLowerCase() || '';
                                    const itemName = item?.raw?.name?.toLowerCase() || '';
                                    return itemValue.includes(searchQuery) || itemName.includes(searchQuery);
                                }">
                                <template v-slot:item="{ props, item }">
                                    <v-list-item v-bind="props" class="model-option"
                                        :title="item.raw.value + ': ' + item.raw.name">
                                        <template v-slot:prepend>
                                            <div class="model-icon-small mr-2" style="background-color: #3b82f6;">
                                                <v-icon size="14" color="white">{{ item.raw.icon }}</v-icon>
                                            </div>
                                        </template>
                                    </v-list-item>
                                </template>

                                <template v-slot:selection="{ item }">
                                    <v-chip variant="text" size="medium"
                                        :text="item.raw.value + ': ' + item.raw.name"></v-chip>
                                </template>
                            </v-autocomplete>
                        </div>

                        <!-- Configuration Preview -->
                        <div v-if="selectedModelToLoad && modelConfigPreview" class="config-preview-section">
                            <div class="section-title mb-3">Configuration Preview</div>
                            <v-card variant="outlined" class="config-preview-card">
                                <v-card-text class="pa-3">
                                    <div class="preview-grid">
                                        <div class="preview-item">
                                            <span class="preview-label">Provider:</span>
                                            <span class="preview-value">{{ modelConfigPreview.provider }}</span>
                                        </div>
                                        <div class="preview-item">
                                            <span class="preview-label">Model:</span>
                                            <span class="preview-value">{{ (modelConfigPreview.model ||
                                                '').split('|')[0]
                                                }}</span>
                                        </div>
                                        <div class="preview-item">
                                            <span class="preview-label">Temperature:</span>
                                            <span class="preview-value">{{ modelConfigPreview.temperature }}</span>
                                        </div>
                                        <div class="preview-item">
                                            <span class="preview-label">Max Tokens:</span>
                                            <span class="preview-value">{{ modelConfigPreview.maxTokens }}</span>
                                        </div>
                                        <div class="preview-item">
                                            <span class="preview-label">Top P:</span>
                                            <span class="preview-value">{{ modelConfigPreview.topP }}</span>
                                        </div>
                                        <div class="preview-item">
                                            <span class="preview-label">Frequency Penalty:</span>
                                            <span class="preview-value">{{ modelConfigPreview.frequencyPenalty }}</span>
                                        </div>
                                    </div>
                                </v-card-text>
                            </v-card>
                        </div>

                        <!-- Warning -->
                        <v-alert v-if="selectedModelToLoad" type="warning" variant="tonal" density="compact"
                            class="mt-4">
                            <template v-slot:prepend>
                                <v-icon>mdi-alert</v-icon>
                            </template>
                            <div class="text-caption">
                                <strong>Note:</strong> Loading configuration will overwrite current AI model settings in
                                this
                                node.
                            </div>
                        </v-alert>
                    </div>
                </v-card-text>

                <v-card-actions class="dialog-actions">
                    <v-btn @click="closeLoadFromModelDialog" variant="outlined" class="mr-2"
                        :disabled="loadingModelConfig">
                        Cancel
                    </v-btn>
                    <v-btn @click="confirmLoadFromModel" :loading="loadingModelConfig" :disabled="!selectedModelToLoad"
                        color="success" variant="elevated">
                        <v-icon class="mr-2">mdi-download</v-icon>
                        Load Configuration
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Smart Intent Load from Model Dialog -->
        <v-dialog v-model="smartIntentLoadFromModelDialog" max-width="600" persistent>
            <v-card class="load-model-dialog">
                <v-card-title class="dialog-header">
                    <div class="header-content">
                        <v-icon class="mr-3" color="white" size="28">mdi-download</v-icon>
                        <span class="text-h5 font-weight-bold">
                            Load Config for {{ getLoadConfigTitle() }}
                        </span>
                    </div>
                </v-card-title>

                <v-card-text class="dialog-content">
                    <div class="load-model-content">
                        <!-- Model Selection -->
                        <div class="model-selection-section">
                            <div class="section-title mb-3">Select Model to Load</div>
                            <v-autocomplete v-model="selectedSmartIntentModelToLoad" :items="uniqueAiModelOptions"
                                item-title="searchableText" item-value="modelId" label="Select Model" variant="outlined"
                                density="comfortable" hide-details="auto" prepend-inner-icon="mdi-robot" class="mb-4"
                                placeholder="Choose a model to load configuration from"
                                :loading="loadingAvailableModels" clearable
                                :custom-filter="(value, query, item) => {
                                    if (!query) return true;
                                    const searchQuery = query.toLowerCase();
                                    const itemValue = item?.raw?.value?.toString().toLowerCase() || '';
                                    const itemName = item?.raw?.name?.toLowerCase() || '';
                                    return itemValue.includes(searchQuery) || itemName.includes(searchQuery);
                                }">
                                <template v-slot:item="{ props, item }">
                                    <v-list-item v-bind="props" class="model-option"
                                        :title="item.raw.value + ': ' + item.raw.name">
                                        <template v-slot:prepend>
                                            <div class="model-icon-small mr-2" style="background-color: #3b82f6;">
                                                <v-icon size="14" color="white">{{ item.raw.icon }}</v-icon>
                                            </div>
                                        </template>
                                    </v-list-item>
                                </template>

                                <template v-slot:selection="{ item }">
                                    <v-chip variant="text" size="medium"
                                        :text="item.raw.value + ': ' + item.raw.name"></v-chip>
                                </template>
                            </v-autocomplete>
                        </div>

                        <!-- Configuration Preview -->
                        <div v-if="selectedSmartIntentModelToLoad && smartIntentModelConfigPreview" class="config-preview-section">
                            <div class="section-title mb-3">Configuration Preview</div>
                            <v-card variant="outlined" class="config-preview-card">
                                <v-card-text class="pa-3">
                                    <div class="preview-grid">
                                        <div class="preview-item">
                                            <span class="preview-label">Provider:</span>
                                            <span class="preview-value">{{ smartIntentModelConfigPreview.provider }}</span>
                                        </div>
                                        <div class="preview-item">
                                            <span class="preview-label">Model:</span>
                                            <span class="preview-value">{{ (smartIntentModelConfigPreview.model ||
                                                '').split('|')[0]
                                                }}</span>
                                        </div>
                                        <div class="preview-item">
                                            <span class="preview-label">Temperature:</span>
                                            <span class="preview-value">{{ smartIntentModelConfigPreview.temperature }}</span>
                                        </div>
                                        <div class="preview-item">
                                            <span class="preview-label">Max Tokens:</span>
                                            <span class="preview-value">{{ smartIntentModelConfigPreview.maxTokens }}</span>
                                        </div>
                                        <div class="preview-item">
                                            <span class="preview-label">Top P:</span>
                                            <span class="preview-value">{{ smartIntentModelConfigPreview.topP }}</span>
                                        </div>
                                        <div class="preview-item">
                                            <span class="preview-label">Frequency Penalty:</span>
                                            <span class="preview-value">{{ smartIntentModelConfigPreview.frequencyPenalty }}</span>
                                        </div>
                                    </div>
                                </v-card-text>
                            </v-card>
                        </div>

                        <!-- Warning -->
                        <v-alert v-if="selectedSmartIntentModelToLoad" type="warning" variant="tonal" density="compact"
                            class="mt-4">
                            <template v-slot:prepend>
                                <v-icon>mdi-alert</v-icon>
                            </template>
                            <div class="text-caption">
                                <strong>Note:</strong> Loading configuration will overwrite current AI settings for {{ getLoadConfigTitle() }}.
                            </div>
                        </v-alert>
                    </div>
                </v-card-text>

                <v-card-actions class="dialog-actions">
                    <v-btn @click="closeSmartIntentLoadFromModelDialog" variant="outlined" class="mr-2"
                        :disabled="loadingSmartIntentModelConfig">
                        Cancel
                    </v-btn>
                    <v-btn @click="confirmSmartIntentLoadFromModel" :loading="loadingSmartIntentModelConfig" :disabled="!selectedSmartIntentModelToLoad"
                        color="success" variant="elevated">
                        <v-icon class="mr-2">mdi-download</v-icon>
                        Load Configuration
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Success Snackbar -->
        <v-snackbar v-model="showSnackbar" :timeout="3000" :color="snackBarColor" location="bottom center">
            <v-icon class="me-2">mdi-check-circle</v-icon>
            {{ snackbarMessage }}
        </v-snackbar>

        <!-- Success Dialog -->
        <v-dialog v-model="successDialog" max-width="500" persistent>
            <v-card class="success-dialog">
                <v-card-title class="success-dialog-header">
                    <div class="success-header-content">
                        <v-icon class="success-icon" size="32">mdi-check-circle</v-icon>
                        <span class="success-title">Success!</span>
                    </div>
                </v-card-title>

                <v-card-text class="success-dialog-content">
                    <div class="success-message">
                        {{ successMessage }}
                    </div>

                    <div class="success-details">
                        <div class="success-detail-item">
                            <v-icon size="16" class="mr-2">mdi-chart-timeline-variant</v-icon>
                            <span>Flow: {{ flowName }}</span>
                        </div>
                        <div class="success-detail-item">
                            <v-icon size="16" class="mr-2">mdi-clock-outline</v-icon>
                            <span>{{ new Date().toLocaleString() }}</span>
                        </div>
                    </div>
                </v-card-text>

                <v-card-actions class="success-dialog-actions">
                    <v-spacer></v-spacer>
                    <v-btn @click="successDialog = false" color="success" variant="elevated" class="success-ok-btn">
                        <v-icon class="mr-2">mdi-check</v-icon>
                        OK
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Confirmation Exit Dialog -->
        <v-dialog v-model="confirmExitDialog" persistent max-width="500">
            <v-card class="confirmation-dialog">
                <v-card-title class="dialog-header d-flex align-center">
                    <v-icon color="warning" size="28" class="mr-3">mdi-alert-circle</v-icon>
                    <div class="header-content">
                        <h3 class="text-h5 mb-0">Confirm Navigation</h3>
                    </div>
                </v-card-title>

                <v-card-text class="dialog-content pt-6">
                    <div class="confirmation-content">
                        <p class="text-body-1 mb-1">
                            Are you sure you want to leave?
                        </p>
                        <p class="text-body-1 mb-4">
                            Any unsaved changes will be lost.
                        </p>
                        
                        <v-alert type="warning" variant="tonal" class="mb-0">
                            <template v-slot:prepend>
                                <v-icon>mdi-content-save-alert</v-icon>
                            </template>
                            <strong>Unsaved Changes:</strong> Your current flow modifications have not been saved.
                        </v-alert>
                    </div>
                </v-card-text>

                <v-card-actions class="dialog-actions pa-6">
                    <v-btn @click="confirmExit" color="warning" variant="elevated" class="elevation-2">
                        <v-icon class="mr-2" style="transform: scaleX(-1);">mdi-exit-to-app</v-icon>
                        Leave Anyway
                    </v-btn>
                    <v-spacer></v-spacer>
                    <v-btn @click="cancelExit" variant="outlined" color="primary" class="mr-3">
                        Stay Here
                        <v-icon class="ml-2">mdi-arrow-right</v-icon>
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import { MarkerType, VueFlow } from '@vue-flow/core'
import { Background } from '@vue-flow/background'
import { Controls } from '@vue-flow/controls'
import { ref, computed, toRef, reactive } from "vue";

// Import styles
import '@vue-flow/core/dist/style.css'
import '@vue-flow/core/dist/theme-default.css'
import '@vue-flow/controls/dist/style.css'

import FlowNode from '@/components/FlowComponents/FlowNode.vue'
import FlowEdge from '@/components/FlowComponents/FlowEdge.vue'
import NodePanel from '@/components/FlowComponents/NodePanel.vue'

export default {
    name: "Flow",
    inject: ["flagNav"],
    components: {
        VueFlow,
        Background,
        Controls,
        FlowNode,
        FlowEdge,
        NodePanel
    },
    provide() {
        return {
            lockFlowCreatorStatus: computed(() => this.lockStatus),
            aiModelOptions: computed(() => this.aiModelOptions),
            flowEdges: computed(() => this.tempEdge),
        };
    },
    data() {
        return {
            interactiveControls: true,
            unableToAccessModel: [],
            // Edit mode properties
            isEditMode: false,
            isViewMode: false,
            editFlowId: null,
            // Save/Load UI state
            saveDialog: false,
            saveFormValid: false,
            saving: false,
            loading: false,
            successDialog: false,
            // Load from Model state
            loadFromModelDialog: false,
            selectedModelToLoad: null,
            availableModels: [],
            modelConfigPreview: null,
            loadingAvailableModels: false,
            loadingModelConfig: false,
            // Smart Intent Load Config properties (separate from AI Model)
            smartIntentLoadFromModelDialog: false,
            selectedSmartIntentModelToLoad: null,
            smartIntentModelConfigPreview: null,
            loadingSmartIntentModelConfig: false,
            currentLoadingSmartIntentIndex: null, // Track which intent is being loaded
            // Confirmation dialog properties
            confirmExitDialog: false,
            pendingNavigation: null,
            isNavigatingAway: false, // Flag to prevent double confirmation
            // Save form data
            saveFlowData: {
                name: '',
                saveAsTemplate: false,
                createBackup: true,
                publishFlow: false
            },
            // Flow metadata
            flowId: null,
            flowName: 'Untitled Flow',
            flowCreatedAt: null,

            isDraggingNode: false,
            dialog: false,
            showSnackbar: false,
            snackBarColor: 'success',
            snackbarMessage: '',
            inspectorExpanded: false,
            selectedNode: null,
            selectedNodes: [], // Track multiple selected nodes

            // Undo/Redo system
            history: [], // Stack for storing flow states
            historyIndex: -1, // Current position in history
            maxHistorySize: 50, // Maximum number of undo states
            isPerformingUndoRedo: false, // Flag to prevent infinite loops
            propertyUpdateTimer: null, // Timer for debouncing property updates
            isDraggingNodes: false, // Flag to track node dragging
            dragStartPositions: {}, // Store initial positions before drag

            // Copy & Paste system
            copiedNodes: [], // Store copied nodes data
            pasteOffset: 100, // Offset for pasted nodes

            DDVector: [],
            listDataIntegration: [],
            DDGroupReplace: [],
            DDProductGroup: [],
            listDDModel: [],
            listDDIntentGroup: [],
            listMonitorLineName: [],
            listDDProvider: [],
            DDModelShow: [],
            DDTimeZone: [],
            modelID: null,
            LLmsModel: {
                provider: null,
                model: null
            },
            // Document data properties (similar to IntentSessionDetail.vue)
            documentDataTable: [],
            documentDataHeaders: [
                {
                    title: "",
                    key: "selected",
                    align: "center",
                    sortable: false,
                    width: "10px",
                },
                {
                    title: "Text Original",
                    key: "textORG",
                    align: "start",
                    sortable: true,
                    width: "50%",
                },
                {
                    title: "Text English",
                    key: "textEN",
                    align: "start",
                    sortable: true,
                    width: "50%",
                }
            ],
            documentDataItemsPerPage: 10,
            documentDataTotalTable: 0,
            documentDataOptions: {},
            loadingDocumentDataTable: false,
            showDocumentDataTable: false,
            selectedDocumentData: [],
            documentDataExpanded: false, // Control expand/collapse state
            // Model configuration panels control
            modelConfigPanels: ['basic'], // Default to basic panel open
            intentaiModelConfigPanels: ['intentaibasic'], // Default to basic panel open

            // Prompt template properties
            showPromptTem: false,
            promptTemplateDialog: false,
            promptTemplates: [],
            promptTemplateGroups: [],
            groupPromptTemplate: null,
            showMenu: false,
            x: 0,
            y: 0,
            
            // Smart Intent Prompt Template properties
            smartIntentPromptTemplateDialog: false,
            smartIntentCurrentIndex: null,
            smartIntentGroupPromptTemplate: null,
            smartIntentTemplateSearchQuery: '',
            
            // Smart Intent Fallback Prompt Template properties
            smartIntentFallbackPromptTemplateDialog: false,
            smartIntentFallbackGroupPromptTemplate: null,
            smartIntentFallbackTemplateSearchQuery: '',
            
            dialogShowAll: false,
            templateSearchQuery: '',

            // IntentAI Prompt template properties
            intentaiPromptTemplateDialog: false,
            showIntentaiMenu: false,
            dialogShowAllIntentai: false,
            intentaiTemplateSearchQuery: '',
            intentaiGroupPromptTemplate: null,

            nodeInspector: {
                label: '',
                color: '#1976D2',
                placeholderText: '',
                required: false,
                selectedModel: null,
                systemPrompt: '',
                temperature: null,
                maxTokens: null,
                // AI Model Configuration properties
                provider: null,
                model: null,
                useBaseModelPrompt: false,
                gender: 'male',
                typingSpeed: null,
                maxInputLength: 200000,
                streamResponse: false,
                enableTime: null,
                timeZone: null,
                jsonResponse: false,
                memoryTurn: null,
                memoryDuration: null,
                shortTermMemory: null,
                longTermMemory: false,
                isChainMassage: false,
                lenChainMassage: null,
                timeSecondChainMassage: null,
                topP: null,
                frequencyPenalty: null,
                presencePenalty: null,
                stopSequence: null,
                loadingBar: false,
                loadingBarDuration: 0,
                monitorLineUserId: null,
                monitorSocialID: null,
                showLineName: false,
                forbiddenWords: [''],
                intentGroupName: '',
                intents: [],
                // Guardrail AI properties
                guardrailType: 'content_safety',
                guardrailModel: 'gpt-4',
                guardrailPrompt: '',
                safetyThreshold: 80,
                enableLogging: true,
                // API Trigger properties
                apiUrl: '',
                httpMethod: 'POST',
                requestBody: '',
                headers: '',
                textResponse: '',
                jsonValidate: '{\n   "Name":"Value"\n}',
                modelJsonValidator: null,
                listHeadersAPITrigger: [
                    {
                        key: '',
                        value: '',
                    },
                ],
                listValidation: [
                    {
                        key: '',
                        typeValidation: '',
                        numberOfCharacter: '',
                        regExp: '',
                        promptMessage: '',
                    },
                ],
                // Contact Agent properties
                contactAgentMessage: '',
                botDisableTime: null,
                // Switch properties
                switchName: '',
                switchOutputs: [],
                // Condition properties
                conditionType: 'value_comparison',
                leftOperand: '',
                operator: 'equals',
                rightOperand: '',
                conditionExpression: '',
                trueLabel: 'True',
                falseLabel: 'False',
                description: '',
                minValue: 0,
                maxValue: 100,
                expectedValue: true,
                // Delay properties
                delayType: 'fixed',
                delayValue: 5,
                delayUnit: 'seconds',
                randomMin: 1,
                randomMax: 10,
                delayMessage: '',
                showProgress: true,
                delayExpression: '',
                // Documents properties
                document: [],
                textReplacement: [],
                topNChunks: 5,
                cosineSimilarity: 0.7,
                selectedDocumentData: [],
                // Database Integration properties
                tableName: null,
                sqlExtractionModel: null,
                promptSqlNoData: '',
                // Products properties
                productGroupList: [],
                productTopNChunks: 5,
                productCosineSimilarity: 0.7,
                // Knowledge API properties
                knowledgeApiUrl: '',
                knowledgeHttpMethod: 'GET',
                knowledgeRequestBody: '',
                listHeaders: [
                    {
                        key: '',
                        value: '',
                    },
                ],
                responseDataPath: '',
                // SharePoint properties
                sharepoint: {
                    siteUrl: '',
                    tenantId: '',
                    clientId: '',
                    clientSecret: '',
                    folderPath: '',
                    filename: ''
                },
                // Email properties
                emailProvider: 'mailgun',
                toEmail: '',
                ccEmails: [],
                bccEmails: [],
                subject: '',
                emailTemplate: 'custom',
                emailBody: '',
                isHtml: false,
                // Smart Intent properties
                smartIntentGroupName: '',
                smartIntents: null,
                smartIntentFallback: {
                        useAI: false,
                        provider: '',
                        model: '',
                        temperature: 0.7,
                        maxTokens: 150,
                        topP: 1.0,
                        presencePenalty: 0.0,
                        frequencyPenalty: 0.0,
                        streamingEnabled: true,
                        lineFlexMessage: false,
                        systemPrompt: '',
                        forbiddenWords: []
                    },
            },
            tempNode: [],
            tempEdge: [],
            uniqueAiModelOptions: [],
            aiModelOptions: [],
            DDTypeValidation: [
                {
                    name: "Phone",
                    value: "PV",
                },
                {
                    name: "Email",
                    value: "EV",
                },
                {
                    name: "Max Character",
                    value: "MCV",
                },
                {
                    name: "Regular Expression",
                    value: "RV",
                },
            ],

            // Variables list for JSON editor
            availableVariables: [
                {
                    key: '{transactionID}',
                    label: 'Transaction ID',
                    description: 'Unique transaction identifier',
                    icon: 'mdi-identifier',
                    category: 'System'
                },
                {
                    key: '{channel}',
                    label: 'Channel',
                    description: 'Current communication channel',
                    icon: 'mdi-forum',
                    category: 'System'
                },
                {
                    key: '{channelID}',
                    label: 'Channel ID',
                    description: 'Channel identifier',
                    icon: 'mdi-pound',
                    category: 'System'
                },
                {
                    key: '{userChannel}',
                    label: 'User Channel',
                    description: 'User specific channel',
                    icon: 'mdi-account-circle',
                    category: 'User'
                },
                {
                    key: '{sessionID}',
                    label: 'Session ID',
                    description: 'User session identifier',
                    icon: 'mdi-clock-outline',
                    category: 'Session'
                }
            ],

            // Variables panel state
            variablesPanelExpanded: false,
            selectedVariableCategory: 'All',
        }
    },
    computed: {
        // uniqueAiModelOptions() {
        //     const seen = new Set();
        //     return this.aiModelOptions.filter(option => {
        //         const duplicate = seen.has(option.value);
        //         seen.add(option.value);
        //         return !duplicate;
        //     });
        // },
        
        uniqueProviderOptions() {
            // Extract unique providers from AI model options
            const providers = new Set();
            this.uniqueAiModelOptions.forEach(option => {
                if (option.value && option.value.includes('|')) {
                    const provider = option.value.split('|')[0];
                    providers.add(provider);
                } else if (option.name) {
                    // Fallback to extracting from name
                    const parts = option.name.split(' ');
                    if (parts.length > 0) {
                        providers.add(parts[0]);
                    }
                }
            });
            return Array.from(providers).sort();
        },
        
        filteredPromptTemplates() {

            if (this.groupPromptTemplate === null) {
                return [];
            }
            return this.DDPromptTemplate.filter(
                (item) => item.groupPromptTemplateID === this.groupPromptTemplate
            );
        },

        searchedTemplates() {
            if (!this.templateSearchQuery) {
                return this.filteredPromptTemplates;
            }

            const query = this.templateSearchQuery.toLowerCase();
            return this.filteredPromptTemplates.filter(template =>
                template.name.toLowerCase().includes(query) ||
                (template.prompt && template.prompt.toLowerCase().includes(query))
            );
        },

        // IntentAI Prompt Template computed properties
        filteredIntentaiPromptTemplates() {
            if (this.intentaiGroupPromptTemplate === null) {
                return [];
            }
            return this.DDPromptTemplate.filter(
                (item) => item.groupPromptTemplateID === this.intentaiGroupPromptTemplate
            );
        },

        searchedIntentaiTemplates() {
            if (!this.intentaiTemplateSearchQuery) {
                return this.filteredIntentaiPromptTemplates;
            }

            const query = this.intentaiTemplateSearchQuery.toLowerCase();
            return this.filteredIntentaiPromptTemplates.filter(template =>
                template.name.toLowerCase().includes(query) ||
                (template.prompt && template.prompt.toLowerCase().includes(query))
            );
        },

        // Smart Intent Prompt Template computed properties
        filteredSmartIntentPromptTemplates() {
            if (this.smartIntentGroupPromptTemplate === null) {
                return [];
            }
            return this.DDPromptTemplate.filter(
                (item) => item.groupPromptTemplateID === this.smartIntentGroupPromptTemplate
            );
        },

        smartIntentSearchedTemplates() {
            if (!this.smartIntentTemplateSearchQuery) {
                return this.filteredSmartIntentPromptTemplates;
            }

            const query = this.smartIntentTemplateSearchQuery.toLowerCase();
            return this.filteredSmartIntentPromptTemplates.filter(template =>
                template.name.toLowerCase().includes(query) ||
                (template.prompt && template.prompt.toLowerCase().includes(query))
            );
        },

        // Smart Intent Fallback Prompt Template computed properties
        filteredSmartIntentFallbackPromptTemplates() {
            if (this.smartIntentFallbackGroupPromptTemplate === null) {
                return [];
            }
            return this.DDPromptTemplate.filter(
                (item) => item.groupPromptTemplateID === this.smartIntentFallbackGroupPromptTemplate
            );
        },

        smartIntentFallbackSearchedTemplates() {
            if (!this.smartIntentFallbackTemplateSearchQuery) {
                return this.filteredSmartIntentFallbackPromptTemplates;
            }

            const query = this.smartIntentFallbackTemplateSearchQuery.toLowerCase();
            return this.filteredSmartIntentFallbackPromptTemplates.filter(template =>
                template.name.toLowerCase().includes(query) ||
                (template.prompt && template.prompt.toLowerCase().includes(query))
            );
        },

        // Filter variables by category
        filteredVariables() {
            if (this.selectedVariableCategory === 'All') {
                return this.availableVariables;
            }
            return this.availableVariables.filter(v => v.category === this.selectedVariableCategory);
        },

        // Get unique categories
        variableCategories() {
            const categories = ['All', ...new Set(this.availableVariables.map(v => v.category))];
            return categories;
        },
        lockStatus() {
            // ตรวจสอบว่า instance ของ vueFlow พร้อมใช้งานหรือไม่
            if (this.$refs.vueFlow) {
                // ปุ่ม lock จะควบคุมค่า nodesDraggable เราจึงใช้ค่านี้เป็นตัวชี้วัดสถานะ
                // ถ้า nodesDraggable เป็น false หรือ undefined แสดงว่า "Locked"
                const nodesDraggable = this.$refs.vueFlow.nodesDraggable;
                return nodesDraggable === false;
            }
            // ค่าเริ่มต้นก่อนที่ component จะ mount (unlocked)
            return false;
        },
        DDTimeZone() {
            return [
                {
                    value: "Asia/Bangkok",
                    name: "Thailand",
                },
                {
                    value: "Asia/Hong_Kong",
                    name: "Hong Kong",
                },
                {
                    value: "Asia/Tokyo",
                    name: "Japan",
                },
                {
                    value: "America/New_York",
                    name: "USA",
                },
                {
                    value: "Europe/London",
                    name: "England",
                },
                {
                    value: "Australia/Sydney",
                    name: "Australia",
                },
            ];
        },
        // Undo/Redo computed properties
        canUndo() {
            return this.historyIndex > 0;
        },
        canRedo() {
            return this.historyIndex < this.history.length - 1;
        }
    },
    watch: {
        // Watch for changes in document selection to fetch document data
        'nodeInspector.documentVectorName'(newValue) {
            if (newValue && this.nodeInspector.enableSpecificDataSelection) {
                this.getDocumentData();
            } else {
                this.resetDocumentDataTable();
            }
        },

        // Watch for model selection to show preview
        selectedModelToLoad(newValue) {
            if (newValue) {
                this.loadModelConfigPreview(newValue);
            } else {
                this.modelConfigPreview = null;
            }
        },

        // Watch for Smart Intent model selection to show preview
        selectedSmartIntentModelToLoad(newValue) {
            if (newValue) {
                this.loadSmartIntentModelConfigPreview(newValue);
            } else {
                this.smartIntentModelConfigPreview = null;
            }
        },

        // Watch for uniqueAiModelOptions to be loaded and filter messageInput models if needed
        uniqueAiModelOptions(newValue) {
            if (newValue && newValue.length > 0 && this.tempNode && this.tempNode.length > 0) {
                // Check if there are messageInput nodes that need filtering
                let hasUnfilteredMessageInput = this.tempNode.some(node => 
                    node.data && 
                    node.data.type === 'messageInput' && 
                    node.data.selectedModel && 
                    node.data.selectedModel.length > 0
                );

                if (hasUnfilteredMessageInput) {
                    console.log('AI Models loaded, applying filter to messageInput nodes');
                    this.filterMessageInputModels();
                }
            }
        }
    },
    methods: {
        async checkRouteMode() {
            const { id, mode, importData } = this.$route.query;

            if (importData) {
                // Handle imported flow data
                await this.handleImportedData(importData);
            } else if (id) {
                // Handle edit/view mode
                this.editFlowId = id;
                this.isEditMode = mode === 'edit';
                this.isViewMode = mode === 'view';

                // Load the flow data
                await this.loadFlowForEdit(id);

            }
        },
        goBack() {
                // Use custom confirmation dialog if in edit mode with unsaved changes
            this.showExitConfirmation(() => {
                this.$router.push('/FlowList');
            });
        },
        generateGuid() {
            return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
                const r = Math.random() * 16 | 0;
                const v = c === 'x' ? r : (r & 0x3 | 0x8);
                return v.toString(16);
            });
        },

        // Undo/Redo System Methods
        saveStateToHistory(action = 'Unknown Action') {
            if (this.isPerformingUndoRedo) return;

            // Create a deep copy of current state
            const currentState = {
                nodes: JSON.parse(JSON.stringify(this.tempNode)),
                edges: JSON.parse(JSON.stringify(this.tempEdge)),
                action: action,
                timestamp: new Date().toISOString()
            };

            // Remove any history entries after current index (if we're not at the end)
            if (this.historyIndex < this.history.length - 1) {
                this.history = this.history.slice(0, this.historyIndex + 1);
            }

            // Add new state to history
            this.history.push(currentState);

            // Limit history size
            if (this.history.length > this.maxHistorySize) {
                this.history.shift();
            } else {
                this.historyIndex++;
            }
        },

        undo() {
            if (!this.canUndo) return;

            this.isPerformingUndoRedo = true;
            
            try {
                this.historyIndex--;
                const previousState = this.history[this.historyIndex];
                
                // Restore previous state
                this.tempNode = JSON.parse(JSON.stringify(previousState.nodes));
                this.tempEdge = JSON.parse(JSON.stringify(previousState.edges));
                
                // Clear selection when undoing
                this.selectedNode = null;
                this.selectedNodes = [];
                this.closeInspector();
                
                this.showSuccess(`Undone: ${previousState.action}`);
                
            } finally {
                this.isPerformingUndoRedo = false;
            }
        },

        redo() {
            if (!this.canRedo) return;

            this.isPerformingUndoRedo = true;
            
            try {
                this.historyIndex++;
                const nextState = this.history[this.historyIndex];
                
                // Restore next state
                this.tempNode = JSON.parse(JSON.stringify(nextState.nodes));
                this.tempEdge = JSON.parse(JSON.stringify(nextState.edges));
                
                // Clear selection when redoing
                this.selectedNode = null;
                this.selectedNodes = [];
                this.closeInspector();
                
                this.showSuccess(`Redone: ${nextState.action}`);
                
            } finally {
                this.isPerformingUndoRedo = false;
            }
        },

        initializeHistory() {
            // Save initial state
            this.saveStateToHistory('Initial State');
        },
        // Load GPT Models (async version)
        loadGPTModels() {
            return new Promise((resolve, reject) => {
                this.$call.DDGPTModel(
                    (dataResponse) => {
                        if (dataResponse.status === 0) {
                            this.aiModelOptions = dataResponse.data.map(m => ({
                                name: m.name,
                                value: m.modelDisplayID,
                                modelId: m.id,
                                icon: 'mdi-robot',
                                color: '#10a37f',
                                searchableText: `${m.modelDisplayID}: ${m.name}` // สำหรับการค้นหา
                            }));
                            this.uniqueAiModelOptions = this.aiModelOptions.filter((option, index, self) =>
                                index === self.findIndex((t) => (
                                    t.value === option.value
                                ))
                            );
                        } else {
                            this.aiModelOptions = [];
                        }
                        resolve();
                    },
                    (err) => {
                        this.aiModelOptions = [];
                        reject(err);
                    }
                );
            });
        },
        // Load Model Management Data (async version)
        GetAllDDModelManagement() {
            return new Promise((resolve, reject) => {
                let self = this;
                self.$call.GetAllDDModelManagement(
                    0,
                    (dataResponse) => {
                        if (dataResponse.status == 0) {
                            self.DDVector = dataResponse.data.vectorDBName;
                            self.listDataIntegration = dataResponse.data.dataIntegration;
                            self.DDGroupReplace = dataResponse.data.groupReplace;
                            self.DDProductGroup = dataResponse.data.productGroup;
                            self.listDDModel = dataResponse.data.modelData;
                            self.listDDIntentGroup = dataResponse.data.intentGroup;
                            self.listSqlExtract = dataResponse.data.modelPublih.filter((e) => e.typeName == 'Bot');
                            self.listMonitorLineName = dataResponse.data.monitorSocialLine;
                            self.DDPromptTemplate = dataResponse.data.promptTemplate.listData;
                            self.DDGroupPromptTemplate = dataResponse.data.groupPromptTemplate;
                            self.listDDProvider = dataResponse.data.provider;
                        } else {
                            self.DDVector = [];
                            self.listDataIntegration = [];
                            self.listSqlExtract = [];
                            // self.listGuardRailModel = [];
                            self.DDGroupReplace = [];
                            self.DDProductGroup = [];
                            self.DDPromptTemplate = [];
                            self.listDDProvider = [];
                            self.listDDModel = [];
                        }
                        resolve();
                    },
                    (errorResource) => {
                        console.error(errorResource);
                        reject(errorResource);
                    }
                );
            });
        },
        // Method to filter messageInput models after uniqueAiModelOptions is loaded
        filterMessageInputModels() {
            if (!this.uniqueAiModelOptions || this.uniqueAiModelOptions.length === 0) {
                return;
            }

            if (!this.tempNode || this.tempNode.length === 0) {
                return;
            }

            let messageInputNodes = this.tempNode.filter(n => n.data && n.data.type === 'messageInput');
            
            if (messageInputNodes.length > 0) {
                messageInputNodes.forEach(messageInputNode => {
                    if (messageInputNode.data.selectedModel && Array.isArray(messageInputNode.data.selectedModel) && messageInputNode.data.selectedModel.length > 0) {
                        this.unableToAccessModel = messageInputNode.data.selectedModel;
                        let tempUniqueModels = this.uniqueAiModelOptions.map(option => option.value);
                        this.unableToAccessModel = this.unableToAccessModel.filter(model => !tempUniqueModels.includes(model));
                        
                        // Update the specific node
                        let filteredModels = messageInputNode.data.selectedModel.filter(model => !this.unableToAccessModel.includes(model));
                        
                        // Update tempNode array
                        this.tempNode = this.tempNode.map(node => {
                            if (node.id === messageInputNode.id) {
                                return {
                                    ...node,
                                    data: {
                                        ...node.data,
                                        selectedModel: filteredModels
                                    }
                                };
                            }
                            return node;
                        });
                        
                        if (this.unableToAccessModel.length > 0) {
                            console.log(`Filtered out inaccessible models from node ${messageInputNode.id}: ${this.unableToAccessModel.join(', ')}`);
                        }
                    }
                });
            }
        },
        // Reload all models and data (useful for refresh actions)
        async reloadModels() {
            try {
                await Promise.all([
                    this.loadGPTModels(),
                    this.GetAllDDModelManagement()
                ]);
                this.showSuccess('Models reloaded successfully');
            } catch (error) {
                console.error('Failed to reload models:', error);
                this.showError('Failed to reload models. Please try again.');
            }
        },
        clearHistory() {
            this.history = [];
            this.historyIndex = -1;
            this.saveStateToHistory('Flow Saved');
            // Show info message about history being cleared
            this.showInfo('Undo/Redo history cleared');
            console.log('History cleared after save');
        },
        onSelectionChange({ nodes }) {
            // event นี้จะทำงานหลัง onSelectionDragEnd

            // เช็คว่าเพิ่งลากเสร็จ และมี node ที่ถูกเลือกแค่ตัวเดียวหรือไม่
            if (this.wasJustDragSelecting && nodes.length === 1) {
                this.selectedNodes = []; // ยกเลิกการเลือก
            }

            // รีเซ็ตค่าธงเสมอ เพื่อให้การคลิกครั้งต่อไปทำงานปกติ
            this.wasJustDragSelecting = false;

            // อัปเดต selectedNodes
            this.selectedNodes = nodes;

            // อัปเดต selectedNode สำหรับ inspector panel
            if (this.selectedNodes.length === 1) {
                this.selectedNode = this.selectedNodes[0];
            } else {
                this.selectedNode = null;
            }
        },
        onNodeClick({ node }) {
            this.openNodeInspector(node);
        },
        openNodeInspector(node) {
            this.selectedNode = node;

            // Default Base Prompt text
            const defaultBasePrompt = `Find the Intent from the message of users.
#Condition for Response
1. If the user asks a question related to or similar in meaning to the #Group Intent below. You must response 1. Otherwise, 0`;

            // Initialize inspector values from node data
            this.nodeInspector = {
                label: node.data.label || '',
                color: node.data.color || '#1976D2',
                placeholderText: node.data.placeholderText || '',
                required: node.data.required || false,
                selectedModel: Array.isArray(node.data.selectedModel)
                    ? node.data.selectedModel
                    : (node.data.selectedModel ? [node.data.selectedModel] : []),
                systemPrompt: node.data.systemPrompt || '',
                temperature: node.data.temperature || null,
                maxTokens: node.data.maxTokens || null,
                // AI Model Configuration properties (matching ModelManagement.vue defaults)
                provider: node.data.provider || null,
                model: node.data.model || null,
                useBaseModelPrompt: node.data.useBaseModelPrompt !== undefined ? node.data.useBaseModelPrompt : false,
                gender: node.data.gender || 'male',
                typingSpeed: node.data.typingSpeed || null,
                maxInputLength: node.data.maxInputLength || 200000,
                streamResponse: node.data.streamResponse !== undefined ? node.data.streamResponse : false,
                jsonResponse: node.data.jsonResponse !== undefined ? node.data.jsonResponse : false,
                enableTime: node.data.enableTime || null,
                timeZone: node.data.timeZone || null,
                memoryTurn: node.data.memoryTurn || null,
                memoryDuration: node.data.memoryDuration || null,
                shortTermMemory: node.data.shortTermMemory || null,
                longTermMemory: node.data.longTermMemory !== undefined ? node.data.longTermMemory : false,
                isChainMassage: node.data.isChainMassage !== undefined ? node.data.isChainMassage : false,
                lenChainMassage: node.data.lenChainMassage || null,
                timeSecondChainMassage: node.data.timeSecondChainMassage || null,
                topP: node.data.topP || null,
                frequencyPenalty: node.data.frequencyPenalty || null,
                presencePenalty: node.data.presencePenalty || null,
                stopSequence: node.data.stopSequence || null,
                loadingBar: node.data.loadingBar !== undefined ? node.data.loadingBar : false,
                loadingBarDuration: node.data.loadingBarDuration || 0,
                monitorLineUserId: node.data.monitorLineUserId || null,
                monitorSocialID: node.data.monitorSocialID || null,
                showLineName: node.data.showLineName !== undefined ? node.data.showLineName : false,
                forbiddenWords: node.data.forbiddenWords || [''],
                intentGroupName: node.data.intentGroupName || '',
                intents: node.data.type === 'intent'
                    ? (node.data.intents || [
                        {
                            id: `intent-${this.generateGuid()}`,
                            name: 'Intent Name',
                            priority: 0, // Default priority
                            prompt: '',
                            basePrompt: defaultBasePrompt, // Use default base prompt
                            enableBasePrompt: false, // Disable base prompt by default
                            messages: [
                                { id: `msg-${this.generateGuid()}`, text: '' }
                            ],
                            mainAction: { id: `main-${this.generateGuid()}`, label: 'Main Action' },
                            parallelActions: []
                        }
                    ])
                    : [],
                examples: node.data.examples || '',
                // Initialize actions and outputs for intent nodes
                actions: node.data.type === 'intent'
                    ? (node.data.actions || [
                        {
                            id: `action-${this.generateGuid()}`,
                            label: 'Action 1',
                            parallels: [
                                { id: `parallel-${this.generateGuid()}`, label: 'Parallel Action 1' }
                            ]
                        }
                    ])
                    : [],
                outputs: node.data.type === 'intent'
                    ? (node.data.outputs || [])
                    : [],
                // Smart Intent properties
                smartIntentGroupName: node.data.smartIntentGroupName || '',
                smartIntents: node.data.type === 'smartIntent'
                    ? (node.data.smartIntents || [
                        {
                            id: `smart-intent-${this.generateGuid()}`,
                            name: 'Smart Intent Name',
                            priority: 0,
                            prompt: '',
                            basePrompt: defaultBasePrompt,
                            enableBasePrompt: false,
                            useMemory: false,
                            messages: [
                                { id: `msg-${this.generateGuid()}`, text: '' }
                            ],
                            mainAction: {
                                id: `main-${this.generateGuid()}`,
                                label: 'Main Action',
                                useAI: true,
                                // Basic Information - match AI Model defaults
                                provider: null,
                                model: null,
                                apiVersion: null,
                                gender: 'male',
                                typingSpeed: null,
                                maxInputLength: 200000,
                                // Response Options - match AI Model defaults
                                streamResponse: false,
                                enableTime: null,
                                timeZone: null,
                                jsonResponse: false,
                                // AI Parameters - match AI Model defaults
                                temperature: null,
                                topP: null,
                                frequencyPenalty: null,
                                presencePenalty: null,
                                maxTokens: null,
                                stopSequence: null,
                                // Memory Settings - match AI Model defaults
                                memoryTurn: null,
                                memoryDuration: null,
                                shortTermMemory: null,
                                longTermMemory: false,
                                // Multi-turn Input Configuration - match AI Model defaults
                                isChainMassage: false,
                                lenChainMassage: null,
                                timeSecondChainMassage: null,
                                // Forbidden Words - match AI Model defaults
                                forbiddenWords: [''],
                                // Knowledge Configuration
                                knowledgeType: '',
                                // Knowledge Sources Enable Flags
                                enableDocuments: false,
                                enableDatabaseIntegration: false,
                                enableProducts: false,
                                enableApiKnowledge: false,
                                // Documents properties
                                document: [],
                                topNChunks: 5,
                                cosineSimilarity: 0.5,
                                // Database Integration properties
                                tableName: null,
                                sqlExtractionModel: null,
                                promptSqlNoData: '',
                                // Products properties
                                productGroupList: [],
                                productTopNChunks: 5,
                                productCosineSimilarity: 0.5,
                                // API Knowledge properties
                                knowledgeApiUrl: '',
                                knowledgeHttpMethod: 'GET',
                                knowledgeResponseDataPath: ''
                            }
                        }
                    ])
                    : [],
                smartIntentFallback: node.data.type === 'smartIntent'
                    ? (node.data.smartIntentFallback || {
                        useAI: false,
                        // Basic Information - match AI Model defaults
                        provider: null,
                        model: null,
                        gender: 'male',
                        typingSpeed: null,
                        maxInputLength: 200000,
                        // Response Options - match AI Model defaults
                        streamResponse: false,
                        enableTime: null,
                        timeZone: null,
                        // AI Parameters - match AI Model defaults
                        temperature: null,
                        topP: null,
                        frequencyPenalty: null,
                        presencePenalty: null,
                        maxTokens: null,
                        stopSequence: null,
                        // Memory Settings - match AI Model defaults
                        memoryTurn: null,
                        memoryDuration: null,
                        shortTermMemory: null,
                        longTermMemory: false,
                        // Multi-turn Input Configuration - match AI Model defaults
                        isChainMassage: false,
                        lenChainMassage: null,
                        timeSecondChainMassage: null,
                        // Forbidden Words - match AI Model defaults
                        forbiddenWords: ['']
                    })
                    : {},
                // Guardrail AI properties
                guardrailType: node.data.guardrailType || 'content_safety',
                guardrailModel: node.data.guardrailModel || 'gpt-4',
                guardrailPrompt: node.data.guardrailPrompt || '',
                safetyThreshold: node.data.safetyThreshold || 80,
                enableLogging: node.data.enableLogging !== undefined ? node.data.enableLogging : true,
                // Intent AI properties
                intentaiModel: Array.isArray(node.data.intentaiModel)
                    ? (node.data.intentaiModel.length > 0 ? node.data.intentaiModel[0] : null)
                    : (node.data.intentaiModel || (Array.isArray(node.data.intentModel) ? (node.data.intentModel.length > 0 ? node.data.intentModel[0] : null) : node.data.intentModel) || null),
                intentAiModelApiVersion: node.data.intentAiModelApiVersion || null,
                intentaiProvider: node.data.intentaiProvider || node.data.provider || '',
                intentaiGender: node.data.intentaiGender || node.data.gender || '',
                intentaiTypingSpeed: node.data.intentaiTypingSpeed || node.data.typingSpeed || 0,
                intentaiMaxInputLength: node.data.intentaiMaxInputLength || node.data.maxInputLength || 4000,
                intentaiStreamResponse: node.data.intentaiStreamResponse !== undefined ? node.data.intentaiStreamResponse : (node.data.streamResponse !== undefined ? node.data.streamResponse : true),
                intentaiJsonResponse: node.data.intentaiJsonResponse !== undefined ? node.data.intentaiJsonResponse : (node.data.jsonResponse !== undefined ? node.data.jsonResponse : true),
                intentaiEnableTime: node.data.intentaiEnableTime !== undefined ? node.data.intentaiEnableTime : (node.data.enableTime !== undefined ? node.data.enableTime : false),
                intentaiTimeZone: node.data.intentaiTimeZone || node.data.timeZone || '',
                intentaiMemoryTurn: node.data.intentaiMemoryTurn || node.data.memoryTurn || 5,
                intentaiMemoryDuration: node.data.intentaiMemoryDuration || node.data.memoryDuration || 60,
                intentaiShortTermMemory: node.data.intentaiShortTermMemory || node.data.shortTermMemory || 3,
                intentaiLongTermMemory: node.data.intentaiLongTermMemory !== undefined ? node.data.intentaiLongTermMemory : (node.data.longTermMemory !== undefined ? node.data.longTermMemory : false),
                intentaiIsChainMassage: node.data.intentaiIsChainMassage !== undefined ? node.data.intentaiIsChainMassage : (node.data.isChainMassage !== undefined ? node.data.isChainMassage : false),
                intentaiLenChainMassage: node.data.intentaiLenChainMassage || node.data.lenChainMassage || 5,
                intentaiTimeSecondChainMassage: node.data.intentaiTimeSecondChainMassage || node.data.timeSecondChainMassage || 30,
                intentaiTemperature: node.data.intentaiTemperature || node.data.temperature || 0.7,
                intentaiTopP: node.data.intentaiTopP || node.data.topP || 1.0,
                intentaiFrequencyPenalty: node.data.intentaiFrequencyPenalty || node.data.frequencyPenalty || 0,
                intentaiPresencePenalty: node.data.intentaiPresencePenalty || node.data.presencePenalty || 0,
                intentaiMaxTokens: node.data.intentaiMaxTokens || node.data.maxTokens || 1000,
                intentaiSystemPrompt: node.data.intentaiSystemPrompt || node.data.intentSystemPrompt || '',
                intentaiExamples: node.data.intentaiExamples || node.data.intentExamples || '',
                intentaiConfidence: node.data.intentaiConfidence || node.data.intentConfidence || 75,
                intentaiFallbackIntent: node.data.intentaiFallbackIntent || node.data.fallbackIntent || 'unknown',
                intentaiShowLineName: node.data.intentaiShowLineName !== undefined ? node.data.intentaiShowLineName : (node.data.showLineName !== undefined ? node.data.showLineName : false),
                intentaiLoadingBar: node.data.intentaiLoadingBar !== undefined ? node.data.intentaiLoadingBar : (node.data.loadingBar !== undefined ? node.data.loadingBar : false),
                intentaiLoadingBarDuration: node.data.intentaiLoadingBarDuration || node.data.loadingBarDuration || 0,
                intentaiMonitorLineUserId: node.data.intentaiMonitorLineUserId || node.data.monitorLineUserId || null,
                intentaiMonitorSocialID: node.data.intentaiMonitorSocialID || node.data.monitorSocialID || null,
                intentaiForbiddenWords: node.data.intentaiForbiddenWords || node.data.forbiddenWords || [''],
                // API Trigger properties
                apiUrl: node.data.apiUrl || '',
                httpMethod: node.data.httpMethod || 'POST',
                headers: node.data.headers || '',
                textResponse: node.data.textResponse || '',
                jsonValidate: node.data.jsonValidate || '{\n   "Name":"Value"\n}',
                modelJsonValidator: node.data.modelJsonValidator || null,
                listHeadersAPITrigger: node.data.listHeadersAPITrigger || [
                    {
                        key: '',
                        value: '',
                    },
                ],
                listValidation: node.data.listValidation || [
                    {
                        key: '',
                        typeValidation: '',
                        numberOfCharacter: '',
                        regExp: '',
                        promptMessage: '',
                    },
                ],
                // Contact Agent properties
                departmentName: node.data.departmentName || '',
                contactAgentMessage: node.data.contactAgentMessage || '',
                handoffMessage: node.data.handoffMessage || '',
                botDisableTime: node.data.botDisableTime || null,
                disableBot: node.data.disableBot !== undefined ? node.data.disableBot : true,
                // Switch properties
                switchName: node.data.switchName || '',
                switchOutputs: node.data.type === 'switch'
                    ? (node.data.switchOutputs || [
                        {
                            id: `switch-output-${this.generateGuid()}`, // Use GUID
                            name: 'Output 1',
                            condition: ''
                        },
                        {
                            id: `switch-output-${this.generateGuid()}`, // Use GUID
                            name: 'Output 2',
                            condition: ''
                        }
                    ])
                    : [],
                // Condition properties
                conditionType: node.data.conditionType || 'value_comparison',
                leftOperand: node.data.leftOperand || '',
                operator: node.data.operator || 'equals',
                rightOperand: node.data.rightOperand || '',
                conditionExpression: node.data.conditionExpression || '',
                trueLabel: node.data.trueLabel || 'True',
                falseLabel: node.data.falseLabel || 'False',
                minValue: node.data.minValue || 0,
                maxValue: node.data.maxValue || 100,
                expectedValue: node.data.expectedValue !== undefined ? node.data.expectedValue : true,
                // Delay properties
                delayType: node.data.delayType || 'fixed',
                delayValue: node.data.delayValue || 5,
                delayUnit: node.data.delayUnit || 'seconds',
                randomMin: node.data.randomMin || 1,
                randomMax: node.data.randomMax || 10,
                delayMessage: node.data.delayMessage || '',
                showProgress: node.data.showProgress !== undefined ? node.data.showProgress : true,
                delayExpression: node.data.delayExpression || '',
                // Documents properties
                document: node.data.document || [],
                selectedDocumentData: node.data.selectedDocumentData || [],
                enableSpecificDataSelection: node.data.enableSpecificDataSelection !== undefined ? node.data.enableSpecificDataSelection : false,
                documentVectorName: node.data.documentVectorName || '',
                textReplacement: node.data.textReplacement || [],
                topNChunks: node.data.topNChunks || 5,
                cosineSimilarity: node.data.cosineSimilarity || 0.7,
                // Database Integration properties
                tableName: node.data.tableName || null,
                sqlExtractionModel: node.data.sqlExtractionModel || null,
                promptSqlNoData: node.data.promptSqlNoData || '',
                // Products properties
                productGroupList: node.data.productGroupList || [],
                productTopNChunks: node.data.productTopNChunks || 5,
                productCosineSimilarity: node.data.productCosineSimilarity || 0.7,
                // Knowledge API properties
                knowledgeApiUrl: node.data.knowledgeApiUrl || '',
                knowledgeHttpMethod: node.data.knowledgeHttpMethod || 'GET',
                listHeaders: node.data.listHeaders || [
                    {
                        key: '',
                        value: '',
                    },
                ],
                knowledgeRequestBody: node.data.knowledgeRequestBody || '',
                responseDataPath: node.data.responseDataPath || '',
                // SharePoint properties
                sharepoint: {
                    siteUrl: node.data.sharepoint?.siteUrl || '',
                    tenantId: node.data.sharepoint?.tenantId || '',
                    clientId: node.data.sharepoint?.clientId || '',
                    clientSecret: node.data.sharepoint?.clientSecret || '',
                    folderPath: node.data.sharepoint?.folderPath || '',
                    filename: node.data.sharepoint?.filename || ''
                },
                // Email properties
                emailProvider: node.data.emailProvider || 'mailgun',
                toEmail: node.data.toEmail || '',
                ccEmails: node.data.ccEmails || [],
                bccEmails: node.data.bccEmails || [],
                subject: node.data.subject || '',
                emailTemplate: node.data.emailTemplate || 'custom',
                emailBody: node.data.emailBody || '',
                isHtml: node.data.isHtml !== undefined ? node.data.isHtml : false,
            };
            
            // Process smartIntent forbiddenWords to ensure empty item at the end (similar to AI model logic)
            if (node.data.type === 'smartIntent') {
                // Handle smartIntents array
                if (this.nodeInspector.smartIntents && this.nodeInspector.smartIntents.length > 0) {
                    this.nodeInspector.smartIntents.forEach(smartIntent => {
                        if (smartIntent.mainAction && smartIntent.mainAction.forbiddenWords) {
                            const lastItem = smartIntent.mainAction.forbiddenWords[smartIntent.mainAction.forbiddenWords.length - 1];
                            if (lastItem !== '') {
                                smartIntent.mainAction.forbiddenWords.push('');
                            }
                        } else if (smartIntent.mainAction) {
                            smartIntent.mainAction.forbiddenWords = [''];
                        }
                    });
                }
                
                // Handle smartIntentFallback forbiddenWords
                if (this.nodeInspector.smartIntentFallback && this.nodeInspector.smartIntentFallback.forbiddenWords) {
                    const lastItem = this.nodeInspector.smartIntentFallback.forbiddenWords[this.nodeInspector.smartIntentFallback.forbiddenWords.length - 1];
                    if (lastItem !== '') {
                        this.nodeInspector.smartIntentFallback.forbiddenWords.push('');
                    }
                } else if (this.nodeInspector.smartIntentFallback) {
                    this.nodeInspector.smartIntentFallback.forbiddenWords = [''];
                }
            }
            
            // Initialize provider dropdown for model nodes
            if (node.data.type === 'model' && this.nodeInspector.provider) {
                this.loadingModelConfig = true;
                this.changeProvider(this.nodeInspector.provider).then(() => {
                    this.loadingModelConfig = false;
                });
            }
            
            // Initialize provider dropdown for intentAI nodes
            if (node.data.type === 'intentAI' && this.nodeInspector.intentaiProvider) {
                this.loadingModelConfig = true;
                this.changeProvider(this.nodeInspector.intentaiProvider).then(() => {
                    this.loadingModelConfig = false;
                });
            }
            
            // Show inspector as floating panel instead of dialog
            // this.inspectorDialog = true;

            // Set initial expand state for document data selection if enabled
            if (node.data.type === 'documents' && this.nodeInspector.enableSpecificDataSelection) {
                this.documentDataExpanded = true;
            } else {
                this.documentDataExpanded = false;
            }

            // Check for validation warnings when node is selected
            this.checkNodeValidation(node);
        },
        validateSingleNode(node) {
            let hasWarning = false;

            // Validation for messageInput node
            if (node.data.type === 'messageInput') {
                const selectedModel = node.data.selectedModel;
                if (!selectedModel || node.data.selectedModel === undefined || (Array.isArray(selectedModel) && selectedModel.length === 0)) {
                    hasWarning = true;
                }
            }

            if (node.data.type === 'model') {
                // Check if provider and model are selected
                if (!node.data.provider || !node.data.model) {
                    hasWarning = true;
                }
            }

            if (node.data.type === 'intent') {
                // เช็คว่า Intent มีการกำหนด fallback intent หรือไม่
                let fallbackEdges = this.tempEdge.filter(edge => edge.source === node.id && edge.sourceHandle === 'intent-fallback');

                // เช็คว่า Intent มีการต่อไปยัง node intentAI หรือไม่ จาก output-bottom
                let intentAIEdges = this.tempEdge.filter(edge => edge.source === node.id && edge.sourceHandle === 'output-bottom');

                if (fallbackEdges.length === 0 || intentAIEdges.length === 0) {
                    hasWarning = true; // Missing fallback intent or intentAI connection
                }
            }

            if (node.data.type === 'smartIntent') {
                // เช็คว่า Smart Intent มีการกำหนด fallback intent หรือไม่
                let fallbackEdges = this.tempEdge.filter(edge => edge.source === node.id && edge.sourceHandle === 'smart-intent-fallback');

                // เช็คว่า Smart Intent มีการต่อไปยัง node intentAI หรือไม่ จาก output-bottom
                let intentAIEdges = this.tempEdge.filter(edge => edge.source === node.id && edge.sourceHandle === 'output-bottom');

                if (fallbackEdges.length === 0 || intentAIEdges.length === 0) {
                    hasWarning = true; // Missing fallback intent or intentAI connection
                }

                // Validate Smart Intent knowledge sources
                if (node.data.smartIntents && Array.isArray(node.data.smartIntents)) {
                    node.data.smartIntents.forEach(smartIntent => {
                        if (smartIntent.mainAction && smartIntent.mainAction.useAI) {
                            // Validate Documents
                            if (smartIntent.mainAction.enableDocuments) {
                                if (!smartIntent.mainAction.document || (Array.isArray(smartIntent.mainAction.document) && smartIntent.mainAction.document.length === 0)) {
                                    hasWarning = true;
                                }
                            }

                            // Validate Database Integration
                            if (smartIntent.mainAction.enableDatabaseIntegration) {
                                if (!smartIntent.mainAction.tableName) {
                                    hasWarning = true;
                                }
                            }

                            // Validate Products
                            if (smartIntent.mainAction.enableProducts) {
                                if (!smartIntent.mainAction.productGroupList || smartIntent.mainAction.productGroupList.length === 0) {
                                    hasWarning = true;
                                }
                            }

                            // Validate API Knowledge
                            if (smartIntent.mainAction.enableApiKnowledge) {
                                if (!smartIntent.mainAction.knowledgeApiUrl || smartIntent.mainAction.knowledgeApiUrl.trim() === '') {
                                    hasWarning = true;
                                }
                            }
                        }
                    });
                }

                // Validate Smart Intent Fallback knowledge sources
                if (node.data.smartIntentFallback && node.data.smartIntentFallback.useAI) {
                    // Validate Documents in Fallback
                    if (node.data.smartIntentFallback.enableDocuments) {
                        if (!node.data.smartIntentFallback.document || (Array.isArray(node.data.smartIntentFallback.document) && node.data.smartIntentFallback.document.length === 0)) {
                            hasWarning = true;
                        }
                    }

                    // Validate Database Integration in Fallback
                    if (node.data.smartIntentFallback.enableDatabaseIntegration) {
                        if (!node.data.smartIntentFallback.tableName) {
                            hasWarning = true;
                        }
                    }

                    // Validate Products in Fallback
                    if (node.data.smartIntentFallback.enableProducts) {
                        if (!node.data.smartIntentFallback.productGroupList || node.data.smartIntentFallback.productGroupList.length === 0) {
                            hasWarning = true;
                        }
                    }

                    // Validate API Knowledge in Fallback
                    if (node.data.smartIntentFallback.enableApiKnowledge) {
                        if (!node.data.smartIntentFallback.knowledgeApiUrl || node.data.smartIntentFallback.knowledgeApiUrl.trim() === '') {
                            hasWarning = true;
                        }
                    }
                }
            }

            if (node.data.type === 'intentAI') {
                // Check if provider and model are selected (use intentai properties only)
                if (!node.data.intentaiProvider || !node.data.intentaiModel) {
                    hasWarning = true;
                }
            }

            if (node.data.type === 'apiTrigger') {
                // Check if API URL is provided
                if (!node.data.apiUrl || node.data.apiUrl.trim() === '' || node.data.apiUrl === undefined) {
                    hasWarning = true;
                }
            }

            if (node.data.type === 'contactAgent') {
                if (!node.data.botDisableTime || node.data.botDisableTime == 0 || node.data.contactAgentMessage.trim() === '') {
                    hasWarning = true; // Invalid date format
                }
            }

            if (node.data.type === 'documents') {
                // Check if document vector name is provided
                if (!node.data.document || (Array.isArray(node.data.document) && node.data.document.length === 0)) {
                    hasWarning = true;
                }
            }

            if (node.data.type === 'databaseIntegration') {
                // Check if table name is provided
                if (!node.data.tableName) {
                    hasWarning = true;
                }
            }

            if (node.data.type === 'apiknowledge') {
                // Check if Knowledge API URL is provided
                if (!node.data.knowledgeApiUrl || node.data.knowledgeApiUrl.trim() === '' || node.data.knowledgeApiUrl === undefined) {
                    hasWarning = true;
                }
            }

            if (node.data.type === 'products') {
                // Check if product group list is provided
                if (!node.data.productGroupList || node.data.productGroupList.length === 0) {
                    hasWarning = true;
                }
            }

            if (node.data.type === 'sharepoint') {
                // Check required fields for SharePoint node
                if (!node.data.sharepoint || 
                    !node.data.sharepoint.siteUrl || node.data.sharepoint.siteUrl.trim() === '' ||
                    !node.data.sharepoint.tenantId || node.data.sharepoint.tenantId.trim() === '' ||
                    !node.data.sharepoint.clientId || node.data.sharepoint.clientId.trim() === '' ||
                    !node.data.sharepoint.clientSecret || node.data.sharepoint.clientSecret.trim() === '' ||
                    !node.data.sharepoint.filename || node.data.sharepoint.filename.trim() === '') {
                    hasWarning = true;
                }
            }

            if (node.data.type === 'email') {
                // Check required fields for Email node
                if (!node.data.toEmail || node.data.toEmail.trim() === '') {
                    hasWarning = true;
                } else {
                    // Validate email format
                    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                    if (!emailRegex.test(node.data.toEmail.trim())) {
                        hasWarning = true;
                    }
                }
                
                if (!node.data.subject || node.data.subject.trim() === '') {
                    hasWarning = true;
                }
                
                if (node.data.emailTemplate === 'custom' && (!node.data.emailBody || node.data.emailBody.trim() === '')) {
                    hasWarning = true;
                }

                if(!node.data.emailBody || node.data.emailBody.trim() === '' || node.data.emailBody.length > 10000) {
                    hasWarning = true;
                }
            }

            return hasWarning;
        },
        checkNodeValidation(node) {
            const hasWarning = this.validateSingleNode(node);

            this.$nextTick(() => {
                // Update node data directly if warning state changes
                if (node.data.showWarning !== hasWarning) {
                    const nodeIndex = this.tempNode.findIndex(n => n.id === node.id);
                    if (nodeIndex !== -1) {
                        this.tempNode[nodeIndex].data.showWarning = hasWarning;
                    }
                }
            });
        },
        checkAffectedNodesValidation(nodeIds) {
            // Check validation only for specified nodes
            nodeIds.forEach(nodeId => {
                const node = this.tempNode.find(n => n.id === nodeId);
                if (!node) return;

                const hasWarning = this.validateSingleNode(node);

                // Update node data directly if warning state changes
                if (node.data.showWarning !== hasWarning) {
                    const nodeIndex = this.tempNode.findIndex(n => n.id === node.id);
                    if (nodeIndex !== -1) {
                        this.tempNode[nodeIndex].data.showWarning = hasWarning;
                    }
                }
            });
        },
        checkAllNodesValidation() {
            // Check validation for all nodes in the flow
            this.tempNode.forEach(node => {
                const hasWarning = this.validateSingleNode(node);

                // Update node data directly if warning state changes
                if (node.data.showWarning !== hasWarning) {
                    const nodeIndex = this.tempNode.findIndex(n => n.id === node.id);
                    if (nodeIndex !== -1) {
                        this.tempNode[nodeIndex].data.showWarning = hasWarning;
                    }
                }
            });
        },
        closeInspector() {
            this.selectedNode = null;
            this.inspectorExpanded = false;
        },
        clearSelection() {
            // Clear all node selections
            this.tempNode.forEach(node => {
                node.selected = false;
            });
            this.selectedNodes = [];
            this.selectedNode = null;
        },
        // Real-time update methods
        updateNodeProperty(property, value) {
            if (!this.selectedNode) return;

            // Find the node in our tempNode array
            const nodeIndex = this.tempNode.findIndex(node => node.id === this.selectedNode.id);
            if (nodeIndex === -1) return;

            // Handle provider change
            // if (property === 'provider' || property === 'intentaiProvider') {
            //     // Skip if already loading or if the value hasn't changed
            //     if (this.loadingModelConfig || this.nodeInspector[property] === value) {
            //         console.log('Skipping provider change (already loading or unchanged):', value);
            //         return;
            //     }
            //     console.log('Changing provider to (updateNodeProperty):', value);
            //     this.loadingModelConfig = true;  // Set flag before calling
            //     this.changeProvider(value).finally(() => {
            //         this.loadingModelConfig = false;  // Reset flag after completion
            //     });
            // }

            // // Handle model change
            // if (property === 'model' || property === 'intentaiModel') {
            //     this.changeModel(value);
            // }

            // Update the node data immediately
            let updatedData;
            
            // Handle nested properties (e.g., sharepoint.siteUrl)
            if (property.includes('.')) {
                const [parentKey, childKey] = property.split('.');
                updatedData = {
                    ...this.tempNode[nodeIndex].data,
                    [parentKey]: {
                        ...this.tempNode[nodeIndex].data[parentKey],
                        [childKey]: value
                    }
                };
            } else {
                updatedData = {
                    ...this.tempNode[nodeIndex].data,
                    [property]: value
                };
            }

            // Replace the entire node to ensure reactivity
            this.tempNode = [
                ...this.tempNode.slice(0, nodeIndex),
                {
                    ...this.tempNode[nodeIndex],
                    data: updatedData
                },
                ...this.tempNode.slice(nodeIndex + 1)
            ];

            // Update selectedNode reference
            this.selectedNode = this.tempNode[nodeIndex];

            // Update nodeInspector to keep it in sync
            if (property.includes('.')) {
                const [parentKey, childKey] = property.split('.');
                if (!this.nodeInspector[parentKey]) {
                    this.nodeInspector[parentKey] = {};
                }
                this.nodeInspector[parentKey][childKey] = value;
            } else {
                this.nodeInspector[property] = value;
            }

            // Auto-assign apiVersion when model changes
            if (property === 'model') {
                this.$nextTick(() => {
                    const selectedModel = this.listDDModel.find(m => m.modelName === value);
                    if (selectedModel && this.nodeInspector.apiVersion !== selectedModel.apiVersion) {
                        this.nodeInspector.apiVersion = selectedModel.apiVersion;
                        if (this.selectedNode) {
                            this.selectedNode.data.apiVersion = selectedModel.apiVersion;
                            const nodeIndex = this.tempNode.findIndex(n => n.id === this.selectedNode.id);
                            if (nodeIndex !== -1) {
                                this.tempNode[nodeIndex].data.apiVersion = selectedModel.apiVersion;
                            }
                        }
                    }
                });
            }

            // Auto-assign intentAiModelApiVersion when intentaiModel changes
            if (property === 'intentaiModel') {
                this.$nextTick(() => {
                    const selectedModel = this.listDDModel.find(m => m.modelName === value);
                    if (selectedModel && this.nodeInspector.intentAiModelApiVersion !== selectedModel.apiVersion) {
                        this.nodeInspector.intentAiModelApiVersion = selectedModel.apiVersion;
                        if (this.selectedNode) {
                            this.selectedNode.data.intentAiModelApiVersion = selectedModel.apiVersion;
                            const nodeIndex = this.tempNode.findIndex(n => n.id === this.selectedNode.id);
                            if (nodeIndex !== -1) {
                                this.tempNode[nodeIndex].data.intentAiModelApiVersion = selectedModel.apiVersion;
                            }
                        }
                    }
                });
            }

            this.checkNodeValidation(this.tempNode[nodeIndex]);

            // Debounce history saving for property updates
            this.debouncedSavePropertyChange(property);
        },

        // Debounced method to save property changes to history
        debouncedSavePropertyChange(property) {
            // Clear existing timer
            if (this.propertyUpdateTimer) {
                clearTimeout(this.propertyUpdateTimer);
            }
            
            // Set new timer to save after 1 second of inactivity
            this.propertyUpdateTimer = setTimeout(() => {
                if (!this.isPerformingUndoRedo) {
                    this.saveStateToHistory(`Update ${property}`);
                }
            }, 1000);
        },
        updateValidatedNodeProperty(property, value, minValue = null, maxValue = null) {
            // Validate the value
            let validatedValue = value;

            // Convert to number if it's a string
            if (typeof value === 'string' && value !== '') {
                validatedValue = parseFloat(value);
            }

            // Check if it's a valid number
            if (isNaN(validatedValue)) {
                return; // Don't update if not a valid number
            }

            // Apply min/max constraints
            if (minValue !== null && validatedValue < minValue) {
                validatedValue = minValue;
            }
            if (maxValue !== null && validatedValue > maxValue) {
                validatedValue = maxValue;
            }

            // Round cosine similarity values to 4 decimal places
            if (property === 'cosineSimilarity' || property === 'productCosineSimilarity') {
                validatedValue = Math.round(validatedValue * 10000) / 10000;
            }

            // Update the node property with validated value
            this.updateNodeProperty(property, validatedValue);

            // Update the nodeInspector to reflect the validated value
            this.nodeInspector[property] = validatedValue;
        },

        // Validation methods for blur events
        validateAndFixTopNChunks(event) {
            const value = event.target.value;
            let validatedValue = value;

            // Convert to number if it's a string
            if (typeof value === 'string' && value !== '') {
                validatedValue = parseInt(value);
            }

            // Check if it's a valid number
            if (isNaN(validatedValue) || validatedValue < 1) {
                validatedValue = 1; // Default minimum value
            }

            // Update the property
            this.updateNodeProperty('topNChunks', validatedValue);
            this.nodeInspector.topNChunks = validatedValue;
        },

        validateAndFixCosineSimilarity(event) {
            const value = event.target.value;
            let validatedValue = value;

            // Convert to number if it's a string
            if (typeof value === 'string' && value !== '') {
                validatedValue = parseFloat(value);
            }

            // Check if it's a valid number and apply constraints
            if (isNaN(validatedValue) || validatedValue < 0.0001) {
                validatedValue = 0.0001; // Default minimum value
            }
            if (validatedValue > 1) {
                validatedValue = 1; // Default maximum value
            }

            // Round to 4 decimal places
            validatedValue = Math.round(validatedValue * 10000) / 10000;

            // Update the property
            this.updateNodeProperty('cosineSimilarity', validatedValue);
            this.nodeInspector.cosineSimilarity = validatedValue;
        },

        validateAndFixProductTopNChunks(event) {
            const value = event.target.value;
            let validatedValue = value;

            // Convert to number if it's a string
            if (typeof value === 'string' && value !== '') {
                validatedValue = parseInt(value);
            }

            // Check if it's a valid number
            if (isNaN(validatedValue) || validatedValue < 1) {
                validatedValue = 1; // Default minimum value
            }

            // Update the property
            this.updateNodeProperty('productTopNChunks', validatedValue);
            this.nodeInspector.productTopNChunks = validatedValue;
        },

        validateAndFixProductCosineSimilarity(event) {
            const value = event.target.value;
            let validatedValue = value;

            // Convert to number if it's a string
            if (typeof value === 'string' && value !== '') {
                validatedValue = parseFloat(value);
            }

            // Check if it's a valid number and apply constraints
            if (isNaN(validatedValue) || validatedValue < 0.0001) {
                validatedValue = 0.0001; // Default minimum value
            }
            if (validatedValue > 1) {
                validatedValue = 1; // Default maximum value
            }

            // Round to 4 decimal places
            validatedValue = Math.round(validatedValue * 10000) / 10000;

            // Update the property
            this.updateNodeProperty('productCosineSimilarity', validatedValue);
            this.nodeInspector.productCosineSimilarity = validatedValue;
        },

        // Validation methods for Smart Intent fields
        validateAndFixSmartIntentTopNChunks(event, smartIntentIndex) {
            const value = event.target.value;
            let validatedValue = value;

            // Convert to number if it's a string
            if (typeof value === 'string' && value !== '') {
                validatedValue = parseInt(value);
            }

            // Check if it's a valid number
            if (isNaN(validatedValue) || validatedValue < 1) {
                validatedValue = 1; // Default minimum value
            }

            // Update the nested property
            this.nodeInspector.smartIntents[smartIntentIndex].mainAction.topNChunks = validatedValue;
            this.updateSmartIntentProperty(smartIntentIndex, 'topNChunks', validatedValue);
        },

        validateAndFixSmartIntentCosineSimilarity(event, smartIntentIndex) {
            const value = event.target.value;
            let validatedValue = value;

            // Convert to number if it's a string
            if (typeof value === 'string' && value !== '') {
                validatedValue = parseFloat(value);
            }

            // Check if it's a valid number and apply constraints
            if (isNaN(validatedValue) || validatedValue < 0.0001) {
                validatedValue = 0.0001; // Default minimum value
            }
            if (validatedValue > 1) {
                validatedValue = 1; // Default maximum value
            }

            // Round to 4 decimal places
            validatedValue = Math.round(validatedValue * 10000) / 10000;

            // Update the nested property
            this.nodeInspector.smartIntents[smartIntentIndex].mainAction.cosineSimilarity = validatedValue;
            this.updateSmartIntentProperty(smartIntentIndex, 'cosineSimilarity', validatedValue);
        },

        validateAndFixSmartIntentProductTopNChunks(event, smartIntentIndex) {
            const value = event.target.value;
            let validatedValue = value;

            // Convert to number if it's a string
            if (typeof value === 'string' && value !== '') {
                validatedValue = parseInt(value);
            }

            // Check if it's a valid number
            if (isNaN(validatedValue) || validatedValue < 1) {
                validatedValue = 1; // Default minimum value
            }

            // Update the nested property
            this.nodeInspector.smartIntents[smartIntentIndex].mainAction.productTopNChunks = validatedValue;
            this.updateSmartIntentProperty(smartIntentIndex, 'productTopNChunks', validatedValue);
        },

        validateAndFixSmartIntentProductCosineSimilarity(event, smartIntentIndex) {
            const value = event.target.value;
            let validatedValue = value;

            // Convert to number if it's a string
            if (typeof value === 'string' && value !== '') {
                validatedValue = parseFloat(value);
            }

            // Check if it's a valid number and apply constraints
            if (isNaN(validatedValue) || validatedValue < 0.0001) {
                validatedValue = 0.0001; // Default minimum value
            }
            if (validatedValue > 1) {
                validatedValue = 1; // Default maximum value
            }

            // Round to 4 decimal places
            validatedValue = Math.round(validatedValue * 10000) / 10000;

            // Update the nested property
            this.nodeInspector.smartIntents[smartIntentIndex].mainAction.productCosineSimilarity = validatedValue;
            this.updateSmartIntentProperty(smartIntentIndex, 'productCosineSimilarity', validatedValue);
        },

        // Validation methods for Smart Intent Fallback fields
        validateAndFixSmartIntentFallbackProductTopNChunks(event) {
            const value = event.target.value;
            let validatedValue = value;

            // Convert to number if it's a string
            if (typeof value === 'string' && value !== '') {
                validatedValue = parseInt(value);
            }

            // Check if it's a valid number
            if (isNaN(validatedValue) || validatedValue < 1) {
                validatedValue = 1; // Default minimum value
            }

            // Update the nested property
            this.nodeInspector.smartIntentFallback.productTopNChunks = validatedValue;
            this.updateSmartIntentFallbackProperty('productTopNChunks', validatedValue);
        },

        validateAndFixSmartIntentFallbackProductCosineSimilarity(event) {
            const value = event.target.value;
            let validatedValue = value;

            // Convert to number if it's a string
            if (typeof value === 'string' && value !== '') {
                validatedValue = parseFloat(value);
            }

            // Check if it's a valid number and apply constraints
            if (isNaN(validatedValue) || validatedValue < 0.0001) {
                validatedValue = 0.0001; // Default minimum value
            }
            if (validatedValue > 1) {
                validatedValue = 1; // Default maximum value
            }

            // Round to 4 decimal places
            validatedValue = Math.round(validatedValue * 10000) / 10000;

            // Update the nested property
            this.nodeInspector.smartIntentFallback.productCosineSimilarity = validatedValue;
            this.updateSmartIntentFallbackProperty('productCosineSimilarity', validatedValue);
        },

        // Additional validation methods for Smart Intent Fallback Documents fields
        validateAndFixSmartIntentFallbackTopNChunks(event) {
            const value = event.target.value;
            let validatedValue = value;

            // Convert to number if it's a string
            if (typeof value === 'string' && value !== '') {
                validatedValue = parseInt(value);
            }

            // Check if it's a valid number
            if (isNaN(validatedValue) || validatedValue < 1) {
                validatedValue = 1; // Default minimum value
            }

            // Update the nested property
            this.nodeInspector.smartIntentFallback.topNChunks = validatedValue;
            this.updateSmartIntentFallbackProperty('topNChunks', validatedValue);
        },

        validateAndFixSmartIntentFallbackCosineSimilarity(event) {
            const value = event.target.value;
            let validatedValue = value;

            // Convert to number if it's a string
            if (typeof value === 'string' && value !== '') {
                validatedValue = parseFloat(value);
            }

            // Check if it's a valid number and apply constraints
            if (isNaN(validatedValue) || validatedValue < 0.0001) {
                validatedValue = 0.0001; // Default minimum value
            }
            if (validatedValue > 1) {
                validatedValue = 1; // Default maximum value
            }

            // Round to 4 decimal places
            validatedValue = Math.round(validatedValue * 10000) / 10000;

            // Update the nested property
            this.nodeInspector.smartIntentFallback.cosineSimilarity = validatedValue;
            this.updateSmartIntentFallbackProperty('cosineSimilarity', validatedValue);
        },

        // Validation method specifically for Smart Intent properties
        updateValidatedSmartIntentProperty(smartIntentIndex, property, value, minValue = null, maxValue = null) {
            // Validate the value
            let validatedValue = value;

            // Convert to number if it's a string
            if (typeof value === 'string' && value !== '') {
                validatedValue = parseFloat(value);
            }

            // Check if it's a valid number
            if (isNaN(validatedValue)) {
                return; // Don't update if not a valid number
            }

            // Apply min/max constraints
            if (minValue !== null && validatedValue < minValue) {
                validatedValue = minValue;
            }
            if (maxValue !== null && validatedValue > maxValue) {
                validatedValue = maxValue;
            }

            // Round cosine similarity values to 4 decimal places
            if (property === 'cosineSimilarity' || property === 'productCosineSimilarity') {
                validatedValue = Math.round(validatedValue * 10000) / 10000;
            }

            // Update the smart intent property with validated value
            this.nodeInspector.smartIntents[smartIntentIndex].mainAction[property] = validatedValue;
            
            // Call updateSmartIntentData to save the changes and trigger validation
            this.updateSmartIntentData();
        },

        // Validation method specifically for Smart Intent Fallback properties
        updateValidatedSmartIntentFallbackProperty(property, value, minValue = null, maxValue = null) {
            // Validate the value
            let validatedValue = value;

            // Convert to number if it's a string
            if (typeof value === 'string' && value !== '') {
                validatedValue = parseFloat(value);
            }

            // Check if it's a valid number
            if (isNaN(validatedValue)) {
                return; // Don't update if not a valid number
            }

            // Apply min/max constraints
            if (minValue !== null && validatedValue < minValue) {
                validatedValue = minValue;
            }
            if (maxValue !== null && validatedValue > maxValue) {
                validatedValue = maxValue;
            }

            // Round cosine similarity values to 4 decimal places
            if (property === 'cosineSimilarity' || property === 'productCosineSimilarity') {
                validatedValue = Math.round(validatedValue * 10000) / 10000;
            }

            // Update the smart intent fallback property with validated value
            this.nodeInspector.smartIntentFallback[property] = validatedValue;
            
            // Call updateSmartIntentFallbackData to save the changes and trigger validation
            this.updateSmartIntentFallbackData();
        },

        // Special validation for Bot Disable Time
        validateAndUpdateBotDisableTime(value) {
            // Allow empty value (bot remains active)
            if (!value || value === '') {
                this.updateNodeProperty('botDisableTime', '');
                this.nodeInspector.botDisableTime = '';
                return;
            }

            // Convert to number
            let numValue = parseFloat(value);

            // Check if it's a valid number
            if (isNaN(numValue)) {
                return; // Don't update if not a valid number
            }

            // Ensure minimum value is 1
            if (numValue < 1) {
                numValue = 1;
                // Show warning to user
                this.showWarning('Bot Disable Time must be at least 1 minute');
            }

            // Update with validated value
            this.updateNodeProperty('botDisableTime', numValue);
            this.nodeInspector.botDisableTime = numValue;
        },
        updateIntentData() {
            if (!this.selectedNode || this.selectedNode.data.type !== 'intent') return;

            this.updateNodeProperty('intents', this.nodeInspector.intents);
        },
        // API Trigger methods
        updateHeaderProperty(index, property, value) {
            if (!this.nodeInspector.listHeadersAPITrigger[index]) return;
            this.nodeInspector.listHeadersAPITrigger[index][property] = value;
            this.updateNodeProperty('listHeadersAPITrigger', this.nodeInspector.listHeadersAPITrigger);
        },
        updateValidationProperty(index, property, value) {
            if (!this.nodeInspector.listValidation[index]) return;
            this.nodeInspector.listValidation[index][property] = value;
            this.updateNodeProperty('listValidation', this.nodeInspector.listValidation);
        },
        addHeader() {
            if (!this.nodeInspector.listHeadersAPITrigger) {
                this.nodeInspector.listHeadersAPITrigger = [];
            }
            this.nodeInspector.listHeadersAPITrigger.push({
                key: '',
                value: '',
            });
            this.updateNodeProperty('listHeadersAPITrigger', this.nodeInspector.listHeadersAPITrigger);
        },
        addValidation() {
            if (!this.nodeInspector.listValidation) {
                this.nodeInspector.listValidation = [];
            }
            this.nodeInspector.listValidation.push({
                key: '',
                typeValidation: '',
                numberOfCharacter: '',
                regExp: '',
                promptMessage: '',
            });
            this.updateNodeProperty('listValidation', this.nodeInspector.listValidation);
        },
        deleteHeader(index) {
            if (this.nodeInspector.listHeadersAPITrigger && this.nodeInspector.listHeadersAPITrigger.length > 0) {
                this.nodeInspector.listHeadersAPITrigger.splice(index, 1);
                this.updateNodeProperty('listHeadersAPITrigger', this.nodeInspector.listHeadersAPITrigger);
            }
        },
        deleteValidation(index) {
            if (this.nodeInspector.listValidation && this.nodeInspector.listValidation.length > 0) {
                this.nodeInspector.listValidation.splice(index, 1);
                this.updateNodeProperty('listValidation', this.nodeInspector.listValidation);
            }
        },
        // Knowledge API Header methods
        updateKnowledgeAPIHeaderProperty(index, property, value) {
            if (!this.nodeInspector.listHeaders[index]) return;
            this.nodeInspector.listHeaders[index][property] = value;
            this.updateNodeProperty('listHeaders', this.nodeInspector.listHeaders);
        },
        addKnowledgeAPIHeader() {
            if (!this.nodeInspector.listHeaders) {
                this.nodeInspector.listHeaders = [];
            }
            this.nodeInspector.listHeaders.push({
                key: '',
                value: '',
            });
            this.updateNodeProperty('listHeaders', this.nodeInspector.listHeaders);
        },
        deleteKnowledgeAPIHeader(index) {
            if (this.nodeInspector.listHeaders && this.nodeInspector.listHeaders.length > 0) {
                this.nodeInspector.listHeaders.splice(index, 1);
                this.updateNodeProperty('listHeaders', this.nodeInspector.listHeaders);
            }
        },
        // Get category color
        getCategoryColor(category) {
            const colors = {
                'System': 'blue',
                'User': 'green',
                'Session': 'purple',
                'Input': 'orange',
                'Context': 'teal'
            };
            return colors[category] || 'grey';
        },
        // Utility methods for node type styling
        getNodeTypeColor(type) {
            const colors = {
                'messageInput': 'green',
                'model': 'blue',
                'guardrailAI': 'green',
                'intentAI': 'purple',
                'intentClassifier': 'purple',
                'intent': 'purple',
                'smartIntent': 'purple',
                'apiTrigger': 'blue',
                'contactAgent': 'red',
                'switch': 'red',
                'condition': 'green',
                'delay': 'blue',
                'documents': 'blue',
                'api': 'amber',
                'response': 'red',
                'databaseIntegration': 'green',
                'apiknowledge': 'indigo',
                'products': 'amber',
                'webhook': 'green',
                'email': 'red',
                'sharepoint': 'green',
            };
            return colors[type] || 'grey';
        },
        getNodeTypeIcon(type) {
            const icons = {
                'messageInput': 'mdi-chat',
                'model': 'mdi-robot',
                'guardrailAI': 'mdi-shield-check',
                'intentAI': 'mdi-target-variant',
                'intentClassifier': 'mdi-sort-variant',
                'intent': 'mdi-crosshairs-question',
                'smartIntent': 'mdi-robot-confused',
                'apiTrigger': 'mdi-rocket-launch',
                'contactAgent': 'mdi-account-supervisor',
                'switch': 'mdi-arrow-decision',
                'condition': 'mdi-swap-horizontal-bold',
                'delay': 'mdi-timer',
                'documents': 'mdi-file-document-multiple',
                'api': 'mdi-api',
                'response': 'mdi-message-reply-text',
                'databaseIntegration': 'mdi-database-search',
                'apiknowledge': 'mdi-cloud',
                'products': 'mdi-package-variant',
                'webhook': 'mdi-webhook',
                'email': 'mdi-email',
                'sharepoint': 'mdi-microsoft-sharepoint',
            };
            return icons[type] || 'mdi-circle';
        },
        getNodeTypeLabel(type) {
            const labels = {
                'messageInput': 'Message Input',
                'model': 'AI Model',
                'guardrailAI': 'Guardrail AI',
                'intentAI': 'Intent AI Model',
                'intentClassifier': 'Intent Classifier',
                'intent': 'Intent',
                'apiTrigger': 'API Trigger',
                'contactAgent': 'Contact Agent',
                'switch': 'Switch',
                'condition': 'Condition',
                'delay': 'Delay',
                'documents': 'Documents',
                'databaseIntegration': 'Database Integration',
                'apiknowledge': 'Knowledge API',
                'products': 'Products'
            };
            return labels[type] || type;
        },
        // Forbidden words management methods (ลอกมาจาก ModelManagement.vue)
        isEnterForbidden(evt, index) {
            let charCode = evt.which ? evt.which : evt.keyCode;
            if (charCode === 13) {
                evt.preventDefault();
            } else {
                this.checkNewForbiddenWord(index);
                return true;
            }
        },
        checkNewForbiddenWord(index) {
            // Initialize forbiddenWords array if it doesn't exist
            if (!this.nodeInspector.forbiddenWords) {
                this.nodeInspector.forbiddenWords = [''];
            }

            // Add new empty word if we're at the last index
            if (this.nodeInspector.forbiddenWords.length === index + 1) {
                this.nodeInspector.forbiddenWords.push('');
                this.updateNodeProperty('forbiddenWords', this.nodeInspector.forbiddenWords);
            }
            return true;
        },
        checkDeleteForbiddenWord(index, value) {
            if (!this.nodeInspector.forbiddenWords) return;
            if (value === '' && this.nodeInspector.forbiddenWords.length > 1) {
                this.nodeInspector.forbiddenWords.splice(index, 1);
                this.updateNodeProperty('forbiddenWords', this.nodeInspector.forbiddenWords);
            }
        },
        updateForbiddenWordProperty(index, value) {
            // Initialize forbiddenWords array if it doesn't exist
            if (!this.nodeInspector.forbiddenWords) {
                this.nodeInspector.forbiddenWords = [''];
            }

            // Ensure the array has enough elements
            while (this.nodeInspector.forbiddenWords.length <= index) {
                this.nodeInspector.forbiddenWords.push('');
            }

            this.nodeInspector.forbiddenWords[index] = value;
            this.updateNodeProperty('forbiddenWords', this.nodeInspector.forbiddenWords);
        },
        removeForbiddenWordDirect(index) {
            if (!this.nodeInspector.forbiddenWords || this.nodeInspector.forbiddenWords.length <= 1) return;
            this.nodeInspector.forbiddenWords.splice(index, 1);
            this.updateNodeProperty('forbiddenWords', this.nodeInspector.forbiddenWords);
        },
        // เก็บ methods เดิมไว้เผื่อใช้ที่อื่น
        updateForbiddenWord(index, value) {
            this.updateForbiddenWordProperty(index, value);
            this.checkDeleteForbiddenWord(index, value);
        },
        removeForbiddenWord(index) {
            this.removeForbiddenWordDirect(index);
        },
        
        // Smart Intent Forbidden words management methods
        updateSmartIntentForbiddenWord(smartIntentIndex, index, value) {
            const smartIntent = this.nodeInspector.smartIntents[smartIntentIndex];
            if (!smartIntent.mainAction) {
                smartIntent.mainAction = {};
            }
            if (!smartIntent.mainAction.forbiddenWords) {
                smartIntent.mainAction.forbiddenWords = [''];
            }
            
            // Ensure the array has enough elements
            while (smartIntent.mainAction.forbiddenWords.length <= index) {
                smartIntent.mainAction.forbiddenWords.push('');
            }

            smartIntent.mainAction.forbiddenWords[index] = value;
            this.updateSmartIntentData();
        },
        
        removeSmartIntentForbiddenWord(smartIntentIndex, index) {
            const smartIntent = this.nodeInspector.smartIntents[smartIntentIndex];
            if (!smartIntent.mainAction?.forbiddenWords || smartIntent.mainAction.forbiddenWords.length <= 1) return;
            smartIntent.mainAction.forbiddenWords.splice(index, 1);
            this.updateSmartIntentData();
        },
        
        isEnterSmartIntentForbidden(evt, smartIntentIndex, index) {
            let charCode = evt.which ? evt.which : evt.keyCode;
            if (charCode === 13) {
                evt.preventDefault();
            } else {
                this.checkNewSmartIntentForbiddenWord(smartIntentIndex, index);
                return true;
            }
        },
        
        checkNewSmartIntentForbiddenWord(smartIntentIndex, index) {
            const smartIntent = this.nodeInspector.smartIntents[smartIntentIndex];
            if (!smartIntent.mainAction) {
                smartIntent.mainAction = {};
            }
            if (!smartIntent.mainAction.forbiddenWords) {
                smartIntent.mainAction.forbiddenWords = [''];
            }

            // Add new empty word if we're at the last index
            if (smartIntent.mainAction.forbiddenWords.length === index + 1) {
                smartIntent.mainAction.forbiddenWords.push('');
                this.updateSmartIntentData();
            }
            return true;
        },
        
        checkDeleteSmartIntentForbiddenWord(smartIntentIndex, index, value) {
            const smartIntent = this.nodeInspector.smartIntents[smartIntentIndex];
            if (!smartIntent.mainAction?.forbiddenWords) return;
            if (value === '' && smartIntent.mainAction.forbiddenWords.length > 1) {
                smartIntent.mainAction.forbiddenWords.splice(index, 1);
                this.updateSmartIntentData();
            }
        },
        
        updateSmartIntentForbiddenWordProperty(smartIntentIndex, index, value) {
            const smartIntent = this.nodeInspector.smartIntents[smartIntentIndex];
            if (!smartIntent.mainAction) {
                smartIntent.mainAction = {};
            }
            if (!smartIntent.mainAction.forbiddenWords) {
                smartIntent.mainAction.forbiddenWords = [''];
            }

            // Ensure the array has enough elements
            while (smartIntent.mainAction.forbiddenWords.length <= index) {
                smartIntent.mainAction.forbiddenWords.push('');
            }

            smartIntent.mainAction.forbiddenWords[index] = value;
            
            // Add new empty word if we're typing in the last field and it's not empty
            if (smartIntent.mainAction.forbiddenWords.length === index + 1 && value !== '') {
                smartIntent.mainAction.forbiddenWords.push('');
            }
            
            this.updateSmartIntentData();
        },
        
        removeSmartIntentForbiddenWordDirect(smartIntentIndex, index) {
            const smartIntent = this.nodeInspector.smartIntents[smartIntentIndex];
            if (!smartIntent.mainAction?.forbiddenWords || smartIntent.mainAction.forbiddenWords.length <= 1) return;
            smartIntent.mainAction.forbiddenWords.splice(index, 1);
            this.updateSmartIntentData();
        },
        
        // Smart Intent Fallback Forbidden words management methods
        updateSmartIntentFallbackForbiddenWord(index, value) {
            if (!this.nodeInspector.smartIntentFallback.forbiddenWords) {
                this.nodeInspector.smartIntentFallback.forbiddenWords = [''];
            }
            
            // Ensure the array has enough elements
            while (this.nodeInspector.smartIntentFallback.forbiddenWords.length <= index) {
                this.nodeInspector.smartIntentFallback.forbiddenWords.push('');
            }

            this.nodeInspector.smartIntentFallback.forbiddenWords[index] = value;
            this.updateSmartIntentFallbackData();
        },
        
        removeSmartIntentFallbackForbiddenWord(index) {
            if (!this.nodeInspector.smartIntentFallback.forbiddenWords || this.nodeInspector.smartIntentFallback.forbiddenWords.length <= 1) return;
            this.nodeInspector.smartIntentFallback.forbiddenWords.splice(index, 1);
            this.updateSmartIntentFallbackData();
        },
        
        isEnterSmartIntentFallbackForbidden(evt, index) {
            let charCode = evt.which ? evt.which : evt.keyCode;
            if (charCode === 13) {
                evt.preventDefault();
            } else {
                this.checkNewSmartIntentFallbackForbiddenWord(index);
                return true;
            }
        },
        
        checkNewSmartIntentFallbackForbiddenWord(index) {
            if (!this.nodeInspector.smartIntentFallback.forbiddenWords) {
                this.nodeInspector.smartIntentFallback.forbiddenWords = [''];
            }

            // Add new empty word if we're at the last index
            if (this.nodeInspector.smartIntentFallback.forbiddenWords.length === index + 1) {
                this.nodeInspector.smartIntentFallback.forbiddenWords.push('');
            }
        },
        
        checkDeleteSmartIntentFallbackForbiddenWord(index, value) {
            if (!value && this.nodeInspector.smartIntentFallback.forbiddenWords && this.nodeInspector.smartIntentFallback.forbiddenWords.length > 1) {
                this.removeSmartIntentFallbackForbiddenWord(index);
            }
        },
        
        // IntentAI Forbidden words management methods
        isEnterIntentaiForbidden(evt, index) {
            let charCode = evt.which ? evt.which : evt.keyCode;
            if (charCode === 13) {
                evt.preventDefault();
            } else {
                this.checkNewIntentaiForbiddenWord(index);
                return true;
            }
        },
        checkNewIntentaiForbiddenWord(index) {
            // Initialize intentaiForbiddenWords array if it doesn't exist
            if (!this.nodeInspector.intentaiForbiddenWords) {
                this.nodeInspector.intentaiForbiddenWords = [''];
            }

            // Add new empty word if we're at the last index
            if (this.nodeInspector.intentaiForbiddenWords.length === index + 1) {
                this.nodeInspector.intentaiForbiddenWords.push('');
                this.updateNodeProperty('intentaiForbiddenWords', this.nodeInspector.intentaiForbiddenWords);
            }
            return true;
        },
        checkDeleteIntentaiForbiddenWord(index, value) {
            if (!this.nodeInspector.intentaiForbiddenWords) return;
            if (value === '' && this.nodeInspector.intentaiForbiddenWords.length > 1) {
                this.nodeInspector.intentaiForbiddenWords.splice(index, 1);
                this.updateNodeProperty('intentaiForbiddenWords', this.nodeInspector.intentaiForbiddenWords);
            }
        },
        updateIntentaiForbiddenWordProperty(index, value) {
            // Initialize intentaiForbiddenWords array if it doesn't exist
            if (!this.nodeInspector.intentaiForbiddenWords) {
                this.nodeInspector.intentaiForbiddenWords = [''];
            }

            // Ensure the array has enough elements
            while (this.nodeInspector.intentaiForbiddenWords.length <= index) {
                this.nodeInspector.intentaiForbiddenWords.push('');
            }

            this.nodeInspector.intentaiForbiddenWords[index] = value;
            this.updateNodeProperty('intentaiForbiddenWords', this.nodeInspector.intentaiForbiddenWords);
        },
        removeIntentaiForbiddenWordDirect(index) {
            if (!this.nodeInspector.intentaiForbiddenWords || this.nodeInspector.intentaiForbiddenWords.length <= 1) return;
            this.nodeInspector.intentaiForbiddenWords.splice(index, 1);
            this.updateNodeProperty('intentaiForbiddenWords', this.nodeInspector.intentaiForbiddenWords);
        },
        // Provider and Model management methods
        async changeProvider(value) {
            return new Promise((resolve) => {
                console.log('Provider changed to:', value);
                if (!value) {
                    resolve();
                    return;
                }

                if (value == "Google" || value == "Anthropic" || value == "Azure") {
                    this.DDModelShow = this.listDDModel.filter(
                        (e) => e.provider == value
                    );
                } else {
                    this.DDModelShow = this.listDDModel.filter(
                        (e) => e.provider == value
                    );
                }

                // Clear model when provider changes (only if not loading from model config)
                if (!this.loadingModelConfig) {
                    // Check if this is intentAI node
                    if (this.selectedNode && this.selectedNode.data.type === 'intentAI') {
                        if (this.nodeInspector.intentaiModel) {
                            this.nodeInspector.intentaiModel = null;
                            this.updateNodeProperty('intentaiModel', null);
                        }
                    } else {
                        // For other node types
                        if (this.nodeInspector.model) {
                            this.nodeInspector.model = null;
                            this.updateNodeProperty('model', null);
                        }
                    }
                }

                // Give Vue time to update the dropdown
                this.$nextTick(() => {
                    resolve();
                });
            });
        },
        async changeModel(value) {
            if (!value) {
                return;
            }

            // Check if this is intentAI node and get the correct property
            const isIntentAI = this.selectedNode && this.selectedNode.data.type === 'intentAI';
            
            let ddm = this.listDDModel.find((e) => e.modelName == value);
            if (ddm) {
                // Update the correct property based on node type
                if (isIntentAI) {
                    this.nodeInspector.intentaiModel = value;
                } else {
                    this.nodeInspector.model = value;
                }
            }
        },
        toggleLockStatus() {
            if (this.$refs.vueFlow) {
                const flow = this.$refs.vueFlow;

                // Get current state - if nodesDraggable is undefined, default to true (unlocked)
                const isCurrentlyLocked = flow.nodesDraggable === false;
                const shouldLock = !isCurrentlyLocked; // Toggle the state
                // Apply new state
                flow.nodesDraggable = !shouldLock;
                flow.nodesConnectable = !shouldLock;
                flow.elementsSelectable = !shouldLock;
                flow.zoomOnScroll = !shouldLock;
                flow.zoomOnDoubleClick = !shouldLock;
                // flow.panOnDrag = !shouldLock ? [1, 2] : false;

                // Update interactiveControls to match lock status
                this.interactiveControls = !shouldLock;

                // Force Vue to re-render
                this.$forceUpdate();
            } else {
                console.warn('VueFlow ref not available');
            }
        },
        onEdgeMouseEnter({ edge: hoveredEdge }) {
            hoveredEdge.style = { strokeWidth: 1.6 };
        },
        onEdgeMouseLeave({ edge: hoveredEdge }) {
            hoveredEdge.style = { strokeWidth: 1.5 };
        },

        // Handle clicking on the pane (background) to clear selection
        onPaneClick() {
            // Clear node selection and close inspector
            this.selectedNodes = [];
            this.selectedNode = null;
            this.closeInspector();
        },

        // Node drag event handlers for undo/redo
        onNodeDragStart({ nodes }) {
            this.isDraggingNodes = true;
            // Store initial positions of all selected nodes
            this.dragStartPositions = {};
            nodes.forEach(node => {
                this.dragStartPositions[node.id] = {
                    x: node.position.x,
                    y: node.position.y
                };
            });
        },

        onNodeDragStop({ nodes }) {
            this.isDraggingNodes = false;
            
            // Check if any node actually moved
            let hasMovement = false;
            for (const node of nodes) {
                const startPos = this.dragStartPositions[node.id];
                if (startPos && (Math.abs(startPos.x - node.position.x) > 1 || Math.abs(startPos.y - node.position.y) > 1)) {
                    hasMovement = true;
                    break;
                }
            }
            
            // Only save to history if there was actual movement
            if (hasMovement && !this.isPerformingUndoRedo) {
                const nodeCount = nodes.length;
                this.saveStateToHistory(`Move ${nodeCount} Node${nodeCount > 1 ? 's' : ''}`);
            }
            
            // Clear stored positions
            this.dragStartPositions = {};
        },
        // Optimized node changes to reduce re-renders & expensive work during drag
        onNodesChange(changes) {
            // Build index map once (O(n)) and reuse (O(1) lookups)
            const indexMap = new Map(this.tempNode.map((n, i) => [n.id, i]));

            let anySelectionChanged = false;

            changes.forEach(change => {
                if (change.type === 'position' && change.position) {
                    const nodeIndex = indexMap.get(change.id);
                    if (nodeIndex !== undefined) {
                        const node = this.tempNode[nodeIndex];
                        // Mutate position directly (cheaper than replacing whole node)
                        node.position.x = change.position.x;
                        node.position.y = change.position.y;
                    }
                } else if (change.type === 'remove') {
                    this.tempNode = this.tempNode.filter(node => node.id !== change.id);
                    this.tempEdge = this.tempEdge.filter(edge => edge.source !== change.id && edge.target !== change.id);
                } else if (change.type === 'select') {
                    const nodeIndex = indexMap.get(change.id);
                    if (nodeIndex !== undefined) {
                        this.tempNode[nodeIndex].selected = change.selected;
                        anySelectionChanged = true;
                    }
                }
            });

            if (anySelectionChanged) {
                this.selectedNodes = this.tempNode.filter(node => node.selected);
            }
        },
        onEdgesChange(changes) {
            // Collect affected node IDs from edge changes
            const affectedNodeIds = new Set();

            changes.forEach(change => {
                if (change.type === 'add') {
                    // Handle new edge
                    this.tempEdge.push(change.item);
                    affectedNodeIds.add(change.item.source);
                    affectedNodeIds.add(change.item.target);
                } else if (change.type === 'remove') {
                    // Find the edge before removing to get source/target
                    const edgeToRemove = this.tempEdge.find(edge => edge.id === change.id);
                    if (edgeToRemove) {
                        affectedNodeIds.add(edgeToRemove.source);
                        affectedNodeIds.add(edgeToRemove.target);
                    }
                    // Handle edge removal
                    this.tempEdge = this.tempEdge.filter(edge => edge.id !== change.id);
                }
            });

            // Check validation only for affected nodes
            if (affectedNodeIds.size > 0) {
                this.$nextTick(() => {
                    this.checkAffectedNodesValidation(Array.from(affectedNodeIds));
                });
            }
        },
        onConnect(connection) {

            // Validation rules for connections
            const validationResult = this.validateConnection(connection);
            if (!validationResult.isValid) {
                this.showWarning(validationResult.message);
                return;
            }

            // Check if edge already exists to prevent duplicates
            const existingEdge = this.tempEdge.find(edge =>
                edge.source === connection.source &&
                edge.target === connection.target &&
                edge.sourceHandle === connection.sourceHandle &&
                edge.targetHandle === connection.targetHandle
            );

            if (existingEdge) {
                console.warn('Edge already exists between these nodes and handles');
                this.showWarning('Connection already exists between these nodes');
                return;
            }

            // Create new edge
            const newEdge = {
                id: `e${connection.source}-${connection.target}-${this.generateGuid()}`,
                source: connection.source,
                target: connection.target,
                sourceHandle: connection.sourceHandle,
                targetHandle: connection.targetHandle,
                type: 'flow-edge',
                markerEnd: MarkerType.ArrowClosed,
                animated: false,
                showButton: false
            };

            this.tempEdge.push(newEdge);

            // Save state to history
            const sourceNode = this.tempNode.find(node => node.id === connection.source);
            const targetNode = this.tempNode.find(node => node.id === connection.target);
            const sourceName = sourceNode ? sourceNode.data.label || 'Node' : 'Node';
            const targetName = targetNode ? targetNode.data.label || 'Node' : 'Node';
            this.saveStateToHistory(`Connect ${sourceName} → ${targetName}`);

            // Check validation only for source and target nodes
            this.$nextTick(() => {
                this.checkAffectedNodesValidation([connection.source, connection.target]);
            });
        },
        validateConnection(connection) {
            const sourceNode = this.tempNode.find(node => node.id === connection.source);
            const targetNode = this.tempNode.find(node => node.id === connection.target);

            if (!sourceNode || !targetNode) {
                return {
                    isValid: false,
                    message: 'Source or target node not found'
                };
            }

            // Rule 1: ห้ามเชื่อมต่อกับตัวเอง
            if (connection.source === connection.target) {
                return {
                    isValid: false,
                    message: 'Cannot connect node to itself'
                };
            }

            // Rule 2: ห้ามลากจาก input handle
            if (connection.sourceHandle === 'input') {
                return {
                    isValid: false,
                    message: 'Cannot drag from input handle. Input handles can only receive connections.'
                };
            }

            // Rule 3: ห้ามลากไป output handle
            if (connection.targetHandle && connection.targetHandle !== 'input') {
                return {
                    isValid: false,
                    message: 'Cannot connect to output handle. Output handles can only send connections.'
                };
            }

            // Rule 4: ตรวจสอบการเชื่อมต่อตาม node type
            const sourceNodeType = sourceNode.data.type;
            const targetNodeType = targetNode.data.type;

            // Rule 4a: Message Input node ต้องเป็น start node (ไม่รับ input)
            if (targetNodeType === 'messageInput') {
                return {
                    isValid: false,
                    message: 'Message Input node cannot receive connections as it is a start node'
                };
            }

            // Rule 4b: Response node ต้องเป็น end node (ไม่ส่ง output ต่อ)
            if (sourceNodeType === 'response') {
                return {
                    isValid: false,
                    message: 'Response node cannot send connections as it is an end node'
                };
            }

            // Rule 5: ตรวจสอบ handle types ที่เฉพาะเจาะจง
            const handleValidation = this.validateHandleTypes(sourceNode, targetNode, connection);
            if (!handleValidation.isValid) {
                return handleValidation;
            }

            // Rule 6: ห้ามสร้าง circular reference
            const circularValidation = this.checkCircularReference(connection);
            if (!circularValidation.isValid) {
                return circularValidation;
            }

            // Rule 7: จำกัดจำนวน input connections
            const inputLimitValidation = this.validateInputLimit(sourceNode, targetNode, connection);
            if (!inputLimitValidation.isValid) {
                return inputLimitValidation;
            }

            // Rule 8: จำกัดจำนวน output connections สำหรับ messageInput node
            const outputLimitValidation = this.validateOutputLimit(sourceNode, targetNode, connection);
            if (!outputLimitValidation.isValid) {
                return outputLimitValidation;
            }

            return { isValid: true };
        },
        // Validate specific handle types based on node types
        validateHandleTypes(sourceNode, targetNode, connection) {
            const sourceType = sourceNode.data.type;
            const targetType = targetNode.data.type;
            const sourceHandle = connection.sourceHandle;
            const targetHandle = connection.targetHandle;

            if (sourceType === 'messageInput') {
                const allowedTargets = ['model', 'intent', 'smartIntent'];
                if (!allowedTargets.includes(targetType)) {
                    return {
                        isValid: false,
                        message: 'Message Input node can only connect to AI Model nodes or Intent Handler nodes'
                    };
                }

            }

            if (sourceType === 'api') {
                const allowedTargets = ['model', 'intent', 'smartIntent'];
                if (!allowedTargets.includes(targetType)) {
                    return {
                        isValid: false,
                        message: 'API Call Input node can only connect to AI Model nodes or Intent Handler nodes'
                    };
                }

            }

            // Intent node handle validation
            if (sourceType === 'intent') {
                if (sourceHandle == 'output-bottom') {
                    const allowedTargets = ['intentAI'];
                    if (!allowedTargets.includes(targetType)) {
                        return {
                            isValid: false,
                            message: 'Source model of Intent node can only connect to Intent AI nodes'
                        };
                    }
                }
                else {
                    const allowedTargets = ['intent', 'model', 'response', 'apiTrigger', 'contactAgent', 'email', 'smartIntent'];
                    if (!allowedTargets.includes(targetType)) {
                        return {
                            isValid: false,
                            message: 'Intent node can only connect to Intent, AI Model, API Trigger, Contact Agent nodes or Response'
                        };
                    }
                }
            }

            // Smart Intent node handle validation
            if (sourceType === 'smartIntent') {
                if (sourceHandle == 'output-bottom') {
                    const allowedTargets = ['intentAI'];
                    if (!allowedTargets.includes(targetType)) {
                        return {
                            isValid: false,
                            message: 'Source model of Intent node can only connect to Intent AI nodes'
                        };
                    }
                }
                else {
                    const allowedTargets = ['intent', 'model', 'response', 'apiTrigger', 'contactAgent', 'email', 'smartIntent'];
                    if (!allowedTargets.includes(targetType)) {
                        return {
                            isValid: false,
                            message: 'Smart Intent node can only connect to Intent, AI Model, API Trigger, Contact Agent nodes or Response'
                        };
                    }
                }
            }

            // AI Model node handle validation
            if (sourceType === 'model') {
                if (sourceHandle === 'output-bottom') {
                    const allowedTargets = ['databaseIntegration', 'documents', 'products', 'apiknowledge', 'sharepoint'];
                    if (!allowedTargets.includes(targetType)) {
                        return {
                            isValid: false,
                            message: 'AI Model node knowledge souce can only connect to knowledge nodes;'
                        };
                    }
                } else {
                    // If not output-bottom, it can connect to any node type
                    const allowedTargets = ['model', 'intent', 'response', 'email', 'smartIntent'];
                    if (!allowedTargets.includes(targetType)) {
                        return {
                            isValid: false,
                            message: 'AI Model node can only connect to AI Model, Intent or Response nodes'
                        };
                    }
                }
            }

            if(sourceType === 'email') {
                const allowedTargets = ['model', 'response', 'contactAgent', 'apiTrigger', 'email', 'intent', 'smartIntent'];
                if (!allowedTargets.includes(targetType)) {
                    return {
                        isValid: false,
                        message: "Email node can't connect to this node type"
                    };
                }

            }

            // API Trigger node handle validation
            if (sourceType === 'apiTrigger') {
                const allowedTargets = ['model', 'response', 'contactAgent', 'apiTrigger', 'smartIntent'];
                if (!allowedTargets.includes(targetType)) {
                    return {
                        isValid: false,
                        message: 'API Trigger node can only connect to AI Model, Response nodes, API Trigger nodes or Contact Agent nodes'
                    };
                }
            }
            if (sourceType === 'contactAgent') {
                const allowedTargets = ['model', 'response', 'contactAgent', 'apiTrigger', 'intent', 'smartIntent'];
                if (!allowedTargets.includes(targetType)) {
                    return {
                        isValid: false,
                        message: 'Contact Agent node can only connect to AI Model, Response nodes, API Trigger nodes or Contact Agent nodes'
                    };
                }
            }

            // Condition node handle validation
            if (sourceType === 'condition') {
                const validHandles = ['output-true', 'output-false'];
                const isValidHandle = !sourceHandle || validHandles.includes(sourceHandle);

                if (!isValidHandle) {
                    return {
                        isValid: false,
                        message: 'Invalid output handle for Condition node'
                    };
                }
            }

            // Documents/Knowledge nodes should only connect to AI models or processing nodes
            if (['documents', 'databaseIntegration', 'products', 'apiknowledge', 'sharepoint'].includes(sourceType)) {
                const allowedTargets = ['model'];
                if (!allowedTargets.includes(targetType)) {
                    return {
                        isValid: false,
                        message: `${this.getNodeTypeLabel(sourceType)} can only connect to AI Model nodes`
                    };
                }
            }

            return { isValid: true };
        },

        // Check for circular references
        checkCircularReference(connection) {
            // Create a temporary edge list with the new connection
            const tempEdges = [...this.tempEdge, {
                source: connection.source,
                target: connection.target
            }];

            // Use DFS to detect cycles
            const visited = new Set();
            const recursionStack = new Set();

            const hasCycle = (nodeId) => {
                if (recursionStack.has(nodeId)) {
                    return true; // Cycle detected
                }
                if (visited.has(nodeId)) {
                    return false; // Already processed
                }

                visited.add(nodeId);
                recursionStack.add(nodeId);

                // Find all outgoing edges from this node
                const outgoingEdges = tempEdges.filter(edge => edge.source === nodeId);

                for (const edge of outgoingEdges) {
                    if (hasCycle(edge.target)) {
                        return true;
                    }
                }

                recursionStack.delete(nodeId);
                return false;
            };

            // Check for cycles starting from any node
            for (const node of this.tempNode) {
                if (!visited.has(node.id) && hasCycle(node.id)) {
                    return {
                        isValid: false,
                        message: 'This connection would create a circular reference in the flow'
                    };
                }
            }

            return { isValid: true };
        },
        // Validate input connection limits
        validateInputLimit(sourceNode, targetNode, connection) {

            const targetType = targetNode.data.type;

            // Count existing input connections to target node
            const existingInputs = this.tempEdge.filter(edge =>
                edge.target === connection.target && edge.targetHandle === 'input'
            ).length;

            if (targetType === 'documents' || targetType === 'products') {
                const existingModelBottomInputs = this.tempEdge.filter(edge => edge.source == sourceNode.id
                    && edge.targetNode.data.type === targetType
                    && edge.sourceHandle == 'output-bottom').length;

                // If there is already one connection from an AI Model output-bottom handle to documents node
                if (existingModelBottomInputs >= 1) {
                    return {
                        isValid: false,
                        message: `AI Model can only connect to one ${targetType === 'documents' ? 'Documents' : 'Products'} node from output-bottom handle`
                    };
                }
            }

            // Most nodes should only have one input connection
            const singleInputNodes = [
                'delay', 'apiTrigger', 'contactAgent',
                'guardrailAI', 'intentAI', 'documents', 'databaseIntegration', 'products'
            ];

            if (singleInputNodes.includes(targetType) && existingInputs >= 1) {
                return {
                    isValid: false,
                    message: `${this.getNodeTypeLabel(targetType)} node can only have one input connection`
                };
            }

            // Special cases for nodes that can have multiple inputs
            if (targetType === 'switch' && existingInputs >= 3) {
                return {
                    isValid: false,
                    message: 'Switch node can have maximum 3 input connections'
                };
            }

            if (targetType === 'condition' && existingInputs >= 2) {
                return {
                    isValid: false,
                    message: 'Condition node can have maximum 2 input connections'
                };
            }

            return { isValid: true };
        },
        // Validate output connection limits
        validateOutputLimit(sourceNode, targetNode, connection) {
            const sourceType = sourceNode.data.type;
            const sourceHandle = connection.sourceHandle;

            // Count existing output connections from source node
            const existingOutputs = this.tempEdge.filter(edge =>
                edge.source === connection.source
            ).length;

            // messageInput node can only have one output connection
            if (sourceType === 'messageInput' && existingOutputs >= 1) {
                return {
                    isValid: false,
                    message: 'Message Input node can only have one output connection'
                };
            }

            // Intent node - limit each handle to one connection
            if (sourceType === 'intent') {
                // Check fallback handle
                if (sourceHandle === 'intent-fallback') {
                    const existingFallbackOutputs = this.tempEdge.filter(edge =>
                        edge.source === connection.source && edge.sourceHandle === 'intent-fallback'
                    ).length;

                    if (existingFallbackOutputs >= 1) {
                        return {
                            isValid: false,
                            message: 'Intent fallback can only have one output connection'
                        };
                    }
                }
                // Check main action handles (format: {intent.id}-main)
                else if (sourceHandle && sourceHandle.endsWith('-main')) {
                    const existingMainOutputs = this.tempEdge.filter(edge =>
                        edge.source === connection.source && edge.sourceHandle === sourceHandle
                    ).length;

                    if (existingMainOutputs >= 1) {
                        return {
                            isValid: false,
                            message: 'Each intent action can only have one output connection'
                        };
                    }
                }
                // Check parallel action handles (format: {intent.id}-{parallel.id})
                else if (sourceHandle && sourceHandle !== 'output-bottom' && sourceHandle !== 'input') {
                    const existingParallelOutputs = this.tempEdge.filter(edge =>
                        edge.source === connection.source && edge.sourceHandle === sourceHandle
                    ).length;

                    if (existingParallelOutputs >= 1) {
                        return {
                            isValid: false,
                            message: 'Each intent action can only have one output connection'
                        };
                    }
                }
                else if (sourceHandle && sourceHandle == 'output-bottom') {
                    const existingBottomOutputs = this.tempEdge.filter(edge =>
                        edge.source === connection.source && edge.sourceHandle === sourceHandle
                    ).length;

                    if (existingBottomOutputs >= 1) {
                        return {
                            isValid: false,
                            message: 'Intent can only have one Intent AI connection'
                        };
                    }
                }
            }
            if (sourceType === 'smartIntent') {
                // Check fallback handle
                if (sourceHandle === 'smart-intent-fallback') {
                    const existingFallbackOutputs = this.tempEdge.filter(edge =>
                        edge.source === connection.source && edge.sourceHandle === 'smart-intent-fallback'
                    ).length;

                    if (existingFallbackOutputs >= 1) {
                        return {
                            isValid: false,
                            message: 'Smart Intent fallback can only have one output connection'
                        };
                    }
                }
                // Check main action handles (format: {intent.id}-main)
                else if (sourceHandle && sourceHandle.endsWith('main')) {
                    const existingMainOutputs = this.tempEdge.filter(edge =>
                        edge.source === connection.source && edge.sourceHandle === sourceHandle
                    ).length;

                    if (existingMainOutputs >= 1) {
                        return {
                            isValid: false,
                            message: 'Each intent action can only have one output connection'
                        };
                    }
                }
                else if (sourceHandle && sourceHandle == 'output-bottom') {
                    const existingBottomOutputs = this.tempEdge.filter(edge =>
                        edge.source === connection.source && edge.sourceHandle === sourceHandle
                    ).length;

                    if (existingBottomOutputs >= 1) {
                        return {
                            isValid: false,
                            message: 'Smart intent can only have one Intent AI connection'
                        };
                    }
                }
            }

            // General rule for all other source handles on the right side (Position.Right)
            // This covers: model, api, condition, switch, delay, webhook, email, apiTrigger, contactAgent, etc.
            if (sourceHandle && sourceHandle !== 'output-bottom' && sourceHandle !== 'input') {
                const existingHandleOutputs = this.tempEdge.filter(edge =>
                    edge.source === connection.source && edge.sourceHandle === sourceHandle
                ).length;

                // Check specific handle types that should be limited to one connection
                const limitedHandles = [
                    'output', // Standard output handle for most nodes
                    'output-success', // Success output for some nodes
                    'output-true', // Condition true output
                    'output-false', // Condition false output
                    'switch-default' // Switch default output
                ];

                // Check if it's a switch output handle or other limited handles
                const isSwitchOutput = sourceHandle.startsWith('switch-output-');
                const isLimitedHandle = limitedHandles.includes(sourceHandle);

                if ((isSwitchOutput || isLimitedHandle) && existingHandleOutputs >= 1) {
                    let nodeName = this.getNodeTypeLabel(sourceType);
                    let handleName = sourceHandle;
                    
                    if (isSwitchOutput) {
                        handleName = 'switch output';
                    } else if (sourceHandle === 'output-true') {
                        handleName = 'true output';
                    } else if (sourceHandle === 'output-false') {
                        handleName = 'false output';
                    } else if (sourceHandle === 'switch-default') {
                        handleName = 'default output';
                    } else if (sourceHandle === 'output' || sourceHandle === 'output-success') {
                        handleName = 'output';
                    }

                    return {
                        isValid: false,
                        message: `${nodeName} ${handleName} can only have one output connection`
                    };
                }
            }

            return { isValid: true };
        },
        // Helper function to apply default values to a node based on its type
        applyNodeDefaults(node) {
            const defaultBasePrompt = `Find the Intent from the message of users.
#Condition for Response
1. If the user asks a question related to or similar in meaning to the #Group Intent below. You must response 1. Otherwise, 0`;

            // Apply comprehensive defaults based on openNodeInspector logic
            node.data = {
                ...node.data,
                // Common properties
                color: node.data.color || '#1976D2',
                placeholderText: node.data.placeholderText || '',
                required: node.data.required || false,
                selectedModel: Array.isArray(node.data.selectedModel)
                    ? node.data.selectedModel
                    : (node.data.selectedModel ? [node.data.selectedModel] : []),
                systemPrompt: node.data.systemPrompt || '',
                temperature: node.data.temperature || null,
                maxTokens: node.data.maxTokens || null,
                
                // AI Model Configuration properties
                provider: node.data.provider || null,
                model: node.data.model || null,
                useBaseModelPrompt: node.data.useBaseModelPrompt !== undefined ? node.data.useBaseModelPrompt : false,
                gender: node.data.gender || 'male',
                typingSpeed: node.data.typingSpeed || null,
                maxInputLength: node.data.maxInputLength || 200000,
                streamResponse: node.data.streamResponse !== undefined ? node.data.streamResponse : false,
                jsonResponse: node.data.jsonResponse !== undefined ? node.data.jsonResponse : false,
                enableTime: node.data.enableTime || null,
                timeZone: node.data.timeZone || null,
                memoryTurn: node.data.memoryTurn || null,
                memoryDuration: node.data.memoryDuration || null,
                shortTermMemory: node.data.shortTermMemory || null,
                longTermMemory: node.data.longTermMemory !== undefined ? node.data.longTermMemory : false,
                isChainMassage: node.data.isChainMassage !== undefined ? node.data.isChainMassage : false,
                lenChainMassage: node.data.lenChainMassage || null,
                timeSecondChainMassage: node.data.timeSecondChainMassage || null,
                topP: node.data.topP || null,
                frequencyPenalty: node.data.frequencyPenalty || null,
                presencePenalty: node.data.presencePenalty || null,
                stopSequence: node.data.stopSequence || null,
                loadingBar: node.data.loadingBar !== undefined ? node.data.loadingBar : false,
                loadingBarDuration: node.data.loadingBarDuration || 0,
                monitorLineUserId: node.data.monitorLineUserId || null,
                monitorSocialID: node.data.monitorSocialID || null,
                showLineName: node.data.showLineName !== undefined ? node.data.showLineName : false,
                forbiddenWords: node.data.forbiddenWords || [''],
                
                // Intent specific defaults
                intentGroupName: node.data.intentGroupName || '',
                examples: node.data.examples || '',
                
                // Intent AI properties with comprehensive defaults
                intentaiModel: Array.isArray(node.data.intentaiModel)
                    ? (node.data.intentaiModel.length > 0 ? node.data.intentaiModel[0] : null)
                    : (node.data.intentaiModel || (Array.isArray(node.data.intentModel) ? (node.data.intentModel.length > 0 ? node.data.intentModel[0] : null) : node.data.intentModel) || null),
                intentaiProvider: node.data.intentaiProvider || node.data.provider || '',
                intentaiGender: node.data.intentaiGender || node.data.gender || 'male',
                intentaiTypingSpeed: node.data.intentaiTypingSpeed || node.data.typingSpeed || 0,
                intentaiMaxInputLength: node.data.intentaiMaxInputLength || node.data.maxInputLength || 4000,
                intentaiStreamResponse: node.data.intentaiStreamResponse !== undefined ? node.data.intentaiStreamResponse : (node.data.streamResponse !== undefined ? node.data.streamResponse : true),
                intentaiJsonResponse: node.data.intentaiJsonResponse !== undefined ? node.data.intentaiJsonResponse : (node.data.jsonResponse !== undefined ? node.data.jsonResponse : true),
                intentaiEnableTime: node.data.intentaiEnableTime !== undefined ? node.data.intentaiEnableTime : (node.data.enableTime !== undefined ? node.data.enableTime : false),
                intentaiTimeZone: node.data.intentaiTimeZone || node.data.timeZone || '',
                intentaiMemoryTurn: node.data.intentaiMemoryTurn || node.data.memoryTurn || 5,
                intentaiMemoryDuration: node.data.intentaiMemoryDuration || node.data.memoryDuration || 60,
                intentaiShortTermMemory: node.data.intentaiShortTermMemory || node.data.shortTermMemory || 3,
                intentaiLongTermMemory: node.data.intentaiLongTermMemory !== undefined ? node.data.intentaiLongTermMemory : (node.data.longTermMemory !== undefined ? node.data.longTermMemory : false),
                intentaiIsChainMassage: node.data.intentaiIsChainMassage !== undefined ? node.data.intentaiIsChainMassage : (node.data.isChainMassage !== undefined ? node.data.isChainMassage : false),
                intentaiLenChainMassage: node.data.intentaiLenChainMassage || node.data.lenChainMassage || 5,
                intentaiTimeSecondChainMassage: node.data.intentaiTimeSecondChainMassage || node.data.timeSecondChainMassage || 30,
                intentaiTemperature: node.data.intentaiTemperature || node.data.temperature || 0.7,
                intentaiTopP: node.data.intentaiTopP || node.data.topP || 1.0,
                intentaiFrequencyPenalty: node.data.intentaiFrequencyPenalty || node.data.frequencyPenalty || 0,
                intentaiPresencePenalty: node.data.intentaiPresencePenalty || node.data.presencePenalty || 0,
                intentaiMaxTokens: node.data.intentaiMaxTokens || node.data.maxTokens || 1000,
                intentaiSystemPrompt: node.data.intentaiSystemPrompt || node.data.intentSystemPrompt || '',
                intentaiExamples: node.data.intentaiExamples || node.data.intentExamples || '',
                intentaiConfidence: node.data.intentaiConfidence || node.data.intentConfidence || 75,
                intentaiFallbackIntent: node.data.intentaiFallbackIntent || node.data.fallbackIntent || 'unknown',
                intentaiShowLineName: node.data.intentaiShowLineName !== undefined ? node.data.intentaiShowLineName : (node.data.showLineName !== undefined ? node.data.showLineName : false),
                intentaiLoadingBar: node.data.intentaiLoadingBar !== undefined ? node.data.intentaiLoadingBar : (node.data.loadingBar !== undefined ? node.data.loadingBar : false),
                intentaiLoadingBarDuration: node.data.intentaiLoadingBarDuration || node.data.loadingBarDuration || 0,
                intentaiMonitorLineUserId: node.data.intentaiMonitorLineUserId || node.data.monitorLineUserId || null,
                intentaiMonitorSocialID: node.data.intentaiMonitorSocialID || node.data.monitorSocialID || null,
                intentaiForbiddenWords: node.data.intentaiForbiddenWords || node.data.forbiddenWords || [''],
                
                // Guardrail AI properties
                guardrailType: node.data.guardrailType || 'content_safety',
                guardrailModel: node.data.guardrailModel || 'gpt-4',
                guardrailPrompt: node.data.guardrailPrompt || '',
                safetyThreshold: node.data.safetyThreshold || 80,
                enableLogging: node.data.enableLogging !== undefined ? node.data.enableLogging : true,
                
                // API Trigger properties
                apiUrl: node.data.apiUrl || '',
                httpMethod: node.data.httpMethod || 'POST',
                headers: node.data.headers || '',
                textResponse: node.data.textResponse || '',
                jsonValidate: node.data.jsonValidate || '{\n   "Name":"Value"\n}',
                modelJsonValidator: node.data.modelJsonValidator || null,
                listHeadersAPITrigger: node.data.listHeadersAPITrigger || [{ key: '', value: '' }],
                listValidation: node.data.listValidation || [{ key: '', typeValidation: '', numberOfCharacter: '', regExp: '', promptMessage: '' }],
                
                // Contact Agent properties
                departmentName: node.data.departmentName || '',
                contactAgentMessage: node.data.contactAgentMessage || '',
                handoffMessage: node.data.handoffMessage || '',
                botDisableTime: node.data.botDisableTime || null,
                disableBot: node.data.disableBot !== undefined ? node.data.disableBot : true,
                
                // Switch properties
                switchName: node.data.switchName || '',
                
                // Condition properties
                conditionType: node.data.conditionType || 'value_comparison',
                leftOperand: node.data.leftOperand || '',
                operator: node.data.operator || 'equals',
                rightOperand: node.data.rightOperand || '',
                conditionExpression: node.data.conditionExpression || '',
                trueLabel: node.data.trueLabel || 'True',
                falseLabel: node.data.falseLabel || 'False',
                minValue: node.data.minValue || 0,
                maxValue: node.data.maxValue || 100,
                expectedValue: node.data.expectedValue !== undefined ? node.data.expectedValue : true,
                
                // Delay properties
                delayType: node.data.delayType || 'fixed',
                delayValue: node.data.delayValue || 5,
                delayUnit: node.data.delayUnit || 'seconds',
                randomMin: node.data.randomMin || 1,
                randomMax: node.data.randomMax || 10,
                delayMessage: node.data.delayMessage || '',
                showProgress: node.data.showProgress !== undefined ? node.data.showProgress : true,
                delayExpression: node.data.delayExpression || '',
                
                // Documents properties
                document: node.data.document || [],
                selectedDocumentData: node.data.selectedDocumentData || [],
                enableSpecificDataSelection: node.data.enableSpecificDataSelection !== undefined ? node.data.enableSpecificDataSelection : false,
                documentVectorName: node.data.documentVectorName || '',
                textReplacement: node.data.textReplacement || [],
                topNChunks: node.data.topNChunks || 5,
                cosineSimilarity: node.data.cosineSimilarity || 0.7,
                
                // Database Integration properties
                tableName: node.data.tableName || null,
                sqlExtractionModel: node.data.sqlExtractionModel || null,
                promptSqlNoData: node.data.promptSqlNoData || '',
                
                // Products properties
                productGroupList: node.data.productGroupList || [],
                productTopNChunks: node.data.productTopNChunks || 5,
                productCosineSimilarity: node.data.productCosineSimilarity || 0.7,
                
                // Knowledge API properties
                knowledgeApiUrl: node.data.knowledgeApiUrl || '',
                knowledgeHttpMethod: node.data.knowledgeHttpMethod || 'GET',
                listHeaders: node.data.listHeaders || [{ key: '', value: '' }],
                knowledgeRequestBody: node.data.knowledgeRequestBody || '',
                responseDataPath: node.data.responseDataPath || '',
                
                // SharePoint properties
                sharepoint: node.data.sharepoint || {
                    siteUrl: '',
                    tenantId: '',
                    clientId: '',
                    clientSecret: '',
                    folderPath: '',
                    filename: ''
                },
                
                // Email properties
                emailProvider: node.data.emailProvider || 'mailgun',
                toEmail: node.data.toEmail || '',
                ccEmails: node.data.ccEmails || [],
                bccEmails: node.data.bccEmails || [],
                subject: node.data.subject || '',
                emailTemplate: node.data.emailTemplate || 'custom',
                emailBody: node.data.emailBody || '',
                isHtml: node.data.isHtml !== undefined ? node.data.isHtml : false
            };

            // Type-specific defaults
            if (node.data.type === 'intent') {
                if (!node.data.intents || node.data.intents.length === 0) {
                    node.data.intents = [
                        {
                            id: `intent-${this.generateGuid()}`,
                            name: 'Intent Name',
                            priority: 0,
                            prompt: '',
                            basePrompt: defaultBasePrompt,
                            enableBasePrompt: false,
                            messages: [
                                { id: `msg-${this.generateGuid()}`, text: '' }
                            ],
                            mainAction: { id: `main-${this.generateGuid()}`, label: 'Main Action' },
                            parallelActions: []
                        }
                    ];
                }
                if (!node.data.actions || node.data.actions.length === 0) {
                    node.data.actions = [
                        {
                            id: `action-${this.generateGuid()}`,
                            label: 'Action 1',
                            parallels: [
                                { id: `parallel-${this.generateGuid()}`, label: 'Parallel Action 1' }
                            ]
                        }
                    ];
                }
                node.data.outputs = node.data.outputs || [];
            }

            if (node.data.type === 'smartIntent') {
                if (!node.data.smartIntents || node.data.smartIntents.length === 0) {
                    node.data.smartIntents = [
                        {
                            id: `smart-intent-${this.generateGuid()}`,
                            name: 'Smart Intent Name',
                            priority: 0,
                            prompt: '',
                            basePrompt: defaultBasePrompt,
                            enableBasePrompt: false,
                            useMemory: false,
                            messages: [
                                { id: `msg-${this.generateGuid()}`, text: '' }
                            ],
                            mainAction: {
                                id: `main-${this.generateGuid()}`,
                                label: 'Action',
                                useAI: true,
                                provider: null,
                                model: null,
                                gender: 'male',
                                typingSpeed: null,
                                maxInputLength: 200000,
                                streamResponse: false,
                                jsonResponse: false,
                                enableTime: null,
                                timeZone: null,
                                temperature: null,
                                topP: null,
                                frequencyPenalty: null,
                                presencePenalty: null,
                                maxTokens: null,
                                stopSequence: null,
                                memoryTurn: null,
                                memoryDuration: null,
                                shortTermMemory: null,
                                longTermMemory: false,
                                isChainMassage: false,
                                lenChainMassage: null,
                                timeSecondChainMassage: null,
                                forbiddenWords: [''],
                                // Knowledge Configuration
                                knowledgeType: '',
                                // Documents properties
                                document: [],
                                topNChunks: 5,
                                cosineSimilarity: 0.7,
                                // Database Integration properties
                                tableName: null,
                                sqlExtractionModel: null,
                                promptSqlNoData: '',
                                // Products properties
                                productGroupList: [],
                                productTopNChunks: 5,
                                productCosineSimilarity: 0.7,
                                // API Knowledge properties
                                knowledgeApiUrl: '',
                                knowledgeHttpMethod: 'GET',
                                knowledgeResponseDataPath: ''
                            }
                        }
                    ];
                }
                if (!node.data.smartIntentFallback) {
                    node.data.smartIntentFallback = {
                        useAI: false,
                        provider: null,
                        model: null,
                        gender: 'male',
                        typingSpeed: null,
                        maxInputLength: 200000,
                        streamResponse: false,
                        jsonResponse: false,
                        enableTime: null,
                        timeZone: null,
                        temperature: null,
                        topP: null,
                        frequencyPenalty: null,
                        presencePenalty: null,
                        maxTokens: null,
                        stopSequence: null,
                        memoryTurn: null,
                        memoryDuration: null,
                        shortTermMemory: null,
                        longTermMemory: false,
                        isChainMassage: false,
                        lenChainMassage: null,
                        timeSecondChainMassage: null,
                        forbiddenWords: ['']
                    };
                }
                node.data.smartIntentGroupName = node.data.smartIntentGroupName || '';
            }

            if (node.data.type === 'switch') {
                if (!node.data.switchOutputs || node.data.switchOutputs.length === 0) {
                    node.data.switchOutputs = [
                        {
                            id: `switch-output-${this.generateGuid()}`,
                            name: 'Output 1',
                            condition: ''
                        },
                        {
                            id: `switch-output-${this.generateGuid()}`,
                            name: 'Output 2',
                            condition: ''
                        }
                    ];
                }
                node.data.switchName = node.data.switchName || 'Multi-Path';
            }

            return node;
        },

        // Calculate center position of current viewport
        getCenterPosition() {
            try {
                // Get the VueFlow instance
                const vueFlow = this.$refs.vueFlow;
                if (!vueFlow) {
                    console.warn('VueFlow ref not found, using fallback position');
                    return { x: 400, y: 200 }; // Fallback
                }

                // Try multiple methods to get viewport information
                let viewport, dimensions;
                
                // Method 1: Direct access to VueFlow methods
                if (typeof vueFlow.getViewport === 'function') {
                    viewport = vueFlow.getViewport();
                }
                
                if (typeof vueFlow.getDimensions === 'function') {
                    dimensions = vueFlow.getDimensions();
                }

                // Method 2: Access through VueFlow store/state if direct methods don't work
                if (!viewport && vueFlow.viewport) {
                    viewport = vueFlow.viewport;
                }

                if (!dimensions && vueFlow.dimensions) {
                    dimensions = vueFlow.dimensions;
                }

                // Method 3: Access through VueFlow internal state
                if (!viewport || !dimensions) {
                    const flowContainer = vueFlow.$el || vueFlow.$refs?.wrapper || document.querySelector('.vue-flow');
                    if (flowContainer) {
                        const rect = flowContainer.getBoundingClientRect();
                        dimensions = dimensions || { width: rect.width, height: rect.height };
                        
                        // If we still don't have viewport, try to get it from the flow
                        if (!viewport) {
                            // Get transform from the flow viewport
                            const viewportElement = flowContainer.querySelector('.vue-flow__viewport');
                            if (viewportElement) {
                                const transform = viewportElement.style.transform;
                                const match = transform.match(/translate\(([^)]+)\) scale\(([^)]+)\)/);
                                if (match) {
                                    const [, translate, scale] = match;
                                    const [x, y] = translate.split(',').map(v => parseFloat(v.replace('px', '')));
                                    viewport = { x, y, zoom: parseFloat(scale) };
                                }
                            }
                        }
                    }
                }

                // If we still don't have complete viewport info, use fallback
                if (!viewport || !dimensions) {
                    return { x: 400, y: 200 };
                }

                
                // Calculate center position in flow coordinates
                const centerX = (-viewport.x + dimensions.width / 2) / viewport.zoom;
                const centerY = (-viewport.y + dimensions.height / 2) / viewport.zoom;
                
                // Default node size (approximate)
                const nodeWidth = 200;  // Average node width
                const nodeHeight = 100; // Average node height
                
                // Adjust position so that center of node is at calculated center
                const adjustedX = centerX - (nodeWidth / 2);
                const adjustedY = centerY - (nodeHeight / 2);
                
                return { x: adjustedX, y: adjustedY };
            } catch (error) {
                return { x: 400, y: 200 }; // Fallback
            }
        },

        // New methods for node management
        addNewNode(nodeData) {
            // Check if trying to add messageInput node when one already exists
            if (nodeData.data.type === 'messageInput') {
                const existingMessageInput = this.tempNode.find(node => node.data.type === 'messageInput');
                if (existingMessageInput) {
                    this.showError('Message Input node already exists. Only one Message Input node is allowed per flow.');
                    return;
                }
            }

            const newNode = {
                ...nodeData,
                id: this.generateGuid(),
                position: this.getCenterPosition() // Use dynamic center position
            };

            // Apply comprehensive defaults
            this.applyNodeDefaults(newNode);

            this.tempNode.push(newNode);

            // Save state to history
            this.saveStateToHistory(`Add ${nodeData.data.label} Node`);

            this.$nextTick(() => {
                const getTempNode = this.tempNode.find(node => node.id === newNode.id);
                if (getTempNode) {
                    this.checkNodeValidation(getTempNode);
                    
                    // Select the new node first to enable updateNodeProperty
                    this.selectedNode = getTempNode;
                    this.selectedNodes = [getTempNode];
                    
                    // Sync nodeInspector with the new node data
                    this.nodeInspector = { ...getTempNode.data };
                    
                    // Trigger updateNodeProperty for critical properties to ensure proper initialization
                    // This ensures the node data is properly registered and validated
                    if (getTempNode.data.type === 'model' || getTempNode.data.type === 'intentAI') {
                        if (getTempNode.data.selectedModel) {
                            this.updateNodeProperty('selectedModel', getTempNode.data.selectedModel);
                        }
                    }
                }
                
                // Focus and select the new node
                this.focusAndSelectNode(newNode.id);
            });
            // Show success message
            this.showSuccess(`Added ${nodeData.data.label} to flow`);

        },
        // Helper function to focus and select a newly added node
        focusAndSelectNode(nodeId) {
            this.$nextTick(() => {
                // Find the new node
                const newNode = this.tempNode.find(node => node.id === nodeId);
                if (newNode) {
                    // First, clear all existing selections by setting selected to false
                    this.tempNode.forEach(node => {
                        if (node.selected) {
                            node.selected = false;
                        }
                    });

                    // Clear edges selection too
                    this.tempEdge.forEach(edge => {
                        if (edge.selected) {
                            edge.selected = false;
                        }
                    });

                    // Force update the arrays to trigger reactivity
                    this.tempNode = [...this.tempNode];
                    this.tempEdge = [...this.tempEdge];

                    // Wait a bit then properly select the node
                    setTimeout(() => {
                        // Find the node again after array update
                        const nodeToSelect = this.tempNode.find(node => node.id === nodeId);
                        if (nodeToSelect) {
                            // Set the node as selected
                            nodeToSelect.selected = true;
                            
                            // Update the selection state
                            this.selectedNode = nodeToSelect;
                            this.selectedNodes = [nodeToSelect];
                            
                            // Force another reactivity update to trigger visual selection
                            this.tempNode = [...this.tempNode];
                            
                            // Trigger the selection change event manually to ensure VueFlow updates
                            this.$nextTick(() => {
                                // Call onNodeClick to ensure all selection logic is properly executed
                                this.onNodeClick({ node: nodeToSelect });
                                
                                // Focus the view on the new node with smooth animation
                                setTimeout(() => {
                                    if (this.$refs.vueFlow) {
                                        try {
                                            this.$refs.vueFlow.fitView({
                                                nodes: [nodeToSelect],
                                                padding: 0.3,
                                                duration: 500,
                                                minZoom: 0.8,
                                                maxZoom: 1.5
                                            });
                                        } catch (error) {
                                            console.warn('Failed to focus on node:', error);
                                        }
                                    }
                                }, 100);
                            });
                        }
                    }, 50);
                }
            });
        },
        handleDeleteNode(nodeId) {
            const nodeToDelete = this.tempNode.find(node => node.id === nodeId);
            const nodeName = nodeToDelete ? nodeToDelete.data.label || 'Node' : 'Node';

            // Remove the node
            this.tempNode = this.tempNode.filter(node => node.id !== nodeId);

            // Remove all edges connected to this node
            this.tempEdge = this.tempEdge.filter(edge =>
                edge.source !== nodeId && edge.target !== nodeId
            );

            // Save state to history
            this.saveStateToHistory(`Delete ${nodeName}`);

            // Show success message
            this.showSuccess('Node deleted successfully');

            // Close inspector panel if the deleted node was selected
            if (this.selectedNode && this.selectedNode.id === nodeId) {
                this.selectedNodes = [];
                this.selectedNode = null;
                this.closeInspector();
            }
        },
        handleKeyDown(event) {
            // Check for Undo shortcut (Ctrl+Z)
            if (event.ctrlKey && event.code === 'KeyZ' && !event.shiftKey && !this.isViewMode) {
                event.preventDefault();
                this.undo();
            }
            // Check for Redo shortcuts (Ctrl+Y or Ctrl+Shift+Z)
            else if ((event.ctrlKey && event.code === 'KeyY') || 
                     (event.ctrlKey && event.shiftKey && event.code === 'KeyZ') && !this.isViewMode) {
                event.preventDefault();
                this.redo();
            }
            // Check for Copy shortcut (Ctrl+C)
            else if (event.ctrlKey && event.code === 'KeyC' && !this.isViewMode && this.selectedNodes.length > 0) {
                event.preventDefault();
                this.handleCopySelectedNodes();
            }
            // Check for Paste shortcut (Ctrl+V)
            else if (event.ctrlKey && event.code === 'KeyV' && !this.isViewMode && this.copiedNodes.length > 0) {
                event.preventDefault();
                this.handlePasteNodes();
            }
            // Check if Delete key is pressed and we're not in view mode
            else if (event.key === 'Delete' && !this.isViewMode && this.selectedNodes.length > 0) {
                event.preventDefault();
                this.handleDeleteSelectedNodes();
            }
            // Check if Ctrl+D is pressed for duplication (using event.code for keyboard layout independence)
            else if (event.ctrlKey && event.code === 'KeyD' && !this.isViewMode && this.selectedNodes.length > 0) {
                event.preventDefault();
                this.handleDuplicateSelectedNodes();
            }
            // Alternative: Also support Ctrl+Shift+D for duplication (fallback)
            else if (event.ctrlKey && event.shiftKey && event.code === 'KeyD' && !this.isViewMode && this.selectedNodes.length > 0) {
                event.preventDefault();
                this.handleDuplicateSelectedNodes();
            }
            // Fallback: Use event.key for older browsers or special cases
            else if (event.ctrlKey && event.key.toLowerCase() === 'd' && !this.isViewMode && this.selectedNodes.length > 0) {
                event.preventDefault();
                this.handleDuplicateSelectedNodes();
            }
        },
        focusFlowContainer() {
            // Ensure the flow container can receive keyboard events
            this.$nextTick(() => {
                if (this.$refs.flowContainer) {
                    this.$refs.flowContainer.focus();
                }
            });
        },
        handleDeleteSelectedNodes() {
            if (this.selectedNodes.length === 0) {
                return;
            }

            // Collect all node IDs to delete
            const nodeIdsToDelete = this.selectedNodes.map(node => node.id);
            const nodeCount = nodeIdsToDelete.length;
            
            // Remove all selected nodes
            this.tempNode = this.tempNode.filter(node => !nodeIdsToDelete.includes(node.id));

            // Remove all edges connected to any of the deleted nodes
            this.tempEdge = this.tempEdge.filter(edge =>
                !nodeIdsToDelete.includes(edge.source) && !nodeIdsToDelete.includes(edge.target)
            );

            // Save state to history
            this.saveStateToHistory(`Delete ${nodeCount} Node${nodeCount > 1 ? 's' : ''}`);

            // Show success message
            this.showSuccess(`${nodeCount} node${nodeCount > 1 ? 's' : ''} deleted successfully`);

            // Clear selection and close inspector
            this.selectedNodes = [];
            this.selectedNode = null;
            this.closeInspector();
        },
        handleDuplicateSelectedNodes() {
            if (this.selectedNodes.length === 0) {
                return;
            }
            
            // Check if any selected node is messageInput type
            const hasMessageInputNode = this.selectedNodes.some(node => node.data.type === 'messageInput');
            if (hasMessageInputNode) {
                this.showError('Cannot duplicate Message Input node. Only one Message Input node is allowed per flow.');
                return;
            }
            
            console.log('Duplicating nodes:', this.selectedNodes);
            const duplicatedNodes = [];
            const oldToNewNodeIds = new Map(); // Map old node IDs to new node IDs
            const offsetX = 60; // Horizontal offset for duplicated nodes
            const offsetY = 60; // Vertical offset for duplicated nodes

            // Create duplicated nodes
            this.selectedNodes.forEach(originalNode => {
                const newNodeId = this.generateGuid();

                // Deep clone the node data to avoid reference issues
                let stgData = JSON.stringify(originalNode.data);
                stgData = stgData.replaceAll(originalNode.id, newNodeId);

                oldToNewNodeIds.set(originalNode.id, newNodeId);
                const clonedData = JSON.parse(stgData);
                
                // Update label if it exists
                if (clonedData.label) {
                    clonedData.label = clonedData.label.replace(' (Copy)', '') + ' (Copy)';
                }

                const duplicatedNode = {
                    ...originalNode,
                    id: newNodeId,
                    position: {
                        x: originalNode.position.x + offsetX,
                        y: originalNode.position.y + offsetY
                    },
                    selected: false, // Don't select the duplicated nodes initially
                    data: clonedData
                };

                duplicatedNodes.push(duplicatedNode);
            });

            // Add duplicated nodes to the flow
            this.tempNode.push(...duplicatedNodes);

            // Save state to history
            const nodeCount = duplicatedNodes.length;
            this.saveStateToHistory(`Duplicate ${nodeCount} Node${nodeCount > 1 ? 's' : ''}`);

            // Find and duplicate edges between the selected nodes only
            const selectedNodeIds = this.selectedNodes.map(node => node.id);
            const edgesToDuplicate = this.tempEdge.filter(edge => 
                selectedNodeIds.includes(edge.source) && selectedNodeIds.includes(edge.target)
            );

/*
            const duplicatedEdges = edgesToDuplicate.map(originalEdge => ({
                ...originalEdge,
                id: this.generateUniqueId(),
                source: oldToNewNodeIds.get(originalEdge.source),
                target: oldToNewNodeIds.get(originalEdge.target)
            }));

            // Add duplicated edges to the flow
            this.tempEdge.push(...duplicatedEdges);
*/
            // Show success message
            this.showSuccess(`${nodeCount} node${nodeCount > 1 ? 's' : ''} duplicated successfully`);

            // Clear current selection first
            this.selectedNodes = [];
            this.selectedNode = null;

            // Select the duplicated nodes after a brief delay to ensure they're rendered
            this.$nextTick(() => {
                // Update the selected state of duplicated nodes
                duplicatedNodes.forEach(node => {
                    const nodeInFlow = this.tempNode.find(n => n.id === node.id);
                    if (nodeInFlow) {
                        nodeInFlow.selected = true;
                    }
                });
                
                this.selectedNodes = duplicatedNodes;
                
                // If only one node was duplicated, open it in inspector
                if (duplicatedNodes.length === 1) {
                    this.selectedNode = duplicatedNodes[0];
                    this.openNodeInspector(duplicatedNodes[0]);
                }
            });
        },
        handleCopySelectedNodes() {
            if (this.selectedNodes.length === 0) {
                return;
            }
            this.copiedNodes = [];
            
            
            // Deep clone the selected nodes data
            this.copiedNodes = this.selectedNodes.map(node => {
                // Deep clone the node data to avoid reference issues
                let stgData = JSON.stringify(node.data);
                const clonedData = JSON.parse(stgData);
                
                return {
                    ...node,
                    data: clonedData
                };
            });
            
            // Also copy edges between the selected nodes
            const selectedNodeIds = this.selectedNodes.map(node => node.id);
            
            // Show success message
            const nodeCount = this.copiedNodes.length;
            this.showSuccess(`${nodeCount} node${nodeCount > 1 ? 's' : ''} copied to clipboard`);
        },
        handlePasteNodes() {
            if (this.copiedNodes.length === 0) {
                return;
            }
            
            // Check if any copied node is messageInput type and if one already exists
            const hasMessageInputNode = this.copiedNodes.some(node => node.data.type === 'messageInput');
            if (hasMessageInputNode) {
                const existingMessageInput = this.tempNode.find(node => node.data.type === 'messageInput');
                if (existingMessageInput) {
                    this.showError('Cannot paste Message Input node. Only one Message Input node is allowed per flow.');
                    return;
                }
            }
            
            const pastedNodes = [];
            const oldToNewNodeIds = new Map(); // Map old node IDs to new node IDs
            
            // Get center position for pasting
            const centerPosition = this.getCenterPosition();
            
            // Calculate bounding box of copied nodes to maintain relative positions
            let minX = Infinity, minY = Infinity;
            this.copiedNodes.forEach(node => {
                minX = Math.min(minX, node.position.x);
                minY = Math.min(minY, node.position.y);
            });
            
            // Create pasted nodes
            this.copiedNodes.forEach(originalNode => {
                const newNodeId = this.generateGuid();
                
                // Deep clone the node data to avoid reference issues
                let stgData = JSON.stringify(originalNode.data);
                stgData = stgData.replaceAll(originalNode.id, newNodeId);
                
                oldToNewNodeIds.set(originalNode.id, newNodeId);
                const clonedData = JSON.parse(stgData);
                
                // Update label if it exists to indicate it's a copy
                if (clonedData.label) {
                    clonedData.label = clonedData.label.replace(' (Copy)', '') + ' (Copy)';
                }
                
                // Calculate new position relative to center with maintained relative positions
                const relativeX = originalNode.position.x - minX;
                const relativeY = originalNode.position.y - minY;
                
                const pastedNode = {
                    ...originalNode,
                    id: newNodeId,
                    position: {
                        x: centerPosition.x + relativeX,
                        y: centerPosition.y + relativeY
                    },
                    selected: false, // Don't select the pasted nodes initially
                    data: clonedData
                };
                
                pastedNodes.push(pastedNode);
            });
            
            // Add pasted nodes to the flow
            this.tempNode.push(...pastedNodes);
            // Save state to history
            const nodeCount = pastedNodes.length;
            this.saveStateToHistory(`Paste ${nodeCount} Node${nodeCount > 1 ? 's' : ''}`);
            
            // Show success message
            this.showSuccess(`${nodeCount} node${nodeCount > 1 ? 's' : ''} pasted successfully`);
            
            // Clear current selection first
            this.selectedNodes = [];
            this.selectedNode = null;
        },
        handleRemoveOutgoingEdge(edgeId) {
            // Find edge details before removing for history
            const edgeToRemove = this.tempEdge.find(edge => edge.id === edgeId);
            
            // Remove the specific edge by ID
            this.tempEdge = this.tempEdge.filter(edge => edge.id !== edgeId);
            
            // Save to history
            if (edgeToRemove) {
                const sourceNode = this.tempNode.find(node => node.id === edgeToRemove.source);
                const targetNode = this.tempNode.find(node => node.id === edgeToRemove.target);
                const sourceName = sourceNode ? sourceNode.data.label || 'Node' : 'Node';
                const targetName = targetNode ? targetNode.data.label || 'Node' : 'Node';
                this.saveStateToHistory(`Disconnect ${sourceName} → ${targetName}`);
            } else {
                this.saveStateToHistory('Remove Connection');
            }
        },
        handleNodeDataUpdate(nodeId, newData) {
            const nodeIndex = this.tempNode.findIndex(node => node.id === nodeId);
            if (nodeIndex !== -1) {
                this.tempNode = [
                    ...this.tempNode.slice(0, nodeIndex),
                    {
                        ...this.tempNode[nodeIndex],
                        data: { ...this.tempNode[nodeIndex].data, ...newData }
                    },
                    ...this.tempNode.slice(nodeIndex + 1)
                ];
            }
        },
        onDrop(event) {
            event.preventDefault();
            const nodeData = event.dataTransfer.getData('application/reactflow');
            if (nodeData) {
                const nodeTemplate = JSON.parse(nodeData);

                // Get VueFlow instance
                const vueFlowInstance = this.$refs.vueFlow;
                if (vueFlowInstance) {
                    let position;

                    // Try to use screenToFlowPosition if available (newer versions)
                    if (typeof vueFlowInstance.screenToFlowPosition === 'function') {
                        const flowPosition = vueFlowInstance.screenToFlowPosition({
                            x: event.clientX,
                            y: event.clientY
                        });
                        position = {
                            x: flowPosition.x - 75,
                            y: flowPosition.y - 25
                        };
                    }
                    // Fallback to manual calculation
                    else {
                        const flowElement = vueFlowInstance.$el.querySelector('.vue-flow__viewport') || vueFlowInstance.$el;
                        const flowBounds = flowElement.getBoundingClientRect();

                        // Get current viewport transform
                        const viewport = vueFlowInstance.getViewport ? vueFlowInstance.getViewport() : { x: 0, y: 0, zoom: 1 };

                        // Calculate the position relative to the flow viewport
                        const relativeX = event.clientX - flowBounds.left;
                        const relativeY = event.clientY - flowBounds.top;

                        // Convert screen coordinates to flow coordinates accounting for zoom and pan
                        position = {
                            x: (relativeX - viewport.x) / viewport.zoom - 75,
                            y: (relativeY - viewport.y) / viewport.zoom - 25
                        };
                    }

                    const newNode = {
                        id: this.generateGuid(),
                        type: 'flow-node',
                        position: position,
                        data: { ...nodeTemplate.data }
                    };

                    // Check if trying to add messageInput node when one already exists
                    if (newNode.data.type === 'messageInput') {
                        const existingMessageInput = this.tempNode.find(node => node.data.type === 'messageInput');
                        if (existingMessageInput) {
                            this.showError('Message Input node already exists. Only one Message Input node is allowed per flow.');
                            return;
                        }
                    }

                    // Apply comprehensive defaults
                    this.applyNodeDefaults(newNode);

                    this.tempNode.push(newNode);
                    this.$nextTick(() => {
                        const getTempNode = this.tempNode.find(node => node.id === newNode.id);
                        if (getTempNode) {
                            this.checkNodeValidation(getTempNode);
                        }
                        
                        // Focus and select the new node
                        this.focusAndSelectNode(newNode.id);
                    });
                    // Show success message
                    this.showSuccess(`Added ${nodeTemplate.data.label} to flow`);
                } else {
                    console.error('VueFlow instance not available');
                }
            }
        },
        // Update SaveFlow method to use new targets structure
        async SaveFlow() {
            try {
                // this.dialog = true;

                // Check if there are any nodes in the flow has data.warningShow = true
                // if (this.tempNode.some(node => node.data.showWarning)) {
                //     this.showError('Please resolve all warnings before saving the flow');
                //     return;
                // }


                if (!this.tempNode || this.tempNode.length === 0) {
                    this.showError('Cannot save empty flow');
                    return;
                }

                // Convert to flat structure with targets
                const flatNodes = this.convertToFlatStructure();

                const flowData = {
                    id: this.flowId || null,
                    name: this.flowName || 'Untitled Flow',
                    flowDetails: flatNodes
                };

                // Validate flow structure
                const validationResult = this.validateFlatFlow(flowData);
                if (!validationResult.isValid) {
                    this.showError(`Flow validation failed: ${validationResult.errors.join(', ')}`);
                    return;
                }

                // Choose the appropriate API method
                const apiMethod = this.flowId ? 'UpdateFlow' : 'CreateFlow';

                // Save to API
                const response = await this.saveFlowToAPI(flowData, apiMethod);

                if (response.success) {
                    // Update flow metadata
                    this.flowId = response.flowId || this.flowId;
                    this.flowName = flowData.name;
                    this.clearHistory();
                    this.showSuccess(this.isEditMode ? 'Flow updated successfully' : 'Flow saved successfully');
                    this.successDialog = true; // Show success dialog

                    this.$router.replace({ path: '/FlowCreator', query: { id: this.flowId } });
                    this.isEditMode = true;
                    // Automatically hide success dialog after 2 seconds
                    setTimeout(() => {
                        this.successDialog = false;
                    }, 2000);
                } else {
                    throw new Error(response.message || 'Failed to save flow');
                }

            } catch (error) {
                console.error('Error saving flow:', error);
                this.showError(`Failed to save flow: ${error.message}`);
            } finally {
                this.dialog = false;
            }
        },
        convertToFlatStructure() {
            const flatNodes = [];

            // First pass: create all nodes without targets
            this.tempNode.forEach(node => {
                const flatNode = {
                    id: node.id,
                    nodeType: node.data.type,
                    position: node.position,
                    data: this.extractNodeData(node),
                    targets: []
                };
                flatNodes.push(flatNode);
            });

            // Second pass: populate targets based on edges
            this.tempEdge.forEach(edge => {
                const sourceNode = flatNodes.find(node => node.id === edge.source);
                if (sourceNode) {
                    const targetInfo = {
                        nodeId: edge.target,
                        sourceHandle: edge.sourceHandle || null
                    };
                    sourceNode.targets.push(targetInfo);
                }
            });

            return flatNodes;
        },
        convertFromFlatStructure(flatNodes) {
            const flowNodes = [];
            const flowEdges = [];

            flatNodes.forEach(flatNode => {
                // Parse string data back to objects (handle both string and object for backward compatibility)
                const position = typeof flatNode.position === 'string'
                    ? JSON.parse(flatNode.position)
                    : flatNode.position;

                const data = typeof flatNode.data === 'string'
                    ? JSON.parse(flatNode.data)
                    : flatNode.data;

                const targets = typeof flatNode.targets === 'string'
                    ? JSON.parse(flatNode.targets)
                    : (flatNode.targets || []);

                // Create flow node
                const expandedData = this.expandNodeData(flatNode.nodeType, data);

                const flowNode = {
                    id: flatNode.id,
                    type: 'flow-node',
                    position: position,
                    data: {
                        type: flatNode.nodeType,
                        label: data.label,
                        ...expandedData
                    },
                    selected: false
                };
                flowNodes.push(flowNode);

                // Create edges from targets
                if (targets && Array.isArray(targets)) {
                    targets.forEach((target, index) => {
                        const edge = {
                            id: `e${flatNode.id}-${target.nodeId}-${this.generateGuid()}`,
                            source: flatNode.id,
                            target: target.nodeId,
                            sourceHandle: target.sourceHandle || null,
                            targetHandle: 'input',
                            type: 'flow-edge',
                            markerEnd: MarkerType.ArrowClosed,
                            animated: false,
                            showButton: false
                        };

                        flowEdges.push(edge);
                    });
                }
            });

            return { nodes: flowNodes, edges: flowEdges };
        },

        findRootNodes() {
            const allNodeIds = new Set(this.tempNode.map(node => node.id));
            const targetNodeIds = new Set();

            // Collect all target node IDs from edges
            this.tempEdge.forEach(edge => {
                targetNodeIds.add(edge.target);
            });

            // Root nodes are those that are not targets of any edge
            const rootNodes = Array.from(allNodeIds).filter(nodeId => !targetNodeIds.has(nodeId));

            return rootNodes;
        },

        // Recursively build nested node structure
        buildNestedNode(nodeId, nodeMap, processedNodes) {
            if (processedNodes.has(nodeId)) {
                return null; // Avoid circular references
            }

            processedNodes.add(nodeId);
            const node = nodeMap.get(nodeId);

            if (!node) {
                return null;
            }

            // Find all outgoing edges from this node
            const outgoingEdges = this.tempEdge.filter(edge => edge.source === nodeId);

            // Group edges by source handle for intent nodes with multiple outputs
            const edgeGroups = this.groupEdgesBySourceHandle(outgoingEdges, node);

            // Build target nodes
            node.target = edgeGroups.map(group => {
                const targetNodes = group.edges.map(edge => {
                    const targetNode = this.buildNestedNode(edge.target, nodeMap, new Set(processedNodes));
                    if (targetNode) {
                        // Add source information to target node
                        targetNode.source = group.sourceHandle ? `${nodeId}.${group.sourceHandle}` : nodeId;
                    }
                    return targetNode;
                }).filter(targetNode => targetNode !== null);

                return targetNodes;
            }).flat();

            return node;
        },
        // Group edges by source handle (for nodes with multiple outputs like intent, switch)
        groupEdgesBySourceHandle(edges, sourceNode) {
            if (sourceNode.nodeType === 'intent' || sourceNode.nodeType === 'switch') {
                // Group by source handle
                const groups = new Map();

                edges.forEach(edge => {
                    const sourceHandle = edge.sourceHandle || 'default';
                    if (!groups.has(sourceHandle)) {
                        groups.set(sourceHandle, {
                            sourceHandle: sourceHandle === 'default' ? null : sourceHandle,
                            edges: []
                        });
                    }
                    groups.get(sourceHandle).edges.push(edge);
                });

                return Array.from(groups.values());
            } else {
                // Single group for nodes with single output
                return [{
                    sourceHandle: null,
                    edges: edges
                }];
            }
        },
        // Extract node-specific data based on node type
        extractNodeData(node) {
            const baseData = {
                // label: node.data.label
            };

            switch (node.data.type) {
                case 'messageInput':
                    return {
                        ...baseData,
                        modelDisplayId: [
                            ...(Array.isArray(node.data.selectedModel)
                                ? node.data.selectedModel
                                : (node.data.selectedModel ? [node.data.selectedModel] : [])),
                            
                        ],
                        unableToAccessModel: [
                            ...(Array.isArray(this.unableToAccessModel)
                                ? this.unableToAccessModel
                                : (this.unableToAccessModel ? [this.unableToAccessModel] : []))
                        ]
                    };

                case 'model':
                    return {
                        ...baseData,
                        // Basic model configuration
                        provider: node.data.provider,
                        model: node.data.model,
                        apiVersion: node.data.apiVersion,
                        modelDisplayId: node.data.selectedModel,
                        systemPrompt: node.data.systemPrompt,
                        useBaseModelPrompt: node.data.useBaseModelPrompt,

                        // AI Parameters
                        temperature: node.data.temperature,
                        maxTokens: node.data.maxTokens,
                        topP: node.data.topP,
                        frequencyPenalty: node.data.frequencyPenalty,
                        presencePenalty: node.data.presencePenalty,
                        stopSequence: node.data.stopSequence,

                        // Behavior Settings
                        gender: node.data.gender,
                        typingSpeed: node.data.typingSpeed,
                        maxInputLength: node.data.maxInputLength,

                        // Response Options
                        streamResponse: node.data.streamResponse,
                        jsonResponse: node.data.jsonResponse,
                        enableTime: node.data.enableTime,
                        timeZone: node.data.timeZone,

                        // Memory Settings (convert memoryDuration to negative like ModelManagement.vue)
                        memoryTurn: node.data.memoryTurn,
                        memoryDuration: node.data.memoryDuration > 0
                            ? -node.data.memoryDuration
                            : node.data.memoryDuration,
                        shortTermMemory: node.data.shortTermMemory,
                        longTermMemory: node.data.longTermMemory,
                        isChainMassage: node.data.isChainMassage,
                        lenChainMassage: node.data.lenChainMassage,
                        timeSecondChainMassage: node.data.timeSecondChainMassage,

                        // Line Configuration
                        loadingBar: node.data.loadingBar,
                        loadingBarDuration: node.data.loadingBarDuration,
                        showLineName: node.data.showLineName,
                        monitorLineUserId: node.data.monitorLineUserId,
                        monitorSocialID: node.data.monitorSocialID,
                        forbiddenWords: node.data.forbiddenWords
                    };

                case 'intent':
                    return {
                        ...baseData,
                        intentGroupName: node.data.intentGroupName,
                        intents: node.data.intents || []
                    };

                case 'smartIntent':
                    return {
                        ...baseData,
                        smartIntentGroupName: node.data.smartIntentGroupName,
                        smartIntents: node.data.smartIntents || [],
                        smartIntentFallback: node.data.smartIntentFallback || {}
                    };

                case 'switch':
                    return {
                        ...baseData,
                        switchName: node.data.switchName,
                        switchOutputs: node.data.switchOutputs || []
                    };

                case 'condition':
                    return {
                        ...baseData,
                        conditionType: node.data.conditionType,
                        leftOperand: node.data.leftOperand,
                        operator: node.data.operator,
                        rightOperand: node.data.rightOperand,
                        conditionExpression: node.data.conditionExpression,
                        trueLabel: node.data.trueLabel,
                        falseLabel: node.data.falseLabel,
                        minValue: node.data.minValue,
                        maxValue: node.data.maxValue,
                        expectedValue: node.data.expectedValue
                    };

                case 'delay':
                    return {
                        ...baseData,
                        delayType: node.data.delayType,
                        delayValue: node.data.delayValue,
                        delayUnit: node.data.delayUnit,
                        randomMin: node.data.randomMin,
                        randomMax: node.data.randomMax,
                        delayMessage: node.data.delayMessage,
                        showProgress: node.data.showProgress,
                        delayExpression: node.data.delayExpression
                    };

                case 'apiTrigger':
                    return {
                        ...baseData,
                        apiUrl: node.data.apiUrl,
                        httpMethod: node.data.httpMethod || 'GET',
                        textResponse: node.data.textResponse,
                        jsonValidate: node.data.jsonValidate,
                        modelJsonValidator: node.data.modelJsonValidator,
                        listHeadersAPITrigger: node.data.listHeadersAPITrigger,
                        listValidation: node.data.listValidation
                    };

                case 'contactAgent':
                    return {
                        ...baseData,
                        contactAgentMessage: node.data.contactAgentMessage,
                        botDisableTime: node.data.botDisableTime,
                    };

                case 'guardrailAI':
                    return {
                        ...baseData,
                        guardrailModel: node.data.guardrailModel,
                        guardrailPrompt: node.data.guardrailPrompt,
                        safetyThreshold: node.data.safetyThreshold,
                        enableLogging: node.data.enableLogging
                    };

                case 'intentAI':
                    return {
                        ...baseData,
                        // Basic model configuration
                        intentaiProvider: node.data.intentaiProvider,
                        intentaiModel: node.data.intentaiModel,
                        intentAiModelApiVersion: node.data.intentAiModelApiVersion,
                        intentaiSystemPrompt: node.data.intentaiSystemPrompt,
                        intentaiExamples: node.data.intentaiExamples,
                        intentaiConfidence: node.data.intentaiConfidence,
                        intentaiFallbackIntent: node.data.intentaiFallbackIntent,

                        // AI Parameters
                        intentaiTemperature: node.data.intentaiTemperature,
                        intentaiMaxTokens: node.data.intentaiMaxTokens,
                        intentaiTopP: node.data.intentaiTopP,
                        intentaiFrequencyPenalty: node.data.intentaiFrequencyPenalty,
                        intentaiPresencePenalty: node.data.intentaiPresencePenalty,
                        intentaiStopSequence: node.data.intentaiStopSequence,

                        // Behavior Settings
                        intentaiGender: node.data.intentaiGender,
                        intentaiTypingSpeed: node.data.intentaiTypingSpeed,
                        intentaiMaxInputLength: node.data.intentaiMaxInputLength,

                        // Response Options
                        intentaiStreamResponse: node.data.intentaiStreamResponse,
                        intentaiEnableTime: node.data.intentaiEnableTime,
                        intentaiTimeZone: node.data.intentaiTimeZone,
                        intentaiJsonResponse: node.data.intentaiJsonResponse,
                        

                        // Memory Settings (convert memoryDuration to negative like ModelManagement.vue)
                        intentaiMemoryTurn: node.data.intentaiMemoryTurn,
                        intentaiMemoryDuration: node.data.intentaiMemoryDuration > 0
                            ? -node.data.intentaiMemoryDuration
                            : node.data.intentaiMemoryDuration,
                        intentaiShortTermMemory: node.data.intentaiShortTermMemory,
                        intentaiLongTermMemory: node.data.intentaiLongTermMemory,
                        intentaiIsChainMassage: node.data.intentaiIsChainMassage,
                        intentaiLenChainMassage: node.data.intentaiLenChainMassage,
                        intentaiTimeSecondChainMassage: node.data.intentaiTimeSecondChainMassage,

                        // Line Configuration
                        intentaiShowLineName: node.data.intentaiShowLineName,
                        intentaiLoadingBar: node.data.intentaiLoadingBar,
                        intentaiLoadingBarDuration: node.data.intentaiLoadingBarDuration,
                        intentaiMonitorLineUserId: node.data.intentaiMonitorLineUserId,
                        intentaiMonitorSocialID: node.data.intentaiMonitorSocialID,
                        intentaiForbiddenWords: node.data.intentaiForbiddenWords
                    };

                case 'documents':
                    return {
                        ...baseData,
                        document: node.data.document,
                        textReplacement: node.data.textReplacement,
                        topNChunks: node.data.topNChunks,
                        cosineSimilarity: node.data.cosineSimilarity,
                        selectedDocumentData: node.data.selectedDocumentData || [],
                        enableSpecificDataSelection: node.data.enableSpecificDataSelection || false,
                        documentVectorName: ''
                    };

                case 'databaseIntegration':
                    return {
                        ...baseData,
                        tableName: node.data.tableName,
                        sqlExtractionModel: node.data.sqlExtractionModel,
                        promptSqlNoData: node.data.promptSqlNoData
                    };

                case 'products':
                    return {
                        ...baseData,
                        productGroupList: Array.isArray(node.data.productGroupList)
                            ? node.data.productGroupList.map(item => ({
                                id: item.id,
                                name: item.name
                            }))
                            : [],
                        productTopNChunks: node.data.productTopNChunks,
                        productCosineSimilarity: node.data.productCosineSimilarity
                    };

                case 'apiknowledge':
                    return {
                        ...baseData,
                        knowledgeApiUrl: node.data.knowledgeApiUrl,
                        knowledgeHttpMethod: node.data.knowledgeHttpMethod || 'GET',
                        knowledgeRequestBody: node.data.knowledgeRequestBody,
                        responseDataPath: node.data.responseDataPath,
                        listHeaders: node.data.listHeaders || [],
                    };

                case 'email':
                    return {
                        ...baseData,
                        emailProvider: node.data.emailProvider || 'mailgun',
                        toEmail: node.data.toEmail || '',
                        ccEmails: node.data.ccEmails || [],
                        bccEmails: node.data.bccEmails || [],
                        subject: node.data.subject || '',
                        emailTemplate: node.data.emailTemplate || 'custom',
                        emailBody: node.data.emailBody || '',
                        isHtml: node.data.isHtml !== undefined ? node.data.isHtml : false
                    };

                case 'sharepoint':
                    return {
                        ...baseData,
                        sharepoint: {
                            siteUrl: node.data.sharepoint?.siteUrl || '',
                            tenantId: node.data.sharepoint?.tenantId || '',
                            clientId: node.data.sharepoint?.clientId || '',
                            clientSecret: node.data.sharepoint?.clientSecret || '',
                            folderPath: node.data.sharepoint?.folderPath || '',
                            filename: node.data.sharepoint?.filename || ''
                        }
                    };

                default:
                    return baseData;
            }
        },

        // Validate nested flow structure
        validateNestedFlow(flowData) {
            const errors = [];

            // Check for required flow properties
            if (!flowData.name || flowData.name.trim() === '') {
                errors.push('Flow name is required');
            }

            // Check for at least one root node
            if (!flowData.nodes || flowData.nodes.length === 0) {
                errors.push('Flow must contain at least one root node');
            }

            // Validate nested structure
            const allNodeIds = new Set();
            const validateNode = (node, path = '') => {
                if (!node || !node.id) {
                    errors.push(`Node at path ${path} is missing ID`);
                    return;
                }

                if (allNodeIds.has(node.id)) {
                    errors.push(`Duplicate node ID found: ${node.id} at path ${path}`);
                    return;
                }

                allNodeIds.add(node.id);

                if (!node.nodeType) {
                    errors.push(`Node ${node.id} is missing nodeType`);
                }

                // Validate node-specific data
                // this.validateNodeData(node, errors);

                // Recursively validate target nodes
                if (node.target && Array.isArray(node.target)) {
                    node.target.forEach((targetNode, index) => {
                        validateNode(targetNode, `${path}.target[${index}]`);
                    });
                }
            };

            flowData.nodes.forEach((rootNode, index) => {
                validateNode(rootNode, `nodes[${index}]`);
            });

            return {
                isValid: errors.length === 0,
                errors
            };
        },

        // Validate specific node data
        validateNodeData(node, errors) {
            switch (node.nodeType) {
                case 'messageInput':
                    const messageInputModels = Array.isArray(node.data.modelDisplayId)
                        ? node.data.modelDisplayId
                        : (node.data.modelDisplayId ? [node.data.modelDisplayId] : []);
                    if (!messageInputModels.length) {
                        errors.push(`Message Input node (${node.id}) requires at least one selected model`);
                    }
                    break;
                case 'model':
                    if (!node.data.provider || !node.data.model) {
                        errors.push(`AI Model node (${node.id}) requires a selected model`);
                    }
                    break;
                
                case 'intentAI':
                    if (!node.data.intentaiModel || !node.data.intentaiProvider) {
                        errors.push(`Intent AI node (${node.id}) requires a model and provider`);
                    }
                    break;

                case 'intent':
                    if (!node.data.intents || node.data.intents.length === 0) {
                        errors.push(`Intent node (${node.id}) requires at least one intent`);
                    }
                    break;

                case 'switch':
                    if (!node.data.switchOutputs || node.data.switchOutputs.length < 2) {
                        errors.push(`Switch node (${node.id}) requires at least two output paths`);
                    }
                    break;

                case 'apiTrigger':
                    if (!node.data.apiUrl) {
                        errors.push(`API Trigger node (${node.id}) requires API URL`);
                    }
                    break;

                case 'apiknowledge':
                    if (!node.data.knowledgeApiUrl) {
                        errors.push(`Knowledge API node (${node.id}) requires API URL`);
                    }
                    break;

                case 'email':
                    if (!node.data.toEmail || node.data.toEmail.trim() === '') {
                        errors.push(`Email node (${node.id}) requires a recipient email address`);
                    } else {
                        // Basic email validation
                        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                        if (!emailRegex.test(node.data.toEmail.trim())) {
                            errors.push(`Email node (${node.id}) has invalid recipient email format`);
                        }
                    }
                    
                    if (!node.data.subject || node.data.subject.trim() === '') {
                        errors.push(`Email node (${node.id}) requires a subject`);
                    }
                    
                    if (node.data.emailTemplate === 'custom' && (!node.data.emailBody || node.data.emailBody.trim() === '')) {
                        errors.push(`Email node (${node.id}) requires email content when using custom template`);
                    }
                    break;

                case 'sharepoint':
                    if (!node.data.sharepoint || 
                        !node.data.sharepoint.siteUrl || node.data.sharepoint.siteUrl.trim() === '' ||
                        !node.data.sharepoint.tenantId || node.data.sharepoint.tenantId.trim() === '' ||
                        !node.data.sharepoint.clientId || node.data.sharepoint.clientId.trim() === '' ||
                        !node.data.sharepoint.clientSecret || node.data.sharepoint.clientSecret.trim() === '' ||
                        !node.data.sharepoint.folderPath || node.data.sharepoint.folderPath.trim() === '' ||
                        !node.data.sharepoint.filename || node.data.sharepoint.filename.trim() === '') {
                        errors.push(`SharePoint node (${node.id}) requires all configuration fields: Site URL, Tenant ID, Client ID, Client Secret, Folder Path, and Filename`);
                    }
                    break;
            }
        },
        // Expand node data back to full structure
        expandNodeData(nodeType, data) {
            const expanded = { ...data };

            switch (nodeType) {
                case 'messageInput':
                    expanded.selectedModel = Array.isArray(data.modelDisplayId)
                        ? data.modelDisplayId
                        : (data.modelDisplayId ? [data.modelDisplayId] : []);
                    break;
                case 'model':
                    // Basic model configuration
                    expanded.selectedModel = data.modelDisplayId;
                    expanded.provider = data.provider || null;
                    expanded.model = data.model || null;
                    expanded.apiVersion = data.apiVersion || null;
                    expanded.systemPrompt = data.systemPrompt || '';
                    expanded.useBaseModelPrompt = data.useBaseModelPrompt !== undefined ? data.useBaseModelPrompt : false;

                    // AI Parameters
                    expanded.temperature = data.temperature || null;
                    expanded.maxTokens = data.maxTokens || null;
                    expanded.topP = data.topP || null;
                    expanded.frequencyPenalty = data.frequencyPenalty || null;
                    expanded.presencePenalty = data.presencePenalty || null;
                    expanded.stopSequence = data.stopSequence || null;

                    // Behavior Settings
                    expanded.gender = data.gender || 'male';
                    expanded.typingSpeed = data.typingSpeed || null;
                    expanded.maxInputLength = data.maxInputLength || 200000;

                    // Response Options
                    expanded.streamResponse = data.streamResponse !== undefined ? data.streamResponse : false;
                    expanded.jsonResponse = data.jsonResponse !== undefined ? data.jsonResponse : false;
                    expanded.enableTime = data.enableTime || false;
                    expanded.timeZone = data.timeZone || null;

                    // Memory Settings (convert memoryDuration from negative like ModelManagement.vue)
                    expanded.memoryTurn = data.memoryTurn || null;
                    expanded.memoryDuration = data.memoryDuration < 0
                        ? Math.abs(data.memoryDuration)
                        : data.memoryDuration;
                    expanded.shortTermMemory = data.shortTermMemory || null;
                    expanded.longTermMemory = data.longTermMemory !== undefined ? data.longTermMemory : false;
                    expanded.isChainMassage = data.isChainMassage !== undefined ? data.isChainMassage : false;
                    expanded.lenChainMassage = data.lenChainMassage || null;
                    expanded.timeSecondChainMassage = data.timeSecondChainMassage || null;

                    // Line Configuration
                    expanded.loadingBar = data.loadingBar !== undefined ? data.loadingBar : false;
                    expanded.loadingBarDuration = data.loadingBarDuration || 0;
                    expanded.showLineName = data.showLineName !== undefined ? data.showLineName : false;
                    expanded.monitorLineUserId = data.monitorLineUserId || null;
                    expanded.monitorSocialID = data.monitorSocialID || null;
                    expanded.forbiddenWords = data.forbiddenWords || [''];
                    break;
                case 'intentAI':
                    // Basic intentAI configuration
                    expanded.intentaiProvider = data.intentaiProvider || data.provider || null;
                    // Handle intentaiModel - extract single value from array if needed
                    expanded.intentaiModel = Array.isArray(data.intentaiModel)
                        ? (data.intentaiModel.length > 0 ? data.intentaiModel[0] : null)
                        : (data.intentaiModel || data.model || null);
                    expanded.intentAiModelApiVersion = data.intentAiModelApiVersion || null;
                    expanded.intentaiSystemPrompt = data.intentaiSystemPrompt || data.intentSystemPrompt || '';
                    expanded.intentaiExamples = data.intentaiExamples || data.intentExamples || '';
                    expanded.intentaiConfidence = data.intentaiConfidence || data.intentConfidence || 75;
                    expanded.intentaiFallbackIntent = data.intentaiFallbackIntent || data.fallbackIntent || 'unknown';

                    // AI Parameters
                    expanded.intentaiTemperature = data.intentaiTemperature || data.temperature || null;
                    expanded.intentaiMaxTokens = data.intentaiMaxTokens || data.maxTokens || null;
                    expanded.intentaiTopP = data.intentaiTopP || data.topP || null;
                    expanded.intentaiFrequencyPenalty = data.intentaiFrequencyPenalty || data.frequencyPenalty || null;
                    expanded.intentaiPresencePenalty = data.intentaiPresencePenalty || data.presencePenalty || null;
                    expanded.intentaiStopSequence = data.intentaiStopSequence || data.stopSequence || null;

                    // Behavior Settings
                    expanded.intentaiGender = data.intentaiGender || data.gender || 'male';
                    expanded.intentaiTypingSpeed = data.intentaiTypingSpeed || data.typingSpeed || null;
                    expanded.intentaiMaxInputLength = data.intentaiMaxInputLength || data.maxInputLength || 200000;

                    // Response Options
                    expanded.intentaiStreamResponse = data.intentaiStreamResponse !== undefined ? data.intentaiStreamResponse : (data.streamResponse !== undefined ? data.streamResponse : false);
                    expanded.intentaiJsonResponse = data.intentaiJsonResponse !== undefined ? data.intentaiJsonResponse : (data.jsonResponse !== undefined ? data.jsonResponse : false);
                    expanded.intentaiEnableTime = data.intentaiEnableTime !== undefined ? data.intentaiEnableTime : (data.enableTime || false);
                    expanded.intentaiTimeZone = data.intentaiTimeZone || data.timeZone || null;

                    // Memory Settings (convert memoryDuration from negative like ModelManagement.vue)
                    expanded.intentaiMemoryTurn = data.intentaiMemoryTurn || data.memoryTurn || 5;
                    expanded.intentaiMemoryDuration = data.intentaiMemoryDuration < 0
                        ? Math.abs(data.intentaiMemoryDuration)
                        : (data.memoryDuration < 0 ? Math.abs(data.memoryDuration) : data.intentaiMemoryDuration || data.memoryDuration);
                    expanded.intentaiShortTermMemory = data.intentaiShortTermMemory || data.shortTermMemory || null;
                    expanded.intentaiLongTermMemory = data.intentaiLongTermMemory !== undefined ? data.intentaiLongTermMemory : (data.longTermMemory !== undefined ? data.longTermMemory : false);
                    expanded.intentaiIsChainMassage = data.intentaiIsChainMassage !== undefined ? data.intentaiIsChainMassage : (data.isChainMassage !== undefined ? data.isChainMassage : false);
                    expanded.intentaiLenChainMassage = data.intentaiLenChainMassage || data.lenChainMassage || null;
                    expanded.intentaiTimeSecondChainMassage = data.intentaiTimeSecondChainMassage || data.timeSecondChainMassage || null;

                    // Line Configuration
                    expanded.intentaiShowLineName = data.intentaiShowLineName !== undefined ? data.intentaiShowLineName : (data.showLineName !== undefined ? data.showLineName : false);
                    expanded.intentaiLoadingBar = data.intentaiLoadingBar !== undefined ? data.intentaiLoadingBar : (data.loadingBar !== undefined ? data.loadingBar : false);
                    expanded.intentaiLoadingBarDuration = data.intentaiLoadingBarDuration || data.loadingBarDuration || 0;
                    expanded.intentaiMonitorLineUserId = data.intentaiMonitorLineUserId || data.monitorLineUserId || null;
                    expanded.intentaiMonitorSocialID = data.intentaiMonitorSocialID || data.monitorSocialID || null;
                    expanded.intentaiForbiddenWords = data.intentaiForbiddenWords || data.forbiddenWords || [''];
                    break;
                case 'intent':
                    // Ensure required arrays exist
                    expanded.intents = data.intents || [];
                    expanded.actions = data.actions || [];
                    expanded.outputs = data.outputs || [];
                    break;
                case 'smartIntent':
                    // Ensure all smartIntent properties are properly loaded
                    expanded.smartIntentGroupName = data.smartIntentGroupName || '';
                    expanded.smartIntents = data.smartIntents || [];
                    
                    // Process each smartIntent to ensure forbiddenWords have empty item at the end
                    if (expanded.smartIntents && expanded.smartIntents.length > 0) {
                        expanded.smartIntents.forEach(smartIntent => {
                            if (smartIntent.mainAction) {
                                // Ensure apiVersion is restored
                                if (smartIntent.mainAction.apiVersion === undefined) {
                                    smartIntent.mainAction.apiVersion = null;
                                }

                                // Ensure forbiddenWords array exists and has empty item at the end
                                if (smartIntent.mainAction.forbiddenWords && Array.isArray(smartIntent.mainAction.forbiddenWords)) {
                                    // Only add empty item if the last item is not already empty
                                    const lastItem = smartIntent.mainAction.forbiddenWords[smartIntent.mainAction.forbiddenWords.length - 1];
                                    if (lastItem !== '') {
                                        smartIntent.mainAction.forbiddenWords.push('');
                                    }
                                } else {
                                    smartIntent.mainAction.forbiddenWords = [''];
                                }
                            }
                        });
                    }
                    
                    expanded.smartIntentFallback = data.smartIntentFallback || {
                        useAI: false,
                        provider: null,
                        model: null,
                        apiVersion: null,
                        gender: 'male',
                        typingSpeed: null,
                        maxInputLength: 200000,
                        streamResponse: false,
                        jsonResponse: false,
                        enableTime: null,
                        timeZone: null,
                        temperature: null,
                        topP: null,
                        frequencyPenalty: null,
                        presencePenalty: null,
                        maxTokens: null,
                        stopSequence: null,
                        memoryTurn: null,
                        memoryDuration: null,
                        shortTermMemory: null,
                        longTermMemory: false,
                        isChainMassage: false,
                        lenChainMassage: null,
                        timeSecondChainMassage: null,
                        forbiddenWords: ['']
                    };
                    
                    // Ensure fallback forbiddenWords has empty item at the end
                    if (expanded.smartIntentFallback.forbiddenWords && Array.isArray(expanded.smartIntentFallback.forbiddenWords)) {
                        const lastItem = expanded.smartIntentFallback.forbiddenWords[expanded.smartIntentFallback.forbiddenWords.length - 1];
                        if (lastItem !== '') {
                            expanded.smartIntentFallback.forbiddenWords.push('');
                        }
                    } else {
                        expanded.smartIntentFallback.forbiddenWords = [''];
                    }
                    break;
                case 'contactAgent':
                    // Ensure all contact agent properties are properly loaded
                    expanded.departmentName = data.departmentName || '';
                    expanded.contactAgentMessage = data.contactAgentMessage || '';
                    expanded.handoffMessage = data.handoffMessage || '';
                    expanded.botDisableTime = data.botDisableTime || null;
                    expanded.disableBot = data.disableBot !== undefined ? data.disableBot : true;
                    break;
                case 'apiTrigger':
                    // Ensure all API trigger properties are properly loaded
                    expanded.apiUrl = data.apiUrl || '';
                    expanded.httpMethod = data.httpMethod || 'GET';
                    expanded.headers = data.headers || '';
                    expanded.textResponse = data.textResponse || '';
                    expanded.jsonValidate = data.jsonValidate || '{\n   "Name":"Value"\n}';
                    expanded.modelJsonValidator = data.modelJsonValidator || null;
                    expanded.listHeadersAPITrigger = data.listHeadersAPITrigger || [
                        {
                            key: '',
                            value: '',
                        },
                    ];
                    expanded.listValidation = data.listValidation || [
                        {
                            key: '',
                            typeValidation: '',
                            numberOfCharacter: '',
                            regExp: '',
                            promptMessage: '',
                        },
                    ];
                    break;
                case 'products':
                    // Ensure all products node properties are properly loaded
                    expanded.productGroupList = data.productGroupList || [];
                    expanded.productTopNChunks = data.productTopNChunks || 5;
                    expanded.productCosineSimilarity = data.productCosineSimilarity || 0.7;
                    break;
                case 'documents':
                    // Ensure all documents node properties are properly loaded
                    expanded.document = data.document || [];
                    expanded.textReplacement = data.textReplacement || [];
                    expanded.topNChunks = data.topNChunks || 5;
                    expanded.cosineSimilarity = data.cosineSimilarity || 0.7;
                    expanded.selectedDocumentData = data.selectedDocumentData || [];
                    expanded.enableSpecificDataSelection = data.enableSpecificDataSelection || false;
                    expanded.documentVectorName = '';
                    break;
                case 'apiknowledge':
                    // Ensure all apiknowledge node properties are properly loaded
                    expanded.knowledgeApiUrl = data.knowledgeApiUrl || '';
                    expanded.knowledgeHttpMethod = data.knowledgeHttpMethod || 'GET';
                    expanded.knowledgeRequestBody = data.knowledgeRequestBody || '';
                    expanded.responseDataPath = data.responseDataPath || 'data';
                    expanded.listHeaders = data.listHeaders || [];
                    expanded.description = data.description || '';
                    break;
                case 'email':
                    // Ensure all email node properties are properly loaded
                    expanded.emailProvider = data.emailProvider || 'mailgun';
                    expanded.toEmail = data.toEmail || '';
                    expanded.ccEmails = data.ccEmails || [];
                    expanded.bccEmails = data.bccEmails || [];
                    expanded.subject = data.subject || '';
                    expanded.emailTemplate = data.emailTemplate || 'custom';
                    expanded.emailBody = data.emailBody || '';
                    expanded.isHtml = data.isHtml !== undefined ? data.isHtml : false;
                    break;
                case 'sharepoint':
                    // Ensure all SharePoint node properties are properly loaded
                    expanded.sharepoint = data.sharepoint || {
                        siteUrl: '',
                        tenantId: '',
                        clientId: '',
                        clientSecret: '',
                        folderPath: '',
                        filename: ''
                    };
                    break;
            }
            return expanded;
        },
        validateFlatFlow(flowData) {
            const errors = [];

            // Check for required flow properties
            if (!flowData.name || flowData.name.trim() === '') {
                errors.push('Flow name is required');
            }

            // Check for at least one node
            if (!flowData.flowDetails || flowData.flowDetails.length === 0) {
                errors.push('Flow must contain at least one node');
            }

            // Validate nodes
            const nodeIds = new Set();
            flowData.flowDetails.forEach((node, index) => {
                if (!node.id) {
                    errors.push(`Node ${index + 1} is missing ID`);
                }

                if (nodeIds.has(node.id)) {
                    errors.push(`Duplicate node ID found: ${node.id}`);
                }
                nodeIds.add(node.id);

                if (!node.nodeType) {
                    errors.push(`Node ${node.id} is missing nodeType`);
                }

                if (!node.position) {
                    errors.push(`Node ${node.id} is missing position`);
                }

                // Validate node-specific data
                // this.validateNodeData(node, errors);
            });

            // Validate target references
            const allNodeIds = new Set(flowData.flowDetails.map(node => node.id));
            flowData.flowDetails.forEach(node => {
                if (node.targets && Array.isArray(node.targets)) {
                    node.targets.forEach((target, targetIndex) => {
                        if (!target.nodeId) {
                            errors.push(`Node ${node.id} target ${targetIndex + 1} is missing nodeId`);
                        } else if (!allNodeIds.has(target.nodeId)) {
                            errors.push(`Node ${node.id} references non-existent target node: ${target.nodeId}`);
                        }
                    });
                }
            });

            return {
                isValid: errors.length === 0,
                errors
            };
        },

        // API call to save flow
        async saveFlowToAPI(flowData, apiMethod = 'CreateFlow') {
            return new Promise((resolve, reject) => {
                const apiFlowData = {
                    ...flowData,
                    flowDetails: flowData.flowDetails.map(node => ({
                        ...node,
                        position: JSON.stringify(node.position),
                        data: JSON.stringify(node.data),
                        targets: JSON.stringify(node.targets)
                    }))
                };
                apiFlowData.flowId = this.flowId;
                this.$call[apiMethod](
                    apiFlowData,
                    (dataResponse) => {
                        if (dataResponse.status === 0) {
                            resolve({
                                success: true,
                                flowId: dataResponse.data?.flowId || dataResponse.data?.id,
                                message: dataResponse.message
                            });
                        } else {
                            resolve({
                                success: false,
                                message: dataResponse.message || 'Unknown error occurred'
                            });
                        }
                    },
                    (errorResponse) => {
                        reject(new Error(errorResponse.message || 'API call failed'));
                    }
                );
            });
        },

        // Validate flow structure
        validateFlow(flowData) {
            const errors = [];

            // Check for required flow properties
            if (!flowData.name || flowData.name.trim() === '') {
                errors.push('Flow name is required');
            }

            // Check for at least one node
            if (!flowData.nodes || flowData.nodes.length === 0) {
                errors.push('Flow must contain at least one node');
            }

            // Validate nodes
            flowData.nodes.forEach((node, index) => {
                if (!node.id) {
                    errors.push(`Node ${index + 1} is missing ID`);
                }
                if (!node.data || !node.data.type) {
                    errors.push(`Node ${index + 1} is missing type`);
                }
                if (!node.position) {
                    errors.push(`Node ${index + 1} is missing position`);
                }

                // Validate specific node types
                switch (node.data.type) {
                    case 'model':
                        if (!node.data.selectedModel) {
                            errors.push(`AI Model node (${node.id}) requires a selected model`);
                        }
                        break;
                    case 'intent':
                        if (!node.data.intents || node.data.intents.length === 0) {
                            errors.push(`Intent node (${node.id}) requires at least one intent`);
                        }
                        break;
                    case 'switch':
                        if (!node.data.switchOutputs || node.data.switchOutputs.length < 2) {
                            errors.push(`Switch node (${node.id}) requires at least two output paths`);
                        }
                        break;
                    case 'apiTrigger':
                        if (!node.data.apiUrl) {
                            errors.push(`API Trigger node (${node.id}) requires API URL`);
                        }
                        break;
                }
            });

            // Validate edges
            flowData.edges.forEach((edge, index) => {
                if (!edge.source || !edge.target) {
                    errors.push(`Edge ${index + 1} is missing source or target`);
                }

                // Check if source and target nodes exist
                const sourceExists = flowData.nodes.some(node => node.id === edge.source);
                const targetExists = flowData.nodes.some(node => node.id === edge.target);

                if (!sourceExists) {
                    errors.push(`Edge ${index + 1} references non-existent source node: ${edge.source}`);
                }
                if (!targetExists) {
                    errors.push(`Edge ${index + 1} references non-existent target node: ${edge.target}`);
                }
            });

            return {
                isValid: errors.length === 0,
                errors
            };
        },

        // Quick save function for auto-save
        async quickSave() {
            if (this.flowId) {
                // Only auto-save if flow already exists
                await this.SaveFlow();
            }
        },

        // Show success message
        showSuccess(message) {
            this.snackbarMessage = message;
            this.snackBarColor = 'success';
            this.showSnackbar = true;
        },

        // Show error message
        showError(message) {
            this.snackbarMessage = message;
            this.snackBarColor = 'error';
            this.showSnackbar = true;
            console.error(message);
        },

        // Show warning message
        showWarning(message) {
            this.snackbarMessage = message;
            this.snackBarColor = 'warning';
            this.showSnackbar = true;
        },

        // Show info message
        showInfo(message) {
            this.snackbarMessage = message;
            this.snackBarColor = 'info';
            this.showSnackbar = true;
        },
        // Load flow data for editing
        async loadFlowForEdit(flowId) {
            try {
                this.dialog = true;

                const response = await this.loadFlowFromAPI(flowId);

                if (response.success) {
                    const flowData = response.data;
                    // Convert flow data to editor format
                    if (flowData.flowDetails && Array.isArray(flowData.flowDetails)) {
                        const { nodes, edges } = this.convertFromFlatStructure(flowData.flowDetails);
                        this.tempNode = nodes;
                        this.tempEdge = edges;
                    } else {
                        // Fallback for old format
                        this.tempNode = flowData.nodes || [];
                        this.tempEdge = flowData.edges || [];
                    }
                    let messageInputTemp = this.tempNode.find(n => n.data && n.data.type === 'messageInput');
                    if (messageInputTemp) {
                        // Only filter models if uniqueAiModelOptions is available (to prevent losing models on reload)
                        if (this.uniqueAiModelOptions && this.uniqueAiModelOptions.length > 0) {
                            this.unableToAccessModel = messageInputTemp.data.selectedModel;
                            let tempUniqueModels = this.uniqueAiModelOptions.map(option => option.value);
                            this.unableToAccessModel = this.unableToAccessModel.filter(model => !tempUniqueModels.includes(model));
                            this.tempNode = this.tempNode.map(node => {
                                if (node.data && node.data.type === 'messageInput') {
                                    let filteredModels = node.data.selectedModel.filter(model => !this.unableToAccessModel.includes(model));
                                    return {
                                        ...node,
                                        data: {
                                            ...node.data,
                                            selectedModel: filteredModels
                                        }
                                    };
                                }
                                return node;
                            });
                        } else {
                            // If uniqueAiModelOptions is not loaded yet, keep original selectedModel values
                            // They will be filtered later when models are loaded
                            console.warn('AI Model options not loaded yet, keeping original selectedModel values');
                        }
                    }
                    // Update flow metadata
                    this.flowId = flowData.id;
                    this.flowName = flowData.name;
                    this.flowCreatedAt = flowData.createDate;

                    this.showSuccess(`Flow "${flowData.name}" loaded successfully`);

                    // Fit view after loading nodes
                    this.$nextTick(() => {
                        // Check validation for all nodes after loading
                        this.checkAllNodesValidation();

                        setTimeout(() => {
                            // Fit view to show all nodes if in edit or view mode
                            if ((this.isEditMode || this.isViewMode) && this.$refs.vueFlow && this.tempNode.length > 0) {
                                try {
                                    this.$refs.vueFlow.fitView({
                                        padding: 0.2,
                                        minZoom: 0.5,
                                        maxZoom: 1.5,
                                        duration: 800,
                                        includeHiddenNodes: false
                                    });
                                } catch (error) {
                                    console.warn('Failed to fit view:', error);
                                }
                            }
                        }, 200);
                    });

                } else {
                    throw new Error(response.message || 'Failed to load flow');
                }

            } catch (error) {
                console.error('Error loading flow for edit:', error);
                this.showError(`Failed to load flow: ${error.message}`);

                // Redirect back to flow list on error
                this.$router.push('/FlowList');
            } finally {
                this.dialog = false;
                this.$nextTick(() => {
                    setTimeout(() => {
                        // Allow time for UI to stabilize
                    }, 300);
                });
            }
        },
        async handleImportedData(importDataString) {
            try {
                const flowData = JSON.parse(importDataString);

                // Validate imported data
                if (!flowData.name || !flowData.flowDetails) {
                    throw new Error('Invalid flow structure');
                }

                // Convert imported data to editor format
                if (flowData.flowDetails && Array.isArray(flowData.flowDetails)) {
                    const { nodes, edges } = this.convertFromFlatStructure(flowData.flowDetails);
                    this.tempNode = nodes;
                    this.tempEdge = edges;
                } else {
                    this.tempNode = flowData.nodes || [];
                    this.tempEdge = flowData.edges || [];
                }

                // Set flow metadata
                this.flowId = null; // New flow from import
                this.flowName = `${flowData.name} (Imported)`;
                this.isEditMode = true; // Allow editing of imported flow

                this.showSuccess(`Flow "${flowData.name}" imported successfully`);

                // Clear the import data from URL
                this.$router.replace({ path: '/FlowCreator' });

                this.$nextTick(() => {
                    setTimeout(() => {
                        // Allow time for UI to stabilize
                    }, 300);
                });
                this.$nextTick(() => {
                    setTimeout(() => {
                        // Fit view after importing nodes
                        if (this.$refs.vueFlow && this.tempNode.length > 0) {
                            try {
                                this.$refs.vueFlow.fitView({
                                    padding: 0.1,
                                    minZoom: 0.5,
                                    maxZoom: 1.5,
                                    duration: 800,
                                    includeHiddenNodes: false
                                });
                            } catch (error) {
                                console.warn('Failed to fit view after import:', error);
                            }
                        }
                    }, 200);
                });

            } catch (error) {
                console.error('Error handling imported data:', error);
                this.showError('Failed to import flow data');
                this.$router.push('/FlowList');
            }
        },
        // Set view-only mode
        setViewOnlyMode() {
            this.$nextTick(() => {
                if (this.$refs.vueFlow) {
                    const flow = this.$refs.vueFlow;
                    // Lock all interactions
                    flow.nodesDraggable = false;
                    flow.nodesConnectable = false;
                    flow.elementsSelectable = false;
                    this.interactiveControls = false;
                }
            });
        },

        // Load flow with nested structure support
        async LoadFlow(flowId) {
            try {
                this.dialog = true;

                const response = await this.loadFlowFromAPI(flowId);

                if (response.success) {
                    const flowData = response.data;

                    // Check if it's flat structure (version 2.0+) or old nested structure
                    if (flowData.metadata?.version && parseFloat(flowData.metadata.version) >= 2.0) {
                        // Check if it uses targets or source format
                        const firstNode = flowData.nodes[0];
                        if (firstNode && firstNode.targets !== undefined) {
                            // New targets format
                            const { nodes, edges } = this.convertFromFlatStructure(flowData.nodes);
                            this.tempNode = nodes;
                            this.tempEdge = edges;
                        } else if (firstNode && firstNode.source !== undefined) {
                            // Old source format - convert to new format
                            const { nodes, edges } = this.convertFromSourceFormat(flowData.nodes);
                            this.tempNode = nodes;
                            this.tempEdge = edges;
                        } else {
                            throw new Error('Invalid flow data format');
                        }
                    } else {
                        // Load old nested structure directly (backward compatibility)
                        this.tempNode = flowData.nodes.map(node => ({
                            ...node,
                            selected: false
                        }));
                        this.tempEdge = flowData.edges || [];
                    }

                    // Update flow metadata
                    this.flowId = flowData.id;
                    this.flowName = flowData.name;
                    this.flowCreatedAt = flowData.metadata?.createdAt;

                    // Restore viewport if available
                    if (flowData.metadata?.viewport && this.$refs.vueFlow) {
                        setTimeout(() => {
                            this.$refs.vueFlow.setViewport(flowData.metadata.viewport);
                        }, 300);
                    }

                    // Check validation for all nodes after loading
                    this.$nextTick(() => {
                        this.checkAllNodesValidation();
                    });

                    this.showSuccess('Flow loaded successfully');
                } else {
                    throw new Error(response.message || 'Failed to load flow');
                }

            } catch (error) {
                console.error('Error loading flow:', error);
                this.showError(`Failed to load flow: ${error.message}`);
            } finally {
                this.dialog = false;
            }
        },
        async loadFlowFromAPI(flowId) {
            return new Promise((resolve, reject) => {
                this.$call.GetFlowById(
                    flowId,
                    (dataResponse) => {
                        if (dataResponse.status === 0) {
                            resolve({
                                success: true,
                                data: dataResponse.data
                            });
                        } else {
                            resolve({
                                success: false,
                                message: dataResponse.message || 'Failed to load flow'
                            });
                        }
                    },
                    (errorResponse) => {
                        reject(new Error(errorResponse.message || 'API call failed'));
                    }
                );
            });
        },
        // Helper method to convert old source format to new targets format
        convertFromSourceFormat(sourceNodes) {
            const flowNodes = [];
            const flowEdges = [];

            sourceNodes.forEach(sourceNode => {
                // Create flow node
                const flowNode = {
                    id: sourceNode.id,
                    type: 'flow-node',
                    position: sourceNode.position,
                    data: {
                        type: sourceNode.nodeType,
                        label: sourceNode.data.label,
                        ...this.expandNodeData(sourceNode.nodeType, sourceNode.data)
                    },
                    selected: false
                };

                flowNodes.push(flowNode);

                // Create edge if source exists
                if (sourceNode.source) {
                    const edge = {
                        id: `e${sourceNode.source}-${sourceNode.id}-${Date.now()}`,
                        source: sourceNode.source,
                        target: sourceNode.id,
                        sourceHandle: sourceNode.sourceHandle || null,
                        targetHandle: 'input',
                        type: 'flow-edge',
                        markerEnd: 'arrowclosed',
                        animated: false,
                        showButton: false
                    };

                    flowEdges.push(edge);
                }
            });

            return { nodes: flowNodes, edges: flowEdges };
        },


        clearFlow() {
            this.tempNode = [];
            this.tempEdge = [];
            
            // Save state to history
            this.saveStateToHistory('Clear Flow');
            
            this.showSuccess('Flow cleared');
        },
        // Methods for managing intent group, intents, and their actions
        addInspectorIntent() {
            if (!this.nodeInspector.intents) {
                this.nodeInspector.intents = [];
            }
            const defaultBasePrompt = `Find the Intent from the message of users.
#Condition for Response
1. If the user asks a question related to or similar in meaning to the #Group Intent below. You must response 1. Otherwise, 0`;

            const newIntent = {
                id: `intent-${this.generateGuid()}`, // Use GUID
                name: `Intent Name ${this.nodeInspector.intents.length + 1}`,
                priority: 0, // Default priority
                prompt: '',
                basePrompt: defaultBasePrompt, // Use default base prompt
                enableBasePrompt: false, // Disable base prompt by default
                messages: [
                    { id: `msg-${this.generateGuid()}`, text: '' } // Use GUID
                ],
                mainAction: {
                    id: `main-${this.generateGuid()}`, // Use GUID
                    label: 'Main Action'
                },
                parallelActions: []
            };
            this.nodeInspector.intents.push(newIntent);
            this.updateIntentData();
        },
        removeInspectorIntent(index) {
            if (this.nodeInspector.intents && this.nodeInspector.intents.length > 1) {
                const intentToRemove = this.nodeInspector.intents[index];

                // Collect all source handles related to this intent (main action + parallel actions)
                const sourceHandlesToRemove = [];

                // Add main action source handle
                sourceHandlesToRemove.push(`${intentToRemove.id}-main`);

                // Add parallel actions source handles
                if (intentToRemove.parallelActions) {
                    intentToRemove.parallelActions.forEach(parallel => {
                        sourceHandlesToRemove.push(`${intentToRemove.id}-${parallel.id}`);
                    });
                }

                // Remove all edges connected to this intent's actions
                this.tempEdge = this.tempEdge.filter(edge =>
                    !(edge.source === this.selectedNode.id && sourceHandlesToRemove.includes(edge.sourceHandle))
                );

                // Remove the intent
                this.nodeInspector.intents.splice(index, 1);
                this.updateIntentData();
            }
        },
        addInspectorParallelAction(intentIndex) {
            const intent = this.nodeInspector.intents[intentIndex];
            if (!intent.parallelActions) {
                intent.parallelActions = [];
            }
            const newParallel = {
                id: `parallel-${this.generateGuid()}`,
                label: `Parallel Action ${intent.parallelActions.length + 1}`
            };
            intent.parallelActions.push(newParallel);
            this.updateIntentData();
        },
        removeInspectorParallelAction(intentIndex, parallelIndex) {
            const intent = this.nodeInspector.intents[intentIndex];
            if (intent.parallelActions && intent.parallelActions.length > parallelIndex) {
                const parallelToRemove = intent.parallelActions[parallelIndex];
                const sourceHandleToRemove = `${intent.id}-${parallelToRemove.id}`;

                // Remove edges connected to this parallel action
                this.tempEdge = this.tempEdge.filter(edge =>
                    !(edge.source === this.selectedNode.id && edge.sourceHandle === sourceHandleToRemove)
                );

                // Remove the parallel action
                intent.parallelActions.splice(parallelIndex, 1);
                this.updateIntentData();
            }
        },
        addInspectorIntentMessage(intentIndex) {
            const intent = this.nodeInspector.intents[intentIndex];
            if (!intent.messages) {
                intent.messages = [];
            }
            const newMessage = {
                id: `msg-${this.generateGuid()}`,
                text: ''
            };
            intent.messages.push(newMessage);
            this.updateIntentData();
        },
        removeInspectorIntentMessage(intentIndex, messageIndex) {
            const intent = this.nodeInspector.intents[intentIndex];
            if (intent.messages && intent.messages.length > 1) {
                intent.messages.splice(messageIndex, 1);
                this.updateIntentData();
            }
        },
        convertIntentsToActions(intents) {
            const actions = [];
            intents.forEach((intent, intentIndex) => {
                // Add main action for each intent
                actions.push({
                    id: `${intent.id}-main`,
                    label: `${intent.mainAction.label}`,
                    type: 'main',
                    intentId: intent.id,
                });

                // Add parallel actions for each intent
                intent.parallelActions.forEach((parallel, parallelIndex) => {
                    actions.push({
                        id: `${intent.id}-${parallel.id}`,
                        label: `${parallel.label}`,
                        type: 'parallel',
                        intentId: intent.id,
                    });
                });
            });
            return actions;
        },
        convertIntentsToOutputs(intents) {
            const outputs = [];
            intents.forEach((intent, intentIndex) => {
                // Add main action output for each intent
                outputs.push({
                    id: `${intent.id}-main`,
                    label: `${intent.mainAction.label}`,
                    type: 'main',
                    intentId: intent.id,
                });

                // Add parallel outputs for each intent
                intent.parallelActions.forEach((parallel, parallelIndex) => {
                    outputs.push({
                        id: `${intent.id}-${parallel.id}`,
                        label: `${parallel.label}`,
                        type: 'parallel',
                        intentId: intent.id,
                    });
                });
            });
            return outputs;
        },
        // Methods for managing intent actions and parallels (Legacy - keep for compatibility)
        addInspectorAction() {
            if (!this.nodeInspector.actions) {
                this.nodeInspector.actions = [];
            }
            const newAction = {
                id: `action-${this.generateGuid()}`,
                label: `Action ${this.nodeInspector.actions.length + 1}`,
                parallels: [
                    { id: `parallel-${this.generateGuid()}`, label: 'Parallel Action 1' }
                ]
            };
            this.nodeInspector.actions.push(newAction);
        },
        removeInspectorAction(index) {
            if (this.nodeInspector.actions && this.nodeInspector.actions.length > 1) {
                this.nodeInspector.actions.splice(index, 1);
            }
        },
        addInspectorParallel(actionIndex) {
            if (!this.nodeInspector.actions[actionIndex].parallels) {
                this.nodeInspector.actions[actionIndex].parallels = [];
            }
            const action = this.nodeInspector.actions[actionIndex];
            const newParallel = {
                id: `parallel-${this.generateGuid()}`,
                label: `Parallel Action ${action.parallels.length + 1}`
            };
            action.parallels.push(newParallel);
        },
        removeInspectorParallel(actionIndex, parallelIndex) {
            const action = this.nodeInspector.actions[actionIndex];
            if (action.parallels && action.parallels.length > 1) {
                action.parallels.splice(parallelIndex, 1);
            }
        },

        // Switch methods
        addInspectorSwitchOutput() {
            if (!this.nodeInspector.switchOutputs) {
                this.nodeInspector.switchOutputs = [];
            }
            const newOutput = {
                id: `switch-output-${this.generateGuid()}`,
                name: `Output ${this.nodeInspector.switchOutputs.length + 1}`,
                condition: '',
                description: '',
                // Simple condition builder properties
                conditionMode: 'simple',
                conditionType: 'text_contains',
                sourceField: 'input.text',
                searchValue: '',
                compareValue: '',
                caseSensitive: false,
                operator: 'gt',
                expectedValue: true,
                regexPattern: '',
                regexFlags: '',
                lengthOperator: 'gt',
                lengthValue: 0,
                lengthValueMax: 100
            };
            this.nodeInspector.switchOutputs.push(newOutput);
            this.updateSwitchData();
        },
        removeInspectorSwitchOutput(index) {
            if (this.nodeInspector.switchOutputs && this.nodeInspector.switchOutputs.length > 2) {
                this.nodeInspector.switchOutputs.splice(index, 1);
                this.updateSwitchData();
            }
        },
        updateSwitchData() {
            if (!this.selectedNode || this.selectedNode.data.type !== 'switch') return;

            this.updateNodeProperty('switchOutputs', this.nodeInspector.switchOutputs);
            this.updateNodeProperty('switchName', this.nodeInspector.switchName);
        },

        // Smart Intent Methods
        addInspectorSmartIntent() {
            if (!this.nodeInspector.smartIntents) {
                this.nodeInspector.smartIntents = [];
            }
            const defaultBasePrompt = `Find the Intent from the message of users.
#Condition for Response
1. If the user asks a question related to or similar in meaning to the #Group Intent below. You must response 1. Otherwise, 0`;

            const newSmartIntent = {
                id: `smart-intent-${this.generateGuid()}`,
                name: `Smart Intent ${this.nodeInspector.smartIntents.length + 1}`,
                priority: 0,
                prompt: '',
                basePrompt: defaultBasePrompt,
                enableBasePrompt: false,
                useMemory: false,
                messages: [
                    { id: `msg-${this.generateGuid()}`, text: '' }
                ],
                mainAction: {
                    id: `main-${this.generateGuid()}`,
                    label: 'Action',
                    useAI: false,
                    // Basic Information - match AI Model defaults
                    provider: null,
                    model: null,
                    gender: 'male',
                    typingSpeed: null,
                    maxInputLength: 200000,
                    // Response Options - match AI Model defaults
                    streamResponse: false,
                    jsonResponse: false,
                    enableTime: false,
                    timeZone: null,
                    // AI Parameters - match AI Model defaults
                    temperature: null,
                    topP: null,
                    frequencyPenalty: null,
                    presencePenalty: null,
                    maxTokens: null,
                    stopSequence: null,
                    // Output Control - match AI Model defaults
                    streamingEnabled: true,
                    // Line Configuration - match AI Model defaults
                    lineFlexMessage: false,
                    // System Prompt - match AI Model defaults
                    systemPrompt: '',
                    // Forbidden Words - match AI Model defaults
                    forbiddenWords: [''],
                    // Memory Settings - match AI Model defaults
                    memoryTurn: null,
                    memoryDuration: null,
                    shortTermMemory: null,
                    longTermMemory: false,
                    // Multi-turn Input Configuration - match AI Model defaults
                    isChainMassage: false,
                    lenChainMassage: null,
                    timeSecondChainMassage: null,
                    // Knowledge Configuration
                    knowledgeType: '',
                    // Knowledge Sources Enable Flags
                    enableDocuments: false,
                    enableDatabaseIntegration: false,
                    enableProducts: false,
                    enableApiKnowledge: false,
                    // Documents properties
                    document: [],
                    topNChunks: 5,
                    cosineSimilarity: 0.5,
                    // Database Integration properties
                    tableName: null,
                    sqlExtractionModel: null,
                    promptSqlNoData: '',
                    // Products properties
                    productGroupList: [],
                    productTopNChunks: 5,
                    productCosineSimilarity: 0.5,
                    // API Knowledge properties
                    knowledgeApiUrl: '',
                    knowledgeHttpMethod: 'GET',
                    listHeaders: [],
                    knowledgeRequestBody: '',
                    knowledgeResponseDataPath: ''
                }
            };
            this.nodeInspector.smartIntents.push(newSmartIntent);
            this.updateSmartIntentData();
        },

        removeInspectorSmartIntent(index) {
            if (this.nodeInspector.smartIntents && this.nodeInspector.smartIntents.length > 1) {
                const smartIntentToRemove = this.nodeInspector.smartIntents[index];

                // Collect source handles related to this smart intent
                const sourcesToRemove = [];
                sourcesToRemove.push(`${smartIntentToRemove.id}-main`);

                // Remove edges connected to these source handles
                this.tempEdge = this.tempEdge.filter(edge => 
                    !sourcesToRemove.includes(edge.sourceHandle)
                );

                // Remove the smart intent
                this.nodeInspector.smartIntents.splice(index, 1);
                this.updateSmartIntentData();
            }
        },

        addInspectorSmartIntentMessage(smartIntentIndex) {
            const smartIntent = this.nodeInspector.smartIntents[smartIntentIndex];
            const newMessage = {
                id: `msg-${this.generateGuid()}`,
                text: ''
            };
            smartIntent.messages.push(newMessage);
            this.updateSmartIntentData();
        },

        removeInspectorSmartIntentMessage(smartIntentIndex, messageIndex) {
            const smartIntent = this.nodeInspector.smartIntents[smartIntentIndex];
            if (smartIntent.messages && smartIntent.messages.length > 1) {
                smartIntent.messages.splice(messageIndex, 1);
                this.updateSmartIntentData();
            }
        },

        updateSmartIntentData() {
            if (!this.selectedNode || this.selectedNode.data.type !== 'smartIntent') return;
            
            this.updateNodeProperty('smartIntents', this.nodeInspector.smartIntents);
            this.updateNodeProperty('smartIntentGroupName', this.nodeInspector.smartIntentGroupName);
            
            // Trigger validation check for real-time feedback
            this.checkNodeValidation(this.selectedNode);
        },
        
        updateSmartIntentProviderChange(smartIntentIndex, newProvider) {
            // Clear model when provider changes
            this.nodeInspector.smartIntents[smartIntentIndex].mainAction.model = null;
            this.nodeInspector.smartIntents[smartIntentIndex].mainAction.apiVersion = null;
            this.updateSmartIntentData();
        },

        updateSmartIntentModelChange(smartIntentIndex, newModel) {
            // Auto-assign apiVersion when model changes
            this.$nextTick(() => {
                const selectedModel = this.listDDModel.find(m => m.modelName === newModel);
                if (selectedModel) {
                    this.nodeInspector.smartIntents[smartIntentIndex].mainAction.apiVersion = selectedModel.apiVersion;
                }
                this.updateSmartIntentData();
            });
        },

        updateSmartIntentFallbackData() {
            if (!this.selectedNode || this.selectedNode.data.type !== 'smartIntent') return;
            
            // Initialize fallback if it doesn't exist
            if (!this.nodeInspector.smartIntentFallback) {
                this.nodeInspector.smartIntentFallback = {
                    useAI: false,
                    provider: '',
                    model: '',
                    apiVersion: null,
                    // Basic Information
                    gender: 'male',
                    typingSpeed: null,
                    maxInputLength: 200000,
                    // Response Options
                    streamingEnabled: false,
                    enableTime: false,
                    timeZone: null,
                    // AI Parameters
                    temperature: 0.7,
                    topP: 1.0,
                    frequencyPenalty: 0.0,
                    presencePenalty: 0.0,
                    maxTokens: 150,
                    stopSequence: '',
                    // Memory Settings
                    memoryTurn: null,
                    memoryDuration: null,
                    shortTermMemory: null,
                    longTermMemory: false,
                    // Multi-turn Input Configuration
                    isChainMassage: false,
                    lenChainMassage: null,
                    timeSecondChainMassage: null,
                    // Line Configuration
                    showLineName: false,
                    loadingBar: false,
                    loadingBarDuration: 0,
                    monitorLineUserId: null,
                    monitorSocialID: null,
                    lineFlexMessage: false,
                    // System Prompt
                    systemPrompt: '',
                    // Forbidden Words
                    forbiddenWords: ['']
                };
            }
            
            this.updateNodeProperty('smartIntentFallback', this.nodeInspector.smartIntentFallback);
            
            // Trigger validation check for real-time feedback
            this.checkNodeValidation(this.selectedNode);
        },
        
        updateSmartIntentFallbackProviderChange(newProvider) {
            // Clear model when provider changes
            this.nodeInspector.smartIntentFallback.model = '';
            this.nodeInspector.smartIntentFallback.apiVersion = null;
            this.updateSmartIntentFallbackData();
        },

        updateSmartIntentFallbackModelChange(newModel) {
            // Auto-assign apiVersion when model changes
            this.$nextTick(() => {
                const selectedModel = this.listDDModel.find(m => m.modelName === newModel);
                if (selectedModel) {
                    this.nodeInspector.smartIntentFallback.apiVersion = selectedModel.apiVersion;
                }
                this.updateSmartIntentFallbackData();
            });
        },

        // Smart Intent Knowledge API Header Management Methods
        addSmartIntentKnowledgeAPIHeader(smartIntentIndex) {
            const smartIntent = this.nodeInspector.smartIntents[smartIntentIndex];
            if (!smartIntent.mainAction.listHeaders) {
                smartIntent.mainAction.listHeaders = [];
            }
            
            const newHeader = {
                id: `header-${this.generateGuid()}`,
                key: '',
                value: ''
            };
            
            smartIntent.mainAction.listHeaders.push(newHeader);
            this.updateSmartIntentData();
        },

        removeSmartIntentKnowledgeAPIHeader(smartIntentIndex, headerIndex) {
            const smartIntent = this.nodeInspector.smartIntents[smartIntentIndex];
            if (smartIntent.mainAction.listHeaders && smartIntent.mainAction.listHeaders.length > 0) {
                smartIntent.mainAction.listHeaders.splice(headerIndex, 1);
                this.updateSmartIntentData();
            }
        },

        // Smart Intent Fallback Knowledge API Header Management Methods
        addSmartIntentFallbackKnowledgeAPIHeader() {
            if (!this.nodeInspector.smartIntentFallback.listHeaders) {
                this.nodeInspector.smartIntentFallback.listHeaders = [];
            }
            
            const newHeader = {
                id: `header-${this.generateGuid()}`,
                key: '',
                value: ''
            };
            
            this.nodeInspector.smartIntentFallback.listHeaders.push(newHeader);
            this.updateSmartIntentFallbackData();
        },

        removeSmartIntentFallbackKnowledgeAPIHeader(headerIndex) {
            if (this.nodeInspector.smartIntentFallback.listHeaders && this.nodeInspector.smartIntentFallback.listHeaders.length > 0) {
                this.nodeInspector.smartIntentFallback.listHeaders.splice(headerIndex, 1);
                this.updateSmartIntentFallbackData();
            }
        },

        getModelsByProvider(provider) {
            if (!provider) return [];
            
            // Filter models by the specific provider
            // Check both providerName and provider fields for compatibility
            return this.listDDModel.filter(
                model => model.providerName === provider || model.provider === provider
            );
        },
        // Document data methods
        getDocumentData() {
            let self = this;
            // Only fetch data if specific data selection is enabled
            if (!self.nodeInspector.enableSpecificDataSelection) {
                self.resetDocumentDataTable();
                return;
            }

            if (self.nodeInspector.documentVectorName == "" || self.nodeInspector.documentVectorName == null) {
                return;
            }

            this.loadingDocumentDataTable = true;
            self.resetDocumentDataTable();

            let temp = {
                page: 1,
                perPage: 1000,
                name: self.nodeInspector.documentVectorName,
                sortDesc: false,
                sortBy: null,
            };

            self.$call.GetTableVector(
                temp,
                (dataResponse) => {
                    if (dataResponse.status == 0) {
                        self.documentDataTable = dataResponse.data.table.data || [];
                        self.documentDataTotalTable = dataResponse.data.table.total || 0;
                        self.showDocumentDataTable = true;

                        // Initialize selection state for each item
                        self.documentDataTable.forEach((element) => {
                            element.selected = false;
                            // Check if item is already selected
                            if (self.nodeInspector.selectedDocumentData) {
                                const alreadySelected = self.nodeInspector.selectedDocumentData.find(
                                    (selected) => selected.id == element.id
                                );
                                if (alreadySelected) {
                                    element.selected = true;
                                }
                            }
                        });
                    } else {
                        self.documentDataTable = [];
                        self.documentDataTotalTable = 0;
                        self.showDocumentDataTable = false;
                    }
                    self.loadingDocumentDataTable = false;
                },
                (errorResource) => {
                    console.error('Error fetching document data:', errorResource);
                    self.documentDataTable = [];
                    self.documentDataTotalTable = 0;
                    self.showDocumentDataTable = false;
                    self.loadingDocumentDataTable = false;
                }
            );
        },
        selectDocumentData(flag, id, textORG) {
            let self = this;

            if (!self.nodeInspector.selectedDocumentData) {
                self.nodeInspector.selectedDocumentData = [];
            }

            if (flag) {
                // Add to selection
                self.nodeInspector.selectedDocumentData.push({
                    id: id,
                    textORG: textORG
                });

                // Update node property
                self.updateNodeProperty('selectedDocumentData', self.nodeInspector.selectedDocumentData);
            } else {
                // Remove from selection
                if (self.nodeInspector.selectedDocumentData.findIndex(e => e.id == id) > -1) {
                    self.nodeInspector.selectedDocumentData.splice(
                        self.nodeInspector.selectedDocumentData.findIndex(e => e.id == id),
                        1
                    );
                    self.updateNodeProperty('selectedDocumentData', self.nodeInspector.selectedDocumentData);
                }
            }

            // Update UI selection state
            self.documentDataTable.forEach((element) => {
                if (element.id == id) {
                    element.selected = flag;
                }
            });
        },
        deleteSelectedDocumentData(id) {
            let self = this;
            if (self.nodeInspector.selectedDocumentData && self.nodeInspector.selectedDocumentData.length > 0) {
                // Remove from selected data
                self.nodeInspector.selectedDocumentData = self.nodeInspector.selectedDocumentData.filter(item => item.id !== id);
                self.updateNodeProperty('selectedDocumentData', self.nodeInspector.selectedDocumentData);

                // Update table selection state
                self.documentDataTable.forEach((element) => {
                    if (element.id == id) {
                        element.selected = false;
                    }
                });
            }
        },
        onSpecificDataSelectionChange(enabled) {
            let self = this;

            // Update node property
            self.updateNodeProperty('enableSpecificDataSelection', enabled);

            if (enabled) {
                // When enabled, auto-expand and clear document vector selection
                self.documentDataExpanded = true;

                // Clear document vector selection
                self.nodeInspector.documentVectorName = '';
                self.updateNodeProperty('documentVectorName', '');

                // Reset document data table
                self.resetDocumentDataTable();
            } else {
                // When disabled, reset document data and clear selection
                self.resetDocumentDataTable();
                if (self.nodeInspector.selectedDocumentData) {
                    self.nodeInspector.selectedDocumentData = [];
                    self.updateNodeProperty('selectedDocumentData', []);
                }
                self.documentDataExpanded = false;
            }
        },
        resetDocumentDataAndSelection() {
            // Reset table data
            this.resetDocumentDataTable();

            // Reset document vector selection and clear selected data if necessary
            if (this.nodeInspector.documentVectorName) {
                this.nodeInspector.documentVectorName = '';
                this.updateNodeProperty('documentVectorName', '');
            }

            // Clear selected document data if necessary
            if (this.nodeInspector.selectedDocumentData && this.nodeInspector.selectedDocumentData.length > 0) {
                this.nodeInspector.selectedDocumentData = [];
                this.updateNodeProperty('selectedDocumentData', []);
            }
        },
        resetDocumentDataTable() {
            this.documentDataTable = [];
            this.documentDataTotalTable = 0;
            this.showDocumentDataTable = false;
        },
        formatText(text) {
            if (!text) return '';
            return text.replace(/ /g, "&nbsp;");
        },
        generateConditionPreview(output) {
            if (!output.conditionType) return '';

            const source = output.sourceField || 'input.text';

            switch (output.conditionType) {
                case 'text_contains':
                    const containsMethod = output.caseSensitive ? 'includes' : 'toLowerCase().includes';
                    const searchValue = output.caseSensitive ? `'${output.searchValue}'` : `'${(output.searchValue || '').toLowerCase()}'`;
                    return `${source}.${containsMethod}(${searchValue})`;

                case 'text_equals':
                    const equalsMethod = output.caseSensitive ? '===' : 'toLowerCase() ===';
                    const compareValue = output.caseSensitive ? `'${output.compareValue}'` : `'${(output.compareValue || '').toLowerCase()}'`;
                    return `${source}.${equalsMethod} ${compareValue}`;

                case 'number_comparison':
                    const operators = { gt: '>', lt: '<', eq: '===', gte: '>=', lte: '<=', ne: '!==' };
                    const op = operators[output.operator] || '>';
                    return `${source} ${op} ${output.compareValue || 0}`;

                case 'boolean_check':
                    return `${source} === ${output.expectedValue}`;

                case 'regex_match':
                    const flags = output.regexFlags ? `'${output.regexFlags}'` : '';
                    return `new RegExp('${output.regexPattern}'${flags ? ', ' + flags : ''}).test(${source})`;

                case 'length_check':
                    const lengthOp = { gt: '>', lt: '<', eq: '===', between: 'between' }[output.lengthOperator] || '>';
                    if (output.lengthOperator === 'between') {
                        return `${source}.length >= ${output.lengthValue || 0} && ${source}.length <= ${output.lengthValueMax || 100}`;
                    }
                    return `${source}.length ${lengthOp} ${output.lengthValue || 0}`;

                default:
                    return '';
            }
        },
        insertConditionHelper(outputIndex, helperText) {
            if (!this.nodeInspector.switchOutputs[outputIndex]) return;

            const currentCondition = this.nodeInspector.switchOutputs[outputIndex].condition || '';
            const newCondition = currentCondition + (currentCondition ? ' && ' : '') + helperText;

            this.nodeInspector.switchOutputs[outputIndex].condition = newCondition;
            this.updateSwitchData();
        },


        // Save/Load UI Methods
        openSaveDialog() {
            this.saveDialog = true;
        },

        closeSaveDialog() {
            this.saveDialog = false;
        },

        // Load from Model methods
        async openLoadFromModelDialog() {
            this.loadFromModelDialog = true;
        },

        async openSmartIntentLoadFromModelDialog(smartIntentIndex = null) {
            this.currentLoadingSmartIntentIndex = smartIntentIndex; // Store which intent/fallback is being loaded
            this.smartIntentLoadFromModelDialog = true;
        },

        closeLoadFromModelDialog() {
            this.loadFromModelDialog = false;
            this.selectedModelToLoad = null;
            this.modelConfigPreview = null;
        },

        closeSmartIntentLoadFromModelDialog() {
            this.smartIntentLoadFromModelDialog = false;
            this.selectedSmartIntentModelToLoad = null;
            this.smartIntentModelConfigPreview = null;
            this.currentLoadingSmartIntentIndex = null;
        },

        async confirmLoadFromModel() {
            if (!this.selectedModelToLoad) return;

            this.loadingModelConfig = true;
            try {
                // Call API to get model configuration
                this.$call.GetGPTModelById(
                    this.selectedModelToLoad,
                    (dataResponse) => {
                        if (dataResponse.status === 0) {
                            const modelConfig = dataResponse.data;
                            // Check if this is intentAI node
                            const isIntentAI = this.selectedNode && this.selectedNode.data.type === 'intentAI';
                            
                            // Update provider first and trigger model dropdown update
                            if (modelConfig.provider) {
                                if (isIntentAI) {
                                    this.nodeInspector.intentaiProvider = modelConfig.provider;
                                } else {
                                    this.nodeInspector.provider = modelConfig.provider;
                                }
                                // Wait for provider change to complete before setting model
                                this.changeProvider(modelConfig.provider).then(() => {
                                    // Now set the model after dropdown is populated
                                    this.$nextTick(() => {
                                        // Try to find exact match first
                                        let modelValue = modelConfig.model;

                                        // Check if model exists in dropdown
                                        const modelExists = this.DDModelShow.find(m => m.modelName === modelValue);
                                        if (!modelExists && modelValue && modelValue.includes('|')) {
                                            // If not found and contains |, try the part after |
                                            const modelParts = modelValue.split('|');
                                            modelValue = modelParts[modelParts.length - 1];
                                        }

                                        // Set the model value
                                        if (isIntentAI) {
                                            this.nodeInspector.intentaiModel = modelValue;
                                        } else {
                                            this.nodeInspector.model = modelValue;
                                        }

                                        // Continue with other properties
                                        this.loadOtherProperties(modelConfig);
                                    });
                                });
                            } else {
                                // No provider, just load other properties
                                this.loadOtherProperties(modelConfig);
                            }
                        } else {
                            this.showError('Failed to load model configuration');
                            this.loadingModelConfig = false;
                        }
                    },
                    (error) => {
                        console.error('API Error:', error);
                        this.showError('Error loading model configuration');
                        this.loadingModelConfig = false;
                    }
                );
            } catch (error) {
                console.error('Exception:', error);
                this.showError('Error loading model configuration');
                this.loadingModelConfig = false;
            }
        },

        loadOtherProperties(modelConfig) {
            console.log('Loaded model configuration:', modelConfig);
            // Check if this is intentAI node
            const isIntentAI = this.selectedNode && this.selectedNode.data.type === 'intentAI';
            // Load configuration into nodeInspector with proper reactive updates
            if (isIntentAI) {
                this.nodeInspector.intentaiGender = (modelConfig.gender === "m" ? 'male' : 'female') || 'male';
                this.nodeInspector.intentaiTemperature = modelConfig.temperature || null;
                this.nodeInspector.intentaiMaxTokens = modelConfig.max_tokens || null;
                this.nodeInspector.intentaiTopP = modelConfig.top_p || null;
                this.nodeInspector.intentaiFrequencyPenalty = modelConfig.frequency_penalty || null;
                this.nodeInspector.intentaiPresencePenalty = modelConfig.presence_penalty || null;
                this.nodeInspector.intentaiSystemPrompt = modelConfig.promptTemplate || '';
                this.nodeInspector.intentaiTypingSpeed = modelConfig.typingSpeed || null;
                this.nodeInspector.intentaiMaxInputLength = modelConfig.maximumChatInput || 200000;
                this.nodeInspector.intentaiStreamResponse = modelConfig.isStream !== undefined ? modelConfig.isStream : false;
                this.nodeInspector.intentaiJsonResponse = modelConfig.jsonResponse !== undefined ? modelConfig.jsonResponse : false;
                this.nodeInspector.intentaiEnableTime = modelConfig.timeZone ? true : false;
                this.nodeInspector.intentaiTimeZone = modelConfig.timeZone || null;
                this.nodeInspector.intentaiMemoryTurn = modelConfig.historyCount || null;
                this.nodeInspector.intentaiMemoryDuration = modelConfig.historyMinute < 0
                    ? Math.abs(modelConfig.historyMinute)
                    : modelConfig.historyMinute;
                this.nodeInspector.intentaiShortTermMemory = modelConfig.len_ShortMemory || null;
                this.nodeInspector.intentaiLongTermMemory = modelConfig.isLongTerm_Memory !== undefined ? modelConfig.isLongTerm_Memory : false;
                this.nodeInspector.intentaiIsChainMassage = modelConfig.isChainMassage !== undefined ? modelConfig.isChainMassage : false;
                this.nodeInspector.intentaiLenChainMassage = modelConfig.lenChainMassage || null;
                this.nodeInspector.intentaiTimeSecondChainMassage = modelConfig.timeSecondChainMassage || null;
                this.nodeInspector.intentaiShowLineName = modelConfig.isShowNameUserMonitor !== undefined ? modelConfig.isShowNameUserMonitor : false;
                this.nodeInspector.intentaiLoadingBar = modelConfig.loadingBarFlag !== undefined ? modelConfig.loadingBarFlag : false;
                this.nodeInspector.intentaiLoadingBarDuration = modelConfig.loadingBarDuration || 0;
                this.nodeInspector.intentaiMonitorLineUserId = modelConfig.monitorLineUserID || null;
                this.nodeInspector.intentaiMonitorSocialID = modelConfig.monitorSocialID || null;
                this.nodeInspector.intentaiStopSequence = modelConfig.stop || null;
                // Handle forbidden words for intentAI
                if (modelConfig.forbiddenWord) {
                    try {
                        this.nodeInspector.intentaiForbiddenWords = JSON.parse(modelConfig.forbiddenWord);
                        this.nodeInspector.intentaiForbiddenWords.push('');
                    } catch (e) {
                        this.nodeInspector.intentaiForbiddenWords = [''];
                    }
                } else {
                    this.nodeInspector.intentaiForbiddenWords = [''];
                }
            } else {
                this.nodeInspector.gender = (modelConfig.gender === "m" ? 'male' : 'female') || 'male';
                this.nodeInspector.temperature = modelConfig.temperature || null;
                this.nodeInspector.maxTokens = modelConfig.max_tokens || null;
                this.nodeInspector.topP = modelConfig.top_p || null;
                this.nodeInspector.frequencyPenalty = modelConfig.frequency_penalty || null;
                this.nodeInspector.presencePenalty = modelConfig.presence_penalty || null;
                this.nodeInspector.systemPrompt = modelConfig.promptTemplate || '';
                this.nodeInspector.typingSpeed = modelConfig.typingSpeed || null;
                this.nodeInspector.maxInputLength = modelConfig.maximumChatInput || 200000;
                this.nodeInspector.streamResponse = modelConfig.isStream !== undefined ? modelConfig.isStream : false;
                this.nodeInspector.jsonResponse = false;
                this.nodeInspector.enableTime = modelConfig.timeZone ? true : false;
                this.nodeInspector.timeZone = modelConfig.timeZone || null;
                this.nodeInspector.memoryTurn = modelConfig.historyCount || null;
                this.nodeInspector.memoryDuration = modelConfig.historyMinute < 0
                    ? Math.abs(modelConfig.historyMinute)
                    : modelConfig.historyMinute;
                this.nodeInspector.shortTermMemory = modelConfig.len_ShortMemory || null;
                this.nodeInspector.longTermMemory = modelConfig.isLongTerm_Memory !== undefined ? modelConfig.isLongTerm_Memory : false;
                this.nodeInspector.isChainMassage = modelConfig.isChainMassage !== undefined ? modelConfig.isChainMassage : false;
                this.nodeInspector.lenChainMassage = modelConfig.lenChainMassage || null;
                this.nodeInspector.timeSecondChainMassage = modelConfig.timeSecondChainMassage || null;
                this.nodeInspector.showLineName = modelConfig.isShowNameUserMonitor !== undefined ? modelConfig.isShowNameUserMonitor : false;
                this.nodeInspector.loadingBar = modelConfig.loadingBarFlag !== undefined ? modelConfig.loadingBarFlag : false;
                this.nodeInspector.loadingBarDuration = modelConfig.loadingBarDuration || 0;
                this.nodeInspector.monitorLineUserId = modelConfig.monitorLineUserID || null;
                this.nodeInspector.monitorSocialID = modelConfig.monitorSocialID || null;
                this.nodeInspector.stopSequence = modelConfig.stop || null;
                // Handle forbidden words for AI Model
                if (modelConfig.forbiddenWord) {
                    try {
                        this.nodeInspector.forbiddenWords = JSON.parse(modelConfig.forbiddenWord);
                        this.nodeInspector.forbiddenWords.push('');
                    } catch (e) {
                        this.nodeInspector.forbiddenWords = [''];
                    }
                } else {
                    this.nodeInspector.forbiddenWords = [''];
                }
            }
            
            // Common properties for both node types
            this.nodeInspector.loadingBar = modelConfig.loadingBarFlag !== undefined ? modelConfig.loadingBarFlag : false;
            this.nodeInspector.loadingBarDuration = modelConfig.loadingBarDuration || 0;
            this.nodeInspector.showLineName = modelConfig.isShowNameUserMonitor !== undefined ? modelConfig.isShowNameUserMonitor : false;
            this.nodeInspector.monitorLineUserId = modelConfig.monitorLineUserID || null;
            this.nodeInspector.monitorSocialID = modelConfig.monitorSocialID || null;
            

            // Handle forbidden words
            if (modelConfig.forbiddenWord) {
                try {
                    this.nodeInspector.forbiddenWords = JSON.parse(modelConfig.forbiddenWord);
                    this.nodeInspector.forbiddenWords.push('');
                } catch (e) {
                    this.nodeInspector.forbiddenWords = [''];
                }
            } else {
                this.nodeInspector.forbiddenWords = [''];
            }

            // Update node data immediately and force UI refresh
            this.updateAllNodeProperties();
            this.$forceUpdate();

            this.closeLoadFromModelDialog();
            this.showSuccess(`Successfully loaded configuration from ${modelConfig.modelName || 'selected model'}`);
            this.loadingModelConfig = false;
        },

        updateAllNodeProperties() {
            if (!this.selectedNode) return;

            // Update all properties to sync with the node data
            Object.keys(this.nodeInspector).forEach(key => {
                this.updateNodeProperty(key, this.nodeInspector[key]);
            });

            // Force Vue to update the UI
            this.$nextTick(() => {
                this.$forceUpdate();
            });
        },

        getModelTypeColor(provider) {
            const colors = {
                'OpenAI': '#10a37f',
                'Anthropic': '#d97757',
                'Google': '#4285f4',
                'Azure': '#0078d4',
                'AWS': '#ff9900'
            };
            return colors[provider] || '#666666';
        },

        // Smart Intent Config Loading Methods
        async loadSmartIntentModelConfigPreview(modelId = null) {
            const targetModelId = modelId || this.selectedSmartIntentModelToLoad;
            if (!targetModelId) {
                this.smartIntentModelConfigPreview = null;
                return;
            }

            this.loadingSmartIntentModelConfig = true;
            try {
                // Use the same method as AI Model node
                this.$call.GetGPTModelById(
                    targetModelId,
                    (dataResponse) => {
                        if (dataResponse.status === 0) {
                            const modelConfig = dataResponse.data;
                            // Map to preview format like AI Model node
                            this.smartIntentModelConfigPreview = {
                                provider: modelConfig.provider || 'N/A',
                                model: modelConfig.model || 'N/A',
                                temperature: modelConfig.temperature ?? 'N/A',
                                maxTokens: modelConfig.max_tokens ?? 'N/A',
                                topP: modelConfig.top_p ?? 'N/A',
                                frequencyPenalty: modelConfig.frequency_penalty ?? 'N/A',
                                presencePenalty: modelConfig.presence_penalty ?? 'N/A',
                                systemPrompt: modelConfig.promptTemplate ? (modelConfig.promptTemplate.length > 100 ? modelConfig.promptTemplate.substring(0, 100) + '...' : modelConfig.promptTemplate) : 'N/A',
                                // Keep original data for applying config
                                originalData: modelConfig
                            };
                        } else {
                            this.showError('Failed to load model configuration: ' + dataResponse.message);
                            this.smartIntentModelConfigPreview = null;
                        }
                        this.loadingSmartIntentModelConfig = false;
                    },
                    (error) => {
                        console.error('Error loading Smart Intent model config:', error);
                        this.showError('Failed to load model configuration. Please try again.');
                        this.smartIntentModelConfigPreview = null;
                        this.loadingSmartIntentModelConfig = false;
                    }
                );
            } catch (error) {
                console.error('Error loading Smart Intent model config:', error);
                this.showError('Failed to load model configuration. Please try again.');
                this.smartIntentModelConfigPreview = null;
                this.loadingSmartIntentModelConfig = false;
            }
        },

        confirmSmartIntentLoadFromModel() {
            if (!this.smartIntentModelConfigPreview || !this.smartIntentModelConfigPreview.originalData) {
                this.showError('No model configuration to load');
                return;
            }

            const modelConfig = this.smartIntentModelConfigPreview.originalData;

            // Apply configuration based on current selection
            if (this.currentLoadingSmartIntentIndex === 'fallback') {
                // Apply to Fallback directly (fallback doesn't have mainAction)
                this.applyConfigToTarget(modelConfig, this.nodeInspector.smartIntentFallback);
                this.showSuccess(`Successfully loaded configuration from ${modelConfig.modelName || 'selected model'} to Fallback`);
            } else if (this.currentLoadingSmartIntentIndex !== null) {
                // Apply to specific Smart Intent's mainAction
                const smartIntent = this.nodeInspector.smartIntents[this.currentLoadingSmartIntentIndex];
                if (smartIntent && smartIntent.mainAction) {
                    this.applyConfigToTarget(modelConfig, smartIntent.mainAction);
                    this.showSuccess(`Successfully loaded configuration from ${modelConfig.modelName || 'selected model'} to ${smartIntent.name || 'Smart Intent'}`);
                }
            } else {
                this.showError('No target selected for configuration loading');
                return;
            }

            // Force update nodeInspector to sync with data changes
            this.updateSmartIntentData();
            this.updateSmartIntentFallbackData();
            
            // Ensure selectedNode.data is synced with nodeInspector
            if (this.selectedNode && this.selectedNode.data) {
                this.selectedNode.data.smartIntents = JSON.parse(JSON.stringify(this.nodeInspector.smartIntents));
                this.selectedNode.data.smartIntentFallback = JSON.parse(JSON.stringify(this.nodeInspector.smartIntentFallback));
            }
            
            // Force re-sync nodeInspector from selectedNode.data
            this.syncNodeInspectorFromSelectedNode();

            // Update node data and force UI refresh
            this.updateSmartIntentNodeProperties();
            this.$forceUpdate();

            this.closeSmartIntentLoadFromModelDialog();
            this.smartIntentLoadFromModelDialog = false;
            this.loadingSmartIntentModelConfig = false;
        },

        applyConfigToTarget(modelConfig, target) {
            // Basic AI Parameters
            target.provider = modelConfig.provider || null;
            target.model = modelConfig.model || null;
            
            // Auto-assign apiVersion from listDDModel based on the loaded model
            if (modelConfig.model) {
                const selectedModel = this.listDDModel.find(m => m.modelName === modelConfig.model);
                if (selectedModel) {
                    target.apiVersion = selectedModel.apiVersion;
                }
            }

            target.systemPrompt = modelConfig.promptTemplate || '';
            target.temperature = modelConfig.temperature || null;
            target.maxTokens = modelConfig.max_tokens || null;
            target.topP = modelConfig.top_p || null;
            target.presencePenalty = modelConfig.presence_penalty || null;
            target.frequencyPenalty = modelConfig.frequency_penalty || null;
            target.streamingEnabled = modelConfig.isStream !== undefined ? modelConfig.isStream : true;
            target.lineFlexMessage = modelConfig.lineFlexMessage !== undefined ? modelConfig.lineFlexMessage : false;
            
            // Basic Information fields that are specific to Smart Intent mainAction
            if (target.hasOwnProperty('typingSpeed') || target.hasOwnProperty('maxInputLength')) {
                target.typingSpeed = modelConfig.typingSpeed || null;
                target.maxInputLength = modelConfig.maximumChatInput || 200000;
            }
            
            // Response Options fields that are specific to Smart Intent mainAction
            if (target.hasOwnProperty('enableTime') || target.hasOwnProperty('timeZone')) {
                target.enableTime = modelConfig.timeZone ? true : false;
                target.timeZone = modelConfig.timeZone || null;
            }
            
            // Memory Settings fields that are specific to Smart Intent mainAction
            if (target.hasOwnProperty('memoryTurn') || target.hasOwnProperty('memoryDuration') || target.hasOwnProperty('shortTermMemory') || target.hasOwnProperty('longTermMemory')) {
                target.memoryTurn = modelConfig.historyCount || null;
                target.memoryDuration = modelConfig.historyMinute ? Math.abs(modelConfig.historyMinute) : null;
                target.shortTermMemory = modelConfig.len_ShortMemory || null;
                target.longTermMemory = modelConfig.isLongTerm_Memory !== undefined ? modelConfig.isLongTerm_Memory : false;
            }
            
            // Multi-turn Input Configuration fields that are specific to Smart Intent mainAction
            if (target.hasOwnProperty('isChainMassage') || target.hasOwnProperty('lenChainMassage') || target.hasOwnProperty('timeSecondChainMassage')) {
                target.isChainMassage = modelConfig.isChainMassage !== undefined ? modelConfig.isChainMassage : false;
                target.lenChainMassage = modelConfig.lenChainMassage || null;
                target.timeSecondChainMassage = modelConfig.timeSecondChainMassage || null;
            }
            
            target.streamResponse = modelConfig.isStream !== undefined ? modelConfig.isStream : false;
            target.jsonResponse = modelConfig.jsonResponse !== undefined ? modelConfig.jsonResponse : false;
            target.stopSequence = modelConfig.stop || null;
            
            // Advanced configuration - map from AI Model node format
            target.loadingBar = modelConfig.loadingBarFlag !== undefined ? modelConfig.loadingBarFlag : false;
            target.loadingBarDuration = modelConfig.loadingBarDuration || null;
            target.showLineName = modelConfig.isShowNameUserMonitor !== undefined ? modelConfig.isShowNameUserMonitor : false;
            target.monitorLineUserId = modelConfig.monitorLineUserID || null;
            target.monitorSocialID = modelConfig.monitorSocialID || null;
            
            // Set forbidden words if available
            if (modelConfig.forbiddenWord) {
                try {
                    target.forbiddenWords = JSON.parse(modelConfig.forbiddenWord) || [''];
                    target.forbiddenWords.push('');
                } catch (e) {
                    target.forbiddenWords = [''];
                }
            } else {
                target.forbiddenWords = [''];
            }
            
            // Ensure forbidden words is always an array
            if (!Array.isArray(target.forbiddenWords)) {
                target.forbiddenWords = [''];
            }
        },

        updateSmartIntentNodeProperties() {
            if (!this.selectedNode || this.selectedNode.type !== 'smartIntent') return;

            // Update Smart Intent properties to sync with the node data
            if (this.selectedNode.data.smartIntents) {
                this.selectedNode.data.smartIntents = [...this.nodeInspector.smartIntents];
            }
            if (this.selectedNode.data.smartIntentFallback) {
                this.selectedNode.data.smartIntentFallback = { ...this.nodeInspector.smartIntentFallback };
            }

            // Force Vue to update the UI
            this.$nextTick(() => {
                this.$forceUpdate();
            });
        },

        getLoadConfigTitle() {
            if (this.currentLoadingSmartIntentIndex === 'fallback') {
                return 'Fallback';
            } else if (this.currentLoadingSmartIntentIndex !== null) {
                const smartIntent = this.nodeInspector.smartIntents[this.currentLoadingSmartIntentIndex];
                return smartIntent ? (smartIntent.name || `Smart Intent ${this.currentLoadingSmartIntentIndex + 1}`) : 'Smart Intent';
            }
            return 'Smart Intent';
        },

        syncNodeInspectorFromSelectedNode() {
            if (!this.selectedNode) return;
            
            // Sync Smart Intent data back to nodeInspector
            if (this.selectedNode.data.type === 'smartIntent') {
                if (this.selectedNode.data.smartIntents) {
                    this.nodeInspector.smartIntents = JSON.parse(JSON.stringify(this.selectedNode.data.smartIntents));
                    
                    // Ensure each smart intent has all required mainAction properties
                    this.nodeInspector.smartIntents.forEach(smartIntent => {
                        if (!smartIntent.mainAction) {
                            smartIntent.mainAction = {};
                        }
                        // Set defaults for missing properties
                        const defaults = {
                            useAI: false,
                            provider: null,
                            model: null,
                            // Basic Information
                            typingSpeed: null,
                            maxInputLength: 200000,
                            // Response Options
                            enableTime: false,
                            timeZone: null,
                            // AI Parameters
                            temperature: null,
                            topP: null,
                            frequencyPenalty: null,
                            presencePenalty: null,
                            maxTokens: null,
                            stopSequence: null,
                            // Output Control
                            streamingEnabled: true,
                            // Line Configuration
                            lineFlexMessage: false,
                            // System Prompt
                            systemPrompt: '',
                            // Forbidden Words
                            forbiddenWords: [''],
                            // Memory Settings
                            memoryTurn: null,
                            memoryDuration: null,
                            shortTermMemory: null,
                            longTermMemory: false,
                            // Multi-turn Input Configuration
                            isChainMassage: false,
                            lenChainMassage: null,
                            timeSecondChainMassage: null,
                            // Knowledge Configuration
                            knowledgeType: '',
                            // Knowledge Sources Enable Flags
                            enableDocuments: false,
                            enableDatabaseIntegration: false,
                            enableProducts: false,
                            enableApiKnowledge: false,
                            // Documents properties
                            document: [],
                            topNChunks: 5,
                            cosineSimilarity: 0.5,
                            // Database Integration properties
                            tableName: null,
                            sqlExtractionModel: null,
                            promptSqlNoData: '',
                            // Products properties
                            productGroupList: [],
                            productTopNChunks: 5,
                            productCosineSimilarity: 0.5,
                            // API Knowledge properties
                            knowledgeApiUrl: '',
                            knowledgeHttpMethod: 'GET',
                            listHeaders: [],
                            knowledgeRequestBody: '',
                            knowledgeResponseDataPath: ''
                        };
                        Object.keys(defaults).forEach(key => {
                            if (smartIntent.mainAction[key] === undefined) {
                                smartIntent.mainAction[key] = defaults[key];
                            }
                        });
                        // Ensure forbiddenWords is always an array
                        if (!Array.isArray(smartIntent.mainAction.forbiddenWords)) {
                            smartIntent.mainAction.forbiddenWords = [''];
                        }
                    });
                }
                
                if (this.selectedNode.data.smartIntentFallback) {
                    this.nodeInspector.smartIntentFallback = JSON.parse(JSON.stringify(this.selectedNode.data.smartIntentFallback));
                    
                    // Ensure fallback has all required properties
                    const fallbackDefaults = {
                        useAI: false,
                        provider: '',
                        model: '',
                        temperature: 0.7,
                        maxTokens: 150,
                        topP: 1.0,
                        presencePenalty: 0.0,
                        frequencyPenalty: 0.0,
                        streamingEnabled: true,
                        lineFlexMessage: false,
                        systemPrompt: '',
                        forbiddenWords: [''],
                        // Additional fields from AI Model node
                        typingSpeed: 50,
                        maxInputLength: 2000,
                        responseOption: 'text',
                        enableMemory: false,
                        memorySize: 10,
                        stopSequence: '',
                        seedValue: null,
                        enableSafetyFilter: false,
                        enableContentFilter: false
                    };
                    Object.keys(fallbackDefaults).forEach(key => {
                        if (this.nodeInspector.smartIntentFallback[key] === undefined) {
                            this.nodeInspector.smartIntentFallback[key] = fallbackDefaults[key];
                        }
                    });
                    // Ensure forbiddenWords is always an array
                    if (!Array.isArray(this.nodeInspector.smartIntentFallback.forbiddenWords)) {
                        this.nodeInspector.smartIntentFallback.forbiddenWords = [''];
                    }
                }
                
                if (this.selectedNode.data.smartIntentGroupName) {
                    this.nodeInspector.smartIntentGroupName = this.selectedNode.data.smartIntentGroupName;
                }
            }
        },

        async loadModelConfigPreview(modelId) {
            try {
                this.$call.GetGPTModelById(
                    modelId,
                    (dataResponse) => {
                        if (dataResponse.status === 0) {
                            const modelConfig = dataResponse.data;
                            this.modelConfigPreview = {
                                provider: modelConfig.provider || 'N/A',
                                model: modelConfig.model || 'N/A',
                                temperature: modelConfig.temperature ?? 'N/A',
                                maxTokens: modelConfig.max_tokens ?? 'N/A',
                                topP: modelConfig.top_p ?? 'N/A',
                                frequencyPenalty: modelConfig.frequency_penalty ?? 'N/A',
                                presencePenalty: modelConfig.presence_penalty ?? 'N/A',
                                systemPrompt: modelConfig.promptTemplate ? (modelConfig.promptTemplate.length > 100 ? modelConfig.promptTemplate.substring(0, 100) + '...' : modelConfig.promptTemplate) : 'N/A'
                            };
                        }
                    },
                    (error) => {
                        console.error('Error loading model config preview:', error);
                    }
                );
            } catch (error) {
                console.error('Error loading model config preview:', error);
            }
        },

        async confirmSave() {
            this.saving = true;
            try {
                // Call the existing SaveFlow method
                await this.SaveFlow();

                this.closeSaveDialog();

            } catch (error) {
                console.error('Error saving flow:', error);
            } finally {
                this.saving = false;
            }
        },

        async performSave() {
            if (!this.saveFormValid) return;

            this.saving = true;
            try {
                // Update flow metadata
                this.flowName = this.saveFlowData.name;

                // Call the existing SaveFlow method
                await this.SaveFlow();

                this.closeSaveDialog();

            } catch (error) {
                console.error('Error saving flow:', error);
            } finally {
                this.saving = false;
            }
        },

        // Update exportFlow to use targets structure
        exportFlow() {
            try {
                const flatNodes = this.convertToFlatStructure();

                const flowData = {
                    name: this.flowName,
                    nodes: flatNodes,
                    metadata: {
                        exportedAt: new Date().toISOString(),
                        nodeCount: this.tempNode.length,
                        edgeCount: this.tempEdge.length,
                    }
                };

                const dataStr = JSON.stringify(flowData, null, 2);
                const dataBlob = new Blob([dataStr], { type: 'application/json' });

                const link = document.createElement('a');
                link.href = URL.createObjectURL(dataBlob);
                link.download = `Flow_Config_${this.flowName.replace(/\s+/g, '_')}.json`;
                link.click();

                this.showSuccess('Flow exported successfully');
            } catch (error) {
                this.showError('Failed to export flow');
            }
        },

        // Update importFlow to handle targets structure
        importFlow() {
            const input = document.createElement('input');
            input.type = 'file';
            input.accept = '.json';
            input.onchange = (e) => {
                const file = e.target.files[0];
                if (!file) return;

                const reader = new FileReader();
                reader.onload = (e) => {
                    try {
                        const flowData = JSON.parse(e.target.result);
                        
                        // Validate flow data structure
                        if (!flowData.nodes) {
                            throw new Error('Invalid flow file format');
                        }

                        // Check format type
                        const firstNode = flowData.nodes[0];
                        let nodes, edges;
                        if (firstNode && firstNode.targets !== undefined) {
                            // New targets format
                            const result = this.convertFromFlatStructure(flowData.nodes);
                            nodes = result.nodes;
                            edges = result.edges;
                        } else if (firstNode && firstNode.source !== undefined) {
                            // Old source format
                            const result = this.convertFromSourceFormat(flowData.nodes);
                            nodes = result.nodes;
                            edges = result.edges;
                        } else if (flowData.edges) {
                            // Very old format with separate edges
                            nodes = flowData.nodes;
                            edges = flowData.edges || [];
                        } else {
                            throw new Error('Unsupported flow file format');
                        }

                        // Generate new IDs for all nodes and update references
                        const idMap = new Map();
                        nodes.forEach(node => {
                            const oldId = node.id;
                            const newId = this.generateGuid();
                            idMap.set(oldId, newId);
                            node.id = newId;
                        });

                        // Update edge sources and targets
                        edges.forEach(edge => {
                            if (idMap.has(edge.source)) {
                                edge.source = idMap.get(edge.source);
                            }
                            if (idMap.has(edge.target)) {
                                edge.target = idMap.get(edge.target);
                            }
                        });

                        // Update node data targets if they exist
                        nodes.forEach(node => {
                            if (node.data && node.data.targets && Array.isArray(node.data.targets)) {
                                node.data.targets = node.data.targets.map(targetId => 
                                    idMap.has(targetId) ? idMap.get(targetId) : targetId
                                );
                            }
                            // Also check for any other references in node data
                            if (node.data) {
                                const dataStr = JSON.stringify(node.data);
                                const updatedDataStr = dataStr.replace(/("target":\s*"[^"]*")/g, (match, targetPart) => {
                                    const targetId = targetPart.match(/"target":\s*"([^"]*)"/)[1];
                                    if (idMap.has(targetId)) {
                                        return `"target":"${idMap.get(targetId)}"`;
                                    }
                                    return match;
                                });
                                if (dataStr !== updatedDataStr) {
                                    node.data = JSON.parse(updatedDataStr);
                                }
                            }
                        });

                        this.tempNode = nodes;
                        this.tempEdge = edges;

                        this.flowName = flowData.name || 'Imported Flow';

                        this.showSuccess('Flow imported successfully');
                        this.$nextTick(() => {
                            // Check validation for all nodes after importing
                            this.checkAllNodesValidation();

                            setTimeout(() => {
                                // Fit view after importing nodes
                                if (this.$refs.vueFlow && this.tempNode.length > 0) {
                                    try {
                                        this.$refs.vueFlow.fitView({
                                            padding: 0.1,
                                            minZoom: 0.5,
                                            maxZoom: 1.5,
                                            duration: 800,
                                            includeHiddenNodes: false
                                        });
                                    } catch (error) {
                                        console.warn('Failed to fit view after import:', error);
                                    }
                                }
                            }, 200);
                        });

                    } catch (error) {
                        this.showError('Failed to import flow: Invalid file format');
                    }
                };
                reader.readAsText(file);
            };
            input.click();
        },

        // Drag and Drop Methods for JSON Editor
        onDragStart(event, text) {
            event.dataTransfer.setData("text/plain", text);
            event.dataTransfer.effectAllowed = "copy";
        },

        onJsonDragOver(event) {
            event.preventDefault();
            event.stopPropagation();

            const ta = this.getTextareaElement(event.target);
            if (ta) ta.classList.add("drag-over");
        },

        onJsonDragLeave(event) {
            event.stopPropagation();

            const ta = this.getTextareaElement(event.target);
            if (ta) ta.classList.remove("drag-over");
        },

        onJsonDrop(event) {
            event.preventDefault();
            event.stopPropagation(); // ป้องกันการทำงานซ้ำซ้อน

            const text = event.dataTransfer.getData("text/plain");
            if (!text) return;

            // หา textarea element ที่แท้จริง
            const ta = this.getTextareaElement(event.target);
            if (!ta) return;

            ta.classList.remove("drag-over");

            // คำนวณตำแหน่งการแทรกข้อความจากตำแหน่งของเมาส์
            const caretPosition = this.getCaretPositionFromPoint(ta, event.clientX, event.clientY);

            // แทรกข้อความที่ตำแหน่งที่คำนวณได้
            const value = ta.value || "";
            const newValue = value.slice(0, caretPosition) + text + value.slice(caretPosition);

            // อัพเดต textarea และ model
            ta.value = newValue;
            this.nodeInspector.jsonValidate = newValue;
            this.updateNodeProperty("jsonValidate", newValue);

            // เลื่อน caret หลังข้อความที่แทรก
            const newPos = caretPosition + text.length;
            this.$nextTick(() => {
                ta.setSelectionRange(newPos, newPos);
                ta.focus();
            });
        },

        // Drag & drop for Knowledge API JSON
        onKnowledgeJsonDrop(event) {
            event.preventDefault();
            event.stopPropagation();

            const text = event.dataTransfer.getData("text/plain");
            if (!text) return;

            // หา textarea element ที่แท้จริง
            const ta = this.getTextareaElement(event.target);
            if (!ta) return;

            ta.classList.remove("drag-over");

            // คำนวณตำแหน่งการแทรกข้อความจากตำแหน่งของเมาส์
            const caretPosition = this.getCaretPositionFromPoint(ta, event.clientX, event.clientY);

            // แทรกข้อความที่ตำแหน่งที่คำนวณได้
            const value = ta.value || "";
            const newValue = value.slice(0, caretPosition) + text + value.slice(caretPosition);

            // อัพเดต textarea และ model
            ta.value = newValue;
            this.nodeInspector.knowledgeRequestBody = newValue;
            this.updateNodeProperty("knowledgeRequestBody", newValue);

            // เลื่อน caret หลังข้อความที่แทรก
            const newPos = caretPosition + text.length;
            this.$nextTick(() => {
                ta.setSelectionRange(newPos, newPos);
                ta.focus();
            });
        },
        getCaretPositionFromPoint(textarea, clientX, clientY) {
            // สำหรับเบราว์เซอร์สมัยใหม่ที่รองรับ caretPositionFromPoint/caretRangeFromPoint
            if (document.caretPositionFromPoint) {
                const position = document.caretPositionFromPoint(clientX, clientY);
                if (position && position.offsetNode === textarea.firstChild) {
                    return position.offset;
                }
            } else if (document.caretRangeFromPoint) {
                const range = document.caretRangeFromPoint(clientX, clientY);
                if (range && range.startContainer === textarea.firstChild) {
                    return range.startOffset;
                }
            }

            // ถ้าไม่สามารถคำนวณตำแหน่งได้ ให้ใช้ตำแหน่งปัจจุบัน หรือท้ายข้อความ
            return textarea.selectionStart || textarea.value.length;
        },

        // หา <textarea> จริงๆ ใน v-textarea
        getTextareaElement(target) {
            if (target.tagName === "TEXTAREA") return target;
            const container = target.closest(".json-textarea-container");
            return container && container.querySelector("textarea");
        },

        // Click to insert variable
        insertVariable(text) {
            const textarea = this.$refs.jsonTextarea?.$el?.querySelector('textarea');

            if (textarea) {
                const start = textarea.selectionStart || 0;
                const end = textarea.selectionEnd || 0;
                const currentValue = textarea.value || '';

                const newValue =
                    currentValue.substring(0, start) +
                    text +
                    currentValue.substring(end);

                // อัพเดต model
                this.nodeInspector.jsonValidate = newValue;
                this.updateNodeProperty('jsonValidate', newValue);

                // ตั้งตำแหน่ง cursor
                this.$nextTick(() => {
                    textarea.setSelectionRange(start + text.length, start + text.length);
                    textarea.focus();
                });
            }
        },

        // Click to insert variable for Knowledge API
        insertKnowledgeVariable(text) {
            const textarea = this.$refs.knowledgeJsonTextarea?.$el?.querySelector('textarea');

            if (textarea) {
                const start = textarea.selectionStart || 0;
                const end = textarea.selectionEnd || 0;
                const currentValue = textarea.value || '';

                const newValue =
                    currentValue.substring(0, start) +
                    text +
                    currentValue.substring(end);

                // อัพเดต model
                this.nodeInspector.knowledgeRequestBody = newValue;
                this.updateNodeProperty('knowledgeRequestBody', newValue);

                // ตั้งตำแหน่ง cursor
                this.$nextTick(() => {
                    textarea.setSelectionRange(start + text.length, start + text.length);
                    textarea.focus();
                });
            }
        },
        getValidationErrors() {
            const errors = [];

            if (!this.tempNode.length) {
                errors.push('Flow must contain at least one node');
            }

            const isolatedNodes = this.tempNode.filter(node => node.data.showWarning != false);
            
            if (isolatedNodes.length) {
                isolatedNodes.forEach(element => {
                    errors.push(`${element.id} isolated node found`);
                });
            }

            return errors;
        },

        formatDate(dateString) {
            const date = new Date(dateString);
            const now = new Date();
            const diffMs = now - date;
            const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

            if (diffDays === 0) return 'Today';
            if (diffDays === 1) return 'Yesterday';
            if (diffDays < 7) return `${diffDays} days ago`;

            return date.toLocaleDateString();
        },

        handleBeforeUnload(event) {
            // Skip browser confirmation if we're already navigating away via custom dialog
            if (this.isNavigatingAway) {
                return;
            }
            
            // Show warning for page refresh, close tab, or navigate away from the page
            if (!this.isViewMode) {
                const message = 'Are you sure you want to leave? Any unsaved changes will be lost.';
                
                // Modern browsers - this will trigger the browser's confirmation dialog
                event.preventDefault();
                event.returnValue = message;
                
                // For older browsers
                return message;
            }
        },

        // Custom confirmation dialog methods
        showExitConfirmation(callback) {
            if (!this.isViewMode && (this.canUndo || this.canRedo)) {
                this.pendingNavigation = callback;
                this.confirmExitDialog = true;
                return false; // Prevent immediate navigation
            } else {
                callback(); // Proceed with navigation if in view mode
                return true;
            }
        },

        confirmExit() {
            this.confirmExitDialog = false;
            this.isNavigatingAway = true; // Set flag to prevent browser confirmation
            
            if (this.pendingNavigation) {
                // Add a small delay to ensure the flag is set before navigation
                this.$nextTick(() => {
                    this.pendingNavigation();
                    this.pendingNavigation = null;
                });
            }
        },

        cancelExit() {
            this.confirmExitDialog = false;
            this.pendingNavigation = null;
        },

        // Prompt Template Methods
        showPromptTemplate() {
            this.promptTemplateDialog = true;
        },
        
        showSmartIntentPromptTemplate(smartIntentIndex) {
            this.smartIntentCurrentIndex = smartIntentIndex;
            // Reset any existing group selection and search query for fresh start
            this.smartIntentGroupPromptTemplate = null;
            this.smartIntentTemplateSearchQuery = '';
            this.smartIntentPromptTemplateDialog = true;
        },

        setSelectSmartIntentPromptTemplate(template) {
            if (template && template.prompt && this.smartIntentCurrentIndex !== null) {
                const smartIntent = this.nodeInspector.smartIntents[this.smartIntentCurrentIndex];
                if (smartIntent && smartIntent.mainAction) {
                    // Insert template content into Smart Intent system prompt
                    const currentPrompt = smartIntent.mainAction.systemPrompt || '';
                    const templatePrompt = template.prompt;

                    // Add template content (could append or replace based on preference)
                    smartIntent.mainAction.systemPrompt = currentPrompt + (currentPrompt ? '\n\n' : '') + templatePrompt;
                    this.updateSmartIntentData();
                }

                // Close template dialog
                this.smartIntentPromptTemplateDialog = false;
                this.smartIntentCurrentIndex = null; // Reset
            }
        },

        showSmartIntentFallbackPromptTemplate() {
            // Reset any existing group selection and search query for fresh start
            this.smartIntentFallbackGroupPromptTemplate = null;
            this.smartIntentFallbackTemplateSearchQuery = '';
            this.smartIntentFallbackPromptTemplateDialog = true;
        },

        setSelectSmartIntentFallbackPromptTemplate(template) {
            if (template && template.prompt) {
                // Insert template content into Smart Intent Fallback system prompt
                const currentPrompt = this.nodeInspector.smartIntentFallback.systemPrompt || '';
                const templatePrompt = template.prompt;

                // Add template content (could append or replace based on preference)
                this.nodeInspector.smartIntentFallback.systemPrompt = currentPrompt + (currentPrompt ? '\n\n' : '') + templatePrompt;
                this.updateSmartIntentFallbackData();

                // Close template dialog
                this.smartIntentFallbackPromptTemplateDialog = false;
            }
        },

        setSelectPromptTemplate(template) {
            if (template && template.prompt) {
                // Only for regular AI Model nodes
                const currentPrompt = this.nodeInspector.systemPrompt || '';
                const templatePrompt = template.prompt;

                // Add template content (could append or replace based on preference)
                this.nodeInspector.systemPrompt = currentPrompt + (currentPrompt ? '\n\n' : '') + templatePrompt;
                this.updateNodeProperty('systemPrompt', this.nodeInspector.systemPrompt);

                // Close template dialog
                this.promptTemplateDialog = false;
            }
        },
        
        truncatePrompt(prompt) {
            if (!prompt) return '';
            return prompt.length > 100 ? prompt.substring(0, 100) + '...' : prompt;
        },

        onContextMenu(event) {
            if (this.filteredPromptTemplates.length > 0) {
                event.preventDefault();
                this.x = event.clientX;
                this.y = event.clientY;
                this.showMenu = true;
            }
        },

        // IntentAI Prompt Template Methods
        showIntentaiPromptTemplate() {
            this.intentaiPromptTemplateDialog = true;
        },

        setSelectIntentaiPromptTemplate(template) {
            if (template && template.prompt) {
                // Insert template content into intentAI system prompt
                const currentPrompt = this.nodeInspector.intentaiSystemPrompt || '';
                const templatePrompt = template.prompt;

                // Add template content (could append or replace based on preference)
                this.nodeInspector.intentaiSystemPrompt = currentPrompt + (currentPrompt ? '\n\n' : '') + templatePrompt;
                this.updateNodeProperty('intentaiSystemPrompt', this.nodeInspector.intentaiSystemPrompt);

                // Close template dialog
                this.intentaiPromptTemplateDialog = false;
            }
        },

        onIntentaiContextMenu(event) {
            if (this.filteredIntentaiPromptTemplates.length > 0) {
                event.preventDefault();
                this.x = event.clientX;
                this.y = event.clientY;
                this.showIntentaiMenu = true;
            }
        },

        // Input validation functions
        isNumberpositive: function (evt) {
            evt = evt || window.event;
            let charCode = evt.which ? evt.which : evt.keyCode;
            // Block minus sign (45) and any non-digit characters, but allow control keys (0-31)
            if ((charCode > 31 && (charCode < 48 || charCode > 57)) || charCode === 45) {
                evt.preventDefault();
            } else {
                return true;
            }
        },

        maxNumberpositive: function (evt) {
            evt = evt || window.event;
            let charCode = evt.which ? evt.which : evt.keyCode;

            // อนุญาตเฉพาะตัวเลข 0-9 เท่านั้น (บล็อกเครื่องหมายลบและตัวอื่นๆ)
            if (charCode < 48 || charCode > 57) {
                evt.preventDefault();
                return false;
            }

            return true;
        },

        // Function สำหรับตรวจสอบและปรับค่าเมื่อ unfocus หรือกด enter
        validateAndCorrectValue: function (evt, min = 1, max = 200000) {
            let value = parseInt(evt.target.value) || 0;

            if (value < min) {
                evt.target.value = min;
            } else if (value > max) {
                evt.target.value = max;
            }

            // อัปเดตค่าในโมเดล
            const fieldName = evt.target.getAttribute('data-field');
            if (fieldName && this.nodeInspector[fieldName] !== undefined) {
                this.nodeInspector[fieldName] = parseInt(evt.target.value);
                this.updateNodeProperty(fieldName, parseInt(evt.target.value));
            }
        },

        // Email list management methods
        addEmailToList(listType) {
            if(listType == 'ccEmails') {
                if (!this.nodeInspector.ccEmails) {
                    this.nodeInspector.ccEmails = [];
                }
                this.nodeInspector.ccEmails.push('');
            }
            else {
                if (!this.nodeInspector.bccEmails) {
                    this.nodeInspector.bccEmails = [];
                }
                this.nodeInspector.bccEmails.push('');
            }
            this.updateEmailList(listType);
        },

        removeEmailFromList(listType, index) {
            if (this.nodeInspector[listType]) {
                this.nodeInspector[listType].splice(index, 1);
                this.updateEmailList(listType);
            }
        },

        updateEmailList(listType) {
            if(listType == 'ccEmails') {
                if (!this.nodeInspector.ccEmails) {
                    this.nodeInspector.ccEmails = [];
                    this.updateNodeProperty(listType, []);
                    return;
                }
                const filteredEmails = this.nodeInspector.ccEmails.filter(email => email && email.trim() !== '');
                this.nodeInspector.ccEmails = [...this.nodeInspector.ccEmails]; // trigger reactivity
                this.updateNodeProperty(listType, this.nodeInspector.ccEmails);
            }
            else {
                if (!this.nodeInspector.bccEmails) {
                    this.nodeInspector.bccEmails = [];
                    this.updateNodeProperty(listType, []);
                    return;
                }
                const filteredEmails = this.nodeInspector.bccEmails.filter(email => email && email.trim() !== '');
                this.nodeInspector.bccEmails = [...this.nodeInspector.bccEmails]; // trigger reactivity
                this.updateNodeProperty(listType, this.nodeInspector.bccEmails);
            }
        }
    },
    async mounted() {
        try {
            // Load models first and wait for completion
            await Promise.all([
                this.loadGPTModels(),
                this.GetAllDDModelManagement()
            ]);
        } catch (error) {
            console.warn('Failed to load models:', error);
            // Show error message to user
            this.showError('Failed to load AI models. Please refresh the page.');
        }
        
        // Then check route mode after models are loaded
        await this.checkRouteMode();

        // Initialize history after loading
        this.initializeHistory();

        // Check validation for all nodes after loading
        this.$nextTick(() => {
            this.checkAllNodesValidation();
        });

        this.$nextTick(() => {
            if (this.$refs.vueFlow) {
                const flow = this.$refs.vueFlow;
                // Set default unlocked state
                flow.nodesDraggable = true;
                flow.nodesConnectable = true;

                // flow.elementsSelectable = true;
                // flow.zoomOnScroll = true;
                // flow.zoomOnDoubleClick = true;
                // flow.panOnDrag = true;

                // Ensure interactive controls are enabled by default (unlocked state)
                this.interactiveControls = true;
                if (this.isViewMode) {
                    this.setViewOnlyMode();
                }
            }

            // Focus the flow container so it can receive keyboard events
            if (this.$refs.flowContainer) {
                this.$refs.flowContainer.focus();
            }
        });
        
        window.addEventListener('beforeunload', this.handleBeforeUnload);
    },
    beforeRouteLeave(to, from, next) {
        if (!this.isViewMode && !this.isNavigatingAway) {
            // Use custom confirmation dialog instead of browser confirm
            this.showExitConfirmation(() => {
                this.isNavigatingAway = true; // Set flag to prevent handleBeforeUnload from triggering
                next();
            });
        } else {
            next();
        }
    },

    beforeUnmount() {
        window.removeEventListener('beforeunload', this.handleBeforeUnload);
        
        // Clear property update timer
        if (this.propertyUpdateTimer) {
            clearTimeout(this.propertyUpdateTimer);
        }
    }
}
</script>

<style scoped>
.card2 {
    height: 100%;
    border-radius: 16px !important;
    margin-bottom: 2rem;
    padding-top: 1rem;
    position: relative;
}

.flow-container {
    height: 100%;
    width: 100%;
    border: 1px solid rgba(0, 0, 0, 0.1);
    overflow: hidden;
    border-radius: 12px;
    outline: none; /* Remove focus outline */
}

.basic-flow {
    height: 100%;
    background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
    position: relative;
}

/* Header improvements */
:deep(.v-toolbar) {
    border-radius: 12px !important;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12) !important;
}

:deep(.vue-flow__edge) {
    stroke-width: 2;
}

:deep(.vue-flow__edge-path) {
    stroke: #1976d2;
}

:deep(.vue-flow__edge-text) {
    font-size: 12px;
    font-weight: 500;
    fill: #1976d2;
}

:deep(.vue-flow__background) {
    background: transparent !important;
}

:deep(.vue-flow__controls) {
    bottom: 5px;
    background: rgba(255, 255, 255, 0.9);
    border-radius: 12px;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
    display: flex;
    flex-direction: row;
    align-items: center;
    padding: 3px;
    gap: 4px;
}

:deep(.vue-flow__controls-button) {
    background: transparent;
    border: none;
    border-radius: 8px;
    margin: 0;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;
}

:deep(.vue-flow__controls-button:hover) {
    background: rgba(59, 130, 246, 0.1);
    transform: scale(1.05);
}

:deep(.vue-flow__controls-button:active) {
    transform: scale(0.95);
}

/* Enhanced edge hover effects */
:deep(.edge-hovered .vue-flow__edge-path) {
    transition: all 0.3s ease;
    stroke: #ffc400 !important;
    filter: drop-shadow(0 0 5px #ffc400) drop-shadow(0 0 10px #ffc400);
}

/* Node selection effects */
/* :deep(.vue-flow__node.selected) {
        box-shadow: 0 0 0 2px #3b82f6, 0 0 0 4px rgba(59, 130, 246, 0.2);
    } */

/* Responsive design */
@media (max-width: 768px) {
    .flow-container {
        height: 400px;
        margin: 0.5rem;
    }

    :deep(.vue-flow__controls) {
        bottom: 10px;
        left: 10px;
    }
}

/* Loading state */
.flow-loading {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    background: rgba(255, 255, 255, 0.9);
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 1000;
}

.custom-controls-demo {
    position: absolute;
    top: 20px;
    right: 20px;
    z-index: 10;
    background: white;
    padding: 10px 15px;
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    display: flex;
    align-items: center;
    gap: 15px;
}

/* Model Selector Styles */
.model-option {
    padding: 8px 0;
}

.model-icon-small {
    width: 20px;
    height: 20px;
    border-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
}

/* Floating Inspector Panel */
.floating-inspector {
    position: absolute;
    top: 25px;
    right: 10px;
    width: 420px;
    max-height: calc(100vh - 80px);
    background: rgba(255, 255, 255, 0.5);
    border-radius: 20px;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15), 0 8px 32px rgba(0, 0, 0, 0.1);
    z-index: 1000;
    display: flex;
    flex-direction: column;
    transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1);
    border: 1px solid rgba(0, 0, 0, 0.06);
    backdrop-filter: blur(10px);
    animation: slideInRight 0.4s cubic-bezier(0.16, 1, 0.3, 1);
    overflow: hidden;
}

.floating-inspector.inspector-expanded {
    position: absolute;
    top: 20px;
    left: 40%;
    right: 20px;
    bottom: 0px;
    width: auto;
    max-height: none;
    z-index: 1001;
    border-radius: 24px;
}

.inspector-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 24px 28px 20px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.2);
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    border-radius: 20px 20px 0 0;
    position: relative;
    overflow: hidden;
}

.inspector-header::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%);
    pointer-events: none;
}

.inspector-title {
    display: flex;
    align-items: center;
    font-weight: 600;
    font-size: 1.1rem;
    z-index: 1;
    position: relative;
}

.inspector-controls {
    display: flex;
    align-items: center;
    gap: 8px;
    z-index: 1;
    position: relative;
}

.inspector-controls .v-btn {
    background: rgba(255, 255, 255, 0.15) !important;
    backdrop-filter: blur(10px);
    border: 1px solid rgba(255, 255, 255, 0.2) !important;
    transition: all 0.2s ease !important;
}

.inspector-controls .v-btn:hover {
    background: rgba(255, 255, 255, 0.25) !important;
    transform: scale(1.05);
}

.inspector-content {
    flex: 1;
    overflow-y: auto;
    padding: 0px;
    background-color: white;
}

/* Form Sections */
.section-title {
    font-size: 0.875rem;
    font-weight: 600;
    color: #374151;
    margin-bottom: 16px;
    text-transform: uppercase;
    letter-spacing: 0.05em;
}

.section-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
}

.add-btn {
    height: 36px !important;
    font-size: 0.8rem !important;
    font-weight: 600 !important;
    border-radius: 10px !important;
    text-transform: none !important;
    letter-spacing: 0.3px !important;
}

.header-chip {
    font-weight: 700 !important;
    text-transform: uppercase !important;
    letter-spacing: 1px !important;
    border-radius: 16px !important;
    padding: 12px 20px !important;
    min-height: 48px !important;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
    background: linear-gradient(135deg, var(--v-theme-primary) 0%, var(--v-theme-primary-darken-1) 100%) !important;
    border: 2px solid rgba(255, 255, 255, 0.2) !important;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
    position: relative;
    overflow: hidden;
    color: white !important;
}

.header-chip::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
    transition: left 0.5s;
}

.header-chip:hover::before {
    left: 100%;
}

.header-chip:hover {
    transform: translateY(-2px) scale(1.02);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2) !important;
}

.header-chip .v-icon {
    filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.1));
}

/* Color-specific gradients for different node types */
.header-chip.bg-blue {
    background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%) !important;
}

.header-chip.bg-green {
    background: linear-gradient(135deg, #10b981 0%, #047857 100%) !important;
}

.header-chip.bg-teal {
    background: linear-gradient(135deg, #14b8a6 0%, #0f766e 100%) !important;
}

.header-chip.bg-orange {
    background: linear-gradient(135deg, #f97316 0%, #ea580c 100%) !important;
}

.header-chip.bg-purple {
    background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%) !important;
}

.header-chip.bg-red {
    background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%) !important;
}

.header-chip.bg-indigo {
    background: linear-gradient(135deg, #6366f1 0%, #4f46e5 100%) !important;
}

.header-chip.bg-amber {
    background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%) !important;
}

.header-chip.bg-pink {
    background: linear-gradient(135deg, #ec4899 0%, #db2777 100%) !important;
}

/* Node Type Badge */
.node-type-badge {
    display: flex;
    justify-content: center;
    margin-bottom: 20px;
    padding: 8px 0;
}

.node-type-badge .v-chip {
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    border-radius: 12px;
}

/* Modern Input Styles */
.modern-input :deep(.v-field) {
    border-radius: 14px !important;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    background: rgba(255, 255, 255, 0.8);
    backdrop-filter: blur(10px);
}

.modern-input :deep(.v-field--focused) {
    transform: translateY(-1px);
}

.modern-input :deep(.v-field__outline) {
    --v-field-border-opacity: 0.15;
}

.modern-input :deep(.v-field--focused .v-field__outline) {
    --v-field-border-opacity: 1;
    color: rgb(102, 126, 234) !important;
    --v-field-border-width: 2px;
}

.modern-input :deep(.v-label) {
    font-weight: 500;
}
/* Modern Switch */
.modern-switch :deep(.v-switch__track) {
    border-radius: 20px !important;
    background: #e5e7eb !important;
    transition: all 0.3s ease !important;
}

.modern-switch :deep(.v-switch--inset .v-switch__track) {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
}

.modern-switch :deep(.v-switch__thumb) {
    border-radius: 50% !important;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15) !important;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
}

.modern-switch :deep(.v-switch__thumb):hover {
    transform: scale(1.1);
}

/* Slider Styles */
.slider-container {
    padding: 16px 0;
    background: rgba(255, 255, 255, 0.5);
    border-radius: 12px;
    padding: 16px;
    border: 1px solid rgba(0, 0, 0, 0.05);
}

.slider-label {
    font-size: 0.875rem;
    font-weight: 600;
    color: #374151;
    margin-bottom: 12px;
    display: block;
}

.modern-slider :deep(.v-slider-track__fill) {
    background: linear-gradient(90deg, #667eea 0%, #764ba2 100%) !important;
}

.modern-slider :deep(.v-slider-thumb__surface) {
    background: white !important;
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3) !important;
    border: 3px solid #667eea !important;
}

.modern-slider :deep(.v-slider-thumb__surface):hover {
    transform: scale(1.2);
}

/* Intent Cards */
.intents-section {
    padding-right: 4px;
}

.intent-card-inner {
    border-radius: 16px !important;
    border: 1px solid rgba(0, 0, 0, 0.06) !important;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    background: linear-gradient(145deg, #ffffff 0%, #f8fafc 100%);
    overflow: hidden;
}

.intent-card-inner:hover {
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.08) !important;
    transform: translateY(-2px);
}

.intent-card-inner :deep(.v-card-text) {
    background: transparent !important;
}

/* Scrollbar Styles */
.inspector-content::-webkit-scrollbar,
.intents-section::-webkit-scrollbar {
    width: 6px;
}

.inspector-content::-webkit-scrollbar-track,
.intents-section::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.05);
    border-radius: 3px;
}

.inspector-content::-webkit-scrollbar-thumb,
.intents-section::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.2);
    border-radius: 3px;
}

.inspector-content::-webkit-scrollbar-thumb:hover,
.intents-section::-webkit-scrollbar-thumb:hover {
    background: rgba(0, 0, 0, 0.3);
}

/* Animation for smooth transitions */
@keyframes slideInRight {
    from {
        transform: translateX(100%) scale(0.9);
        opacity: 0;
    }

    to {
        transform: translateX(0) scale(1);
        opacity: 1;
    }
}

/* Add button animations */
.add-btn {
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1) !important;
}

.add-btn:hover {
    transform: translateY(-1px) !important;
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3) !important;
}

/* Improved scrollbar */
.inspector-content::-webkit-scrollbar,
.intents-section::-webkit-scrollbar {
    width: 8px;
}

.inspector-content::-webkit-scrollbar-track,
.intents-section::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.03);
    border-radius: 4px;
}

.inspector-content::-webkit-scrollbar-thumb,
.intents-section::-webkit-scrollbar-thumb {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    border-radius: 4px;
    border: 2px solid transparent;
    background-clip: content-box;
}

.inspector-content::-webkit-scrollbar-thumb:hover,
.intents-section::-webkit-scrollbar-thumb:hover {
    background: linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%);
    background-clip: content-box;
}

/* Mobile Responsive for Inspector */
@media (max-width: 768px) {
    .floating-inspector {
        width: calc(100vw - 20px);
        right: 10px;
        left: 10px;
        width: auto;
    }

    .floating-inspector.inspector-expanded {
        width: calc(100vw - 20px);
        left: 10px;
        right: 10px;
        top: 10px;
        bottom: 10px;
    }

    .inspector-header {
        padding: 20px;
    }

    .inspector-content {
        padding: 20px;
    }
}

/* Intent expansion panel styling */
.intent-expansion {
    border-radius: 8px !important;
    overflow: hidden;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08) !important;
}

.intent-expansion .v-expansion-panel {
    border-radius: 8px !important;
}

.intent-expansion .v-expansion-panel-title {
    padding: 16px 20px !important;
    background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%) !important;
    border-bottom: 1px solid rgba(0, 0, 0, 0.06) !important;
    min-height: 56px !important;
}

.intent-expansion .v-expansion-panel-title:hover {
    background: linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%) !important;
}

.intent-expansion .v-expansion-panel-text {
    background: #ffffff !important;
}

.intent-content {
    background: #ffffff;
    border-radius: 0 0 8px 8px;
}

/* Smart Intent card styling */
.smart-intent-card {
    background: #fcfaf8 !important;
    border: 1px solid rgba(37, 99, 235, 0.1) !important;
    border-radius: 12px !important;
    box-shadow: 0 4px 12px rgba(37, 99, 235, 0.08) !important;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
    overflow: hidden !important;
}

.smart-intent-card:hover {
    border-color: rgba(37, 99, 235, 0.2) !important;
    box-shadow: 0 8px 24px rgba(37, 99, 235, 0.12) !important;
    transform: translateY(-2px) !important;
}

.smart-intent-card .v-card-title {
    background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%) !important;
    color: white !important;
    padding: 16px 20px !important;
    font-size: 1.1rem !important;
    font-weight: 600 !important;
    letter-spacing: 0.5px !important;
}

.smart-intent-card .v-card-text {
    padding: 20px !important;
    background: #ffffff !important;
}

/* Smart Intent expansion panel styling */
.smart-intent-expansion {
    padding: 0 !important;
    border-radius: 8px !important;
    overflow: hidden;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08) !important;
}

.smart-intent-expansion .v-expansion-panel {
    border-radius: 8px !important;
}

.smart-intent-expansion .v-expansion-panel-title {
    padding: 16px 20px !important;
    background: #e8f3ff !important;
    border-bottom: 1px solid #4e46e528 !important;
    min-height: 56px !important;
}

.smart-intent-expansion .v-expansion-panel-title:hover {
    background: #c7e1ff !important;
}

.smart-intent-expansion .v-expansion-panel-text {
    background: #ffffff !important;
}
.smart-intent-expansion :deep(.v-expansion-panel-text__wrapper) {
    padding: 0 5px 0 5px !important;
}

.smart-intent-content {
    background: #ffffff;
    border-radius: 0 0 8px 8px;
}

/* Fallback AI Config expansion panel styling */
.fallback-ai-config-expansion :deep(.v-expansion-panel-text .v-expansion-panel-text__wrapper) {
    padding: 0 5px 0 5px !important;
}
.fallback-ai-config-expansion {
    padding: 0 !important;
    border-radius: 8px !important;
    overflow: hidden;
    box-shadow: 0 2px 8px rgba(255, 152, 0, 0.12) !important;
    margin-top: 8px !important;
}

.fallback-ai-config-expansion .v-expansion-panel {
    border-radius: 8px !important;
    border: 1px solid rgba(255, 152, 0, 0.2) !important;
}

.fallback-ai-config-title {
    padding: 16px 20px !important;
    background: linear-gradient(135deg, #fff3e0, #ffe0b2) !important;
    border-bottom: 1px solid rgba(255, 152, 0, 0.3) !important;
    min-height: 56px !important;
    transition: all 0.3s ease !important;
}

.fallback-ai-config-title:hover {
    background: linear-gradient(135deg, #ffe0b2, #ffcc80) !important;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(255, 152, 0, 0.15) !important;
}

.fallback-ai-config-expansion .v-expansion-panel-text {
    background: #ffffff !important;
    border-radius: 0 0 8px 8px !important;
}

.fallback-ai-config-expansion :deep(.v-expansion-panel-text__wrapper) {
    padding: 16px 20px !important;
}

/* AI Main Config expansion panel styling */
.ai-main-config-expansion {
    padding: 0 !important;
    border-radius: 8px !important;
    overflow: hidden;
    box-shadow: 0 2px 8px rgba(33, 150, 243, 0.12) !important;
    margin-top: 8px !important;
}

.ai-main-config-expansion .v-expansion-panel {
    border-radius: 8px !important;
    border: 1px solid rgba(33, 150, 243, 0.2) !important;
}
.ai-main-config-expansion :deep(.v-expansion-panel .v-expansion-panel-text .v-expansion-panel-text__wrapper) {
    padding: 0 5px 0 5px !important;
}

.ai-config-title {
    padding: 16px 20px !important;
    background: linear-gradient(135deg, #e3f2fd, #bbdefb) !important;
    border-bottom: 1px solid rgba(33, 150, 243, 0.3) !important;
    min-height: 56px !important;
    transition: all 0.3s ease !important;
}

.ai-config-title:hover {
    background: linear-gradient(135deg, #bbdefb, #90caf9) !important;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(33, 150, 243, 0.15) !important;
}

.ai-main-config-expansion .v-expansion-panel-text {
    background: #ffffff !important;
    border-radius: 0 0 8px 8px !important;
}

.ai-main-config-expansion :deep(.v-expansion-panel-text__wrapper) {
    padding: 16px 20px !important;
}

/* Switch output expansion panel styling */
.switch-output-expansion {
    border-radius: 8px !important;
    overflow: hidden;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08) !important;
}

.switch-output-expansion .v-expansion-panel {
    border-radius: 8px !important;
}

.switch-output-expansion .v-expansion-panel-title {
    padding: 16px 20px !important;
    background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%) !important;
    border-bottom: 1px solid rgba(0, 0, 0, 0.06) !important;
    min-height: 56px !important;
}

.switch-output-expansion .v-expansion-panel-title:hover {
    background: linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%) !important;
}

.switch-output-expansion .v-expansion-panel-text {
    background: #ffffff !important;
}

.switch-output-content {
    background: #ffffff;
    border-radius: 0 0 8px 8px;
}

.switch-outputs-section {
    max-height: 500px;
    padding-right: 4px;
}

.switch-output-card {
    border-radius: 16px !important;
    border: 1px solid rgba(0, 0, 0, 0.06) !important;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    background: linear-gradient(145deg, #ffffff 0%, #f8fafc 100%);
    overflow: hidden;
}

.switch-output-card:hover {
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.08) !important;
    transform: translateY(-2px);
}

.switch-output-card .v-card-text {
    background: transparent !important;
}

/* Selection box styling */
:deep(.vue-flow__selection) {
    background: rgba(102, 126, 234, 0.08);
    border: 1.5px solid #667eea;
    border-radius: 4px;
}

:deep(.vue-flow__nodesselection-rect) {
    background: transparent;
    border: none;
    box-shadow: none;
}

/* Multi-Selection Indicator */
.floating-multi-selection {
    position: absolute;
    top: 30px;
    right: 20px;
    z-index: 1000;
    max-width: 280px;
}

.multi-selection-card {
    border-radius: 12px !important;
    backdrop-filter: blur(10px);
    background: rgba(255, 255, 255, 0.95) !important;
    border: 1px solid rgba(255, 255, 255, 0.2);
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1) !important;
}

.multi-selection-card .v-card-text {
    background: transparent;
}

/* Condition Configuration Styles */
.condition-builder {
    background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
    border-radius: 12px;
    padding: 16px;
    border: 1px solid rgba(0, 0, 0, 0.06);
}

.row-container {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.condition-builder .modern-input {
    background: white;
    border-radius: 8px;
}

.condition-builder .modern-input :deep(.v-field) {
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.output-labels-section {
    background: linear-gradient(135deg, #fef7ff 0%, #f3e8ff 100%);
    border-radius: 12px;
    padding: 16px;
    border: 1px solid rgba(168, 85, 247, 0.1);
}

.output-labels-section .section-title {
    color: #7c3aed;
    font-weight: 600;
}

.gap-2 {
    gap: 8px;
}

.d-flex.gap-2 {
    display: flex;
}

/* Condition type specific styling */
.condition-builder.text-contains {
    background: linear-gradient(135deg, #fef3e2 0%, #fed7aa 100%);
    border-color: rgba(251, 146, 60, 0.2);
}

.condition-builder.number-range {
    background: linear-gradient(135deg, #ecfccb 0%, #d9f99d 100%);
    border-color: rgba(132, 204, 22, 0.2);
}

.condition-builder.boolean-check {
    background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
    border-color: rgba(59, 130, 246, 0.2);
}

.condition-builder.custom-expression {
    background: linear-gradient(135deg, #fdf2f8 0%, #fce7f3 100%);
    border-color: rgba(236, 72, 153, 0.2);
}

/* Delay Configuration Styles */
.delay-config-section {
    background: linear-gradient(135deg, #fef3f4 0%, #fde2e4 100%);
    border-radius: 12px;
    padding: 16px;
    border: 1px solid rgba(244, 63, 94, 0.1);
}

.delay-config-section .modern-input {
    background: white;
    border-radius: 8px;
}

.delay-config-section .modern-input :deep(.v-field) {
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.duration-preview {
    background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
    border-radius: 12px;
    padding: 12px;
    border: 1px solid rgba(14, 165, 233, 0.1);
}

.duration-preview .v-alert {
    background: transparent !important;
}

/* Delay type specific styling */
.delay-config-section.fixed-delay {
    background: linear-gradient(135deg, #ecfccb 0%, #d9f99d 100%);
    border-color: rgba(132, 204, 22, 0.2);
}

.delay-config-section.random-delay {
    background: linear-gradient(135deg, #fef3e2 0%, #fed7aa 100%);
    border-color: rgba(251, 146, 60, 0.2);
}

.delay-config-section.variable-delay {
    background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
    border-color: rgba(59, 130, 246, 0.2);
}

/* Switch Condition Builder Styles */
.condition-builder-section {
    background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
    border-radius: 12px;
    padding: 16px;
    border: 1px solid rgba(0, 0, 0, 0.06);
}

.condition-section-title {
    color: #475569;
    font-weight: 600;
    font-size: 0.875rem;
    margin: 0;
}

.condition-mode-toggle {
    border-radius: 8px !important;
}

.condition-mode-toggle .v-btn {
    border-radius: 6px !important;
    text-transform: none !important;
    font-weight: 500 !important;
}

.simple-condition-builder,
.advanced-condition-builder {
    background: white;
    border-radius: 10px;
    padding: 12px;
    border: 1px solid rgba(0, 0, 0, 0.04);
    margin-top: 12px;
}

.condition-fields {
    background: linear-gradient(135deg, #fefcfb 0%, #fef7f0 100%);
    border-radius: 8px;
    padding: 12px;
    border: 1px solid rgba(251, 146, 60, 0.1);
    margin-top: 8px;
}

.condition-preview {
    background: rgba(59, 130, 246, 0.1) !important;
    color: #1e40af !important;
    padding: 4px 8px !important;
    border-radius: 4px !important;
    font-family: 'Fira Code', monospace !important;
    font-size: 0.75rem !important;
    word-break: break-all;
}

.condition-hints {
    background: linear-gradient(135deg, #fefcfb 0%, #fef7f0 100%);
    border-radius: 8px;
    padding: 8px;
    border: 1px solid rgba(251, 146, 60, 0.1);
}

.hint-chip {
    cursor: pointer !important;
    transition: all 0.2s ease !important;
    font-family: 'Fira Code', monospace !important;
    font-size: 0.75rem !important;
}

.hint-chip:hover {
    background: rgba(59, 130, 246, 0.1) !important;
    color: #1e40af !important;
}

.gap-2 {
    gap: 8px;
}

/* Switch output specific styling */
.switch-output-expansion .condition-builder-section {
    margin-bottom: 0;
}

.switch-output-expansion .condition-fields .modern-input {
    background: white;
    border-radius: 6px;
}

.switch-output-expansion .condition-fields .modern-input :deep(.v-field) {
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

/* Toolbar Styles */
.flow-toolbar {
    position: absolute;
    top: 10px;
    left: 50%;
    transform: translateX(-50%);
    z-index: 1000;
    width: 500px;
    max-width: 80%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-direction: column;
    padding: 16px 24px;
    backdrop-filter: blur(10px);
    background: rgba(255, 255, 255, 0.3);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    border-radius: 16px;
}

.toolbar-left {
    display: flex;
    align-items: center;
    gap: 20px;
}

.flow-status-chip {
    font-weight: 700 !important;
    text-transform: uppercase !important;
    letter-spacing: 1px !important;
    border-radius: 12px !important;
    padding: 8px 16px !important;
    min-height: 40px !important;
    box-shadow: 0 2px 8px rgba(25, 118, 210, 0.2) !important;
}

.flow-info {
    display: flex;
    flex-direction: row;
    gap: 4px;
}

.confirmation-content {
    padding: 8px 0;
}

.flow-name-display {
    display: flex;
    align-items: center;
    padding: 20px;
    background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
    border-radius: 12px;
    border: 1px solid rgba(0, 0, 0, 0.06);
    margin-bottom: 20px;
}

.flow-subtitle {
    font-size: 0.875rem;
    color: #6b7280;
    margin-top: 4px;
}

.stats-summary {
    display: flex;
    justify-content: space-around;
    padding: 16px;
    background: white;
    border-radius: 8px;
    border: 1px solid rgba(0, 0, 0, 0.06);
    margin-bottom: 16px;
}

.stat-item {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 0.875rem;
    font-weight: 500;
    color: #374151;
}

.stat-item .v-icon {
    opacity: 0.8;
}

.flow-name {
    font-size: 1.25rem;
    font-weight: 600;
    color: #1a202c;
    margin: 0;
}

.flow-status {
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 0.75rem;
    color: #f59e0b;
    font-weight: 500;
}

.flow-status.saved {
    color: green;
}

.toolbar-actions {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    margin-top: 3px;
    padding: 0px 12px;
}

.btn-text {
    margin-left: 4px;
    font-size: 0.8rem;
}

.lock-btn {
    border-radius: 10px !important;
    text-transform: none !important;
    font-weight: 500 !important;
    transition: all 0.2s ease !important;
}

.more-actions-btn {
    border-radius: 50% !important;
    background: transparent !important;
}

/* Dialog Styles */
.save-dialog,
.load-dialog,
.load-model-dialog {
    border-radius: 20px !important;
    overflow: hidden;
}

.dialog-header {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    padding: 24px !important;
    display: flex;
    align-items: center;
    justify-content: space-between;
}

.dialog-header .header-content {
    flex: 1;
}

.dialog-header .text-h5 {
    font-size: 1.25rem !important;
}

.header-content {
    display: flex;
    align-items: center;
}

.dialog-content {
    padding: 28px !important;
    background: #fafafa;
}

.dialog-actions {
    padding: 20px 28px !important;
    background: white;
    border-top: 1px solid rgba(0, 0, 0, 0.06);
}

/* Header Actions */
.header-actions {
    display: flex;
    align-items: center;
    gap: 8px;
}

.load-model-btn {
    transition: all 0.2s ease;
}

.load-model-btn:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

/* Load Model Dialog Specific */
.load-model-content {
    min-height: 200px;
}

.model-selection-section {
    margin-bottom: 20px;
}

.model-list-item {
    padding: 12px 16px;
    border-radius: 8px;
    margin: 4px 0;
}

.model-list-item:hover {
    background-color: #f8fafc;
}

.config-preview-section {
    margin-top: 20px;
}

.config-preview-card {
    border: 1px solid #e5e7eb;
    border-radius: 8px;
}

.preview-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
}

.preview-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 0;
    border-bottom: 1px solid #f3f4f6;
}

.preview-item:last-child {
    border-bottom: none;
}

.preview-label {
    font-weight: 500;
    color: #6b7280;
    font-size: 0.875rem;
}

.preview-value {
    font-weight: 600;
    color: #374151;
    font-size: 0.875rem;
    text-align: right;
}

/* Form Sections */
.form-section {
    background: white;
    padding: 20px;
    margin-bottom: 20px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.section-title {
    font-size: 0.9rem;
    font-weight: 600;
    color: #374151;
    margin-bottom: 16px;
    display: flex;
    align-items: center;
}

/* Stats Grid */
.stats-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 12px;
}

.stat-card {
    border-radius: 8px !important;
}

.stat-number {
    font-size: 1.5rem;
    font-weight: 700;
    line-height: 1;
}

.stat-label {
    font-size: 0.75rem;
    opacity: 0.8;
    margin-top: 4px;
}

/* Flow List */
.search-section {
    padding: 0 4px;
}

.flow-list {
    max-height: 400px;
    overflow-y: auto;
    padding: 4px;
}

.flow-item {
    margin-bottom: 12px;
    cursor: pointer;
    transition: all 0.2s ease;
    border-radius: 12px !important;
}

.flow-item:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1) !important;
    transform: translateY(-1px);
}

.flow-item.selected {
    border-color: #667eea !important;
    box-shadow: 0 0 0 2px rgba(102, 126, 234, 0.2) !important;
}

.flow-icon {
    background: rgba(102, 126, 234, 0.1);
    border-radius: 8px;
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.flow-title {
    font-size: 1rem;
    font-weight: 600;
    color: #1a202c;
}

.flow-meta {
    font-size: 0.75rem;
    color: #6b7280;
    margin-top: 2px;
}

.flow-description {
    font-size: 0.85rem;
    color: #4b5563;
    line-height: 1.4;
    margin-top: 8px;
}

.flow-stats {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.flow-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
}

.no-flows {
    text-align: center;
    padding: 40px 20px;
}

/* Tags Section */
.tags-section {
    margin-top: 8px;
}

/* Alert Styles */
.alert-title {
    font-weight: 600;
    margin-bottom: 4px;
}

/* Modern Input Overrides */
.modern-input :deep(.v-field) {
    border-radius: 10px !important;
    background: rgba(255, 255, 255, 0.9);
    backdrop-filter: blur(10px);
}

/* Responsive Design */
@media (max-width: 768px) {
    .flow-toolbar {
        flex-direction: column;
        gap: 12px;
        padding: 12px 16px;
    }

    .toolbar-left {
        width: 100%;
        justify-content: center;
    }

    .toolbar-actions {
        width: 100%;
        justify-content: center;
    }

    .stats-grid {
        grid-template-columns: repeat(2, 1fr);
    }

    .btn-text {
        display: none;
    }
}

@media (max-width: 480px) {
    .stats-grid {
        grid-template-columns: 1fr;
    }

    .flow-stats {
        flex-direction: row;
        gap: 8px;
    }
}

/* Scrollbar for flow list */
.flow-list::-webkit-scrollbar {
    width: 6px;
}

.flow-list::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.05);
    border-radius: 3px;
}

.flow-list::-webkit-scrollbar-thumb {
    background: rgba(102, 126, 234, 0.3);
    border-radius: 3px;
}

.flow-list::-webkit-scrollbar-thumb:hover {
    background: rgba(102, 126, 234, 0.5);
}

.floating-back-btn {
    position: absolute;
    top: 20px;
    left: 20px;
    z-index: 2000;
    background: rgba(255, 255, 255, 0.95) !important;
    backdrop-filter: blur(10px);
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12), 0 2px 8px rgba(0, 0, 0, 0.08) !important;
    border-radius: 12px !important;
    border: 1px solid rgba(255, 255, 255, 0.2);
    height: 44px !important;
    padding: 0 16px !important;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
    overflow: hidden;
}

.floating-back-btn::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(102, 126, 234, 0.1), transparent);
    transition: left 0.5s ease;
}

.floating-back-btn:hover::before {
    left: 100%;
}

.floating-back-btn:hover {
    background: rgba(255, 255, 255, 1) !important;
    transform: translateY(-2px) scale(1.02);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15), 0 4px 12px rgba(0, 0, 0, 0.1) !important;
}

.floating-back-btn:active {
    transform: translateY(-1px) scale(0.98);
}

.floating-back-btn .v-icon {
    margin-right: 8px;
    transition: transform 0.2s ease;
}

.floating-back-btn:hover .v-icon {
    transform: translateX(-2px);
}

.back-text {
    font-size: 0.875rem;
    font-weight: 600;
    color: #667eea;
    letter-spacing: 0.3px;
    transition: color 0.2s ease;
}

.floating-back-btn:hover .back-text {
    color: #5a67d8;
}

.view-mode-btn {
    background-color: rgb(255, 224, 121);
    height: 40px;
    color: rgb(186, 109, 9);
    border-radius: 8px;
}

/* API Trigger Styles */
.api-section {
    background: rgba(248, 249, 250, 0.5);
    border-radius: 12px;
    padding: 20px;
    border: 1px solid rgba(0, 0, 0, 0.06);
    margin-bottom: 16px;
}

.section-header {
    display: flex;
    align-items: center;
    margin-bottom: 16px;
}

.section-title {
    font-weight: 600;
    font-size: 1rem;
    color: #1976d2;
    margin-bottom: 12px;
}

.header-row,
.validation-row {
    background: white;
    border-radius: 8px;
    padding: 12px;
    border: 1px solid rgba(0, 0, 0, 0.06);
    margin-bottom: 8px;
}

.headers-section,
.validation-section {
    background: rgba(248, 249, 250, 0.3);
    border-radius: 8px;
    padding: 12px;
    border: 1px solid rgba(0, 0, 0, 0.04);
}

.add-btn {
    margin-top: 8px;
    border: 1px dashed #1976d2 !important;
    background: rgba(25, 118, 210, 0.04) !important;
    color: #1976d2 !important;
    border-radius: 8px !important;
    transition: all 0.2s ease !important;
}

.add-btn:hover {
    background: rgba(25, 118, 210, 0.08) !important;
    border-color: #1976d2 !important;
}

/* Responsive Design for API Trigger */
@media (max-width: 768px) {
    .api-section {
        padding: 16px;
    }

    .header-row,
    .validation-row {
        padding: 8px;
    }

    .section-title {
        font-size: 0.9rem;
    }

    .headers-section,
    .validation-section {
        padding: 8px;
    }
}

/* Enhanced JSON Editor Container */
.json-editor-container {
    background: white;
    border-radius: 12px;
    border: 1px solid rgba(0, 0, 0, 0.08);
    overflow: hidden;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
}

/* Variables Panel Header */
.json-variables-header {
    background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);
    padding: 12px 16px;
}

.variables-header-content {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
}

.variables-title {
    display: flex;
    align-items: center;
    font-weight: 600;
    color: #374151;
    flex: 1;
    justify-content: space-between;
}

.category-filter-btn {
    height: 28px !important;
    min-width: auto !important;
    padding: 0 8px !important;
    border-radius: 6px !important;
    font-size: 0.75rem !important;
    color: #6b7280 !important;
    transition: all 0.2s ease !important;
}

.category-filter-btn.filter-active {
    background: rgba(79, 70, 229, 0.1) !important;
    color: #4f46e5 !important;
}

.category-filter-btn:hover {
    background: rgba(0, 0, 0, 0.04) !important;
}

.expand-btn {
    height: 28px !important;
    width: 28px !important;
    border-radius: 6px !important;
    color: #6b7280 !important;
}

.expand-btn:hover {
    background: rgba(0, 0, 0, 0.04) !important;
    color: #374151 !important;
}

/* Category Menu */
.category-menu {
    border-radius: 8px !important;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
    border: 1px solid rgba(0, 0, 0, 0.06) !important;
    min-width: 120px;
}

.category-menu .v-list-item {
    min-height: 36px !important;
    padding: 0 12px !important;
    font-size: 0.875rem !important;
}

.category-menu .v-list-item:hover {
    background: rgba(79, 70, 229, 0.04) !important;
}

.category-menu .v-list-item--active {
    background: rgba(79, 70, 229, 0.1) !important;
    color: #4f46e5 !important;
}

/* Variables Content */
.json-variables-content {
    padding: 16px;
    background: #fafbfc;
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}

/* Compact Grid Layout */
.variables-quick-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(130px, 130px));
    gap: 8px;
    max-height: 160px;
    overflow-y: auto;
    padding: 2px;
}

.variable-chip-compact {
    height: 32px !important;
    padding: 0 8px !important;
    border-radius: 8px !important;
    cursor: grab !important;
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1) !important;
    display: flex !important;
    align-items: center !important;
    justify-content: space-between !important;
    font-size: 0.75rem !important;
    border: 1px solid rgba(0, 0, 0, 0.06) !important;
    user-select: none;
}

.variable-chip-compact:hover {
    transform: translateY(-1px) !important;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15) !important;
    border-color: rgba(0, 0, 0, 0.1) !important;
}

.variable-chip-compact:active {
    cursor: grabbing !important;
    transform: scale(0.98) !important;
}

.variable-label {
    flex: 1;
    font-weight: 500;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    margin: 0 4px;
}

.drag-handle {
    opacity: 0.4;
    transition: opacity 0.2s ease;
    position: absolute;
    right: 12px;
}

.variable-chip-compact:hover .drag-handle {
    opacity: 0.8;
}

.variable-key {
    font-size: 0.75rem !important;
    color: #6b7280 !important;
    font-family: 'Fira Code', monospace !important;
}

.variable-actions {
    display: flex;
    align-items: center;
    gap: 4px;
}

.insert-btn {
    opacity: 0;
    transition: opacity 0.2s ease;
}

.variable-list-item:hover .insert-btn {
    opacity: 1;
}

/* JSON Textarea Container */
.json-textarea-container {
    position: relative;
}

.modern-json-editor {
    border-radius: 0 !important;
    background: white !important;
}

.modern-json-editor :deep(.v-field) {
    border-radius: 0 !important;
    font-family: 'Fira Code', 'Fira Mono', Consolas, Menlo, Courier, monospace !important;
    font-size: 13px !important;
    line-height: 1.5 !important;
    border: none !important;
    box-shadow: none !important;
}

.modern-json-editor :deep(.v-field__outline) {
    display: none !important;
}

.modern-json-editor :deep(textarea) {
    border: 2px solid transparent !important;
    transition: all 0.2s ease !important;
    background: white !important;
    min-height: 120px !important;
}

.modern-json-editor :deep(textarea:focus) {
    box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.1) !important;
}

.modern-json-editor :deep(textarea.drag-over) {
    border-color: #10b981 !important;
    background-color: rgba(16, 185, 129, 0.05) !important;
    box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.1) !important;
}

.modern-json-editor :deep(textarea:hover) {
    cursor: text;
}

.modern-json-editor :deep(textarea.drag-over) {
    cursor: copy !important;
}

/* Insert Helper */
.insert-helper {
    position: absolute;
    bottom: 8px;
    right: 12px;
    display: flex;
    align-items: center;
    background: rgba(255, 255, 255, 0.9);
    backdrop-filter: blur(10px);
    padding: 4px 8px;
    border-radius: 6px;
    border: 1px solid rgba(0, 0, 0, 0.06);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
    pointer-events: none;
}

.helper-text {
    font-size: 0.7rem;
    color: #6b7280;
    font-weight: 500;
}

/* Time Field Container */
.time-field-container {
    max-width: 300px;
}

.time-unit {
    font-size: 0.875rem;
    font-weight: 500;
}

.info-icon {
    opacity: 0.7;
    transition: opacity 0.2s ease;
}

.info-icon:hover {
    opacity: 1;
}

/* Animation for smooth transitions */
.v-enter-active,
.v-leave-active {
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.v-enter-from,
.v-leave-to {
    opacity: 0;
    transform: translateY(-8px);
}

/* Scrollbar Styling */
.variables-quick-grid::-webkit-scrollbar {
    width: 4px;
}

.variables-quick-grid::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.04);
    border-radius: 2px;
}

.variables-quick-grid::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.2);
    border-radius: 2px;
}

.variables-quick-grid::-webkit-scrollbar-thumb:hover {
    background: rgba(0, 0, 0, 0.3);
}

/* New CSS classes to replace inline styles */
.flow-main-container {
    height: 100%;
    width: 100%;
    position: relative;
}

.flow-container-fluid {
    height: 100%;
    width: 100%;
    padding: 10px;
}

.card2 {
    margin-top: 1rem;
    padding: 0;
}

.flow-name-input {
    width: 300px;
}

.flow-actions {
    display: flex;
    gap: 6px;
    align-items: center;
}

/* Undo/Redo Container */
.undo-redo-container {
    position: absolute;
    top: 21px;
    left: 130px;
    display: flex;
    align-items: center;
    gap: 4px;
    background: rgba(255, 255, 255, 0.9);
    border-radius: 10px;
    padding: 4px;
    border: 1px solid rgba(0, 0, 0, 0.08);
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
    z-index: 1201;
    backdrop-filter: blur(10px);
}

.undo-btn, .redo-btn {
    height: 32px !important;
    width: 32px !important;
    min-width: 32px !important;
    border-radius: 8px !important;
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1) !important;
}

.undo-btn:hover:not(:disabled), .redo-btn:hover:not(:disabled) {
    background: rgba(25, 118, 210, 0.08) !important;
    transform: scale(1.05);
    box-shadow: 0 2px 8px rgba(25, 118, 210, 0.15) !important;
}

.undo-btn:disabled, .redo-btn:disabled {
    opacity: 0.4 !important;
    cursor: not-allowed !important;
}

.undo-btn:active:not(:disabled), .redo-btn:active:not(:disabled) {
    transform: scale(0.95);
}

.undo-btn .v-icon, .redo-btn .v-icon {
    transition: all 0.2s ease;
}

.undo-btn:hover:not(:disabled) .v-icon, .redo-btn:hover:not(:disabled) .v-icon {
    color: #1976d2 !important;
}

.menu-list {
    border-radius: 15px;
    box-shadow: 3px 4px 10px rgba(0, 0, 0, 0.2);
}

.menu-divider {
    border: none;
    border-bottom: 1px solid #d5d5d5;
    margin: 6px 8px;
}

.save-btn {
    height: 40px;
    width: 100px;
    border-radius: 12px;
}

.node-panel {
    max-height: 100%;
}

/* Animation for smooth transitions */
.v-enter-active,
.v-leave-active {
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.v-enter-from,
.v-leave-to {
    opacity: 0;
    transform: translateY(-8px);
}

/* Priority Number Styling */
.priority-number {
    font-size: 0.8rem;
    color: #7018ff;
    font-weight: 500;
    background: rgba(219, 176, 255, 0.349);
    padding: 2px 6px;
    border-radius: 4px;
    min-width: 20px;
    text-align: center;
}

/* Success Dialog Styles */
.success-dialog {
    border-radius: 20px !important;
    overflow: hidden;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15) !important;
}

.success-dialog-header {
    background: linear-gradient(135deg, #10b943 0%, #047834 100%);
    color: white;
    padding: 24px !important;
    text-align: center;
    border-bottom: none;
}

.success-header-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
}

.success-icon {
    background: rgba(255, 255, 255, 0.2);
    border-radius: 50%;
    padding: 12px;
    animation: successPulse 2s infinite;
}

@keyframes successPulse {
    0% {
        transform: scale(1);
        box-shadow: 0 0 0 0 rgba(255, 255, 255, 0.4);
    }

    50% {
        transform: scale(1.05);
        box-shadow: 0 0 0 10px rgba(255, 255, 255, 0);
    }

    100% {
        transform: scale(1);
        box-shadow: 0 0 0 0 rgba(255, 255, 255, 0);
    }
}

.success-title {
    font-size: 1.5rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 1px;
}

.success-dialog-content {
    padding: 32px 24px !important;
    text-align: center;
    background: #fafafa;
}

.success-message {
    font-size: 1.1rem;
    font-weight: 600;
    color: #047857;
    margin-bottom: 24px;
    line-height: 1.5;
}

.success-details {
    display: flex;
    flex-direction: column;
    gap: 12px;
    background: white;
    padding: 16px;
    border-radius: 12px;
    border: 1px solid rgba(16, 185, 129, 0.1);
}

.success-detail-item {
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.875rem;
    color: #374151;
    font-weight: 500;
}

.success-detail-item .v-icon {
    color: #10b981;
}

.success-dialog-actions {
    padding: 20px 24px !important;
    background: white;
    border-top: 1px solid rgba(0, 0, 0, 0.06);
}

.success-ok-btn {
    height: 44px !important;
    padding: 0 24px !important;
    border-radius: 12px !important;
    font-weight: 600 !important;
    text-transform: none !important;
    background: linear-gradient(135deg, #10b943 0%, #047834 100%);
    box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3) !important;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
}

.success-ok-btn:hover {
    transform: translateY(-2px) !important;
    box-shadow: 0 8px 24px rgba(16, 185, 129, 0.4) !important;
}

.success-ok-btn:active {
    transform: translateY(0) !important;
}

/* Animation for dialog entrance */
.v-dialog-transition-enter-active {
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.v-dialog-transition-enter-from {
    transform: scale(0.8);
    opacity: 0;
}

/* Mobile responsive */
@media (max-width: 480px) {
    .success-dialog {
        margin: 16px;
    }

    .success-dialog-header {
        padding: 20px !important;
    }

    .success-dialog-content {
        padding: 24px 20px !important;
    }

    .success-title {
        font-size: 1.25rem;
    }

    .success-message {
        font-size: 1rem;
    }

    .success-details {
        padding: 12px;
    }

    .success-detail-item {
        font-size: 0.8rem;
    }
}

/* Confirmation Dialog Styles */
.confirmation-dialog {
    border-radius: 16px !important;
    overflow: hidden;
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.2) !important;
}

.confirmation-dialog .dialog-header {
    background: linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%);
    padding: 24px !important;
    border-bottom: 1px solid rgba(255, 183, 77, 0.2);
}

.confirmation-dialog .header-content {
    flex: 1;
}

.confirmation-dialog .text-h5 {
    color: #e65100;
    font-weight: 600;
}

.confirmation-dialog .dialog-content {
    padding: 24px !important;
    background: #fafafa;
}

.confirmation-content {
    text-align: center;
}

.confirmation-content .text-body-1 {
    color: #424242;
    font-size: 16px;
    line-height: 1.5;
    margin-bottom: 16px;
}

.confirmation-dialog .dialog-actions {
    background: white;
    border-top: 1px solid rgba(0, 0, 0, 0.08);
    padding: 20px 24px !important;
}

.confirmation-dialog .v-btn {
    border-radius: 8px;
    text-transform: none;
    font-weight: 500;
    padding: 0 20px;
    height: 40px;
}

.confirmation-dialog .v-btn--outlined {
    border-width: 2px;
}

.confirmation-dialog .v-btn--elevated {
    box-shadow: 0 4px 12px rgba(255, 152, 0, 0.3) !important;
}

.confirmation-dialog .v-btn--elevated:hover {
    box-shadow: 0 6px 16px rgba(255, 152, 0, 0.4) !important;
    transform: translateY(-1px);
}

.confirmation-dialog .v-btn--outlined:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(25, 118, 210, 0.2);
}

/* Alert styling in confirmation */
.confirmation-dialog .v-alert {
    border-radius: 12px;
    background: rgba(255, 193, 7, 0.1) !important;
    border: 1px solid rgba(255, 193, 7, 0.3);
}

.confirmation-dialog .v-alert .v-alert__content {
    color: #e65100;
    font-size: 14px;
}

/* Animation for dialog entrance */
.v-dialog-transition-enter-active .confirmation-dialog {
    animation: confirmationSlideIn 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
}

@keyframes confirmationSlideIn {
    from {
        opacity: 0;
        transform: translateY(-30px) scale(0.95);
    }
    to {
        opacity: 1;
        transform: translateY(0) scale(1);
    }
}

/* Mobile responsive for confirmation dialog */
@media (max-width: 480px) {
    .confirmation-dialog .dialog-header {
        padding: 20px !important;
    }
    
    .confirmation-dialog .dialog-content {
        padding: 20px !important;
    }
    
    .confirmation-dialog .dialog-actions {
        padding: 16px !important;
        flex-direction: column-reverse;
        gap: 12px;
    }
    
    .confirmation-dialog .v-btn {
        width: 100%;
        margin: 0 !important;
    }
    
    .confirmation-dialog .v-spacer {
        display: none;
    }
}

/* Invalid connection state */
:deep(.vue-flow__handle.invalid-connection) {
    background-color: #ef4444 !important;
    border-color: #dc2626 !important;
    cursor: not-allowed !important;
}

/* Valid connection state */
:deep(.vue-flow__handle.valid-connection) {
    background-color: #10b981 !important;
    border-color: #059669 !important;
    box-shadow: 0 0 0 2px rgba(16, 185, 129, 0.3) !important;
}

/* Connection preview line */
:deep(.vue-flow__connection-line) {
    stroke: #6366f1;
    stroke-width: 2;
    stroke-dasharray: 5, 5;
    animation: dash 1s linear infinite;
}

@keyframes dash {
    to {
        stroke-dashoffset: -10;
    }
}

/* Invalid connection preview */
:deep(.vue-flow__connection-line.invalid) {
    stroke: #ef4444;
    stroke-dasharray: 3, 3;
}

/* Success animation for valid connections */
@keyframes connectionSuccess {
    0% {
        stroke-width: 2;
    }

    50% {
        stroke-width: 4;
    }

    100% {
        stroke-width: 2;
    }
}

:deep(.vue-flow__edge.connection-success .vue-flow__edge-path) {
    animation: connectionSuccess 0.5s ease-in-out;
}

/* Selected Document Data Styles */
.selected-document-data {
    background: rgba(25, 118, 210, 0.04);
    border-radius: 12px;
    padding: 16px;
    border: 1px solid rgba(25, 118, 210, 0.1);
}

.selected-document-data-chip {
    margin: 2px !important;
    font-weight: 500 !important;
    display: inline-flex !important;
    align-items: center !important;
    max-width: 250px !important;
}

.selected-document-data-chip .chip-text {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    display: inline-block;
}

.selected-document-data-chip :deep(.v-chip__content) {
    display: flex !important;
    align-items: center !important;
    max-width: 100% !important;
    overflow: hidden !important;
}

.selected-document-data-chip :deep(.v-chip__close) {
    flex-shrink: 0 !important;
    margin-left: 4px !important;
    order: 2 !important;
}

.selected-document-data-chip :deep(.v-chip__content > span) {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    flex: 1;
    min-width: 0;
}

.custom-divider {
    border: none;
    border-top: 1px solid rgba(0, 0, 0, 0.1);
    margin: 0px 0px 20px 0px;
    width: 100%;
}

/* Enhanced Configuration Panel Styles */
.config-header {
    background: linear-gradient(135deg, #f8f9ff 0%, #e3f2fd 100%);
    border-radius: 16px;
    padding: 24px;
    border: 1px solid rgba(25, 118, 210, 0.1);
    margin-bottom: 24px;
}

.config-panels {
    border: none !important;
    gap: 16px;
}

.config-panels :deep(.v-expansion-panel::after) {
    display: none !important;
}

.panel-item {
    border-radius: 16px !important;
    overflow: hidden;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06) !important;
    border: 1px solid rgba(0, 0, 0, 0.08) !important;
    transition: all 0.3s ease;
}


.panel-title-custom {
    background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);
    padding: 0 24px !important;
}

.panel-icon-wrapper {
    width: 40px;
    height: 40px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(135deg, #1976D2, #1565C0);
    box-shadow: 0 2px 8px rgba(25, 118, 210, 0.3);
}

.panel-title {
    font-size: 16px;
    font-weight: 600;
    color: #1a1a1a;
    line-height: 1.2;
}

.panel-subtitle {
    font-size: 13px;
    color: #666;
    margin-top: 2px;
    line-height: 1.3;
}

.panel-content-wrapper {
    background: #ffffff;
}

.panel-content {
    background: #ffffff;
}

.config-section {
    position: relative;
}

.section-label {
    font-size: 14px;
    font-weight: 600;
    color: #1976D2;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-bottom: 16px;
    position: relative;
    padding-left: 12px;
}

.section-label::before {
    content: '';
    position: absolute;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
    width: 4px;
    height: 16px;
    background: linear-gradient(135deg, #1976D2, #1565C0);
    border-radius: 2px;
}

.config-input {
    margin-bottom: 0px;
}

.config-input :deep(.v-field) {
    border-radius: 12px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
    transition: all 0.3s ease;
}

.config-input :deep(.v-field:hover) {
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.12);
}

.config-input :deep(.v-field--focused) {
    box-shadow: 0 0 0 2px rgba(25, 118, 210, 0.2);
}

/* Enhanced Option Cards */
.option-cards-row {
    gap: 16px;
}

.option-card {
    border-radius: 16px !important;
    transition: all 0.3s ease;
    border: 2px solid rgba(0, 0, 0, 0.08) !important;
    background: linear-gradient(135deg, #ffffff 0%, #fafafa 100%);
}

.option-card:hover {
    border-color: rgba(25, 118, 210, 0.3) !important;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12) !important;
    transform: translateY(-2px);
}

.option-card.option-active {
    border-color: rgba(25, 118, 210, 0.6) !important;
    background: linear-gradient(135deg, #e3f2fd 0%, #f3e5f5 100%);
    box-shadow: 0 4px 16px rgba(25, 118, 210, 0.2) !important;
}

.option-title {
    font-size: 15px;
    font-weight: 600;
    color: #1a1a1a;
    line-height: 1.2;
}

.option-description {
    font-size: 13px;
    color: #666;
    margin-top: 4px;
    line-height: 1.4;
}

/* Memory Toggle Card */
.memory-toggle-card {
    border-radius: 12px !important;
    transition: all 0.3s ease;
}

.memory-toggle-card.toggle-active {
    border-color: rgba(76, 175, 80, 0.5) !important;
    background: linear-gradient(135deg, #e8f5e8 0%, #f1f8e9 100%);
}

.memory-label {
    font-size: 14px;
    font-weight: 600;
    color: #1a1a1a;
}

.memory-description {
    font-size: 12px;
    color: #666;
    margin-top: 2px;
}

/* Switch Option Compact Styles */
.switch-option-compact {
    border: 1px solid rgba(0, 0, 0, 0.08);
    border-radius: 12px;
    background: #ffffff;
    transition: all 0.3s ease;
}

.switch-option-compact:hover {
    border-color: rgba(25, 118, 210, 0.3);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.switch-active-bg {
    background: linear-gradient(135deg, rgba(25, 118, 210, 0.05) 0%, rgba(25, 118, 210, 0.02) 100%) !important;
    border-color: rgba(25, 118, 210, 0.3) !important;
}

/* Compact inline switch styles */
.switch-option-inline {
    display: inline-block;
}

.switch-active-bg-compact {
    background: linear-gradient(135deg, rgba(94, 53, 177, 0.08) 0%, rgba(94, 53, 177, 0.04) 100%) !important;
    border: 1px solid rgba(94, 53, 177, 0.2) !important;
    border-radius: 8px !important;
}

.switch-title-compact {
    font-size: 12px;
    font-weight: 600;
    color: #1a1a1a;
    line-height: 1.1;
    white-space: nowrap;
}

.switch-description-compact {
    font-size: 10px;
    color: #666;
    line-height: 1.2;
    white-space: nowrap;
}

/* Action buttons container responsive layout */
.action-buttons-container {
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.base-prompt-toggle-section {
    width: 100%;
}

.base-prompt-toggle-section .v-card {
    border: 1px solid rgba(58, 183, 73, 0.2);
    transition: all 0.2s ease;
}

.base-prompt-toggle-section .v-card:hover {
    border-color: rgba(35, 140, 49, 0.4);
    box-shadow: 0 2px 8px rgba(58, 183, 77, 0.1);
}

.load-model-button-wrapper {
    display: flex;
    justify-content: flex-end;
}

/* Responsive adjustments for action buttons */
@media (max-width: 600px) {
    .load-model-button-wrapper {
        justify-content: stretch;
    }
    
    .load-model-btn {
        width: 100%;
    }
    
    .switch-title-compact {
        font-size: 11px;
    }
    
    .switch-description-compact {
        font-size: 9px;
    }
    
    .toggle-text-container {
        min-width: 120px;
    }
}

@media (max-width: 400px) {
    .base-prompt-toggle-wrapper .switch-option-inline > div {
        flex-direction: column;
        align-items: flex-start;
        padding: 8px 12px;
    }
    
    .toggle-text-container {
        margin: 4px 0;
        min-width: auto;
    }
    
    .switch-title-compact,
    .switch-description-compact {
        white-space: normal;
    }
}

.switch-title {
    font-size: 14px;
    font-weight: 600;
    color: #1a1a1a;
    line-height: 1.2;
}

.switch-description {
    font-size: 12px;
    color: #666;
    margin-top: 2px;
    line-height: 1.3;
}

/* Responsive adjustments */
@media (max-width: 768px) {
    .config-header {
        padding: 16px;
        margin-bottom: 16px;
    }

    .panel-content {
        padding: 16px !important;
    }

    .panel-title-custom {
        padding: 0 16px !important;
    }

    .option-cards-row {
        gap: 12px;
    }

    /* Smart Intent responsive styles */
    .smart-intent-card .v-card-title {
        padding: 12px 16px !important;
        font-size: 1rem !important;
    }

    .smart-intent-card .v-card-text {
        padding: 16px !important;
    }

    .smart-intent-expansion .v-expansion-panel-title {
        padding: 12px 16px !important;
        min-height: 48px !important;
    }
}

/* Line Configuration Specific Styles */
.line-option-card {
    border-radius: 12px !important;
    transition: all 0.3s ease;
}

.line-option-card.option-active {
    border-color: rgba(76, 175, 80, 0.5) !important;
    background: linear-gradient(135deg, #e8f5e8 0%, #f1f8e9 100%);
}

/* System Prompt Specific Styles */
.prompt-section {
    position: relative;
}

.prompt-card {
    border-radius: 16px !important;
    overflow: hidden;
    border: 2px solid rgba(0, 0, 0, 0.08) !important;
    transition: all 0.3s ease;
}

.prompt-card:hover {
    border-color: rgba(25, 118, 210, 0.3) !important;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12) !important;
}

.prompt-textarea :deep(.v-field) {
    border: none !important;
    box-shadow: none !important;
    background: transparent !important;
}

.prompt-textarea :deep(.v-field__field) {
    padding: 0px !important;
}

.prompt-textarea :deep(.v-field__input) {
    font-family: 'Roboto Mono', monospace !important;
    font-size: 14px !important;
    line-height: 1.6 !important;
}

.tips-card {
    border-radius: 12px !important;
    border: 1px solid rgba(96, 125, 139, 0.2) !important;
}

.tips-card ul {
    margin: 0;
    padding-left: 16px;
}

.tips-card li {
    margin-bottom: 4px;
}

.tips-card li:last-child {
    margin-bottom: 0;
}

/* Prompt Template Styles */
.prompt-template-container {
    padding: 0.5rem 0;
}

.prompt-editor-card {
    border: 1px solid rgba(0, 0, 0, 0.12);
    border-radius: 8px;
    transition: all 0.2s ease;
}

.prompt-editor-card:hover {
    border-color: rgba(25, 118, 210, 0.5);
    box-shadow: 0 1px 3px rgba(227, 6, 6, 0.05);
}

.prompt-textarea {
    font-family: 'Roboto Mono', monospace;
    line-height: 1.5;
    border: none !important;
    box-shadow: none !important;
    padding: 0px 5px !important;
    font-size: 14px;
    min-height: 300px !important;
}

.prompt-editor-card {
    border: 1px solid rgba(0, 0, 0, 0.12) !important;
    box-shadow: none !important;
    transition: all 0.2s ease;
    overflow: hidden;
}

.prompt-editor-card:hover {
    border-color: rgba(25, 118, 210, 0.5) !important;
}

.prompt-templates-card {
    border-radius: 8px;
    border: 1px solid rgba(0, 0, 0, 0.12);
    transition: all 0.3s ease;
}

.template-list-item {
    border-radius: 4px;
    margin-bottom: 2px;
    transition: all 0.2s ease;
}

.template-list-item:hover {
    background-color: rgba(25, 118, 210, 0.04);
}

/* Selection Info Styles */
.selection-info {
    position: absolute;
    bottom: 20px;
    left: 160px;
    z-index: 1000;
    display: flex;
    align-items: flex-start;
    pointer-events: none;
}

.selection-info .v-chip {
    pointer-events: auto;
    margin: 0;
}

.prompt-template-list {
    max-height: 500px;
    overflow-y: auto;
}

.template-list-all {
    max-height: 400px;
    overflow-y: auto;
}

/* Knowledge Sources Styles */
.knowledge-sources-grid {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

/* SharePoint Configuration Subsection Styles */
.subsection {
    background: rgba(248, 250, 252, 0.6);
    border: 1px solid rgba(0, 0, 0, 0.08);
    border-radius: 8px;
    padding: 16px;
    margin-bottom: 16px;
    transition: all 0.2s ease;
}

.subsection:hover {
    /* background: rgba(248, 250, 252, 0.8);
    border-color: rgba(25, 118, 210, 0.2); */
}

.subsection-header {
    display: flex;
    align-items: center;
    margin-bottom: 8px;
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);
    padding-bottom: 8px;
}

.subsection-title {
    font-size: 14px;
    font-weight: 600;
    color: #1a1a1a;
    letter-spacing: 0.025em;
}

.subsection .modern-input:last-child {
    margin-bottom: 0 !important;
}

/* Color coding for different subsections */
.subsection:nth-child(2) {
    background: rgba(25, 118, 210, 0.02);
    border-color: rgba(25, 118, 210, 0.1);
}

.subsection:nth-child(3) {
    background: rgba(76, 175, 80, 0.02);
    border-color: rgba(76, 175, 80, 0.1);
}

.subsection:nth-child(4) {
    background: rgba(33, 150, 243, 0.02);
    border-color: rgba(33, 150, 243, 0.1);
}

.knowledge-source-card {
    background-color: white;
    transition: all 0.2s ease;
}

.knowledge-source-card .border {
    border: 1px solid #e0e0e0 !important;
    transition: border-color 0.2s ease, background-color 0.2s ease;
}

.knowledge-source-card .border:hover {
    border-color: #1976d2 !important;
}

.knowledge-source-frame {
    display: flex; 
    align-items: center; 
    justify-content: space-between;
    flex-direction: column;
    padding: 5px;
}
.knowledge-source-title {
    width: 100%;
    display: flex;
    justify-content: space-between;
    padding: 5px;
}
.knowledge-source-config {
    width: 100%;
    padding: 5px;
}
</style>